import React, { useState, useMemo, useContext, useEffect, useRef, useCallback, useTransition } from "react";
import { DatabaseContext } from "../../../redux/DatabaseStore";
import { getModuleSettingValue, getCurrency } from "../../../utils/moduleSettings";
import { formatCurrency } from "../../../utils/currencyUtils";
import { Card } from "primereact/card";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "../../../components/PrimeCalendar";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import { InputSwitch } from "primereact/inputswitch";
import { TreeTable } from 'primereact/treetable';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { TabView, TabPanel } from "primereact/tabview";
import { Divider } from "primereact/divider";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { useReactToPrint } from "react-to-print";
import { confirmDialog } from "primereact/confirmdialog";
import UtilContext from "../../../components/UtilContext";
import { LocaleContext } from "../../../components/LocaleContext";
import { getRequest, postRequest } from '../../../api/ApiController';
import { defaultProductLogoSrc } from '../../../constants/brandingDisplay';
import { resolvePrintCompanyLogoSrc } from '../../../utils/companyBranding';

/* ========= UTIL ========= */
// Sumy na 2 desatinné miesta – výpočty aj zobrazenie (mena z globálnych Jednotiek)
const roundEur = (n) => Math.round(Number(n || 0) * 100) / 100;

const FORMAT_INT = (n) =>
  new Intl.NumberFormat("sk-SK", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n || 0);

const num = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v)
    .replace(/\u00A0/g, "")
    .replace(/\s/g, "")
    .replace(/€/g, "")
    .replace(/\$/g, "")
    .replace(/,/g, ".");
  const n = Number(s.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const ERROR_TYPE_VALUES = [
  { value: "Interná chyba", labelKey: "OTKEvaluation.InterneChyby" },
  { value: "Externá chyba", labelKey: "OTKEvaluation.ExterneChyby" },
  { value: "Reklamácia", labelKey: "OTKEvaluation.Reklamacie" },
];
const getErrorTypes = (t) => ERROR_TYPE_VALUES.map((x) => ({ value: x.value, label: t(x.labelKey) }));

/* ========= FILTER INPUTS ========= */
const SearchFilterInput = ({ value, onChange }) => (
  <span className="relative w-full">
    <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: "0.9rem" }} />
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="p-inputtext p-component w-full h-[42px] pl-9"
    />
  </span>
);

const CurrencyFilterInput = ({ value, onChange }) => (
  <span className="relative w-full">
    <i className="pi pi-euro absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: "0.9rem" }} />
    <input
      type="text"
      inputMode="decimal"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="p-inputtext p-component w-full h-[42px] pl-9"
    />
  </span>
);

  /* ========= TIME HELPERS (mesiace) ========= */
  // --- Bezpečné parsovanie DB timestampu (lokálny čas) ---
  // Ak tvoje DB časy sú v UTC, pozri nižšie variant USE_UTC = true.
  const USE_UTC = false;

  const parseDbTimestamp = (ts) => {
    if (!ts) return null;
    // Ak už je to Date objekt, rovno ho vráť
    if (ts instanceof Date && !isNaN(ts.getTime())) {
      return ts;
    }
    const str = String(ts).trim();
    // 1) Pokus o klasický formát "YYYY-MM-DD HH:mm:ss" alebo "YYYY-MM-DDTHH:mm:ss"
    const m = str.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/
    );
    if (m) {
      const [, Y, M, D, h = "0", mi = "0", s = "0"] = m;
      const year = Number(Y);
      const month = Number(M);
      const day = Number(D);
      const hour = Number(h);
      const minute = Number(mi);
      const sec = Number(s);
      if ([year, month, day].some((x) => !Number.isFinite(x))) return null;
      return USE_UTC
        ? new Date(Date.UTC(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, sec || 0, 0))
        : new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, sec || 0, 0);
    }
    // 2) Fallback – skús nechať JS, nech to naparsuje (ISO stringy atď.)
    const d = new Date(str);
    if (isNaN(d.getTime())) return null;
    return d;
  };

  const ymKeyLocal = (dt) => dt.getFullYear() * 12 + dt.getMonth();
  const ymKeyUTC   = (dt) => dt.getUTCFullYear() * 12 + dt.getUTCMonth();
  const getYMKey = (dt) => (USE_UTC ? ymKeyUTC(dt) : ymKeyLocal(dt));

  // vždy vytvorí t('OTKEvaluation.Mesiacovy') Date (1. deň v mesiaci)
  const monthDate = (y, m) => new Date(y, m, 1);

  const addMonths = (d, n) =>
    monthDate(d.getFullYear(), d.getMonth() + n);

  // Default: minulý mesiac (MM/YYYY–MM/YYYY). Bez porovnania = len tento range.
  const getDefaultLeftRange = (now = new Date()) => {
    const prev = addMonths(now, -1);      // predošlý mesiac
    return [prev, prev];                  // single mesiac ako range
  };

  // Pri zapnutom „Porovnať s obdobím“: pravý stĺpec = rok dozadu (mesiac pred rokom → minulý mesiac).
  const getDefaultRightYearRange = (now = new Date()) => {
    const start = addMonths(now, -12);    // mesiac pred rokom (napr. 02/2025)
    const end   = addMonths(now, -1);     // predošlý mesiac (napr. 01/2026)
    return [start, end];                  // 12 mesiacov: 02/2025–01/2026
  };

  const getDefaultTrendRange = getDefaultLeftRange;

// Bezpečná koercia na [Date|null, Date|null] + normalizácia poradia
const getRangeEnds = (rangeVal) => {
  if (!rangeVal) return [null, null];

  // ak by ti Calendar vrátil Date namiesto [Date, null]
  if (rangeVal instanceof Date) return [rangeVal, null];

  if (Array.isArray(rangeVal)) {
    const [s, e] = rangeVal;

    // dôležité: NEVYHADZUJ start len preto, že end je null
    return [s ?? null, e ?? null];
  }

  return [null, null];
};

// Range filter po mesiacoch (ak je len 1 dátum, berie sa ako single mesiac)
const filterRecordsByRangeMonths = (records, rangeVal) => {
  const [s, e] = getRangeEnds(rangeVal);

  if (!s && !e) return records;

  const a = getYMKey(s ?? e);   // ak je len jeden koniec, použije sa ten
  const b = getYMKey(e ?? s);
  const [lo, hi] = a <= b ? [a, b] : [b, a];

  return records.filter((r) => {
    const dt = r?.created_at ? parseDbTimestamp(r.created_at) : null;
    if (!dt) return false;
    const k = getYMKey(dt);
    return k >= lo && k <= hi;
  });
};

// --- TAB 2 helpers ---
// parse "YYYY-MM" alebo "MM/YYYY" -> Date(YYYY, MM-1, 1)
const parsePeriodToDate = (p) => {
  if (!p) return null;
  const s = String(p).trim();
  if (/^\d{4}-\d{1,2}$/.test(s)) {
    const [y, m] = s.split("-").map(Number);
    return new Date(y, (m || 1) - 1, 1);
  }
  if (/^\d{1,2}\/\d{4}$/.test(s)) {
    const [m, y] = s.split("/").map(Number);
    return new Date(y, (m || 1) - 1, 1);
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : new Date(dt.getFullYear(), dt.getMonth(), 1);
};

const statToMonthDate = (r) => {
  if (r?.year != null && r?.month != null) return new Date(r.year, r.month - 1, 1);
  if (r?.period) {
    const d = parsePeriodToDate(r.period);
    if (d) return d;
  }
  if (r?.created_at) {
    const d = new Date(r.created_at);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  return null;
};

const filterCompanyStatsByMonth = (stats, monthDate) => {
  if (!monthDate) return [];
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  return (stats || []).filter((r) => {
    const d = statToMonthDate(r);
    return d && d.getFullYear() === y && d.getMonth() === m;
  });
};

const filterCompanyStatsByMonthRange = (stats, startM, endM) => {
  if (!startM || !endM) return [];
  const s = new Date(startM.getFullYear(), startM.getMonth(), 1);
  const e = new Date(endM.getFullYear(), endM.getMonth(), 1);
  return (stats || []).filter((r) => {
    const d = statToMonthDate(r);
    return d && d >= s && d <= e;
  });
};

const filterCompanyStatsByRangeMonths = (stats, rangeVal, maybeEnd) => {
  // Podpora 2 spôsobov volania:
  // 1) filterCompanyStatsByRangeMonths(stats, rangeVal)  kde rangeVal = Date | [Date,Date]
  // 2) filterCompanyStatsByRangeMonths(stats, startDate, endDate)

  let s = null;
  let e = null;

  // varianta (start, end)
  if (rangeVal instanceof Date && (maybeEnd instanceof Date || maybeEnd === null || maybeEnd === undefined)) {
    s = rangeVal;
    e = maybeEnd ?? rangeVal; // ak nie je end, ber ako single mesiac
  } else {
    // varianta (rangeVal)
    [s, e] = getRangeEnds(rangeVal);
  }

  if (!s && !e) return [];

  // ak je len jeden dátum
  if (s && !e) e = s;
  if (!s && e) s = e;

  // single mesiac
  if (s && e && s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return filterCompanyStatsByMonth(stats, s);
  }

  // range mesiacov
  return filterCompanyStatsByMonthRange(stats, s, e);
};

const fmtMonth = (d) =>
  d ? new Intl.DateTimeFormat("sk-SK", { month: "2-digit", year: "numeric" }).format(d) : "";

const fmtMonthRange = (rangeVal) => {
  const [s, e] = getRangeEnds(rangeVal);
  if (!s && !e) return "";
  if (s && e && s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return fmtMonth(s);
  }
  return `${fmtMonth(s)} – ${fmtMonth(e)}`;
};


// PassThrough pre hlavičky (tvoj pôvodný)
const PT_HEADER = {
  headerContent: { style: { display: 'flex', alignItems: 'center', gap: '0.25rem' } },
  headerTitle:   { style: { whiteSpace: 'nowrap' } },
  sortIcon:      { className: 'ml-1' }
};

/* ========= COMPONENT ========= */
export default function OTKEvaluation() {
  const { t } = useContext(LocaleContext);
  const ERROR_TYPES = getErrorTypes(t);
  const [state, dispatch] = useContext(DatabaseContext);
  const {toast,setDialogText,setVisible} = useContext(UtilContext);

  const errorRecords = state.error_records || [];
  const departments = state.departments || [];
  const employees = state.employees || [];
  const groups = state.groups || [];
  const errorTypes = state.error_types || [];
  const companies = state.companies || [];
  const companyStats = state.company_statistics || [];
  const rootCauses = state.root_causes || [];
  const customers = state.companies.filter(comp => comp.types.includes("customer")) || [];

  const otkPrintLogoSrc = useMemo(
    () => resolvePrintCompanyLogoSrc(state?.branding, defaultProductLogoSrc),
    [state?.branding]
  );

  // ===== nastavenia OTK – limity pre trend chybovosti v čase (module_settings: module = 'otk') =====
  const moduleSettings = state.module_settings || [];
  const currencySymbol = getCurrency(moduleSettings);
  const FORMAT_CURRENCY = (n) => formatCurrency(roundEur(n), currencySymbol);

  const getOtkSettingNumber = (key, defaultValue) => {
    const raw = getModuleSettingValue(moduleSettings, "otk", key, String(defaultValue));
    const normalized = String(raw ?? "").replace(",", ".").trim();
    const n = parseFloat(normalized);
    if (Number.isFinite(n)) return n;
    const defNorm = String(defaultValue ?? "").replace(",", ".").trim();
    const defNum = parseFloat(defNorm);
    return Number.isFinite(defNum) ? defNum : 0;
  };

  // default values podľa zadania
  const trendOrdersPctLimit   = getOtkSettingNumber("trend_claim_orders_percent", 2);
  const trendOrdersCountLimit = getOtkSettingNumber("trend_claim_orders_count", 80);
  const trendPartsPctLimit    = getOtkSettingNumber("trend_claim_parts_percent", 2);
  const trendPartsCountLimit  = getOtkSettingNumber("trend_claim_parts_count", 20);
  const trendCostPctLimit     = getOtkSettingNumber("trend_repair_costs_percent", 0.3);
  const trendCostValueLimit   = getOtkSettingNumber("trend_repair_costs_value", 20000);

  /* ====== toolbar filtre ====== */
  const [kinds, setKinds] = useState(ERROR_TYPES.map((k) => k.value));
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentsTouched, setDepartmentsTouched] = useState(false);
  const [selectedStates, setSelectedStates] = useState(["Uznaná"]);
  const [selectedCustomers, setSelectedCustomers] = useState([]); // string[] id-čiek
  const [customersTouched, setCustomersTouched] = useState(false);

  /* ====== progressive loading: dáta pre Vyhodnotenie načítame len tu, nie do globálneho state ====== */
  const [evaluationRecords, setEvaluationRecords] = useState([]);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);
  const [evaluationFetchKey, setEvaluationFetchKey] = useState(0);

  const fetchEvaluationData = useCallback(() => {
    if (departmentsTouched && selectedDepartments.length === 0) {
      setEvaluationRecords([]);
      return;
    }
    const params = new URLSearchParams();
    (kinds || []).forEach((k) => params.append("kinds[]", k));
    (selectedStates || []).forEach((s) => params.append("states[]", s));
    (selectedDepartments || []).forEach((d) => params.append("department_names[]", d));
    if ((selectedCustomers || []).length > 0) {
      (selectedCustomers || []).forEach((c) => params.append("customer_ids[]", c));
    }
    setEvaluationLoading(true);
    setEvaluationError(null);
    getRequest(`/api/error_records/evaluation?${params.toString()}`)
      .then((response) => {
        const data = response?.data ?? response;
        setEvaluationRecords(Array.isArray(data) ? data : (data?.data ?? []));
      })
      .catch((err) => {
        setEvaluationError(err?.message || t('OTKEvaluation.NepodariloSaNacitatData'));
        setEvaluationRecords([]);
      })
      .finally(() => setEvaluationLoading(false));
  }, [kinds, selectedDepartments, selectedStates, selectedCustomers, departmentsTouched]);

  useEffect(() => {
    fetchEvaluationData();
  }, [fetchEvaluationData, evaluationFetchKey]);

  /* ====== tlač ====== */
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [printDialogVisible, setPrintDialogVisible] = useState(false);
  const [printDialogTab, setPrintDialogTab] = useState(0); // 0 = Tab1 (oddelenia), 1 = Tab2 (reklamácie)
  const [printSelection, setPrintSelection] = useState(["all"]); // pole kľúčov: "all" alebo konkrétne tabuľky
  const [printChartFontSize, setPrintChartFontSize] = useState(null);
  const printContainerRef = useRef(null); // kontajner pre klony pri tlači viacerých tabuliek
  const refAllTablesTab1 = useRef();
  const refAllTablesTab2 = useRef();
  const refDeptCounts = useRef();
  const refDeptCountPieces = useRef();
  const refDeptCosts = useRef();
  const refRootCauseCounts = useRef();
  const refTypeGroup = useRef();
  const refGroupType = useRef();
  const refTypeDept = useRef();
  const refDeptGroup = useRef();
  const refClaimedOrders = useRef();
  const refClaimedPieces = useRef();
  const refRepairCost = useRef();
  const refTrendPeriod = useRef();

const [activeTabIndex, setActiveTabIndex] = useState(0);

const [expandedKeysLeft,  setExpandedKeysLeft]  = useState({});
const [expandedKeysRight, setExpandedKeysRight] = useState({});


  // 1) Počet zákazok
const [expandedCountsLeft,  setExpandedCountsLeft]  = useState({});
const [expandedCountsRight, setExpandedCountsRight] = useState({});

// 2) Počet kusov
const [expandedPiecesLeft,  setExpandedPiecesLeft]  = useState({});
const [expandedPiecesRight, setExpandedPiecesRight] = useState({});

// 3) Náklady
const [expandedCostsLeft,  setExpandedCostsLeft]  = useState({});
const [expandedCostsRight, setExpandedCostsRight] = useState({});

// 3) Koreňová príčina
const [expandedRcLeft, setExpandedRcLeft] = useState({});
const [expandedRcRight, setExpandedRcRight] = useState({});

// 4/5) Druh chyby
const [expandedGroupTypeLeft,  setExpandedGroupTypeLeft]  = useState({});
const [expandedGroupTypeRight, setExpandedGroupTypeRight] = useState({});


const [expandRootCauseDialogVisible, setExpandRootCauseDialogVisible] = useState(false);
const [expandGroupTypeDialogVisible, setExpandGroupTypeDialogVisible] = useState(false);
const [expandTypeDeptDialogVisible, setExpandTypeDeptDialogVisible] = useState(false);

// spoločný expand dialog
const [expandDialogVisible, setExpandDialogVisible] = useState(false);
// ktorý strom sa má expandnúť: 'counts' | 'pieces' | 'costs' | null
const [expandDialogTarget, setExpandDialogTarget] = useState(null);

const [expandDialogContext, setExpandDialogContext] = useState(null);


// pre t('OTKEvaluation.PocetZakazok') (Spolu = total)
const [countsSortMeta, setCountsSortMeta] = useState([
  { field: 'total', order: -1 }
]);

// pre t('OTKEvaluation.PocetKusov')
const [piecesSortMeta, setPiecesSortMeta] = useState([
  { field: 'pieces', order: -1 }
]);

// pre t('OTKEvaluation.NakladyZaOddelenie')
const [totalCostsSortMeta, setTotalCostsSortMeta] = useState([
  { field: 'totalCosts', order: -1 }
]);

// pre t('OTKEvaluation.KorenovaPricina')
const [rcSortMeta, setRcSortMeta] = useState([
  { field: 'total', order: -1 }
]);

// pre t('OTKEvaluation.StlpecNaklady')
const [costsSortMeta, setCostsSortMeta] = useState([
  { field: 'cost', order: -1 }
]);

//reklamované zákazky
const [claimedOrdersSortMeta, setClaimedOrdersSortMeta] = useState([
  { field: 'ordersCount', order: -1 }
]);

//reklamované kusy
const [claimedPiecesSortMeta, setClaimedPiecesSortMeta] = useState([
  { field: 'partsCount', order: -1 }
]);

//náklady na opravy
const [repairCostSortMeta, setRepairCostSortMeta] = useState([
  { field: 'costEUR', order: -1 }
]);

// default sorty (DESC)
const [trendSortMetaAbs, setTrendSortMetaAbs] = useState([
  { field: "claimsCost", order: -1 },
]);

const [trendSortMetaPct, setTrendSortMetaPct] = useState([
  { field: "costPct", order: -1 },
]);

const [summarySortMeta, setSummarySortMeta] = useState([
  { field: 'costEUR', order: -1 },
])

  // rows -> TreeTable uzly (oddelenie + zamestnanci)
  // strom pre ľavú/pravú tabuľku; ponechaj departmentName!
const toTreeCounts = (rows, side) =>
  rows.map((r, i) => {
    const nodeKey = `${side}-${r._key}`;
    return {
    key: nodeKey,
    data: {
      key: nodeKey,
      departmentId: r.departmentId ?? r.department?.id ?? null,
      departmentName: r.departmentName,
      internal: r.internal ?? 0,
      external: r.external ?? 0,
      complaints: r.complaints ?? 0,
      total: r.total ?? 0,
      type: r.type || 'department',
    },
    children: (r.employees ? [...r.employees.values()] : []).map((empWrapper, k) => {

      const empInternal   = empWrapper.counts?.['Interná chyba'] ?? 0;
      const empExternal   = empWrapper.counts?.['Externá chyba'] ?? 0;
      const empComplaints = empWrapper.counts?.['Reklamácia'] ?? 0;
      const empTotal      = empWrapper.counts?.total ?? (empInternal + empExternal + empComplaints);

      const empKey = `${side}-${r._key}-emp-${k}`;
      const employeeNode = {
        key: empKey,
        data: {
          key: empKey,
          departmentId: r.departmentId ?? r.department?.id ?? null,
          employeeId: empWrapper.employee?.id ?? null,
          departmentName: empWrapper.employee?.name || empWrapper.name || '',
          internal: empInternal,
          external: empExternal,
          complaints: empComplaints,
          total: empTotal,
          type: 'employee',
        },
        children: [],
      };

      const articleValues = empWrapper.articles ? [...empWrapper.articles.values()] : [];

      employeeNode.children = articleValues.map((art, artIndex) => {
        const artInternal   = art.counts?.['Interná chyba'] ?? 0;
        const artExternal   = art.counts?.['Externá chyba'] ?? 0;
        const artComplaints = art.counts?.['Reklamácia'] ?? 0;
        const artTotal      = art.counts?.total ?? (artInternal + artExternal + artComplaints);

        const artKey = `${side}-${r._key}-emp-${k}-art-${artIndex}`;
        return {
          key: artKey,
          data: {
            key: artKey,
            departmentId: employeeNode.data.departmentId,
            employeeId: employeeNode.data.employeeId,
            articleId: art.articleId ?? art.article_number ?? null,
            articleName: art.articleName ?? '',
            // v stĺpci „Oddelenie“ zobrazujeme článok (číslo / názov)
            departmentName: art.articleId ?? art.article_number ?? '',
            internal: artInternal,
            external: artExternal,
            complaints: artComplaints,
            total: artTotal,
            type: 'article',
          },
          leaf: true,
        };
      });

      return employeeNode;
    }),
  };
  });


    // z Map z aggregateByDept vyrob TreeTable uzly (oddelenia + zamestnanci)
const toTreeCost = (rows, side) =>
  rows.map((r, i) => {
    const rid = r._key ?? i;

    const empArr = Array.isArray(r.employees)
      ? r.employees
      : [...(r.employees?.values?.() ?? [])];

    const departmentId = r.departmentId ?? r.department?.id ?? null;
    const deptKey = `${side}-${rid}`;

    // 1. úroveň – oddelenie
    return {
      key: deptKey,
      data: {
        key: deptKey,
        departmentId,
        departmentName: r.departmentName,

        internalCost: Number(
          r.internalCost ??
            r.costs?.["Interná chyba"] ??
            0
        ),
        externalCost: Number(
          r.externalCost ??
            r.costs?.["Externá chyba"] ??
            0
        ),
        complaintsCost: Number(
          r.complaintsCost ??
            r.costs?.["Reklamácia"] ??
            0
        ),
        totalCosts: Number(
          r.totalCosts ??
            r.costs?.total ??
            ((r.costs?.["Interná chyba"] ?? 0) +
              (r.costs?.["Externá chyba"] ?? 0) +
              (r.costs?.["Reklamácia"] ?? 0))
        ),

        type: r.type || "department",
      },

      // 2. + 3. úroveň – zamestnanec → artikel
      children: empArr.map((empWrapper, k) => {
        const eInternalCost = Number(
          empWrapper.internalCost ??
            empWrapper.costs?.["Interná chyba"] ??
            0
        );
        const eExternalCost = Number(
          empWrapper.externalCost ??
            empWrapper.costs?.["Externá chyba"] ??
            0
        );
        const eComplaintsCost = Number(
          empWrapper.complaintsCost ??
            empWrapper.costs?.["Reklamácia"] ??
            0
        );
        const eTotalCosts = Number(
          empWrapper.totalCosts ??
            empWrapper.costs?.total ??
            ((empWrapper.costs?.["Interná chyba"] ?? 0) +
              (empWrapper.costs?.["Externá chyba"] ?? 0) +
              (empWrapper.costs?.["Reklamácia"] ?? 0))
        );

        const employeeId = empWrapper.employee?.id ?? null;
        const empKey = `${side}-${rid}-emp-${k}`;

        const employeeNode = {
          key: empKey,
          data: {
            key: empKey,
            departmentId,
            employeeId,
            departmentName:
              empWrapper.name ||
              empWrapper.employee?.full_name ||
              empWrapper.employee?.name ||
              "",
            internalCost:   eInternalCost,
            externalCost:   eExternalCost,
            complaintsCost: eComplaintsCost,
            totalCosts:     eTotalCosts,
            type: "employee",
          },
          children: [],
        };

        const articleArr = empWrapper.articles
          ? [...empWrapper.articles.values()]
          : [];

        employeeNode.children = articleArr.map((art, artIndex) => {
          const aInternalCost = Number(
            art.costs?.["Interná chyba"] ?? 0
          );
          const aExternalCost = Number(
            art.costs?.["Externá chyba"] ?? 0
          );
          const aComplaintsCost = Number(
            art.costs?.["Reklamácia"] ?? 0
          );
          const aTotalCosts = Number(
            art.totalCosts ??
              art.costs?.total ??
              ((art.costs?.["Interná chyba"] ?? 0) +
                (art.costs?.["Externá chyba"] ?? 0) +
                (art.costs?.["Reklamácia"] ?? 0))
          );

          const artKey = `${side}-${rid}-emp-${k}-art-${artIndex}`;
          return {
            key: artKey,
            data: {
              key: artKey,
              departmentId,
              employeeId,
              articleId:   art.articleId ?? art.article_number ?? null,
              articleName: art.articleName ?? "",
              departmentName: art.articleId ?? art.article_number ?? "",
              internalCost:   aInternalCost,
              externalCost:   aExternalCost,
              complaintsCost: aComplaintsCost,
              totalCosts:     aTotalCosts,
              type: "article",
            },
            leaf: true,
          };
        });

        return employeeNode;
      }),
    };
  });

const toTreePieces = (rows, side) =>
  rows.map((r, i) => {
    const rid = r._key ?? i;

    const empArr = Array.isArray(r.employees)
      ? r.employees
      : [...(r.employees?.values?.() ?? [])];

    const departmentId = r.departmentId ?? r.department?.id ?? null;
    const deptKey = `${side}-${rid}`;

    // 1. úroveň – oddelenie
    return {
      key: deptKey,
      data: {
        key: deptKey,
        departmentId,
        departmentName: r.departmentName,
        internal:   Number(r.internal   ?? r.piecesByType?.["Interná chyba"] ?? 0),
        external:   Number(r.external   ?? r.piecesByType?.["Externá chyba"] ?? 0),
        complaints: Number(r.complaints ?? r.piecesByType?.["Reklamácia"]    ?? 0),
        pieces:     Number(
          r.pieces ??
          r.piecesTotal ??
          ((r.piecesByType?.["Interná chyba"] ?? 0) +
           (r.piecesByType?.["Externá chyba"] ?? 0) +
           (r.piecesByType?.["Reklamácia"]    ?? 0))
        ),
        type: r.type || "department",
      },
      children: empArr.map((empWrapper, k) => {
        // 2. úroveň – zamestnanec
        const eInternal   = Number(empWrapper.internal   ?? empWrapper.piecesByType?.["Interná chyba"] ?? 0);
        const eExternal   = Number(empWrapper.external   ?? empWrapper.piecesByType?.["Externá chyba"] ?? 0);
        const eComplaints = Number(empWrapper.complaints ?? empWrapper.piecesByType?.["Reklamácia"]    ?? 0);
        const ePieces     = Number(
          empWrapper.pieces ??
          empWrapper.piecesTotal ??
          ((empWrapper.piecesByType?.["Interná chyba"] ?? 0) +
           (empWrapper.piecesByType?.["Externá chyba"] ?? 0) +
           (empWrapper.piecesByType?.["Reklamácia"]    ?? 0))
        );

        const employeeId = empWrapper.employee?.id ?? null;
        const empKey = `${side}-${rid}-emp-${k}`;

        const employeeNode = {
          key: empKey,
          data: {
            key: empKey,
            departmentId,
            employeeId,
            departmentName:
              empWrapper.name ||
              empWrapper.employee?.full_name ||
              empWrapper.employee?.name ||
              "",
            internal:   eInternal,
            external:   eExternal,
            complaints: eComplaints,
            pieces:     ePieces,
            type: "employee",
          },
          children: [],
        };

        // 3. úroveň – artikle
        const articleArr = empWrapper.articles
          ? [...empWrapper.articles.values()]
          : [];

        employeeNode.children = articleArr.map((art, artIndex) => {
          const aInternal   = Number(art.piecesByType?.["Interná chyba"] ?? 0);
          const aExternal   = Number(art.piecesByType?.["Externá chyba"] ?? 0);
          const aComplaints = Number(art.piecesByType?.["Reklamácia"]    ?? 0);
          const aPieces     = Number(
            art.piecesTotal ??
            ((art.piecesByType?.["Interná chyba"] ?? 0) +
             (art.piecesByType?.["Externá chyba"] ?? 0) +
             (art.piecesByType?.["Reklamácia"]    ?? 0))
          );

          const artKey = `${side}-${rid}-emp-${k}-art-${artIndex}`;
          return {
            key: artKey,
            data: {
              key: artKey,
              departmentId,
              employeeId,
              articleId:   art.articleId ?? art.article_number ?? null,
              articleName: art.articleName ?? "",
              departmentName: art.articleId ?? art.article_number ?? "",
              internal:   aInternal,
              external:   aExternal,
              complaints: aComplaints,
              pieces:     aPieces,
              type: "article",
            },
            leaf: true,
          };
        });

        return employeeNode;
      }),
    };
  });

  /* ====== 7) toTree ====== */
const toTreeRootCauseCounts = (rows, side) =>
  rows.map((r, i) => {
    const key0 = `${side}-rc-${r._key}`;

    const rootNode = {
      key: key0,
      data: {
        key: key0,
        rootCauseId: r.rootCauseId ?? null,
        label: r.rootCauseName ?? t('OTKEvaluation.Nezaradene'), // 1. stĺpec
        internal: r.internal ?? 0,
        external: r.external ?? 0,
        complaints: r.complaints ?? 0,
        total: r.total ?? 0,
        type: "rootCause",
      },
      children: [],
    };

    const deptValues = r.departments ? [...r.departments.values()] : [];

    rootNode.children = deptValues.map((depWrap, k) => {
      const depInternal = depWrap.counts?.["Interná chyba"] ?? 0;
      const depExternal = depWrap.counts?.["Externá chyba"] ?? 0;
      const depComplaints = depWrap.counts?.["Reklamácia"] ?? 0;
      const depTotal =
        depWrap.counts?.total ?? (depInternal + depExternal + depComplaints);

      const key1 = `${side}-rc-${r._key}-dep-${k}`;

      const depNode = {
        key: key1,
        data: {
          key: key1,
          rootCauseId: r.rootCauseId ?? null,
          departmentId: depWrap.department?.id ?? null,
          label: depWrap.departmentName ?? t('OTKEvaluation.Nezaradene'),
          internal: depInternal,
          external: depExternal,
          complaints: depComplaints,
          total: depTotal,
          type: "department",
        },
        children: [],
      };

      const empValues = depWrap.employees ? [...depWrap.employees.values()] : [];

      depNode.children = empValues.map((empWrap, empIndex) => {
        const empInternal = empWrap.counts?.["Interná chyba"] ?? 0;
        const empExternal = empWrap.counts?.["Externá chyba"] ?? 0;
        const empComplaints = empWrap.counts?.["Reklamácia"] ?? 0;
        const empTotal =
          empWrap.counts?.total ?? (empInternal + empExternal + empComplaints);

        const key2 = `${side}-rc-${r._key}-dep-${k}-emp-${empIndex}`;

        return {
          key: key2,
          data: {
            key: key2,
            rootCauseId: r.rootCauseId ?? null,
            departmentId: depNode.data.departmentId,
            employeeId: empWrap.employee?.id ?? null,
            label: empWrap.employeeName ?? t('OTKEvaluation.Nezaradene'),
            internal: empInternal,
            external: empExternal,
            complaints: empComplaints,
            total: empTotal,
            type: "employee",
          },
          leaf: true,
        };
      });

      return depNode;
    });

    return rootNode;
  });

// 4-úrovňový strom: Druh -> Skupina -> Oddelenie -> Zamestnanec
const toTreeTypeGroup = (aggMap, side) => {
  const nodes = [];

  if (!aggMap) return nodes;

  const values = aggMap instanceof Map ? aggMap.values() : aggMap;

  for (const et of values) {
    const errorTypeId = et.errorTypeId ?? 0;
    const errorTypeName =
      et.errorType?.name?.sk ||
      et.errorType?.name?.en ||
      t('OTKEvaluation.NeznamyDruh');

    // 1. úroveň – DRUH CHYBY
    const etKey = `${side}-et-${errorTypeId}`;
    const etNode = {
      key: etKey,
      data: {
        key: etKey,
        levelType: "errorType",
        errorTypeName,

        count:  Number(et.count  ?? 0),
        pieces: Number(et.pieces ?? 0),
        cost:   Number(et.cost   ?? 0),
      },
      children: [],
    };

    // 2. úroveň – SKUPINA
    for (const grp of et.groups.values()) {
      const groupId = grp.groupId ?? 0;
      const groupName =
        grp.group?.name?.sk ||
        grp.group?.name?.en ||
        t('OTKEvaluation.NeznamaSkupina');

      const grpKey = `${side}-et-${errorTypeId}-grp-${groupId}`;
      const grpNode = {
        key: grpKey,
        data: {
          key: grpKey,
          levelType: "group",
          errorTypeName,
          groupName,

          count:  Number(grp.count  ?? 0),
          pieces: Number(grp.pieces ?? 0),
          cost:   Number(grp.cost   ?? 0),
        },
        children: [],
      };

      // 3. úroveň – ODDELENIE
      for (const dep of grp.departments.values()) {
        const depId   = dep.departmentId ?? 0;
        const depName = dep.departmentName || t('OTKEvaluation.NezaradeneOddelenie');

        const depKey = `${side}-et-${errorTypeId}-grp-${groupId}-dep-${depId}`;
        const depNode = {
          key: depKey,
          data: {
            key: depKey,
            levelType: "department",
            errorTypeName,
            groupName,
            departmentName: depName,

            count:  Number(dep.count  ?? 0),
            pieces: Number(dep.pieces ?? 0),
            cost:   Number(dep.cost   ?? 0),
          },
          children: [],
        };

        // 4. úroveň – ZAMESTNANEC
        for (const emp of dep.employees.values()) {
          const empName =
            emp.employee?.full_name ||
            emp.employee?.name ||
            emp.employee?.offender_name ||
            t('OTKEvaluation.NeznamyZamestnanec');

          const empKey = `${side}-et-${errorTypeId}-grp-${groupId}-dep-${depId}-emp-${emp.employeeId}`;
          const empNode = {
            key: empKey,
            data: {
              key: empKey,
              levelType: "employee",
              errorTypeName,
              groupName,
              departmentName: depName,
              employeeName: empName,

              count:  Number(emp.count  ?? 0),
              pieces: Number(emp.pieces ?? 0),
              cost:   Number(emp.cost   ?? 0),
            },
            leaf: true,
          };

          depNode.children.push(empNode);
        }

        grpNode.children.push(depNode);
      }

      etNode.children.push(grpNode);
    }

    nodes.push(etNode);
  }

  return nodes;
};

const toTreeGroupType = (aggMapOrArray, side) => {
  const rows = Array.isArray(aggMapOrArray)
    ? aggMapOrArray
    : [...(aggMapOrArray?.values?.() ?? [])];

  const nodes = [];

  rows.forEach((gBucket, gIndex) => {
    const groupName =
      gBucket.group?.name?.sk ||
      gBucket.group?.name?.en ||
      t('OTKEvaluation.NeznamaSkupina');

    const groupKey = gBucket.groupId ?? gIndex;

    // 1. úroveň – skupina
    const groupNodeKey = `${side}-grp-${groupKey}`;
    const groupNode = {
      key: groupNodeKey,
      data: {
        key: groupNodeKey,
        levelType: "group",

        groupName,
        errorTypeName: null,
        departmentName: null,
        employeeName: null,

        count:  Number(gBucket.count  ?? 0),
        cost:   Number(gBucket.cost   ?? 0),
        pieces: Number(gBucket.pieces ?? 0),

        type: "groupTop", // len pre prípadný ďalší styling
      },
      children: [],
    };

    const errorTypeValues = [...(gBucket.errorTypes?.values?.() ?? [])];

    // 2. úroveň – druhy chýb
    groupNode.children = errorTypeValues.map((etBucket, etIndex) => {
      const errorTypeName =
        etBucket.errorType?.name?.sk ||
        etBucket.errorType?.name?.en ||
        t('OTKEvaluation.NeznamyDruh');

      const errorTypeKey = etBucket.errorTypeId ?? etIndex;
      const etNodeKey = `${side}-grp-${groupKey}-et-${errorTypeKey}`;

      const errorTypeNode = {
        key: etNodeKey,
        data: {
          key: etNodeKey,
          levelType: "errorType",

          groupName,
          errorTypeName,
          departmentName: null,
          employeeName: null,

          count:  Number(etBucket.count  ?? 0),
          cost:   Number(etBucket.cost   ?? 0),
          pieces: Number(etBucket.pieces ?? 0),

          type: "errorType",
        },
        children: [],
      };

      const deptValues = [...(etBucket.departments?.values?.() ?? [])];

      // 3. úroveň – oddelenia
      errorTypeNode.children = deptValues.map((dBucket, dIndex) => {
        const departmentName =
          dBucket.departmentName ||
          dBucket.department?.name ||
          t('OTKEvaluation.Nezaradene');

        const depKey = dBucket.departmentId ?? dIndex;
        const deptNodeKey = `${side}-grp-${groupKey}-et-${errorTypeKey}-dep-${depKey}`;

        const deptNode = {
          key: deptNodeKey,
          data: {
            key: deptNodeKey,
            levelType: "department",

            groupName,
            errorTypeName,
            departmentName,
            employeeName: null,

            count:  Number(dBucket.count  ?? 0),
            cost:   Number(dBucket.cost   ?? 0),
            pieces: Number(dBucket.pieces ?? 0),

            type: "department",
          },
          children: [],
        };

        const empValues = [...(dBucket.employees?.values?.() ?? [])];

        // 4. úroveň – zamestnanci
        deptNode.children = empValues.map((eBucket, eIndex) => {
          const emp = eBucket.employee || {};
          const employeeName =
            emp.full_name ||
            emp.name ||
            eBucket.employeeName ||
            t('OTKEvaluation.NeznamyZamestnanec');

          const empNodeKey = `${side}-grp-${groupKey}-et-${errorTypeKey}-dep-${depKey}-emp-${eIndex}`;
          return {
            key: empNodeKey,
            data: {
              key: empNodeKey,
              levelType: "employee",

              groupName,
              errorTypeName,
              departmentName,
              employeeName,

              count:  Number(eBucket.count  ?? 0),
              cost:   Number(eBucket.cost   ?? 0),
              pieces: Number(eBucket.pieces ?? 0),

              type: "employee",
            },
            leaf: true,
          };
        });

        return deptNode;
      });

      return errorTypeNode;
    });

    nodes.push(groupNode);
  });

  return nodes;
};

const toTreeTypeDept = (aggMap, side) => {
  // aggMap je Map z aggregateTypeDept (kľúč = errorType+dept)
  const values = Array.isArray(aggMap)
    ? aggMap
    : [...(aggMap?.values?.() ?? [])];

  const deptMap = new Map();

  for (const bucket of values) {
    const depId   = bucket.department?.id || 0;
    const depName =
      bucket.departmentName ??
      bucket.department?.name ??
      t('OTKEvaluation.Nezaradene');

    const errorTypeId = bucket.errorTypeId ?? bucket.errorType?.id ?? 0;
    const errorTypeName =
      bucket.errorType?.name?.sk ||
      bucket.errorType?.name?.en ||
      bucket.errorTypeName ||
      t('OTKEvaluation.NeznamyDruh');

    if (!deptMap.has(depId)) {
      deptMap.set(depId, {
        department: bucket.department,
        departmentName: depName,
        count:  0,
        cost:   0,
        pieces: 0,
        errorTypes: new Map(),
      });
    }

    const depBucket = deptMap.get(depId);
    depBucket.count  += Number(bucket.count  || 0);
    depBucket.cost   += Number(bucket.cost   || 0);
    depBucket.pieces += Number(bucket.pieces || 0);

    if (!depBucket.errorTypes.has(errorTypeId)) {
      depBucket.errorTypes.set(errorTypeId, {
        errorTypeId,
        errorTypeName,
        count:  0,
        cost:   0,
        pieces: 0,
        employees: new Map(),
      });
    }

    const etBucket = depBucket.errorTypes.get(errorTypeId);
    etBucket.count  += Number(bucket.count  || 0);
    etBucket.cost   += Number(bucket.cost   || 0);
    etBucket.pieces += Number(bucket.pieces || 0);

    const empEntries = bucket.employees
      ? [...bucket.employees.values()]
      : [];

    for (const empData of empEntries) {
      const emp = empData.employee;
      const empId =
        emp?.id ?? emp?.offender_id ?? `emp:${emp?.name ?? ""}`;

      if (!etBucket.employees.has(empId)) {
        etBucket.employees.set(empId, {
          employee: emp,
          employeeName:
            emp?.full_name ||
            emp?.name ||
            "",
          count:  0,
          cost:   0,
          pieces: 0,
          // sem budeme ukladať už len artikle
          lines: new Map(),
        });
      }

      const ee = etBucket.employees.get(empId);
      ee.count  += Number(empData.count  || 0);
      ee.cost   += Number(empData.cost   || 0);
      ee.pieces += Number(empData.pieces || 0);

      const lineEntries = empData.lines
        ? [...empData.lines.values()]
        : [];

      for (const line of lineEntries) {
        const articleId   = line.articleId   ?? null;
        const articleName = line.articleName ?? null;

        // kľúč = len artikel (žiadna objednávka)
        const lineKey = articleId ?? articleName ?? `noArticle-${Math.random()}`;

        if (!ee.lines.has(lineKey)) {
          ee.lines.set(lineKey, {
            articleId,
            articleName,
            count:  0,
            cost:   0,
            pieces: 0,
          });
        }

        const l = ee.lines.get(lineKey);
        l.count  += Number(line.count  || 0);
        l.cost   += Number(line.cost   || 0);
        l.pieces += Number(line.pieces || 0);
      }
    }
  }

  // Prevedieme deptMap → TreeTable uzly
  const deptNodes = [...deptMap.values()].map((depBucket, idx) => {
    const depKey = depBucket.department?.id ?? idx;
    const rootKey = `${side}-dep-${depKey}`;

    // 1. úroveň – Oddelenie
    const rootNode = {
      key: rootKey,
      data: {
        key: rootKey,
        levelType: "department",
        departmentName: depBucket.departmentName,

        errorTypeName: null,
        employeeName:  null,
        orderArticleLabel: null,

        count:  depBucket.count,
        cost:   depBucket.cost,
        pieces: depBucket.pieces,

        type: "department",
      },
      children: [],
    };

    // 2. úroveň – Druhy chýb
    const errorTypeValues = [...depBucket.errorTypes.values()];
    rootNode.children = errorTypeValues.map((etBucket, etIndex) => {
      const etKey = etBucket.errorTypeId ?? etIndex;
      const etNodeKey = `${side}-dep-${depKey}-et-${etKey}`;

      const etNode = {
        key: etNodeKey,
        data: {
          key: etNodeKey,
          levelType: "errorType",
          departmentName: depBucket.departmentName,
          errorTypeName:  etBucket.errorTypeName,
          employeeName:   null,
          orderArticleLabel: null,

          count:  etBucket.count,
          cost:   etBucket.cost,
          pieces: etBucket.pieces,

          type: "employee", // 2. level – máš takto nastylovaný
        },
        children: [],
      };

      // 3. úroveň – Zamestnanci
      const empValues = [...etBucket.employees.values()];
      etNode.children = empValues.map((ee, empIndex) => {
        const empKey = ee.employee?.id ?? empIndex;
        const empNodeKey = `${side}-dep-${depKey}-et-${etKey}-emp-${empKey}`;

        const empNode = {
          key: empNodeKey,
          data: {
            key: empNodeKey,
            levelType: "employee",
            departmentName: depBucket.departmentName,
            errorTypeName:  etBucket.errorTypeName,
            employeeName:   ee.employeeName,
            orderArticleLabel: null,

            count:  ee.count,
            cost:   ee.cost,
            pieces: ee.pieces,

            type: "employee",
          },
          children: [],
        };

        // 4. úroveň – len artikel
        const lineValues = [...ee.lines.values()];
        empNode.children = lineValues.map((line, lineIndex) => {
          const label =
            line.articleName ||
            line.articleId ||
            "(Bez artikla)";

          const artKey = `${side}-dep-${depKey}-et-${etKey}-emp-${empKey}-art-${lineIndex}`;
          return {
            key: artKey,
            data: {
              key: artKey,
              levelType: "orderArticle",
              departmentName: depBucket.departmentName,
              errorTypeName:  etBucket.errorTypeName,
              employeeName:   ee.employeeName,

              orderNumber: null,            // už nepoužívame
              articleId:   line.articleId,
              articleName: line.articleName,
              orderArticleLabel: label,     // bodyTemplate zobrazí len artikel

              count:  line.count,
              cost:   line.cost,
              pieces: line.pieces,

              type: "article",
            },
            leaf: true,
          };
        });

        return empNode;
      });

      return etNode;
    });

    return rootNode;
  });

  return deptNodes;
};

// Oddelenie -> Skupina -> Zamestnanec -> Zákazka/Artikel
const toTreeDeptGroup = (aggInput, side) => {
  const depBuckets = Array.isArray(aggInput)
    ? aggInput
    : [...(aggInput?.values?.() ?? [])];

  const nodes = [];

  depBuckets.forEach((depBucket, dIndex) => {
    const depId = depBucket.department?.id ?? dIndex;

    const departmentName =
      depBucket.departmentName ??
      depBucket.department?.name ??
      t('OTKEvaluation.Nezaradene');

    // 1. úroveň – Oddelenie
    const deptNodeKey = `${side}-dep-${depId}`;
    const deptNode = {
      key: deptNodeKey,
      data: {
        key: deptNodeKey,
        levelType: "department",

        departmentName,
        groupName: null,
        employeeName: null,
        orderArticleLabel: null,

        count:  Number(depBucket.count  ?? 0),
        pieces: Number(depBucket.pieces ?? 0),
        cost:   Number(depBucket.cost   ?? 0),

        type: "department",
      },
      children: [],
    };

    const groupValues = [...(depBucket.groups?.values?.() ?? [])];

    // 2. úroveň – Skupina
    deptNode.children = groupValues.map((grpBucket, gIndex) => {
      const groupId = grpBucket.groupId ?? gIndex;

      const groupName =
        grpBucket.groupName ??
        grpBucket.group?.name?.sk ??
        grpBucket.group?.name?.en ??
        t('OTKEvaluation.NeznamaSkupina');

      const groupNodeKey = `${side}-dep-${depId}-grp-${groupId}`;
      const groupNode = {
        key: groupNodeKey,
        data: {
          key: groupNodeKey,
          levelType: "group",

          departmentName,
          groupName,
          employeeName: null,
          orderArticleLabel: null,

          count:  Number(grpBucket.count  ?? 0),
          pieces: Number(grpBucket.pieces ?? 0),
          cost:   Number(grpBucket.cost   ?? 0),

          type: "department",
        },
        children: [],
      };

      const empValues = [...(grpBucket.employees?.values?.() ?? [])];

      // 3. úroveň – Zamestnanec
      groupNode.children = empValues.map((empBucket, eIndex) => {
        const employeeName =
          empBucket.employeeName ??
          empBucket.employee?.full_name ??
          empBucket.employee?.name ??
          "";

        const employeeNodeKey = `${side}-dep-${depId}-grp-${groupId}-emp-${eIndex}`;
        const employeeNode = {
          key: employeeNodeKey,
          data: {
            key: employeeNodeKey,
            levelType: "employee",

            departmentName,
            groupName,
            employeeName,
            orderArticleLabel: null,

            count:  Number(empBucket.count  ?? 0),
            pieces: Number(empBucket.pieces ?? 0),
            cost:   Number(empBucket.cost   ?? 0),

            type: "employee",
          },
          children: [],
        };

        const items = [...(empBucket.items?.values?.() ?? [])];

        // ---- 4. úroveň – agregované len podľa artikla ----
        const articleMap = new Map();

        items.forEach((it, idx) => {
          const articleId =
            it.articleId ??
            it.article_number ??
            it.articleId ??
            null;

          const articleName =
            it.articleName ??
            it.article_name ??
            null;

          const key = articleId ?? articleName ?? `no-article-${idx}`;

          if (!articleMap.has(key)) {
            articleMap.set(key, {
              articleId,
              articleName,
              count:  0,
              pieces: 0,
              cost:   0,
            });
          }

          const a = articleMap.get(key);
          a.count  += Number(it.count  ?? 0);
          a.pieces += Number(it.pieces ?? 0);
          a.cost   += Number(it.cost   ?? 0);
        });

        employeeNode.children = [...articleMap.values()].map((a, idx) => {
          const label =
            a.articleName ||
            a.articleId ||
            "(Bez artikla)";

          const artKey = `${employeeNode.key}-art-${idx}`;
          return {
            key: artKey,
            data: {
              key: artKey,
              levelType: "order",

              departmentName,
              groupName,
              employeeName,

              orderNumber: null, // už nepoužívame
              articleId:   a.articleId,
              articleName: a.articleName,
              orderArticleLabel: label,

              count:  a.count,
              pieces: a.pieces,
              cost:   a.cost,

              type: "article",
            },
            leaf: true,
          };
        });

        return employeeNode;
      });

      return groupNode;
    });

    nodes.push(deptNode);
  });

  return nodes;
};

  // nodes = tree z TreeTable (countsLeftTree / piecesLeftTree / costsLeftTree)
// maxLevel: 1 = len oddelenia, 2 = + zamestnanci, 3 = + artikle
// maxLevel: 
// 1 = len oddelenia (bez rozbalenia – prakticky nič)
// 2 = oddelenie + zamestnanci
// 3 = oddelenie + zamestnanci + artikle
// Rozbalí uzly po maxDepth tak, aby:
// 2 = root + deti, 3 = + vnuci, 4 = + pravnuci...
const buildExpandedToDepth = (nodes = [], maxDepth = 1) => {
  const expanded = {};

  const walk = (arr, depth) => {
    if (!arr || depth > maxDepth) return;

    for (const n of arr) {
      const hasChildren = Array.isArray(n.children) && n.children.length > 0;

      // expanduj len uzly nad poslednou úrovňou
      if (hasChildren && depth < maxDepth) {
        expanded[n.key] = true;
        walk(n.children, depth + 1);
      }
    }
  };

  // root = depth 1
  walk(nodes, 1);
  return expanded;
};

const handleExpandToDepth = (depth) => {
  if (!expandDialogContext) {
    setExpandDialogVisible(false);
    return;
  }

  // 2) Druh chyby voči skupine (existujúca tabuľka)
  if (expandDialogContext === "typeGroup") {
    const leftExpanded = buildExpandedToDepth(tgLeftTree, depth);

    if (!compareTypeGroupMode) {
      // single režim – len ľavá tabuľka
      setExpandedKeysLeft(leftExpanded);
    } else {
      // compare režim – ľavá + pravá
      const rightExpanded = buildExpandedToDepth(tgRightTree, depth);
      setExpandedKeysLeft(leftExpanded);
      setExpandedKeysRight(rightExpanded);
    }

    setExpandDialogVisible(false);
    return;
  }

  // 3) Skupina chýb voči druhu chýb
  if (expandDialogContext === "groupType") {
    const leftExpanded = buildExpandedToDepth(gtLeftTree, depth);

    if (!compareGroupTypeMode) {
      // single režim
      setExpandedGroupTypeLeft(leftExpanded);
    } else {
      const rightExpanded = buildExpandedToDepth(gtRightTree, depth);
      setExpandedGroupTypeLeft(leftExpanded);
      setExpandedGroupTypeRight(rightExpanded);
    }

    setExpandDialogVisible(false);
    return;
  }

  // 4) 🚨 NOVÁ tabuľka – Oddelenie voči druhu chýb
  if (expandDialogContext === "typeDept") {
    const leftExpanded = buildExpandedToDepth(tdLeftTree, depth);

    if (!compareTypeDeptMode) {
      // single režim – len ľavá tabuľka
      setExpandedTypeDeptLeft(leftExpanded);
    } else {
      // compare režim – ľavá + pravá
      const rightExpanded = buildExpandedToDepth(tdRightTree, depth);
      setExpandedTypeDeptLeft(leftExpanded);
      setExpandedTypeDeptRight(rightExpanded);
    }

    setExpandDialogVisible(false);
    return;
  }

  // fallback – len zavri dialog
  setExpandDialogVisible(false);
};

  // tučné + sivé len pre parent (oddelenie)
  const rowClassName = (node) => {
    const type = node?.data?.type;

    // 3. úroveň – artikel
    if (type === 'article') {
      return {
        'text-slate-600': true,
        'border-b border-[var(--surface-200)]': true,
        'hover:bg-[var(--surface-50)]': true,
        'transition-colors': true,
      };
    }

    // 2. úroveň – zamestnanec
    if (type === 'employee') {
      return {
        'font-medium': true,
        'text-slate-800': true,
        'bg-white': true,
        'border-b border-[var(--surface-100)]': true,
        'hover:bg-[var(--surface-50)]': true,
        'transition-colors': true,
      };
    }

    // 1. úroveň – oddelenie / Dodávateľ / Zákazník...
    return {
      'font-semibold': true,
      'text-slate-900': true,
      'bg-[var(--surface-50)]': true,
      'border-y border-[var(--surface-200)]': true,
      'hover:bg-[var(--surface-100)]': true,
      'transition-colors': true,
    };
  };                           

const getRefForKey = (key, tab) => {
    if (tab === 0) {
      if (key === "all") return refAllTablesTab1.current;
      if (key === "deptCounts") return refDeptCounts.current;
      if (key === "deptPieces") return refDeptCountPieces.current;
      if (key === "deptCosts") return refDeptCosts.current;
      if (key === "rootCauseCounts") return refRootCauseCounts.current;
      if (key === "typeGroup") return refTypeGroup.current;
      if (key === "groupType") return refGroupType.current;
      if (key === "deptType") return refTypeDept.current;
      if (key === "deptGroup") return refDeptGroup.current;
    } else {
      if (key === "all") return refAllTablesTab2.current;
      if (key === "claimedOrders") return refClaimedOrders.current;
      if (key === "claimedPieces") return refClaimedPieces.current;
      if (key === "repairCost") return refRepairCost.current;
      if (key === "trendPeriod") return refTrendPeriod.current;
    }
    return null;
  };

/** Pri tlači odstráni fixnú výšku scrollovateľných blokov v elemente, aby sa vytlačili všetky dáta. */
  const expandScrollablesInElement = (el) => {
    if (!el || !el.querySelectorAll) return;
    const sel = [
      ".p-datatable-scrollable-body",
      ".p-datatable-scrollable-wrapper",
      ".p-datatable-scrollable",
      ".p-treetable-scrollable-body",
      ".p-treetable-scrollable-wrapper",
      ".p-treetable-scrollable",
    ].join(",");
    el.querySelectorAll(sel).forEach((node) => {
      node.style.setProperty("height", "auto", "important");
      node.style.setProperty("max-height", "none", "important");
      node.style.setProperty("min-height", "0", "important");
      node.style.setProperty("overflow", "visible", "important");
      node.style.setProperty("overflow-y", "visible", "important");
      node.style.setProperty("overflow-x", "visible", "important");
    });
  };

  /** Pri tlači: skryje celý riadok header filtrov (filter row). */
  const processFilterRowsForPrint = (el) => {
    if (!el || !el.querySelectorAll) return;
    const filterRows = el.querySelectorAll(".p-datatable thead tr.p-datatable-filter-row, .p-datatable thead tr:has(.p-column-filter), .p-treetable thead tr.p-datatable-filter-row, .p-treetable thead tr:has(.p-column-filter)");
    filterRows.forEach((row) => {
      row.style.display = "none";
    });
    /* Skryť aj celý druhý a ďalší riadok v thead (filter riadky) – fallback ak selektor nechytí */
    el.querySelectorAll(".p-datatable thead, .p-treetable thead").forEach((thead) => {
      const rows = thead.querySelectorAll("tr");
      for (let i = 1; i < rows.length; i++) rows[i].style.display = "none";
    });
  };

  /** Pri tlači: nahradí Calendar, Dropdown, InputSwitch textom – Obdobie, Zobrazenie, Metriky. */
  const processInputsToTextForPrint = (el) => {
    if (!el || !el.querySelectorAll) return;
    el.querySelectorAll(".p-calendar").forEach((cal) => {
      const input = cal.querySelector("input");
      const val = input?.value?.trim() || "";
      const span = document.createElement("span");
      span.className = "print-text-value";
      span.textContent = val || "—";
      span.style.fontSize = "11px";
      cal.parentNode?.replaceChild(span, cal);
    });
    /* Prepínače (InputSwitch, SelectButton) sa pri tlači vôbec nezobrazujú – neprepisujeme na text */
    el.querySelectorAll(".p-dropdown").forEach((dd) => {
      if (dd.closest(".p-datatable thead, .p-treetable thead")) return;
      const label = dd.querySelector(".p-dropdown-label");
      const txt = (label?.innerText || "").trim() || "—";
      const span = document.createElement("span");
      span.className = "print-text-value";
      span.textContent = txt;
      span.style.fontSize = "11px";
      dd.parentNode?.replaceChild(span, dd);
    });
    el.querySelectorAll(".p-multiselect").forEach((ms) => {
      if (ms.closest(".p-datatable thead, .p-treetable thead")) return;
      const labelEl = ms.querySelector(".p-multiselect-label");
      const txt = (labelEl?.innerText || "").trim() || "—";
      const span = document.createElement("span");
      span.className = "print-text-value";
      span.textContent = txt;
      span.style.fontSize = "11px";
      ms.parentNode?.replaceChild(span, ms);
    });
  };

  /** Pri tlači: odstráni vonkajší p-card wrapper (border, shadow) – ponechá len obsah. */
  const processRemoveCardWrapperForPrint = (el) => {
    if (!el || !el.querySelectorAll) return;
    el.querySelectorAll(".print-full > .p-card").forEach((card) => {
      const parent = card.parentNode;
      const header = card.querySelector(".p-card-header");
      const content = card.querySelector(".p-card-content, .p-card-body");
      if (header) parent.insertBefore(header, card);
      if (content) {
        while (content.firstChild) {
          parent.insertBefore(content.firstChild, card);
        }
      }
      card.remove();
    });
  };

  /** Odstráni blok "Obdobie:" s Calendar komponentou z obsahu pred tlačou */
  const processRemovePeriodBlockForPrint = (el) => {
    if (!el || !el.querySelectorAll) return;
    // Nájsť všetky bloky obsahujúce "Obdobie:" a Calendar
    el.querySelectorAll(".flex.items-center.gap-3, .flex.items-center.gap-2").forEach((block) => {
      const text = block.textContent || "";
      if (text.includes("Obdobie:") && block.querySelector(".p-calendar")) {
        block.remove();
      }
    });
    // Tiež odstrániť t('OTKEvaluation.PorovnatSObdobim') ak sa objaví (aj keď má no-print, pre istotu)
    el.querySelectorAll("span, div").forEach((elem) => {
      const text = elem.textContent || "";
      if (text.includes(t('OTKEvaluation.PorovnatSObdobim')) || text.includes(t('OTKEvaluation.KliknutimRozbalte'))) {
        elem.remove();
      }
    });
    
    // Odstrániť celé kontajnery s t('OTKEvaluation.TabulkaGraf') switcherom
    el.querySelectorAll(".flex.items-center.gap-2").forEach((container) => {
      const hasInputSwitch = container.querySelector(".p-inputswitch");
      const text = container.textContent || "";
      // Ak obsahuje InputSwitch a text t('OTKEvaluation.Tabulka') alebo "Graf", odstrániť celý kontajner
      if (hasInputSwitch && (text.includes("Tabuľka") || text.includes("Graf"))) {
        container.remove();
      }
    });
  };

  /** Pri tlači: odstráni sekcie .print-charts-section, ktoré nemajú žiadne grafy (používateľ mal Grafy vypnuté). Tým sa nevygeneruje prázdna oblasť na druhú stranu. */
  const processRemoveEmptyChartSectionsForPrint = (el) => {
    if (!el || !el.querySelectorAll) return;
    el.querySelectorAll(".print-charts-section").forEach((section) => {
      const hasChart =
        section.querySelector(".print-chart-item") != null ||
        section.querySelector(".print-chart-bare") != null ||
        section.querySelector("canvas") != null;
      if (!hasChart) section.remove();
    });
  };

  /** Pri tlači: odstráni obaly Card okolo grafov, nechá len titulok + graf. Zachová print-trend-chart-info (Zobrazenie, Typ, Metriky). */
  const processChartCardsForPrint = (el) => {
    if (!el || !el.querySelectorAll) return;
    el.querySelectorAll(".print-chart-item").forEach((card) => {
      const titleEl = card.querySelector(".p-card-title, [class*='card-title']");
      const title = titleEl?.innerText || "";
      const trendInfoEl = card.querySelector(".print-trend-chart-info");
      const chartEl = card.querySelector("canvas, img");
      if (!chartEl) return;
      const wrap = document.createElement("div");
      wrap.className = "print-chart-bare";
      const titleDiv = document.createElement("div");
      titleDiv.className = "print-chart-bare-title";
      titleDiv.textContent = title;
      wrap.appendChild(titleDiv);
      if (trendInfoEl) {
        const infoClone = trendInfoEl.cloneNode(true);
        infoClone.style.display = "block";
        wrap.appendChild(infoClone);
      }
      wrap.appendChild(chartEl.cloneNode(true));
      card.replaceWith(wrap);
    });
  };

  /** Skontroluje, či obsah na tlač obsahuje grafy (canvas) – pre dlhší timeout pred tlačou. */
  const printContentHasCharts = () => {
    if (printSelection.length === 0) return false;
    if (printSelection.includes("all")) {
      const src = printDialogTab === 0 ? refAllTablesTab1.current : refAllTablesTab2.current;
      return src?.querySelector?.("canvas, .print-chart-item") != null;
    }
    return printSelection.some((key) => {
      const node = getRefForKey(key, printDialogTab);
      return node?.querySelector?.("canvas, .print-chart-item") != null;
    });
  };

  /** Pri tlači prekopíruje obsah canvasov zo zdroja do klónu (canvas sa pri cloneNode nekopíruje). */
  const convertCanvasesToImages = (sourceEl, cloneEl) => {
    if (!sourceEl || !cloneEl) return;
    const srcCanvases = sourceEl.querySelectorAll ? sourceEl.querySelectorAll("canvas") : [];
    const cloneCanvases = cloneEl.querySelectorAll ? cloneEl.querySelectorAll("canvas") : [];
    for (let i = 0; i < Math.min(srcCanvases.length, cloneCanvases.length); i++) {
      const src = srcCanvases[i];
      const clone = cloneCanvases[i];
      try {
        const dataUrl = src.toDataURL ? src.toDataURL("image/png") : null;
        if (dataUrl && clone && clone.parentNode) {
          const img = document.createElement("img");
          img.src = dataUrl;
          img.style.width = "100%";
          img.style.maxWidth = clone.width ? `${clone.width}px` : "100%";
          img.style.height = "auto";
          img.style.display = "block";
          clone.parentNode.replaceChild(img, clone);
        }
      } catch (_) {}
    }
  };

  /** Merge scrollable header do body table – riadok „Oddelenie / Interné / Externé / Reklamácie / Spolu“ musí byť v tlači */
  const mergeScrollableHeadersForPrint = (container) => {
    if (!container || !container.querySelectorAll) return;
    const prependHeaderRows = (headerThead, bodyThead) => {
      if (!headerThead || !bodyThead) return;
      const rows = Array.from(headerThead.querySelectorAll("tr"));
      // Vložiť v opačnom poradí, aby prvý riadok (hlavička stĺpcov) bol na začiatku – tr:not(:first-child) potom skryje len filter row
      for (let i = rows.length - 1; i >= 0; i--) {
        const clone = rows[i].cloneNode(true);
        bodyThead.insertBefore(clone, bodyThead.firstChild);
      }
    };
    // DataTable scrollable
    container.querySelectorAll(".p-datatable-scrollable").forEach((scrollable) => {
      const headerTable = scrollable.querySelector(".p-datatable-scrollable-header table");
      const bodyTable = scrollable.querySelector(".p-datatable-scrollable-body table");
      if (!headerTable || !bodyTable) return;
      const headerThead = headerTable.querySelector("thead");
      if (!headerThead) return;
      const bodyThead = bodyTable.querySelector("thead");
      if (bodyThead) {
        prependHeaderRows(headerThead, bodyThead);
      } else {
        const clonedThead = headerThead.cloneNode(true);
        bodyTable.insertBefore(clonedThead, bodyTable.firstChild);
      }
      const headerWrapper = scrollable.querySelector(".p-datatable-scrollable-header");
      if (headerWrapper) headerWrapper.style.display = "none";
    });
    /* TreeTable: hlavičku NESKRÝVAME ani NEMERGUJEME – necháme pôvodný scrollable header viditeľný
       a len processFilterRowsForPrint skryje filter riadky. Tak to predtým fungovalo. */
  };

  /** Scrollable DataTable/TreeTable renderuje footer samostatne; pri tlači musí ísť až za body riadky. */
  const moveScrollableFootersAfterBodyForPrint = (container) => {
    if (!container || !container.querySelectorAll) return;

    container.querySelectorAll(".p-datatable-scrollable-footer, .p-treetable-scrollable-footer").forEach((footer) => {
      const scrollable = footer.closest(".p-datatable-scrollable, .p-treetable-scrollable, .p-datatable-scrollable-wrapper, .p-treetable-scrollable-wrapper");
      const body = scrollable?.querySelector(".p-datatable-scrollable-body, .p-treetable-scrollable-body");
      if (!scrollable || !body || !footer.parentNode) return;

      if (body.nextSibling !== footer) {
        body.parentNode?.insertBefore(footer, body.nextSibling);
      }
    });
  };

  /** Pri tlači už nevkladáme zopakované rodičovské riadky (prázdne bez súm) – zalamovanie len čistý zlom. */
  const injectRepeatedTreeParents = (_sectionEl) => {
    // Vypnuté: vkladanie klonov oddelenie/zamestnanec s vymazanými súčtami spôsobovalo prázdne riadky.
    // Pri zlome stránky sa tabuľka len zlomí, bez vkladaných placeholder riadkov.
  };

  /** Extrahuje title a subtitle z elementu */
  const extractTitleAndSubtitle = (sourceEl) => {
    if (!sourceEl) return { title: "", subtitle: "" };
    
    let title = "";
    let subtitle = "";
    
    // Title: .p-card-title alebo .p-card-header .p-card-title
    const titleEl = sourceEl.querySelector(".p-card-title") || 
                    sourceEl.querySelector(".p-card-header .p-card-title") ||
                    sourceEl.querySelector(".p-card-header [class*='title']");
    if (titleEl) {
      title = titleEl.textContent.trim();
      // Odstrániť t('OTKEvaluation.PorovnatSObdobim') z title
      title = title.replace(/Porovnať s obdobím/gi, "").trim();
      // Odstrániť prepínač Tabuľka/Graf z nadpisu (môže ostať po clone ako t('OTKEvaluation.Tabulkagraf') alebo t('OTKEvaluation.TabulkaGraf2'))
      title = title.replace(/\s*Tabuľka\s*Graf\s*|\s*Graf\s*Tabuľka\s*/gi, " ").trim();
      title = title.replace(/TabuľkaGraf|GrafTabuľka/gi, "").trim();
      // Odstrániť newline a normalizovať medzery
      title = title.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
    } else {
      // Fallback: prvý element s textom, ktorý vyzerá ako nadpis
      const header = sourceEl.querySelector(".p-card-header");
      if (header) {
        const headerText = header.textContent.trim();
        const lines = headerText.split("\n").map(s => s.trim()).filter(Boolean);
        if (lines.length > 0) {
          title = lines[0].replace(/Porovnať s obdobím/gi, "").trim();
          title = title.replace(/\s*Tabuľka\s*Graf\s*|\s*Graf\s*Tabuľka\s*/gi, " ").trim();
          title = title.replace(/TabuľkaGraf|GrafTabuľka/gi, "").trim();
          title = title.replace(/\s+/g, " ").trim();
        }
      }
    }
    
    // Subtitle: nájsť Calendar inputy (môže byť jeden alebo dva v compare režime)
    const calendarInputs = sourceEl.querySelectorAll(".p-calendar input");
    let subtitleValues = null; // null = single value, array = compare mode [left, right]
    
    if (calendarInputs.length > 0) {
      const values = [];
      calendarInputs.forEach((input) => {
        if (input.value && input.value.trim()) {
          values.push(input.value.trim());
        }
      });
      
      if (values.length === 2) {
        // Compare mode: vrátiť pole hodnôt
        subtitleValues = values;
        subtitle = `${values[0]} / ${values[1]}`; // fallback pre kompatibilitu
      } else if (values.length === 1) {
        // Single mode: jedna hodnota
        subtitle = values[0];
      }
    }
    
    // Ak sme nenašli Calendar inputy, skúsiť text
    if (!subtitle && !subtitleValues) {
      // Hľadať text vedľa "Obdobie:" v texte
      const allText = sourceEl.innerText || "";
      const obdobieMatch = allText.match(/Obdobie:\s*([^\n]+)/i);
      if (obdobieMatch && obdobieMatch[1]) {
        subtitle = obdobieMatch[1].trim();
      } else {
        // Fallback: hľadať dátumové formáty
        const datePatterns = [
          /\d{2}\/\d{2}/,  // MM/YY
          /\d{2}\/\d{2}\s*-\s*\d{2}\/\d{2}/,  // MM/YY - MM/YY (range)
          /\d{2}\.\d{4}/,  // MM.YYYY
          /\d{4}/,         // YYYY
        ];
        for (const pattern of datePatterns) {
          const match = allText.match(pattern);
          if (match) {
            subtitle = match[0];
            break;
          }
        }
      }
    }
    
    return { title, subtitle, subtitleValues };
  };

/** Zostaví obsah pre tlač do printContainerRef (rovnaká logika ako onBeforeGetContent). Vráti Promise. */
  const buildPrintContentAsync = useCallback(() => {
    return new Promise(async (resolve) => {
      if (!printContainerRef.current || printSelection.length === 0) {
        setTimeout(resolve, 100);
        return;
      }
      setIsPrintMode(true);
      await new Promise(r => setTimeout(r, 200));
      setPrintChartFontSize(18);
      const waitBeforeClone = printContentHasCharts() ? 2200 : 350;
      await new Promise(r => setTimeout(r, waitBeforeClone));
      printContainerRef.current.innerHTML = "";

      const keysToPrint = printSelection.includes("all")
        ? (printDialogTab === 0
            ? ["deptCounts", "deptPieces", "deptCosts", "rootCauseCounts", "typeGroup", "groupType", "deptType", "deptGroup"]
            : ["claimedOrders", "claimedPieces", "repairCost", "trendPeriod"])
        : printSelection;

      const sections = [];
      const logoPromises = [];

      keysToPrint.forEach((key, idx) => {
        const sourceEl = getRefForKey(key, printDialogTab);
        if (!sourceEl || !sourceEl.cloneNode) return;

        // Extrahovať title a subtitle
        const { title, subtitle, subtitleValues } = extractTitleAndSubtitle(sourceEl);

        // Clone source
        const clone = sourceEl.cloneNode(true);
        convertCanvasesToImages(sourceEl, clone);

        // Odstrániť pôvodný title/subtitle z clone PRED aplikovaním helper funkcií
        clone.querySelectorAll(".p-card-title, .p-card-header .p-card-title, .p-card-subtitle, .p-card-content > .text-sm.p-card-subtitle").forEach((el) => {
          el.remove();
        });
        
        // Odstrániť všetky elementy obsahujúce t('OTKEvaluation.PorovnatSObdobim')
        clone.querySelectorAll("*").forEach((elem) => {
          const text = elem.textContent || "";
          if (text.includes(t('OTKEvaluation.PorovnatSObdobim'))) {
            elem.remove();
          }
        });
        
        // Odstrániť celé kontajnery s t('OTKEvaluation.TabulkaGraf') switcherom
        clone.querySelectorAll(".flex.items-center.gap-2").forEach((container) => {
          const hasInputSwitch = container.querySelector(".p-inputswitch");
          const text = container.textContent || "";
          // Ak obsahuje InputSwitch a text t('OTKEvaluation.Tabulka') alebo "Graf", odstrániť celý kontajner
          if (hasInputSwitch && (text.includes("Tabuľka") || text.includes("Graf"))) {
            container.remove();
          }
        });

        // Aplikovať helper funkcie na clone (najprv merge hlavičky, potom skryť filter riadky)
        expandScrollablesInElement(clone);
        mergeScrollableHeadersForPrint(clone);
        moveScrollableFootersAfterBodyForPrint(clone);
        processFilterRowsForPrint(clone);
        processInputsToTextForPrint(clone);
        processChartCardsForPrint(clone);
        processRemoveEmptyChartSectionsForPrint(clone);
        processRemoveCardWrapperForPrint(clone);
        processRemovePeriodBlockForPrint(clone);

        // Vytvoriť print-section wrapper (outer table s repeating thead)
        const sectionTable = document.createElement("table");
        sectionTable.className = "print-section";
        if (idx > 0) {
          sectionTable.style.pageBreakBefore = "always";
        }

        const thead = document.createElement("thead");
        const headerRow = document.createElement("tr");
        const headerCell = document.createElement("th");
        headerCell.colSpan = 1;
        headerCell.style.width = "100%";
        headerCell.style.padding = "0";
        headerCell.style.border = "none";

        const headerDiv = document.createElement("div");
        headerDiv.className = "print-section-header";

        const headerLeft = document.createElement("div");
        headerLeft.className = "print-section-header-left";

        if (title) {
          const titleDiv = document.createElement("div");
          titleDiv.className = "print-section-title";
          titleDiv.textContent = title;
          headerLeft.appendChild(titleDiv);
        }

        if (subtitle || subtitleValues) {
          // Ak máme compare mode (dve hodnoty), zobraziť pod seba
          if (subtitleValues && subtitleValues.length === 2) {
            const subtitleContainer = document.createElement("div");
            subtitleContainer.className = "print-section-subtitle";
            
            // Ľavá tabuľka
            const leftSubtitle = document.createElement("div");
            leftSubtitle.textContent = t('OTKEvaluation.PeriodLeft', { value: subtitleValues[0] });
            subtitleContainer.appendChild(leftSubtitle);
            
            // Pravá tabuľka
            const rightSubtitle = document.createElement("div");
            rightSubtitle.textContent = t('OTKEvaluation.PeriodRight', { value: subtitleValues[1] });
            subtitleContainer.appendChild(rightSubtitle);
            
            headerLeft.appendChild(subtitleContainer);
          } else if (subtitle) {
            // Single mode: jedna hodnota
            let rawSubtitle = subtitle.trim() || "";
            // Odstrániť existujúce "Obdobie:" (aj s medzerou)
            rawSubtitle = rawSubtitle.replace(/^Obdobie:\s*/i, "").trim();
            
            // Ak je prázdne → nedávať nič
            let headerSubtitle = "";
            if (rawSubtitle) {
              headerSubtitle = "Obdobie: " + rawSubtitle;
            }
            
            // Vložiť do headeru len ak máme obsah
            if (headerSubtitle) {
              const subtitleDiv = document.createElement("div");
              subtitleDiv.className = "print-section-subtitle";
              subtitleDiv.textContent = headerSubtitle;
              headerLeft.appendChild(subtitleDiv);
            }
          }
        }

        headerDiv.appendChild(headerLeft);

        if (otkPrintLogoSrc) {
          const logoImg = document.createElement("img");
          logoImg.className = "print-section-logo";
          logoImg.src = otkPrintLogoSrc;
          logoImg.alt = "";
          logoImg.setAttribute("style", "max-height: 22mm; max-width: 45mm; object-fit: contain; display: block;");
          headerDiv.appendChild(logoImg);
          
          const logoPromise = new Promise((res) => {
            if (logoImg.complete) res();
            else { logoImg.onload = res; logoImg.onerror = res; }
          });
          logoPromises.push(logoPromise);
        }

        headerCell.appendChild(headerDiv);
        headerRow.appendChild(headerCell);
        thead.appendChild(headerRow);
        sectionTable.appendChild(thead);

        const tbody = document.createElement("tbody");
        const bodyRow = document.createElement("tr");
        const bodyCell = document.createElement("td");
        bodyCell.className = "print-section-body";
        bodyCell.style.padding = "0";
        bodyCell.style.border = "none";
        bodyCell.appendChild(clone);
        bodyRow.appendChild(bodyCell);
        tbody.appendChild(bodyRow);
        sectionTable.appendChild(tbody);

        sections.push(sectionTable);
      });

      // Append všetky sekcie
      sections.forEach((section) => {
        printContainerRef.current.appendChild(section);
      });

      // Počakať na všetky logá a potom inject tree parents
      Promise.all(logoPromises.length > 0 ? logoPromises : [Promise.resolve()]).then(() => {
        window.dispatchEvent(new Event("resize"));
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              // Inject repeated tree parents po stabilizácii layoutu
              printContainerRef.current.querySelectorAll(".print-section").forEach((section) => {
                injectRepeatedTreeParents(section);
              });
              
              // Krátke čakanie na layout klonu (tree parents); grafy sú už vykreslené pred klonovaním
              const layoutDelay = 400;
              setTimeout(resolve, layoutDelay);
            }, 100);
          });
        });
      });
    });
  }, [printSelection, printDialogTab, getRefForKey, extractTitleAndSubtitle, otkPrintLogoSrc, printContentHasCharts]);

  const handlePrint = useReactToPrint({
  content: () => (printContainerRef.current && printSelection.length > 0 ? printContainerRef.current : null),
  onBeforeGetContent: () => buildPrintContentAsync(),
  onAfterPrint: () => {
    setPrintChartFontSize(null);
    setIsPrintMode(false);
  },
  documentTitle: t('OTKEvaluation.StatistikaKontrolyKvality'),
  pageStyle: `
    /* Prefer leaving size/orientation to the browser print dialog */
    @page {
      margin: 12mm;
      /* Chrome 131+: page numbers in footer */
      @bottom-center {
        content: "${t('Common.Page')} " counter(page) " / " counter(pages);
        font-size: 10px;
        font-family: Arial, sans-serif;
      }
    }
    .otk-evaluation-root .print-trend-chart-info { display: none; }
    @media print {
      body { -webkit-print-color-adjust: exact !important; font-family: Arial, sans-serif; }
      table { border-collapse: collapse; width: 100%; }
      .no-print { display: none !important; }
      .otk-evaluation-root .print-trend-chart-info { display: block !important; }

      /* Uniform print: all black, no underline */
      .print-full *, .print-section *, .print-body * {
        color: #000 !important;
        text-decoration: none !important;
      }
      .print-full canvas, .print-section canvas, .print-body canvas,
      .print-full img, .print-section img, .print-body img {
        color: unset !important;
      }

      /* PRINT SECTION: outer table s repeating thead pre kazdu tabulku */
      .print-section {
        width: 100% !important;
        border-collapse: collapse !important;
        page-break-after: always !important;
      }
      .print-section:last-child {
        page-break-after: auto !important;
      }
      .print-section thead {
        display: table-header-group !important;
      }
      .print-section-header {
        display: flex !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        padding: 4mm 0 2mm 0 !important;
      }
      .print-section-header-left {
        flex: 0 0 auto !important;
        text-align: left !important;
      }
      .print-section-title {
        font-size: 18px !important;
        font-weight: 700 !important;
        line-height: 1.3 !important;
        margin: 0 0 4px 0 !important;
        color: #000 !important;
      }
      .print-section-subtitle {
        font-size: 13px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        margin: 0 !important;
        color: #000 !important;
      }
      .print-section-subtitle > div {
        margin-bottom: 2px !important;
      }
      .print-section-subtitle > div:last-child {
        margin-bottom: 0 !important;
      }
      .print-section-logo {
        flex: 0 0 auto !important;
        flex-shrink: 0 !important;
        max-height: 22mm !important;
        max-width: 45mm !important;
        object-fit: contain !important;
        display: block !important;
        visibility: visible !important;
      }
      .print-section-body {
        padding: 0 !important;
      }

      /* Repeat column headers for DataTable/TreeTable after merge */
      .print-section-body .p-datatable thead,
      .print-section-body .p-treetable thead,
      .print-section-body thead.p-treetable-thead,
      .print-section-body thead.p-datatable-thead,
      .print-section-body thead.no-print {
        display: table-header-group !important;
      }
      .print-section-body .p-datatable-scrollable-header {
        display: none !important;
      }
      /* Do not hide TreeTable scrollable header — keep column titles visible;
         processFilterRowsForPrint only hides filter rows */
      /* First thead row always visible */
      .print-section-body .p-datatable thead tr:first-child,
      .print-section-body .p-treetable thead tr:first-child,
      .print-section-body thead tr:first-child {
        display: table-row !important;
      }
      .print-section-body thead tr:first-child th {
        display: table-cell !important;
      }
      .print-section-body .p-datatable thead tr:not(:first-child),
      .print-section-body .p-treetable thead tr:not(:first-child) {
        display: none !important;
      }
      
      /* Repeated parent rows in TreeTable */
      .print-repeated-parent {
        background-color: #f5f5f5 !important;
      }
      .print-repeated-parent td:not(:first-child) {
        color: transparent !important;
      }

      /* Hide on print: subtitle and switches */
      .print-full .p-card-subtitle,
      .print-full .p-card-content > .text-sm.p-card-subtitle,
      .print-full .p-card-header .print-text-value,
      .print-full .p-card-header .p-inputswitch,
      .print-full .p-card-header .no-print,
      .print-full .p-inputswitch,
      .print-full .p-selectbutton {
        display: none !important;
      }

      /* === Typography: table title larger than period === */
      .print-title, .print-full .p-card-title, .print-full .p-card-header .p-card-title {
        font-size: 18px !important;
        font-weight: 700 !important;
        line-height: 1.3 !important;
        margin: 0 0 4px 0 !important;
        color: #000 !important;
      }
      .print-subtitle, .print-full .p-card-subtitle, .print-full .p-card-content > .text-sm {
        font-size: 11px !important;
        font-weight: 400 !important;
        line-height: 1.3 !important;
        margin: 0 0 8px 0 !important;
        color: #000 !important;
      }
      .print-full .print-chart-bare-title {
        font-size: 14px !important;
        font-weight: 700 !important;
        margin-bottom: 4px !important;
        color: #000 !important;
      }
      .print-full thead {
        display: table-header-group !important;
      }
      .print-full tfoot {
        display: table-row-group !important;
      }
      .print-full .p-treetable-tbody > tr {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .print-full table {
        margin-top: 0px !important;
      }
      /* === Colors: black text, links as normal text === */
      .print-full a, .print-full .p-datatable a, .print-full .p-treetable a,
      .print-full [role="button"],
      .print-full a:hover, .print-full a:visited, .print-full a:link { color: #000 !important; text-decoration: none !important; }
      .print-section a, .print-section [role="button"],
      .print-section a:hover, .print-section a:visited, .print-section a:link { color: #000 !important; text-decoration: none !important; }
      .print-body a, .print-body [role="button"],
      .print-body a:hover, .print-body a:visited, .print-body a:link { color: #000 !important; text-decoration: none !important; }
      /* inline blue (#2563eb) → black on print */
      .print-full [style*="2563eb"], .print-section [style*="2563eb"], .print-body [style*="2563eb"] { color: #000 !important; text-decoration: none !important; }
      /* Table headers/cells always black */
      .print-full th, .print-full td, .print-full .p-datatable th, .print-full .p-datatable td,
      .print-full .p-treetable th, .print-full .p-treetable td,
      .print-full .p-sortable-column, .print-full .p-datatable .p-sortable-column,
      .print-full .p-treetable .p-sortable-column,
      .print-full .p-datatable thead th, .print-full .p-treetable thead th,
      .print-full .p-datatable tbody td, .print-full .p-treetable tbody td,
      .print-full .p-datatable tfoot td, .print-full .p-treetable tfoot td { color: #000 !important; }
      /* Hide sort icons on print */
      .print-full .p-sortable-column-icon,
      .print-full .p-datatable .p-sortable-column .p-sortable-column-icon,
      .print-full .p-treetable .p-sortable-column .p-sortable-column-icon,
      .print-full .p-sortable-column .p-icon,
      .print-full .p-datatable .p-sortable-column .p-icon,
      .print-full .p-treetable .p-sortable-column .p-icon { display: none !important; }

      /* Footer via @page @bottom-center (Chrome 131+) */

      /* Disable scroll/fixed height on print */
      .print-full,
      .print-full .p-treetable-scrollable-body,
      .print-full .p-treetable-scrollable-wrapper,
      .print-full .p-treetable-scrollable-body > div,
      .print-full .p-datatable-scrollable-body,
      .print-full .p-datatable-scrollable-wrapper,
      .print-full .p-datatable-scrollable-body > div,
      .print-full .p-datatable-scrollable,
      .print-full .p-datatable-wrapper,
      .print-full [class*="scrollable"] {
        max-height: none !important;
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
      }
      .print-full .p-datatable-scrollable-body,
      .print-full .p-treetable-scrollable-body {
        overflow-y: visible !important;
        overflow-x: visible !important;
      }

      /* new table always on a new page */
      .print-full.print-section-break {
        page-break-before: always !important;
      }
      /* nadpis + tabulka/graf vzdy spolu – okrem print-table-section */
      .print-full {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      /* Tab 2 tabulky: mozu sa zlomit vnutri, aby nevznikala prazdna druha strana */
      .print-full.print-table-section {
        page-break-inside: auto !important;
        break-inside: auto !important;
      }
      /* TreeTable/DataTable: umoznit zlom riadkov (rodic + deti nemusia ist cele na dalsiu stranu) */
      .print-full .p-treetable .p-treetable-tbody > tr,
      .print-full .p-datatable .p-datatable-tbody > tr {
        page-break-inside: auto !important;
        break-inside: auto !important;
      }
      .print-full .p-card {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .print-full .p-card-body, .print-full .p-card-content {
        border: none !important;
        box-shadow: none !important;
        padding: 0 0 8px 0 !important;
      }
      .print-full .p-card-header {
        padding: 0 0 4px 0 !important;
      }
      /* nadpis + header tabulky vzdy dokopy – nikdy na predoslej strane */
      .print-full .p-card-header,
      .print-full .p-card-title,
      .print-full .p-card-content,
      .print-full thead,
      .print-full .p-datatable-thead {
        page-break-after: avoid !important;
        page-break-before: avoid !important;
      }
      /* titulok grafu nikdy oddelene od grafu */
      .print-full .print-chart-bare-title {
        page-break-after: avoid !important;
        break-after: avoid !important;
      }
      /* sekcia s grafmi – nadpis sekcie + grafy spolu */
      .print-full .print-charts-section {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

      /* Tab2: 2 tabulky vedla seba – plna sirka stranky, ziadne scale (ziadne biele okraje vlavo/vpravo) */
      .print-full .print-compare-grid {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 32px !important;
        margin-bottom: 16px !important;
        padding: 0 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
      }
      .print-full .print-compare-grid > *,
      .print-full .print-compare-cell {
        min-width: 0 !important;
        overflow: hidden !important;
        overflow-x: hidden !important;
        isolation: isolate !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .print-full .print-compare-grid .p-datatable,
      .print-full .print-compare-grid .p-datatable-wrapper,
      .print-full .print-compare-grid .p-datatable-scrollable,
      .print-full .print-compare-grid .p-datatable-scrollable-wrapper {
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        margin: 0 !important;
        overflow-x: hidden !important;
      }
      .print-full .print-compare-grid .p-datatable-scrollable-body,
      .print-full .print-compare-grid .p-datatable-scrollable-header,
      .print-full .print-compare-grid .p-datatable-scrollable-footer {
        overflow-x: hidden !important;
      }
      .print-full .print-compare-grid .p-treetable-wrapper,
      .print-full .print-compare-grid .p-treetable-scrollable,
      .print-full .print-compare-grid .p-treetable-scrollable-wrapper {
        padding: 0 !important;
        margin: 0 !important;
        overflow-x: hidden !important;
      }
      .print-full .print-compare-grid .p-treetable-scrollable-body {
        overflow-x: hidden !important;
      }
      /* ziadny horizontalny scrollbar v compare pri tlaci */
      .print-full .print-compare-grid .p-datatable-scrollable-wrapper,
      .print-full .print-compare-grid .p-datatable-scrollable-body,
      .print-full .print-compare-grid .p-treetable-scrollable-wrapper,
      .print-full .print-compare-grid .p-treetable-scrollable-body {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      .print-full .print-compare-grid .p-datatable-scrollable-wrapper::-webkit-scrollbar,
      .print-full .print-compare-grid .p-datatable-scrollable-body::-webkit-scrollbar,
      .print-full .print-compare-grid .p-treetable-scrollable-wrapper::-webkit-scrollbar,
      .print-full .print-compare-grid .p-treetable-scrollable-body::-webkit-scrollbar {
        display: none !important;
      }
      .print-full .print-compare-grid .p-datatable-scrollable-body table,
      .print-full .print-compare-grid .p-datatable-scrollable-header table,
      .print-full .print-compare-grid .p-datatable-scrollable-footer table {
        table-layout: fixed !important;
      }

      /* header len na zaciatku, footer len na konci – nie na kazdej strane (table-row-group = neopakuje sa) */
      /* Header filtre (filter row) v tabulkach sa pri tlaci nezobrazuju */
      .print-full .p-datatable thead tr:not(:first-child),
      .print-full .p-treetable thead tr:not(:first-child) {
        display: none !important;
      }
      .print-full thead,
      .print-full .p-datatable-thead,
      .print-full .p-treetable-thead {
        display: table-header-group !important;
      }
      .print-full tfoot,
      .print-full .p-datatable-tfoot {
        display: table-row-group !important;
      }
      .print-full .p-datatable-scrollable-header,
      .print-full .p-datatable-scrollable-footer,
      .print-full .p-treetable-scrollable-footer {
        position: static !important;
      }
      .print-full .p-datatable-scrollable-body table,
      .print-full .p-datatable-scrollable-header table,
      .print-full .p-datatable-scrollable-footer table,
      .print-full .p-treetable-scrollable-footer table {
        width: 100% !important;
      }

      /* === TABULKY: konzistentna mierka – single aj compare 10px === */
      .print-full .p-datatable th, .print-full .p-datatable td,
      .print-full .p-treetable th, .print-full .p-treetable td {
        font-size: 10px !important;
        padding: 4px 6px !important;
        line-height: 1.2 !important;
      }
      .print-full .p-datatable table, .print-full .p-treetable table {
        table-layout: auto !important;
      }
      .print-full .p-datatable th:first-child, .print-full .p-datatable td:first-child,
      .print-full .p-treetable th:first-child, .print-full .p-treetable td:first-child {
        max-width: 180px !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
      }
      /* Compare: rovnaky font 10px */
      .print-full .print-compare-grid .p-datatable colgroup col:nth-child(1) {
        width: 50% !important;
        min-width: 0 !important;
      }
      .print-full .print-compare-grid .p-datatable colgroup col:nth-child(n+2) {
        width: 12% !important;
        min-width: 0 !important;
      }
      .print-full .print-compare-grid .p-datatable th,
      .print-full .print-compare-grid .p-datatable td {
        font-size: 10px !important;
        padding: 3px 4px !important;
      }
      .print-full .print-compare-grid .p-datatable colgroup col {
        min-width: 0 !important;
      }

      /* Tab1 TreeTable v porovnavacom rezime – zmensit stlpce, aby sa neprekryvali */
      .print-full .print-compare-grid .p-treetable,
      .print-full .print-compare-grid .p-treetable-wrapper,
      .print-full .print-compare-grid .p-treetable-scrollable,
      .print-full .print-compare-grid .p-treetable-scrollable-wrapper {
        width: 100% !important;
        max-width: 100% !important;
      }
      .print-full .print-compare-grid .p-treetable table {
        width: 100% !important;
        table-layout: fixed !important;
      }
      .print-full .print-compare-grid .p-treetable colgroup col:nth-child(1) {
        width: 50% !important;
        min-width: 0 !important;
      }
      .print-full .print-compare-grid .p-treetable colgroup col:nth-child(n+2) {
        width: 12% !important;
        min-width: 0 !important;
      }
      .print-full .print-compare-grid .p-treetable th,
      .print-full .print-compare-grid .p-treetable td {
        font-size: 10px !important;
        padding: 3px 4px !important;
      }
      .print-full .print-compare-grid .p-treetable colgroup col {
        min-width: 0 !important;
      }

      /* filter riadok: prazdne bunky skryte v JS; bunky s hodnotou zmensit */
      .print-full .p-datatable .print-filter-has-value {
        font-size: 8px !important;
        padding: 2px 3px !important;
      }
      .print-full .p-datatable .print-filter-has-value input,
      .print-full .p-datatable .print-filter-has-value .p-dropdown,
      .print-full .p-datatable .print-filter-has-value .p-inputtext {
        font-size: 8px !important;
        min-height: 20px !important;
        height: 20px !important;
        padding: 2px 4px !important;
      }

      /* Grafy: single = pod tabulkou, compare = 4 grafy na dalsiu stranu */
      .print-full .print-charts-section {
        page-break-before: auto !important;
      }
      .print-full .print-charts-section.print-charts-compare {
        page-break-before: always !important;
      }
      .print-full .print-chart-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }


      /* grafy: nadpis + graf vzdy spolu – ak nevojdu, graf sa zmensi */
      .print-full .print-chart-bare {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
        max-height: 80mm !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .print-full .print-chart-bare-title {
        margin-bottom: 4px !important;
        padding: 0 !important;
        line-height: 1.2;
        flex-shrink: 0 !important;
      }
      .print-full .print-chart-bare canvas,
      .print-full .print-chart-bare img {
        width: 100% !important;
        max-height: 65mm !important;
        min-height: 0 !important;
        object-fit: contain !important;
      }
      /* fallback pre stare .print-chart-item ak by zostali */
      .print-full .print-chart-item {
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
      }
      .print-full .print-chart-item .p-card-body,
      .print-full .print-chart-item .p-card-content {
        padding: 0 !important;
      }
      .print-full .print-chart-item .p-card-title {
        font-size: 14px !important;
      }
      .print-full .print-chart-item canvas,
      .print-full .print-chart-item img {
        width: 100% !important;
        height: 100% !important;
      }
      /* 4 grafy (porovnanie): 2×2 grid – max-height, moze sa zmensit ak nevojdu nadpis+graf */
      .print-full .print-chart-row {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        grid-template-rows: 1fr 1fr !important;
        max-height: 175mm !important;
        height: auto !important;
        width: 100% !important;
        gap: 4mm !important;
      }
      /* 2 grafy (single): 1×2 grid – max-height pre zachovanie nadpis+graf spolu */
      .print-full .print-chart-row:has(> .grid:only-child) {
        grid-template-rows: 1fr !important;
        max-height: 85mm !important;
        height: auto !important;
      }
      .print-full .print-chart-row > .grid {
        display: contents !important;
      }
      .print-full .print-chart-bare {
        width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .print-full .print-chart-bare-title {
        flex-shrink: 0 !important;
      }
      .print-full .print-chart-bare canvas,
      .print-full .print-chart-bare img {
        flex: 1 !important;
        min-height: 0 !important;
      }
    }
  `,
});

  // špeciálne ID pre pseudo-oddelenia (môžu byť záporné, agregácia s tým nemá problém)
  const DEPT_IDS = { SUPPLIER: -1, CUSTOMER: -2, UNASSIGNED: 0 };

  const errorRecordsWithDept = useMemo(() => {
    const employeesByOffenderId = new Map((state.employees || []).map(e => [e.offender_id, e]));
    const deptsById           = new Map((state.departments || []).map(d => [d.id, d]));
    const companiesById       = new Map((state.companies   || []).map(c => [c.id, c]));

    return (evaluationRecords || []).map((rec) => {
      const offenderType = rec?.offender?.type; // 'employee' | 'supplier' | 'customer'
      let employee = null;
      let department = null;
      let departmentName = t('OTKEvaluation.Nezaradene');

      if (offenderType === "employee") {
        employee = employeesByOffenderId.get(rec.offender_id) ?? rec.offender?.employee ?? null;
        const dept = rec.offender?.employee?.department ?? (employee ? deptsById.get(employee.department_id) : null);
        department = dept ? { id: dept.id, name: dept.name } : { id: DEPT_IDS.UNASSIGNED, name: t('OTKEvaluation.Nezaradene') };
        departmentName = department.name;
      }

      if (offenderType === "supplier") {
        department = { id: DEPT_IDS.SUPPLIER, name: t('Common.Supplier') };
        departmentName = t('Common.Supplier');
        const raw = rec.offender?.supplier || rec.supplier || null;
        const compId = raw?.company_id ?? raw?.id ?? rec.supplier_id ?? rec.company_id ?? null;
        const comp = (compId != null ? companiesById.get(compId) : null) || raw || null;
        const compName = comp?.name || t('Common.Supplier');
        employee = { id: `sup:${compId ?? rec.offender_id}`, name: compName, department_id: null };
      }

      if (offenderType === "customer") {
        department = { id: DEPT_IDS.CUSTOMER, name: t('Common.Customer') };
        departmentName = t('Common.Customer');
        const raw = rec.offender?.customer || rec.customer || null;
        const compId = raw?.company_id ?? raw?.id ?? rec.customer_id ?? rec.company_id ?? null;
        const comp = (compId != null ? companiesById.get(compId) : null) || raw || null;
        const compName = comp?.name || t('Common.Customer');
        employee = { id: `cus:${compId ?? rec.offender_id}`, name: compName, department_id: null };
      }

      if (!department) {
        department = { id: DEPT_IDS.UNASSIGNED, name: t('OTKEvaluation.Nezaradene') };
        departmentName = t('OTKEvaluation.Nezaradene');
      }

      return {
        ...rec,
        offenderType,
        employee,
        department,
        departmentName,
      };
    });
  }, [evaluationRecords, state.employees, state.departments, state.companies]);

  /* ====== base filter (toolbar) ====== */
 const baseFilteredRecords = useMemo(() => {
  const depNames = new Set(selectedDepartments);
  const states   = new Set(selectedStates);

  // ak user už ťukol do oddelení a výsledok je prázdny => zámerne nič neukazuj
  if (departmentsTouched && depNames.size === 0) return [];

  return errorRecordsWithDept.filter((r) => {
    if (kinds.length && !kinds.includes(r.type)) return false;

    const depName =
      r.departmentName ||
      (r.department && r.department.name) ||
      r.dept_name ||
      null;

    if (depNames.size > 0) {
      if (!depName || !depNames.has(depName)) return false;
    }

    if (states.size && !states.has(r.ack_error)) return false;

    return true;
  });
}, [errorRecordsWithDept, kinds, selectedDepartments, selectedStates, departmentsTouched]);

  // options pre GLOBAL MultiSelect hore (bez položky t('Common.All'), tú robí showSelectAll)
  const departmentMultiOptions = React.useMemo(() => {
    const deptOpts = (departments || [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((d) => ({
        label: d.name,
        value: d.name,               // ⬅ hodnotou bude názov oddelenia
      }));

    return [
      ...deptOpts,
      { label: t('Common.Customer'), value: 'Zákazník' },
      { label: t('Common.Supplier'), value: 'Dodávateľ' },
    ];
  }, [departments]);

const allDepartmentValues = useMemo(
  () => (departmentMultiOptions || []).map(o => String(o.value)),
  [departmentMultiOptions]
);

const customerMultiOptions = useMemo(
  () => (customers || []).map(c => ({ label: c.name, value: String(c.id) })),
  [customers]
);

const allCustomerIds = useMemo(
  () => customerMultiOptions.map(o => o.value),
  [customerMultiOptions]
);

const allCustomerValues = useMemo(
  () => (customerMultiOptions || []).map(o => String(o.value)),
  [customerMultiOptions]
);

const isAllDepartmentsSelected =
  selectedDepartments.length > 0 &&
  selectedDepartments.length === allDepartmentValues.length;

const isAllCustomersSelected =
  selectedCustomers.length > 0 &&
  selectedCustomers.length === allCustomerValues.length;

const claimsBaseRecords = useMemo(() => {
  const depNames = new Set((selectedDepartments || []).map(String));
  const states   = new Set(selectedStates || []);
  const custIds  = new Set((selectedCustomers || []).map(String));

  // ak user už ťukol do oddelení a nechal prázdne -> zámerne nič
  if (departmentsTouched && depNames.size === 0) return [];

  // ak user už ťukol do zákazníkov a nechal prázdne -> zámerne nič
  if (customersTouched && custIds.size === 0) return [];

  return errorRecordsWithDept.filter((r) => {
    // len reklamácie (uprav, ak máš iný identifikátor)
    if (r.type !== "Reklamácia") return false;

    // zákazník
    const companyId =
      r.customer_id ?? r.company_id ?? r.companyId ?? r.customerId ?? null;

    if (!isAllCustomersSelected && custIds.size > 0) {
      if (!companyId || !custIds.has(String(companyId))) return false;
    }

    // oddelenie
    const depName =
      r.departmentName ||
      (r.department && r.department.name) ||
      r.dept_name ||
      null;

    if (!isAllDepartmentsSelected && depNames.size > 0) {
      if (!depName || !depNames.has(String(depName))) return false;
    }

    // stav
    if (states.size && !states.has(r.ack_error)) return false;

    return true;
  });
}, [
  errorRecordsWithDept,
  selectedDepartments,
  selectedStates,
  selectedCustomers,
  departmentsTouched,
  customersTouched,
  isAllDepartmentsSelected,
  isAllCustomersSelected,
]);

const claimsByCompanyMonthFiltered = useMemo(() => {
  const tmp = new Map();

  for (const r of claimsBaseRecords || []) {
    const company_id = r.customer_id ?? r.company_id ?? r.companyId ?? 0;
    if (!company_id) continue;

    const dt = r?.created_at ? parseDbTimestamp(r.created_at) : null;
    if (!dt) continue;

    const year  = dt.getFullYear();
    const month = dt.getMonth() + 1;
    const key = `${company_id}|${year}|${month}`;

    if (!tmp.has(key)) {
      tmp.set(key, {
        company_id,
        year,
        month,
        claimOrdersSet: new Set(),
        claimPieces: 0,
      });
    }

    const it = tmp.get(key);
    //if (r.order_number) it.claimOrdersSet.add(r.order_number);
    if (r.specific_id) it.claimOrdersSet.add(r.specific_id);
    it.claimPieces += num(r.defective_pieces ?? 0);
  }

  const out = new Map();
  for (const [key, val] of tmp.entries()) {
    out.set(key, {
      company_id: val.company_id,
      year: val.year,
      month: val.month,
      claimOrders: val.claimOrdersSet.size,
      claimPieces: val.claimPieces,
    });
  }

  return out;
}, [claimsBaseRecords]);

  // typy, s ktorými rátame v tabuľkách
  const TYPES = ["Interná chyba", "Externá chyba", "Reklamácia"];

  // pomocný „bucket“ pre counts/costs/pieces
  const emptyBucket = () => ({
    "Interná chyba": 0,
    "Externá chyba": 0,
    "Reklamácia": 0,
    total: 0,
  });

  /* ====== agregácie ====== */
  const aggregateByDept = (records) => {
  const map = new Map();

  for (const r of records) {

    const errType = r.type;

    // ak by sa do records náhodou dostal iný typ, preskočíme ho
    if (!TYPES.includes(errType)) continue;
    
    const dep = r.department;
    const depId = dep?.id || 0;

    const cost    = num(r.repair_costs);
    const pieces  = num(r.defective_pieces);
    const emp     = r.employee;

    // ⬇️ TU: z r si vyťahujeme identifikáciu a názov artikla
    const articleId =
      r.article_number;

    const articleName =
      r.article_name;

    if (!map.has(depId)) {
      map.set(depId, {
        department: dep || { id: 0, name: t('OTKEvaluation.Nezaradene') },
        departmentName: dep?.name || t('OTKEvaluation.Nezaradene'),

        // „bucket-y“ s total
        counts:  emptyBucket(),
        costs:   emptyBucket(),
        pieces:  emptyBucket(),

        // pre spätnú kompatibilitu (ak to niekde používaš ako číslo)
        piecesTotal: 0,

        employees: new Map(),
      });
    }

    const d = map.get(depId);

    // ---- department agregáty ----
    d.counts[errType]  += 1;
    d.counts.total     += 1;

    d.costs[errType]   += cost;
    d.costs.total      += cost;

    d.pieces[errType]  += pieces;
    d.pieces.total     += pieces;

    d.piecesTotal      += pieces;

    // ---- zamestnanci + články ----
    if (emp) {
      // zamestnanec, ako doteraz
      if (!d.employees.has(emp.id)) {
        d.employees.set(emp.id, {
          employee: emp,
          counts: emptyBucket(),
          costs:  emptyBucket(),
          pieces: emptyBucket(),
          piecesTotal: 0,

          // ⬇️ NOVÉ – k zamestnancovi pridávame mapu articles
          articles: new Map(),
        });
      }

      const e = d.employees.get(emp.id);

      // agregácia na zamestnanca
      e.counts[errType] += 1;
      e.counts.total    += 1;

      e.costs[errType]  += cost;
      e.costs.total     += cost;

      e.pieces[errType] += pieces;
      e.pieces.total    += pieces;

      e.piecesTotal     += pieces;

      // ---- tretia úroveň – artikel ----
      if (articleId != null) {
        if (!e.articles.has(articleId)) {
          e.articles.set(articleId, {
            articleId,
            articleName,
            counts: emptyBucket(),
            costs:  emptyBucket(),
            pieces: emptyBucket(),
            piecesTotal: 0,
          });
        }

        const a = e.articles.get(articleId);

        a.counts[errType] += 1;
        a.counts.total    += 1;

        a.costs[errType]  += cost;
        a.costs.total     += cost;

        a.pieces[errType] += pieces;
        a.pieces.total    += pieces;

        a.piecesTotal     += pieces;
      }
    }
  }

  return map;
};

  // agregácia „kusov“ po oddelení a type
  const aggregatePiecesByDept = (records) => {
    const map = new Map();

    for (const r of records) {
      const emp    = r.employee;
      const dep    = r.department;
      const depId  = dep?.id || 0;
      const type   = r.type; // "Interná chyba" | "Externá chyba" | "Reklamácia"
      const pieces = num(r.defective_pieces);

      // --- identifikácia artikla (rovnako ako pri prvej tabuľke) ---
      const articleId =
        r.article_number;

      const articleName =
        r.article_name;

      if (!map.has(depId)) {
        map.set(depId, {
          department: dep || { id: 0, name: t('OTKEvaluation.Nezaradene') },
          departmentName: dep?.name || t('OTKEvaluation.Nezaradene'),
          piecesByType: { "Interná chyba": 0, "Externá chyba": 0, "Reklamácia": 0 },
          piecesTotal: 0,
          employees: new Map(), // zamestnanci (→ neskôr aj artikle)
        });
      }
      const d = map.get(depId);

      // ---- oddelenie ----
      if (type && d.piecesByType[type] != null) {
        d.piecesByType[type] += pieces;
      }
      d.piecesTotal += pieces;

      // ---- zamestnanec + artikle ----
      if (emp) {
        if (!d.employees.has(emp.id)) {
          d.employees.set(emp.id, {
            employee: emp,
            piecesByType: { "Interná chyba": 0, "Externá chyba": 0, "Reklamácia": 0 },
            piecesTotal: 0,
            articles: new Map(),   // ⬅️ NOVÉ
          });
        }
        const e = d.employees.get(emp.id);

        // zamestnanec – sumy
        if (type && e.piecesByType[type] != null) {
          e.piecesByType[type] += pieces;
        }
        e.piecesTotal += pieces;

        // tretia úroveň – artikel
        if (articleId != null) {
          if (!e.articles.has(articleId)) {
            e.articles.set(articleId, {
              articleId,
              articleName,
              piecesByType: { "Interná chyba": 0, "Externá chyba": 0, "Reklamácia": 0 },
              piecesTotal: 0,
            });
          }
          const a = e.articles.get(articleId);

          if (type && a.piecesByType[type] != null) {
            a.piecesByType[type] += pieces;
          }
          a.piecesTotal += pieces;
        }
      }
    }

    return map;
  };

  // 4-úrovňová agregácia: Druh chyby -> Skupina -> Oddelenie -> Zamestnanec
const aggregateTypeGroup = (records) => {
  const map = new Map(); // key: errorTypeId

  for (const r of records || []) {
    const errorTypeId = r.error_type_id ?? 0;
    const groupId     = r.group_id ?? 0;

    const dep        = r.department;
    const depId      = dep?.id || 0;
    const depName    = dep?.name || t('OTKEvaluation.NezaradeneOddelenie');

    const emp        = r.employee;
    const empId      = emp?.id ?? emp?.offender_id ?? (emp?.name ? `emp:${emp.name}` : 0);

    const cost   = num(r.repair_costs);
    const pieces = num(r.defective_pieces);

    // 🔹 1. úroveň – DRUH CHYBY
    if (!map.has(errorTypeId)) {
      map.set(errorTypeId, {
        errorTypeId,
        errorType: errorTypes.find((t) => t.id === errorTypeId) || null,

        count:  0,
        cost:   0,
        pieces: 0,

        groups: new Map(), // groupId -> groupBucket
      });
    }
    const etBucket = map.get(errorTypeId);
    etBucket.count  += 1;
    etBucket.cost   += cost;
    etBucket.pieces += pieces;

    // 🔹 2. úroveň – SKUPINA
    const groupKey = groupId || 0;
    if (!etBucket.groups.has(groupKey)) {
      etBucket.groups.set(groupKey, {
        groupId,
        group: groups.find((g) => g.id === groupId) || null,

        count:  0,
        cost:   0,
        pieces: 0,

        departments: new Map(), // depId -> depBucket
      });
    }
    const grpBucket = etBucket.groups.get(groupKey);
    grpBucket.count  += 1;
    grpBucket.cost   += cost;
    grpBucket.pieces += pieces;

    // 🔹 3. úroveň – ODDELENIE
    const depKey = depId || 0;
    if (!grpBucket.departments.has(depKey)) {
      grpBucket.departments.set(depKey, {
        departmentId:   depId,
        department:     dep || null,
        departmentName: depName,

        count:  0,
        cost:   0,
        pieces: 0,

        employees: new Map(), // empId -> empBucket
      });
    }
    const depBucket = grpBucket.departments.get(depKey);
    depBucket.count  += 1;
    depBucket.cost   += cost;
    depBucket.pieces += pieces;

    // 🔹 4. úroveň – ZAMESTNANEC
    if (empId) {
      if (!depBucket.employees.has(empId)) {
        depBucket.employees.set(empId, {
          employeeId: empId,
          employee:   emp,

          count:  0,
          cost:   0,
          pieces: 0,
        });
      }
      const eBucket = depBucket.employees.get(empId);
      eBucket.count  += 1;
      eBucket.cost   += cost;
      eBucket.pieces += pieces;
    }
  }

  return map; // Map<errorTypeId, errorTypeBucket>
};

// Skupina chýb voči druhu chýb
const aggregateGroupType = (records) => {
  const map = new Map(); // key: groupId

  for (const r of records || []) {
    const groupId     = r.group_id ?? 0;
    const errorTypeId = r.error_type_id ?? 0;

    const dep        = r.department;
    const depId      = dep?.id ?? r.department_id ?? 0;
    const depName    = dep?.name || t('OTKEvaluation.Nezaradene');

    const emp        = r.employee;
    const empId      = emp?.id ?? emp?.offender_id ?? (emp ? `emp:${emp.name ?? ""}` : null);

    const cost   = num(r.repair_costs);
    const pieces = num(r.defective_pieces);

    // 1. úroveň – skupina
    if (!map.has(groupId)) {
      map.set(groupId, {
        groupId,
        group: groups.find((g) => g.id === groupId) || null,

        count:  0,
        cost:   0,
        pieces: 0,

        // 2. úroveň – druhy chýb
        errorTypes: new Map(), // key: errorTypeId
      });
    }

    const gBucket = map.get(groupId);
    gBucket.count  += 1;
    gBucket.cost   += cost;
    gBucket.pieces += pieces;

    // 2. úroveň – druh chyby v rámci skupiny
    if (!gBucket.errorTypes.has(errorTypeId)) {
      gBucket.errorTypes.set(errorTypeId, {
        errorTypeId,
        errorType: errorTypes.find((t) => t.id === errorTypeId) || null,

        count:  0,
        cost:   0,
        pieces: 0,

        // 3. úroveň – oddelenia
        departments: new Map(), // key: depId
      });
    }

    const etBucket = gBucket.errorTypes.get(errorTypeId);
    etBucket.count  += 1;
    etBucket.cost   += cost;
    etBucket.pieces += pieces;

    // 3. úroveň – oddelenie
    if (!etBucket.departments.has(depId)) {
      etBucket.departments.set(depId, {
        departmentId: depId,
        departmentName: depName,

        count:  0,
        cost:   0,
        pieces: 0,

        // 4. úroveň – zamestnanci
        employees: new Map(), // key: empId
      });
    }

    const dBucket = etBucket.departments.get(depId);
    dBucket.count  += 1;
    dBucket.cost   += cost;
    dBucket.pieces += pieces;

    // 4. úroveň – zamestnanec
    if (emp && empId != null) {
      if (!dBucket.employees.has(empId)) {
        dBucket.employees.set(empId, {
          employee: emp,
          count:  0,
          cost:   0,
          pieces: 0,
        });
      }

      const eBucket = dBucket.employees.get(empId);
      eBucket.count  += 1;
      eBucket.cost   += cost;
      eBucket.pieces += pieces;
    }
  }

  return map;
};

  const aggregateTypeDept = (records) => {
  const map = new Map(); // key: `${error_type_id}_${depId}`

  for (const r of records) {
    const dep         = r.department;
    const depId       = dep?.id || 0;
    const errorTypeId = r.error_type_id ?? 0;
    const key         = `${errorTypeId}_${depId}`;

    const cost   = num(r.repair_costs);
    const pieces = num(r.defective_pieces);

    const orderNumber  = r.specific_id ?? null;//r.order_number ?? null;
    const articleId    = r.article_number ?? r.article_id ?? null;
    const articleName  = r.article_number ?? r.article_name ?? null;

    if (!map.has(key)) {
      map.set(key, {
        errorTypeId,
        errorType: errorTypes.find((t) => t.id === errorTypeId) || null,

        department: dep || { id: 0, name: t('OTKEvaluation.Nezaradene') },
        departmentName: dep?.name || t('OTKEvaluation.Nezaradene'),

        // agregáty pre riadok (druh + oddelenie)
        count:  0,
        cost:   0,
        pieces: 0,

        // 2. úroveň – zamestnanci
        employees: new Map(),
      });
    }

    const bucket = map.get(key);
    bucket.count  += 1;
    bucket.cost   += cost;
    bucket.pieces += pieces;

    const emp = r.employee;
    if (emp) {
      const empId =
        emp.id ?? emp.offender_id ?? `emp:${emp.name ?? ""}`;

      if (!bucket.employees.has(empId)) {
        bucket.employees.set(empId, {
          employee: emp,
          count:  0,
          cost:   0,
          pieces: 0,

          // 3. úroveň – "riadky" zákazka/artikel
          lines: new Map(),
        });
      }

      const e = bucket.employees.get(empId);
      e.count  += 1;
      e.cost   += cost;
      e.pieces += pieces;

      // 3. úroveň – kombinácia zákazka / artikel
      const lineKey = `${orderNumber ?? "noOrder"}_${articleId ?? articleName ?? "noArticle"}`;

      if (!e.lines.has(lineKey)) {
        e.lines.set(lineKey, {
          orderNumber,
          articleId,
          articleName,
          count:  0,
          cost:   0,
          pieces: 0,
        });
      }

      const line = e.lines.get(lineKey);
      line.count  += 1;
      line.cost   += cost;
      line.pieces += pieces;
    }
  }

  return map;
};

const aggregateDeptGroup = (records) => {
  const map = new Map(); // key: depId

  for (const r of records || []) {
    const dep   = r.department;
    const depId = dep?.id ?? 0;

    const groupId = r.group_id ?? 0;

    const cost   = num(r.repair_costs);
    const pieces = num(r.defective_pieces);
    const emp    = r.employee;

    const orderNumber = r.specific_id ?? null;//r.order_number ?? null;
    const articleId   = r.article_number ?? r.article_id ?? null;
    const articleName = r.article_number ?? r.article_name ?? null;

    // 1. úroveň – oddelenie
    if (!map.has(depId)) {
      map.set(depId, {
        department: dep || { id: 0, name: t('OTKEvaluation.Nezaradene') },
        departmentName: dep?.name || t('OTKEvaluation.Nezaradene'),

        count:  0,
        pieces: 0,
        cost:   0,

        // 2. úroveň – skupiny v rámci oddelenia
        groups: new Map(), // key: groupId
      });
    }

    const depBucket = map.get(depId);
    depBucket.count  += 1;
    depBucket.pieces += pieces;
    depBucket.cost   += cost;

    // 2. úroveň – skupina
    if (!depBucket.groups.has(groupId)) {
      const grp = groups.find((g) => g.id === groupId) || null;

      depBucket.groups.set(groupId, {
        groupId,
        group: grp,
        groupName:
          grp?.name?.sk ||
          grp?.name?.en ||
          t('OTKEvaluation.NeznamaSkupina'),

        count:  0,
        pieces: 0,
        cost:   0,

        // 3. úroveň – zamestnanci
        employees: new Map(), // key: empId
      });
    }

    const grpBucket = depBucket.groups.get(groupId);
    grpBucket.count  += 1;
    grpBucket.pieces += pieces;
    grpBucket.cost   += cost;

    // 3. úroveň – zamestnanec
    if (emp) {
      const empId =
        emp.id ??
        emp.offender_id ??
        `emp:${emp.name ?? ""}`;

      if (!grpBucket.employees.has(empId)) {
        grpBucket.employees.set(empId, {
          employee: emp,
          employeeName:
            emp.full_name ||
            emp.name ||
            "",

          count:  0,
          pieces: 0,
          cost:   0,

          // 4. úroveň – kombinácia Zákazka / Artikel
          items: new Map(), // key: `${orderNumber}__${articleId}`
        });
      }

      const empBucket = grpBucket.employees.get(empId);
      empBucket.count  += 1;
      empBucket.pieces += pieces;
      empBucket.cost   += cost;

      const itemKey = `${orderNumber || "NO_ORDER"}__${articleId || "NO_ART"}`;

      if (!empBucket.items.has(itemKey)) {
        empBucket.items.set(itemKey, {
          orderNumber,
          articleId,
          articleName,

          count:  0,
          pieces: 0,
          cost:   0,
        });
      }

      const item = empBucket.items.get(itemKey);
      item.count  += 1;
      item.pieces += pieces;
      item.cost   += cost;
    }
  }

  return map;
};

  // ktoré hodnoty v `kinds` patria ku ktorým stĺpcom
  const KIND_LABELS = {
    internal: "Interná chyba",
    external: "Externá chyba",
    complaints: "Reklamácia",
  };

  // aké stĺpce sú aktuálne viditeľné
  const visibleKindKeys = Object.entries(KIND_LABELS)
    .filter(([, label]) => kinds.includes(label))
    .map(([key]) => key);

  // dynamická šírka pre stĺpce Interné / Externé / Reklamácie
  const kindColumnStyle = (() => {
    const baseWidth = 120;             // pôvodná šírka jedného stĺpca
    const defaultCount = 3;            // máme 3 stĺpce (internal, external, complaints)
    const visibleCount = visibleKindKeys.length || 1;

    const totalWidth = defaultCount * baseWidth; // 360
    const w = totalWidth / visibleCount;        // napr. 180 pri 2 stĺpcoch

    return { minWidth: w, width: w };
  })();

  const allDepartmentIds = useMemo(
  () => (departments || []).map((d) => d.id),
  [departments]
);

  useEffect(() => {
  // Auto–select všetky oddelenia len dovtedy,
  // kým s filtrom user ešte nič nerobil
  if (
    !departmentsTouched &&
    selectedDepartments.length === 0 &&
    departmentMultiOptions.length > 0
  ) {
    setSelectedDepartments(departmentMultiOptions.map((o) => o.value));
  }
}, [departmentsTouched, selectedDepartments, departmentMultiOptions]);


  const filterTreeByDepartments = (tree, selectedDepartments) => {
  if (!tree) return [];

  const selected = Array.isArray(selectedDepartments)
    ? selectedDepartments
    : [];

  // nič nevybraté → prázdna tabuľka
  if (selected.length === 0) {
    return [];
  }

  const allowed = new Set(selected);

  // root uzly sú oddelenia – filtrujeme podľa názvu oddelenia
  return (tree || []).filter((node) => {
    if (!node || !node.data) return false;

    const data = node.data;
    const depName =
      data.departmentName ||
      (data.department && data.department.name) ||
      null;

    // ak má node názov oddelenia a je medzi vybranými, necháme ho
    return depName != null && allowed.has(depName);
  });
};


useEffect(() => {
  // nastav všetkých zákazníkov ako default výber, ale iba ak user ešte nerobil zmenu
  if (!customersTouched && selectedCustomers.length === 0 && allCustomerIds.length > 0) {
    setSelectedCustomers(allCustomerIds);
  }
}, [allCustomerIds, customersTouched, selectedCustomers]);

/* ====== toolbar UI (štart) ====== */
  const startToolbar = (
    <div className="flex items-center gap-3">
      <Button
        icon="pi pi-refresh"
        label={t('OTKEvaluation.ObnovitData')}
        className="p-button-sm p-button-outlined p-2"
        onClick={() => setEvaluationFetchKey((k) => k + 1)}
        tooltip={t('OTKEvaluation.ZnovuNacitatDataPreVyhodnoteniePodlaAktualnychFiltrov')}
        tooltipOptions={{ position: "bottom" }}
      />
      <Button
        icon="pi pi-save"
        label={t('OTKEvaluation.UlozitNastavenia')}
        className="p-button-sm p-button-outlined p-2"
        onClick={() => saveUserPreferences()}
      />
      <Button
        icon="pi pi-refresh"
        label={t('OTKEvaluation.ResetovatNastavenia')}
        className="p-button-sm p-button-outlined p-button-danger p-2"
        onClick={() => {
          confirmDialog({
            message: t('OTKEvaluation.NaozajChceteResetovatVsetkyNastaveniaNaPredvoleneUlozene'),
            header: t('OTKEvaluation.ResetovatNastavenia'),
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: t('Common.Reset'),
            rejectLabel: t('Common.Cancel'),
            accept: () => resetUserPreferences(),
          });
        }}
      />
    </div>
  );

  /* ====== toolbar UI (pravá strana) ====== */
  const rightToolbarTab1 = (
    <div className="flex items-center gap-3">
      <MultiSelect
        value={kinds}
        options={ERROR_TYPES}
        onChange={(e) => setKinds(e.value)}
        optionLabel="label"
        optionValue="value"
        //display="chip"
        showSelectAll
        selectAllLabel={t('Common.All')}
        placeholder={t('ErrorTypeList')}
        className="w-[388px]"
      />
      <MultiSelect
        value={selectedDepartments}
        options={departmentMultiOptions}
        onChange={(e) => {
          setDepartmentsTouched(true);      // odteraz už user rozhoduje
          setSelectedDepartments(e.value || []);
        }}
        placeholder={t('Common.Department')}
        //display="chip"
        showSelectAll
        selectAllLabel={t('Common.All')}
        maxSelectedLabels={2}
        selectedItemsLabel={t('OTKEvaluation.0Oddeleni')}
        //optionDisabled="disabled" 
        className="w-56"
      />
      <MultiSelect
        value={selectedStates}
        options={[
          { label: t('OTKEvaluation.State.Accepted'), value: "Uznaná" },
          { label: t('OTKEvaluation.State.Rejected'), value: "Neuznaná" },
        ]}        
        onChange={(e) => setSelectedStates(e.value || [])}
        placeholder={t('Common.Status')}
        //display="chip"
        showSelectAll
        selectAllLabel={t('OTKEvaluation.Vsetko')}
        className="w-56"
      />
      <Button
        icon="pi pi-print"
        label={t('Common.PrintAction')}
        className="p-button-sm p-button-outlined p-2"
        onClick={() => {
          setPrintDialogTab(0);
          setPrintSelection(["all"]);
          setPrintDialogVisible(true);
        }}
        tooltip={t('OTKEvaluation.TlacTabuliekVyhodnotenieOddeleni')}
        tooltipOptions={{ position: "bottom" }}
      />
    </div>
  );

  const rightToolbarTab2 = (
  <div className="flex items-center gap-3">
      <MultiSelect
        value={selectedCustomers}
        options={customerMultiOptions}
        onChange={(e) => {
          setCustomersTouched(true);
          setSelectedCustomers(e.value || []);
        }}
        placeholder={t('Common.Customer')}
        showSelectAll
        selectAllLabel={t('Common.All')}
        maxSelectedLabels={2}
        selectedItemsLabel={t('OTKEvaluation.0Zakaznikov')}
        className="w-56"
      />
    <MultiSelect
      value={selectedDepartments}
      options={departmentMultiOptions}
      onChange={(e) => {
        setDepartmentsTouched(true);
        setSelectedDepartments(e.value || []);
      }}
      placeholder={t('Common.Department')}
      showSelectAll
      selectAllLabel={t('Common.All')}
      maxSelectedLabels={2}
      selectedItemsLabel={t('OTKEvaluation.0Oddeleni')}
      className="w-56"
    />
    <MultiSelect
      value={selectedStates}
      options={[
        { label: t('OTKEvaluation.State.Accepted'), value: "Uznaná" },
        { label: t('OTKEvaluation.State.Rejected'), value: "Neuznaná" },
      ]}
      onChange={(e) => setSelectedStates(e.value || [])}
      placeholder={t('Common.Status')}
      showSelectAll
      selectAllLabel={t('OTKEvaluation.Vsetko')}
      className="w-56"
    />
    <Button
      icon="pi pi-print"
      label={t('Common.PrintAction')}
      className="p-button-sm p-button-outlined p-2"
      onClick={() => {
        setPrintDialogTab(1);
        setPrintSelection(["all"]);
        setPrintDialogVisible(true);
      }}
      tooltip={t('OTKEvaluation.TlacTabuliekVyhodnotenieReklamacii')}
      tooltipOptions={{ position: "bottom" }}
    />
  </div>
);

const rightToolbar = activeTabIndex === 0 ? rightToolbarTab1 : rightToolbarTab2;

  /* ====== 1) POČTY ZA ODD. ====== */
  const [compareCountsMode, setCompareCountsMode] = useState(false);
  const [countsLeftRange, setCountsLeftRange] = useState(() => getDefaultLeftRange());
  const [countsRightRange, setCountsRightRange] = useState(null); // IMPORTANT: null, nie []

  useEffect(() => {
    if (compareCountsMode) {
      setCountsRightRange((prev) => prev ?? getDefaultRightYearRange());
    } else {
      setCountsRightRange(null); // voliteľné: ak chceš po vypnutí compare vymazať
    }
  }, [compareCountsMode]);

  const [filtersDeptCountsLeft, setFiltersDeptCountsLeft] = useState({
    departmentName: { value: null, matchMode: "equals" },
    internal: { value: null, matchMode: "contains" },
    external: { value: null, matchMode: "contains" },
    complaints: { value: null, matchMode: "contains" },
    total: { value: null, matchMode: "contains" },
  });
  const [filtersDeptCountsRight, setFiltersDeptCountsRight] = useState({
    departmentName: { value: null, matchMode: "equals" },
    internal: { value: null, matchMode: "contains" },
    external: { value: null, matchMode: "contains" },
    complaints: { value: null, matchMode: "contains" },
    total: { value: null, matchMode: "contains" },
  });
  const [expandedDeptCountsLeft, setExpandedDeptCountsLeft] = useState([]);
  const [expandedDeptCountsRight, setExpandedDeptCountsRight] = useState([]);

  // pre 1 tabuľku vs. 2 tabuľky vedľa seba
  const W1 = { nameMin: 260, numMin: 140 };
  const W2 = { nameMin: 200, numMin: 112 };
  const W  = compareCountsMode ? W2 : W1;

  const EXP = compareCountsMode ? -44 : 0;           // 0 ak expander v stĺpci Oddelenie, 44 ak samostatný
  const TT_MINW = EXP + W.nameMin + 4 * W.num;   // Oddelenie + 4 čísla (+ expander)

  // farby / hranice – drž sa palety Prime (surface) alebo Tailwindu
// hlavička / filter
  const HD = {
    background: 'var(--surface-100)',
    borderBottom: '1px solid var(--surface-200)',
    width: 120,
    minWidth: 120,
  };

  const FT = {
    background: 'var(--surface-100)',
    borderTop: '1px solid var(--surface-200)',
    fontWeight: 600,
    width: 120,
    minWidth: 120,
  };

  const HD_DN = {
    background: 'var(--surface-100)',
    borderBottom: '1px solid var(--surface-200)',
    width: 260,
    minWidth: 260,
  };

  const FT_DN = {
    background: 'var(--surface-100)',
    borderTop: '1px solid var(--surface-200)',
    fontWeight: 600,
    width: 260,
    minWidth: 260,
  };

  // spoločné pre Interné / Externé / Reklamácie / Spolu
  const KIND_COL_STYLE = {
    minWidth: 120,
    // NEzarovnávame doprava
    textAlign: 'left',
  };

  const KIND_HD = {
    background: 'var(--surface-100)',
    borderBottom: '1px solid var(--surface-200)',
  };

  const KIND_FT = {
    background: 'var(--surface-100)',
    borderTop: '1px solid var(--surface-200)',
    fontWeight: 600,
  };

  const showInternal   = kinds.includes('Interná chyba');
  const showExternal   = kinds.includes('Externá chyba');
  const showComplaints = kinds.includes('Reklamácia');


  const deptBodyTemplate = (node) => {
    const type = node?.data?.type;

    let marginLeft = '0.5rem'; // default: oddelenie
    if (type === 'employee') {
      marginLeft = '1.5rem';   // druhá úroveň – zamestnanec
    } else if (type === 'article') {
      marginLeft = '2.5rem';   // tretia úroveň – artikel
    }

    return (
      <div
        className="tt-name-cell"
        style={{
          display: 'inline-block',
          marginLeft,
          whiteSpace: 'nowrap',
        }}
      >
        {node?.data?.departmentName ?? node?.departmentName}
      </div>
    );
  };

{/*const tgNameBodyTemplate = (node) => {
  const type = node?.data?.type;

  let marginLeft = "0.5rem";
  if (type === "employee") {
    marginLeft = "1.5rem";
  } else if (type === "article") {
    marginLeft = "2.25rem"; // môžeš doladiť
  }

  let label = "";

  if (type === "department") {
    label =
      node.data.errorTypeName ??
      node.data.errorType?.name?.sk ??
      node.data.errorType?.name?.en ??
      t('OTKEvaluation.NeznamyDruh');
  } else if (type === "employee") {
    label =
      node.data.employeeName ||
      node.data.employee?.full_name ||
      node.data.employee?.name ||
      t('OTKEvaluation.NeznamyZamestnanec');
  } else if (type === "article") {
    label = node.data.articleId ?? node.data.articleName ?? "";
  }

  return (
    <div
      className="tt-name-cell"
      title={label}
      style={{
        display: "inline-block",
        marginLeft,
        // kľúčové: šírka je relatívna k bunke, nie fixných 240px
        maxWidth: "calc(100% - 2.5rem)", // necháva rezervu na šípku + odsadenie
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      {label}
    </div>
  );
};*/}

// Spoločné štýly pre TreeTable riadky (leaf / mid / root)
const ROW_STYLE_LEAF = {
  "text-slate-600": true,
  "border-b border-[var(--surface-200)]": true,
  "hover:bg-[var(--surface-50)]": true,
  "transition-colors": true,
};
const ROW_STYLE_MID = {
  "font-medium": true,
  "text-slate-800": true,
  "bg-white": true,
  "border-b border-[var(--surface-100)]": true,
  "hover:bg-[var(--surface-50)]": true,
  "transition-colors": true,
};
const ROW_STYLE_ROOT = {
  "font-semibold": true,
  "text-slate-900": true,
  "bg-[var(--surface-50)]": true,
  "border-y border-[var(--surface-200)]": true,
  "hover:bg-[var(--surface-100)]": true,
  "transition-colors": true,
};
const makeRowClassNameByLevel = (midLevelPredicate) => (node) => {
  const lvl = node?.data?.levelType;
  if (lvl === "employee") return ROW_STYLE_LEAF;
  if (midLevelPredicate(lvl)) return ROW_STYLE_MID;
  return ROW_STYLE_ROOT;
};
const rowClassNameTypeGroup = makeRowClassNameByLevel((lvl) => lvl === "group" || lvl === "department");
const rowClassNameGroupType = makeRowClassNameByLevel((lvl) => lvl === "errorType" || lvl === "department");

// názov v prvom stĺpci (Druh / Skupina / Oddelenie / Zamestnanec)
const tgNameBodyTemplate = (node) => {
  const d   = node?.data || {};
  const lvl = d.levelType;

  // label podľa úrovne
  let label = "";
  if (lvl === "employee") {
    label =
      d.employeeName ||
      d.employee?.full_name ||
      d.employee?.name ||
      t('OTKEvaluation.NeznamyZamestnanec');
  } else if (lvl === "department") {
    label =
      d.departmentName ||
      d.department?.name ||
      t('OTKEvaluation.NeznameOddelenie');
  } else if (lvl === "group") {
    label =
      d.groupName ||
      d.group?.name?.sk ||
      d.group?.name?.en ||
      t('OTKEvaluation.NeznamaSkupina');
  } else {
    // errorType (root)
    label =
      d.errorTypeName ||
      d.errorType?.name?.sk ||
      d.errorType?.name?.en ||
      t('OTKEvaluation.NeznamyDruh');
  }

  // odsadenie podľa úrovne – rovnaký pattern ako pri ostatných TT
  let marginLeft = "0.5rem";         // druh chyby
  if (lvl === "group")      marginLeft = "1.5rem";   // skupina
  else if (lvl === "department") marginLeft = "2.5rem";   // oddelenie
  else if (lvl === "employee")   marginLeft = "3.5rem";   // zamestnanec

  return (
    <div
      className="tt-name-cell"
      title={label}
      style={{
        display: "inline-block",
        marginLeft,
        maxWidth: "calc(100% - 2.5rem)", // rezerva na šípku + padding
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      {label}
    </div>
  );
};

const gtNameBodyTemplate = (node) => {
  const d   = node?.data || {};
  const lvl = d.levelType;

  let label = "";
  if (lvl === "group") {
    label = d.groupName ?? "";
  } else if (lvl === "errorType") {
    label = d.errorTypeName ?? "";
  } else if (lvl === "department") {
    label = d.departmentName ?? "";
  } else if (lvl === "employee") {
    label = d.employeeName ?? "";
  } else {
    label =
      d.groupName ||
      d.errorTypeName ||
      d.departmentName ||
      d.employeeName ||
      "";
  }

  let marginLeft = "0.5rem";
  if (lvl === "errorType")      marginLeft = "1.5rem";
  else if (lvl === "department") marginLeft = "2.5rem";
  else if (lvl === "employee")   marginLeft = "3.5rem";

  return (
    <div
      className="tt-name-cell inline-block truncate"
      style={{ marginLeft, maxWidth: "240px" }}
      title={label}
    >
      {label}
    </div>
  );
};

const tdNameBodyTemplate = (node) => {
  const d   = node?.data || {};
  const lvl = d.levelType;

  let label = "";
  if (lvl === "department") {
    label = d.departmentName ?? "";
  } else if (lvl === "errorType") {
    label = d.errorTypeName ?? "";
  } else if (lvl === "employee") {
    label = d.employeeName ?? "";
  } else if (lvl === "orderArticle") {
    label = d.orderArticleLabel ?? "";
  } else {
    label =
      d.departmentName ||
      d.errorTypeName ||
      d.employeeName ||
      d.orderArticleLabel ||
      "";
  }

  // odsadenie podľa úrovne
  let marginLeft = "0.5rem";          // oddelenie
  if (lvl === "errorType")   marginLeft = "1.5rem";
  else if (lvl === "employee")   marginLeft = "2.5rem";
  else if (lvl === "orderArticle") marginLeft = "3.5rem";

  return (
    <div
      className="tt-name-cell inline-block truncate"
      style={{ marginLeft, maxWidth: "240px" }}
      title={label}
    >
      {label}
    </div>
  );
};

const tgGroupBodyTemplate = (node) => {
  const label = node?.data?.groupName || "";

  return (
    <div
      className="inline-block truncate"
      title={label}
      style={{
        maxWidth: "100%",         // nikdy širšie ako bunka
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      {label}
    </div>
  );
};

const dgNameBodyTemplate = (node) => {
  const d   = node?.data || {};
  const lvl = d.levelType; // 'department' | 'group' | 'employee' | 'order'

  let label = "";
  if (lvl === "department") {
    label = d.departmentName ?? "";
  } else if (lvl === "group") {
    label = d.groupName ?? "";
  } else if (lvl === "employee") {
    label = d.employeeName ?? "";
  } else if (lvl === "order") {
    // ⬇️ tu spojíme zákazku + artikel
    const parts = [];
    if (d.orderNumber) parts.push(String(d.orderNumber));
    if (d.articleId)   parts.push(String(d.articleId));

    label = parts.join(" / ");  // napr. "ZAK123 / ART456"
  } else {
    label =
      d.departmentName ||
      d.groupName ||
      d.employeeName ||
      d.orderNumber ||
      d.articleId ||
      "";
  }

  let marginLeft = "0.5rem";
  if (lvl === "group")        marginLeft = "1.5rem";
  else if (lvl === "employee") marginLeft = "2.5rem";
  else if (lvl === "order")    marginLeft = "3.5rem";

  return (
    <div
      className="tt-name-cell inline-block truncate"
      style={{ marginLeft, maxWidth: "240px" }}
      title={label}
    >
      {label}
    </div>
  );
};

  const [countsRightStart, countsRightEnd] = getRangeEnds(countsRightRange);

  // 2) records podľa obdobia – tieto použijeme všade
  const countsLeftSourceRecords = useMemo(
    () => filterRecordsByRangeMonths(baseFilteredRecords, countsLeftRange),
    [baseFilteredRecords, countsLeftRange]
  );

  const countsRightSourceRecords = useMemo(
    () => filterRecordsByRangeMonths(baseFilteredRecords, countsRightRange),
    [baseFilteredRecords, countsRightRange]
  );

  // 3) agregácie – už len nad „sourceRecords“
  const countsLeftAgg = useMemo(
    () => aggregateByDept(countsLeftSourceRecords),
    [countsLeftSourceRecords]
  );

    const countsRightAgg = useMemo(
    () => aggregateByDept(countsRightSourceRecords),
    [countsRightSourceRecords]
  );

    const countsLeftRows = useMemo(
    () => [...countsLeftAgg.values()].map((r, i) => ({
      _key: i,
      departmentId: r.department?.id ?? 0,
      departmentName: r.departmentName,
      internal:   r.counts["Interná chyba"],
      external:   r.counts["Externá chyba"],
      complaints: r.counts["Reklamácia"],
      total:      r.counts.total,
      employees:  r.employees,
    })),
    [countsLeftAgg]
  );

  
const countsRightRows = useMemo(
  () => [...countsRightAgg.values()].map((r, i) => {
    const internal   = r.counts["Interná chyba"];
    const external   = r.counts["Externá chyba"];
    const complaints = r.counts["Reklamácia"];
    return {
      _key: i,
      departmentId: r.department?.id ?? 0,
      departmentName: r.departmentName,
      internal,
      external,
      complaints,
      total: internal + external + complaints,
      employees: r.employees,
    };
  }),
  [countsRightAgg]
);

  const countsLeftTree  = useMemo(() => toTreeCounts(countsLeftRows,  'L'), [countsLeftRows]);
  const countsRightTree = useMemo(() => toTreeCounts(countsRightRows, 'R'), [countsRightRows]);

const mapErrTypeToRecordType = (errType) => {
  if (errType === "internal") return "Interná chyba";
  if (errType === "external") return "Externá chyba";
  if (errType === "complaints") return "Reklamácia";
  return null;
};

const getListTabTarget = (recordType) => {
  if (recordType === "Interná chyba") return { name: t('OTKEvaluation.InternaChyba'), page: t('OTKEvaluation.Zoznamint') };
  if (recordType === "Externá chyba") return { name: t('OTKEvaluation.ExternaChyba'), page: t('OTKEvaluation.Zoznamext') };
  return { name: t('OTKEvaluation.Reklamacia'), page: t('OTKEvaluation.Zoznamrek') };
};

// bezpečný "get" pre dot-notáciu (group.name.sk)
const getByPath = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);

// Extraktory – prispôsobené tomu, čo vidno z tvojich tabulator fields
const getRecDepartmentName = (r) =>
  r.departmentName || getByPath(r, "department.name") || r.dept_name || null;

const getRecGroupName = (r) =>
  r.groupName ||
  getByPath(r, "group.name.sk") ||
  getByPath(r, "group.name.en") ||
  getByPath(r, "group.name") ||
  null;

const getRecErrorTypeName = (r) =>
  r.errorTypeName ||
  getByPath(r, "error_type.name.sk") ||
  getByPath(r, "error_type.name.en") ||
  getByPath(r, "error_type.name") ||
  null;

const getRecEmployeeName = (r) =>
  r.employeeName ||
  getByPath(r, "employee.full_name") ||
  getByPath(r, "employee.name") ||
  getByPath(r, "offender.full_name") ||
  getByPath(r, "offender.name") ||
  r.offender_name ||
  null;

const getRecArticleId = (r) =>
  r.articleId ?? r.article_id ?? r.article_number ?? null;

const getRecArticleName = (r) =>
  r.articleName ?? r.article_name ?? null;

const recordMatchesDeptEmpArticleNode = (rec, nodeData) => {
  if (!nodeData) return false;

  const { departmentId, employeeId, articleId } = nodeData;

  if (departmentId && rec.department?.id !== departmentId) return false;
  if (employeeId && rec.employee?.id !== employeeId) return false;

  if (articleId) {
    const sameArticle =
      rec.article_number === articleId ||
      rec.article_id === articleId ||
      rec.articleId === articleId;

    if (!sameArticle) return false;
  }

  return true;
};

// univerzálny filter: použije len tie kritériá, ktoré node.data reálne obsahuje
const recordMatchesNode = (rec, nodeData) => {
  const {
    errorTypeName,
    groupName,
    departmentName,
    employeeName,
    articleId,
    articleName,
  } = nodeData || {};

  if (errorTypeName && norm(getRecErrorTypeName(rec)) !== norm(errorTypeName)) return false;
  if (groupName     && norm(getRecGroupName(rec))     !== norm(groupName))     return false;
  if (departmentName&& norm(getRecDepartmentName(rec))!== norm(departmentName))return false;

  // zamestnanec: porovnávame podľa mena (keďže v strome nemáš ID)
  if (employeeName  && norm(getRecEmployeeName(rec))  !== norm(employeeName))  return false;

  // artikel (v 3. a 4. tabuľke na 4. úrovni)
  if (articleId != null) {
    const rid = getRecArticleId(rec);
    // porovnanie ako string (kvôli číslu vs string)
    if (rid == null || String(rid) !== String(articleId)) return false;
  } else if (articleName) {
    if (norm(getRecArticleName(rec)) !== norm(articleName)) return false;
  }

  return true;
};

// ====== ROOT CAUSE drilldown support (pridaj nad openOutputFromTreeNode) ======
const getRootCauseIdFromRecord = (rec) =>
  rec?.root_cause_id ?? rec?.root_cause?.id ?? null;

const recordMatchesRootCauseDeptEmpArticleNode = (rec, nodeData) => {
  const nodeRcId = nodeData?.rootCauseId ?? null;

  // ak uzol nesie rootCauseId, musí sedieť s recordom
  if (nodeRcId != null) {
    const recRcId = getRootCauseIdFromRecord(rec);
    if (recRcId == null || recRcId !== nodeRcId) return false;
  }

  // 1. úroveň (rootCause) – stačí rootCause match
  if (nodeData?.type === "rootCause") return true;

  // 2./3. úroveň (department/employee) – necháme dobehnúť pôvodný matcher
  return recordMatchesDeptEmpArticleNode(rec, nodeData);
};

// ====== tolerantné mapovanie errTypeKey (ak ešte nemáš, alebo doplň do svojej) ======
const mapErrTypeToRecordTypeSafe = (k) => {
  if (!k) return null;
  if (k === "internal" || k === "Interná chyba") return "Interná chyba";
  if (k === "external" || k === "Externá chyba") return "Externá chyba";
  if (k === "complaints" || k === "Reklamácia") return "Reklamácia";
  return null;
};

// ====== openOutputFromTreeNode (nahraď celú svoju funkciu týmto) ======
const openOutputFromTreeNode = ({
  node,
  side,
  compareMode,
  leftRecords,
  rightRecords,
  errTypeKey, // "internal" | "external" | "complaints" | null (toleruje aj SK názvy)
  dispatch,
}) => {
  if (!node?.data) return;

  const periodRecords = !compareMode || side === "L" ? leftRecords : rightRecords;
  if (!periodRecords?.length) return;

  // 1) filter podľa úrovne (rootCause/dept/emp/article)
  let matches = periodRecords.filter((rec) =>
    recordMatchesRootCauseDeptEmpArticleNode(rec, node.data)
  );
  if (!matches.length) return;

  // 2) filter podľa stĺpca (typ chyby)
  if (errTypeKey) {
    const type = mapErrTypeToRecordTypeSafe(errTypeKey); // -> "Interná chyba" | "Externá chyba" | "Reklamácia"
    if (!type) return;
    matches = matches.filter((rec) => rec.type === type);
  } else {
    matches = matches.filter((rec) => TYPES.includes(rec.type));
  }

  if (!matches.length) return;

  dispatch({
    type: "create_new_tab",
    name: t('OTKEvaluation.VystupFiltrovane'), translationKey: 'OTKEvaluation.VystupFiltrovane', legacyLabel: t('OTKEvaluation.VystupFiltrovane'),
    page: "OTKStatistics",
    props: {
      drilldownRecords: matches,
      drilldownMeta: { ...node.data, side, errTypeKey: errTypeKey ?? "all" },
    },
  });
};

// univerzálny opener listu – vyberie obdobie podľa side + range, potom filtruje podľa node.data
const openOTKListFromTree = ({
  node,
  side,
  compareMode,
  leftRange,
  rightRange,
  dispatch,
  baseFilteredRecords,
  filterRecordsByRangeMonths,
}) => {
  if (!node?.data) return;

  // keď nie je compare alebo nie je pravý range, vždy použi ľavý
  const range =
    !compareMode || side === "L" || !rightRange ? leftRange : rightRange;

  const periodRecords = filterRecordsByRangeMonths(baseFilteredRecords, range);
  const matches = periodRecords.filter((rec) => recordMatchesNode(rec, node.data));

  if (!matches.length) return;

  dispatch({
    type: "create_new_tab",
    name: t('OTKEvaluation.VystupFiltrovane'), translationKey: 'OTKEvaluation.VystupFiltrovane', legacyLabel: t('OTKEvaluation.VystupFiltrovane'),
    page: "OTKStatistics",
    props: {
      drilldownRecords: matches,
      // voliteľne – ak chceš niekde zobraziť popis filtra
      drilldownMeta: { ...node.data, side },
    },
  });
};

const handleOpenOutput_Counts = React.useCallback(
  ({ node, side, errTypeKey }) => {
    openOutputFromTreeNode({
      node,
      side,
      compareMode: compareCountsMode,
      leftRecords: countsLeftSourceRecords,
      rightRecords: countsRightSourceRecords,
      errTypeKey,
      dispatch,
    });
  },
  [compareCountsMode, countsLeftSourceRecords, countsRightSourceRecords, dispatch]
);


// helper na pekný klikateľný „link“
const CLICKABLE_LEVELS = ["department", "employee", "article"];

  // --- pomocné match funkcie (rovnako ako header filtre v TreeTable) ---
  const norm = (v) => (v ?? "").toString().toLowerCase().trim();
  const numStr = (v) =>
    (v ?? 0).toString().replace(/\s| /g, "").replace(",", "."); // odstraň medzery a NBSP

  const matchEquals = (cell, val) => {
    if (val == null || val === "") return true;
    return norm(cell) === norm(val);
  };

  const matchContains = (cell, val) => {
    if (val == null || val === "") return true;
    // čísla porovnávame ako text bez medzier, rovnako ako „contains“ v headeri
    return numStr(cell).includes(numStr(val));
  };

// --- overí, či node vyhovuje aktuálnym filtrom (len stĺpce, ktoré používaš) ---
const nodeMatchesFilters = (node, filters) => {
  const d = node?.data || {};

  const fDept   = filters?.departmentName?.value;
  const fInt    = filters?.internal?.value;
  const fExt    = filters?.external?.value;
  const fCom    = filters?.complaints?.value;
  const fTot    = filters?.total?.value;     // pre tabuľku „Počet zákazok…“ (Spolu)
  const fPieces = filters?.pieces?.value;    // kusy – používajú viaceré tabuľky

  // 🔹 nové pre Druh chyby voči skupine
  const fErrType = filters?.errorTypeName?.value;
  const fGroup   = filters?.groupName?.value;
  const fCount   = filters?.count?.value;
  const fCost    = filters?.cost?.value;

  // departmentName: equals
  if (!matchEquals(d.departmentName ?? "", fDept)) return false;

  // číselné: contains (rovnako ako v UI)
  if (!matchContains(d.internal   ?? 0, fInt))    return false;
  if (!matchContains(d.external   ?? 0, fExt))    return false;
  if (!matchContains(d.complaints ?? 0, fCom))    return false;

  // „total“ (Spolu = súčet chýb)
  if (!matchContains(d.total  ?? 0, fTot))        return false;

  // kusy – používajú aj počty kusov, aj Druh chyby voči skupine
  if (!matchContains(d.pieces ?? 0, fPieces))     return false;

  // 🔹 Druh chyby voči skupine

  // errorTypeName: equals (dropdown)
  if (!matchEquals(d.errorTypeName ?? "", fErrType)) return false;

  // groupName: equals (dropdown)
  if (!matchEquals(d.groupName ?? "", fGroup))       return false;

  // count / cost: contains (textové filtre ako inde)
  if (!matchContains(d.count ?? 0, fCount))          return false;
  if (!matchContains(d.cost  ?? 0, fCost))           return false;

  return true;
};

  // --- rekurzívne prefiltrovanie stromu: zachovaj rodiča, ak sedí on alebo ľubovoľné dieťa ---
  const filterTree = (nodes = [], filters) => {
    const out = [];
    for (const n of nodes) {
      const childrenRaw = Array.isArray(n.children) ? n.children : [];
      const childrenFiltered = filterTree(childrenRaw, filters);
      const selfOk = nodeMatchesFilters(n, filters);

      if (selfOk || childrenFiltered.length > 0) {
        out.push({
          ...n,
          // dôležité: ak rodič nesedel, ale sedelo dieťa, zobraz iba matchnuté deti
          children: childrenFiltered.length > 0 ? childrenFiltered : n.children,
        });
      }
    }
    return out;
  };

  // --- súčty len cez TOP-LEVEL (oddelenia), presne to, čo vidíš v tabuľke ---
  const sumTopLevel = (nodes = []) => {
    const acc = { internal: 0, external: 0, complaints: 0, total: 0, pieces: 0, costs: 0, };
    for (const n of nodes) {
      const d = n?.data || {};
      acc.internal   += Number(d.internal   || 0);
      acc.external   += Number(d.external   || 0);
      acc.complaints += Number(d.complaints || 0);
      acc.total      += Number(d.total      || 0);
      acc.pieces     += Number(d.pieces     || 0);
      acc.costs      += Number(d.costs      || 0);
    }
    return acc;
  };

 const countsLeftFilteredTree = React.useMemo(
  () => {
    // 1) globálny filter podľa oddelení (MultiSelect hore)
    const base = filterTreeByDepartments(countsLeftTree, selectedDepartments);

    // 2) existujúce kolonkové filtre TreeTable (search inputy v hlavičke)
    return filterTree(base, filtersDeptCountsLeft);
  },
  [countsLeftTree, selectedDepartments, filtersDeptCountsLeft]
);

const totalsLeft = React.useMemo(
  () => sumTopLevel(countsLeftFilteredTree),
  [countsLeftFilteredTree]
);

  // PRAVÁ tabuľka (porovnanie)
  const countsRightFilteredTree = React.useMemo(
  () => {
    const base = filterTreeByDepartments(countsRightTree, selectedDepartments);
    return filterTree(base, filtersDeptCountsRight);
  },
  [countsRightTree, selectedDepartments, filtersDeptCountsRight]
);

const totalsRight = React.useMemo(
  () => sumTopLevel(countsRightFilteredTree),
  [countsRightFilteredTree]
);

  // memo na options do filtra t('Common.Department')
const departmentFilterOptions = React.useMemo(() => {
  const deptOpts = (departments || [])
    .slice()
    .sort((a,b) => a.name.localeCompare(b.name))
    .map(d => ({ label: d.name, value: d.name }));     // filterujeme podľa názvu oddelenia

  return [
    { label: t('Common.All'), value: null },
    ...deptOpts,
    // oddelovač (len vizuálne, disabled)
    { label: '—', value: '__divider__', disabled: true },
    { label: t('Common.Customer'), value: 'Zákazník' },
    { label: t('Common.Supplier'), value: 'Dodávateľ' },
  ];
}, [departments]);

  /* ====== 2) NÁKLADY ZA ODD. ====== */
  const [compareCostsMode, setCompareCostsMode] = useState(false);
  const [costsLeftRange, setCostsLeftRange] = useState(() => getDefaultLeftRange());
  const [costsRightRange, setCostsRightRange] = useState(null);

  useEffect(() => {
    if (compareCostsMode) {
      setCostsRightRange((prev) => prev ?? getDefaultRightYearRange());
    } else {
      setCostsRightRange(null);
    }
  }, [compareCostsMode]);

  const [filtersDeptCostsLeft, setFiltersDeptCostsLeft] = useState({
    departmentName: { value: null, matchMode: "equals" },
    internalCost: { value: null, matchMode: "contains" },
    externalCost: { value: null, matchMode: "contains" },
    complaintsCost: { value: null, matchMode: "contains" },
    totalCosts: { value: null, matchMode: "contains" },
  });
  const [filtersDeptCostsRight, setFiltersDeptCostsRight] = useState({
    departmentName: { value: null, matchMode: "equals" },
    internalCost: { value: null, matchMode: "contains" },
    externalCost: { value: null, matchMode: "contains" },
    complaintsCost: { value: null, matchMode: "contains" },
    totalCosts: { value: null, matchMode: "contains" },
  });
  const [expandedDeptCostsLeft, setExpandedDeptCostsLeft] = useState([]);
  const [expandedDeptCostsRight, setExpandedDeptCostsRight] = useState([]);

 // 1) zdrojové records pre náklady – podľa costsLeftMonth / costsRightRange
const costsLeftSourceRecords = useMemo(
  () => filterRecordsByRangeMonths(baseFilteredRecords, costsLeftRange),
  [baseFilteredRecords, costsLeftRange]
);

const [costsRightStart, costsRightEnd] = getRangeEnds(costsRightRange);

const costsRightSourceRecords = useMemo(
  () => filterRecordsByRangeMonths(baseFilteredRecords, costsRightRange),
  [baseFilteredRecords, costsRightRange]
);

// 2) agregácie nákladov za oddelenie
const costsLeftAgg = useMemo(
  () => aggregateByDept(costsLeftSourceRecords),
  [costsLeftSourceRecords]
);

const costsRightAgg = useMemo(
  () => aggregateByDept(costsRightSourceRecords),
  [costsRightSourceRecords]
);

// 3) ploché riadky
const costsLeftRows = useMemo(
  () =>
    [...costsLeftAgg.values()].map((r, i) => ({
      ...r,
      _key: i,
      departmentId: r.department?.id ?? 0,
      internalCost:    r.costs["Interná chyba"],
      externalCost:    r.costs["Externá chyba"],
      complaintsCost:  r.costs["Reklamácia"],
      totalCosts:
        (r.costs["Interná chyba"] ?? 0) +
        (r.costs["Externá chyba"] ?? 0) +
        (r.costs["Reklamácia"] ?? 0),
    })),
  [costsLeftAgg]
);

const costsRightRows = useMemo(
  () =>
    [...costsRightAgg.values()].map((r, i) => ({
      ...r,
      _key: i,
      departmentId: r.department?.id ?? 0,
      internalCost:    r.costs["Interná chyba"],
      externalCost:    r.costs["Externá chyba"],
      complaintsCost:  r.costs["Reklamácia"],
      totalCosts:
        (r.costs["Interná chyba"] ?? 0) +
        (r.costs["Externá chyba"] ?? 0) +
        (r.costs["Reklamácia"] ?? 0),
    })),
  [costsRightAgg]
);

const costsLeftTree  = useMemo(() => toTreeCost(costsLeftRows,  "L"), [costsLeftRows]);
const costsRightTree = useMemo(() => toTreeCost(costsRightRows, "R"), [costsRightRows]);

const handleOpenOutput_Costs = React.useCallback(
  ({ node, side, errTypeKey }) => {
    openOutputFromTreeNode({
      node,
      side,
      compareMode: compareCostsMode,
      leftRecords: costsLeftSourceRecords,
      rightRecords: costsRightSourceRecords,
      errTypeKey,
      dispatch,
    });
  },
  [compareCostsMode, costsLeftSourceRecords, costsRightSourceRecords, dispatch]
);

    // súčty len cez TOP-LEVEL pre tabuľku nákladov
  const sumTopLevelCosts = (nodes = []) => {
    const acc = { internalCost: 0, externalCost: 0, complaintsCost: 0, totalCosts: 0 };
    for (const n of nodes) {
      const d = n?.data || {};
      acc.internalCost   += Number(d.internalCost    || 0);
      acc.externalCost   += Number(d.externalCost    || 0);
      acc.complaintsCost += Number(d.complaintsCost  || 0);
      acc.totalCosts      += Number(d.totalCosts      || 0);
    }
    return acc;
  };

  // ĽAVÁ tabuľka
 const costsLeftFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(costsLeftTree, selectedDepartments);
    return filterTree(base, filtersDeptCostsLeft);
  },
  [costsLeftTree, selectedDepartments, filtersDeptCostsLeft]
);

const totalsCostsLeft = useMemo(
  () => sumTopLevelCosts(costsLeftFilteredTree),
  [costsLeftFilteredTree]
);

const costsRightFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(costsRightTree, selectedDepartments);
    return filterTree(base, filtersDeptCostsRight);
  },
  [costsRightTree, selectedDepartments, filtersDeptCostsRight]
);

const totalsCostsRight = useMemo(
  () => sumTopLevelCosts(costsRightFilteredTree),
  [costsRightFilteredTree]
);


  /* ====== 2) POČET KUSOV ZA ODD. ====== */
  const [comparePiecesMode, setComparePiecesMode] = useState(false);
  const [piecesLeftRange, setPiecesLeftRange] = useState(() => getDefaultLeftRange());
  const [piecesRightRange, setPiecesRightRange] = useState(null);

    // filtre pre DRUHÚ tabuľku
  const [filtersDeptPiecesLeft, setFiltersDeptPiecesLeft] = useState({
    departmentName: { value: null, matchMode: "equals" },
    internal: { value: null, matchMode: "contains" },
    external: { value: null, matchMode: "contains" },
    complaints: { value: null, matchMode: "contains" },
    pieces: { value: null, matchMode: "contains" },
  });
  const [filtersDeptPiecesRight, setFiltersDeptPiecesRight] = useState({
    departmentName: { value: null, matchMode: "equals" },
    internal: { value: null, matchMode: "contains" },
    external: { value: null, matchMode: "contains" },
    complaints: { value: null, matchMode: "contains" },
    pieces: { value: null, matchMode: "contains" },
  });

    const piecesLeftSourceRecords = useMemo(
    () => filterRecordsByRangeMonths(baseFilteredRecords, piecesLeftRange),
    [baseFilteredRecords, piecesLeftRange]
  );

  const piecesRightSourceRecords = useMemo(
    () => filterRecordsByRangeMonths(baseFilteredRecords, piecesRightRange),
    [baseFilteredRecords, piecesRightRange]
  );


  // riadky pre DRUHÚ tabuľku (používam tie isté obdobia ako v prvej)
  // kusy – agregácie nad rovnakými records ako prvá tabuľka
  const piecesLeftAgg = useMemo(
    () => aggregatePiecesByDept(piecesLeftSourceRecords),
    [piecesLeftSourceRecords]
  );

  const piecesLeftRows = useMemo(
    () =>
      [...piecesLeftAgg.values()].map((r, i) => ({
        ...r,
        _key: i,
        departmentId: r.department?.id ?? 0,
        internal:   r.piecesByType["Interná chyba"],
        external:   r.piecesByType["Externá chyba"],
        complaints: r.piecesByType["Reklamácia"],
        pieces:     r.piecesTotal,
      })),
    [piecesLeftAgg]
  );

  const [piecesRightStart, piecesRightEnd] = getRangeEnds(countsRightRange);
  const piecesRightAgg = useMemo(
    () => aggregatePiecesByDept(piecesRightSourceRecords),
    [piecesRightSourceRecords]
  );

  const piecesRightRows = useMemo(
    () =>
      [...piecesRightAgg.values()].map((r, i) => ({
        ...r,
        _key: i,
        departmentId: r.department?.id ?? 0,
        internal:   r.piecesByType["Interná chyba"],
        external:   r.piecesByType["Externá chyba"],
        complaints: r.piecesByType["Reklamácia"],
        pieces:     r.piecesTotal,
      })),
    [piecesRightAgg]
  );

  const piecesLeftTree  = useMemo(() => toTreePieces(piecesLeftRows,  'L'), [piecesLeftRows]);
  const piecesRightTree = useMemo(() => toTreePieces(piecesRightRows, 'R'), [piecesRightRows]);

  const handleOpenOutput_Pieces = React.useCallback(
  ({ node, side, errTypeKey }) => {
    openOutputFromTreeNode({
      node,
      side,
      compareMode: comparePiecesMode,
      leftRecords: piecesLeftSourceRecords,
      rightRecords: piecesRightSourceRecords,
      errTypeKey,
      dispatch,
    });
  },
  [comparePiecesMode, piecesLeftSourceRecords, piecesRightSourceRecords, dispatch]
);

  const makeClickableCell = (formatFn, openHandler) => (field, errTypeKeyOrNull, side) => (node) => {
    const value = node?.data?.[field] ?? 0;
    const rowType = node?.data?.type;
    if (!value || !CLICKABLE_LEVELS.includes(rowType)) return formatFn(value);
    const onClick = (e) => {
      e.stopPropagation();
      openHandler({ node, side, errTypeKey: errTypeKeyOrNull });
    };
    return (
      <span
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e);
          }
        }}
        style={{ color: "#2563eb", cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
        title={t('OTKEvaluation.OtvoritVystupPrefilter')}
      >
        {formatFn(value)}
      </span>
    );
  };
  const renderClickableCountCell = makeClickableCell(FORMAT_INT, handleOpenOutput_Counts);
  const renderClickablePiecesCell = makeClickableCell(FORMAT_INT, handleOpenOutput_Pieces);
  const renderClickableCostCell = makeClickableCell(FORMAT_CURRENCY, handleOpenOutput_Costs);

  const sumTopLevelPieces = (nodes = []) => {
  const acc = { internal: 0, external: 0, complaints: 0, pieces: 0 };
  for (const n of nodes) {
    const d = n?.data || {};
    acc.internal   += Number(d.internal   || 0);
    acc.external   += Number(d.external   || 0);
    acc.complaints += Number(d.complaints || 0);
    acc.pieces     += Number(d.pieces     || 0);
  }
  return acc;
};

const piecesLeftFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(piecesLeftTree, selectedDepartments);
    return filterTree(base, filtersDeptPiecesLeft);
  },
  [piecesLeftTree, selectedDepartments, filtersDeptPiecesLeft]
);

const piecesRightFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(piecesRightTree, selectedDepartments);
    return filterTree(base, filtersDeptPiecesRight);
  },
  [piecesRightTree, selectedDepartments, filtersDeptPiecesRight]
);

const totalPiecesLeft  = useMemo(() => sumTopLevelPieces(piecesLeftFilteredTree),  [piecesLeftFilteredTree]);
const totalPiecesRight = useMemo(() => sumTopLevelPieces(piecesRightFilteredTree), [piecesRightFilteredTree]);

/* =========================================================
   KOREŇOVÁ PRÍČINA voči ODD. a ZAMESTNANCOVI (POČTY)
   1. úroveň: Koreňová príčina
   2. úroveň: Oddelenie
   3. úroveň: Zamestnanec
   Stĺpce: Interné / Externé / Reklamácie / Spolu
   + prepínanie Tabuľka/Graf + graf (Spolu / Po mesiacoch)

   PREDPOKLAD: tieto utily už máš (poslal si ich):
   - num, FORMAT_INT
   - emptyBucket()
   - parseDbTimestamp, USE_UTC
   - getRangeEnds, addMonths, monthDate, filterRecordsByRangeMonths
   - SearchFilterInput, renderSelectedMulti
   - chartColors, departments, rootCauses, baseFilteredRecords
   - renderClickableCountCell, HD/HD_DN, FT/FT_DN, PT_HEADER, TT_MINW
   ========================================================= */

/* ====== 1) KONŠTANTY ====== */
const RC_TYPES = ["Interná chyba", "Externá chyba", "Reklamácia"];

/* ====== 2) LOKALIZÁCIA názvu root_cause (v DB je JSON v stĺpci name) ====== */
/* ====== 2) LOKALIZÁCIA názvu root_cause (DB: JSON v stĺpci name) ====== */
const RC_LANG =
  (typeof navigator !== "undefined" && navigator.language
    ? navigator.language.slice(0, 2)
    : "sk") || "sk";

const localizeRootCauseName = (rawName) => {
  if (!rawName) return t('OTKEvaluation.Nezaradene');

  // ak už je to objekt {sk,en,de...}
  if (typeof rawName === "object") {
    return (
      rawName?.sk ||
      rawName?.en ||
      rawName?.de ||
      rawName?.[RC_LANG] ||
      Object.values(rawName)[0] ||
      t('OTKEvaluation.Nezaradene')
    );
  }

  // ak je to string (často JSON string)
  const s = String(rawName).trim();
  if (!s) return t('OTKEvaluation.Nezaradene');

  if (s.startsWith("{") && s.endsWith("}")) {
    try {
      const obj = JSON.parse(s);
      return localizeRootCauseName(obj);
    } catch {
      return s; // ak to nie je valid JSON, necháme string
    }
  }

  return s;
};

/* ====== 3) MAPA id -> label z master zoznamu rootCauses ====== */
const rcIdToLabel = useMemo(() => {
  const m = new Map();
  for (const rc of rootCauses) {
    m.set(rc.id, localizeRootCauseName(rc.name));
  }
  return m;
}, [rootCauses, RC_LANG]);

/* ====== 4) EXTRACT root_cause z recordu (už priamo podľa tvojej štruktúry) ====== */
const getRootCauseFromRecord = (r) => {
  const id = r?.root_cause_id ?? r?.root_cause?.id ?? 0;

  // niekedy record môže niesť aj name, ale ak nie, berieme z master mapy
  const rawName = r?.root_cause_name ?? r?.root_cause?.name ?? null;

  const name =
    localizeRootCauseName(rawName) ||
    rcIdToLabel.get(id) ||
    t('OTKEvaluation.Nezaradene');

  return { id, name };
};

/* ====== 6) AGREGÁCIA (rootCause -> department -> employee) ====== */
const aggregateByRootCauseDeptEmp = (records) => {
  const map = new Map();

  for (const r of records) {
    const errType = r.type;
    if (!RC_TYPES.includes(errType)) continue;

    const rc = getRootCauseFromRecord(r);
    const rcId = rc.id ?? 0;

    const dep = r.department;
    const depId = dep?.id ?? 0;
    const depName = dep?.name ?? t('OTKEvaluation.Nezaradene');

    const emp = r.employee;
    const empId = emp?.id ?? 0;
    const empName = emp?.name ?? t('OTKEvaluation.Nezaradene');

    if (!map.has(rcId)) {
      map.set(rcId, {
        rootCause: { id: rcId, name: rc.name },
        rootCauseName: rc.name,
        counts: emptyBucket(),
        departments: new Map(),
      });
    }

    const rcBucket = map.get(rcId);

    // 1) root cause
    rcBucket.counts[errType] += 1;
    rcBucket.counts.total += 1;

    // 2) department
    if (!rcBucket.departments.has(depId)) {
      rcBucket.departments.set(depId, {
        department: dep || { id: 0, name: t('OTKEvaluation.Nezaradene') },
        departmentName: depName,
        counts: emptyBucket(),
        employees: new Map(),
      });
    }

    const d = rcBucket.departments.get(depId);
    d.counts[errType] += 1;
    d.counts.total += 1;

    // 3) employee (aj 0 = nezaradené je ok)
    if (!d.employees.has(empId)) {
      d.employees.set(empId, {
        employee: emp || { id: 0, name: t('OTKEvaluation.Nezaradene') },
        employeeName: empName,
        counts: emptyBucket(),
      });
    }

    const e = d.employees.get(empId);
    e.counts[errType] += 1;
    e.counts.total += 1;
  }

  return map;
};

/* ====== 8) COMPARE + RANGE ====== */
const [compareRcMode, setCompareRcMode] = useState(false);
const [rcLeftRange, setRcLeftRange] = useState(() => getDefaultLeftRange());
const [rcRightRange, setRcRightRange] = useState(null); // IMPORTANT: null, nie []

useEffect(() => {
  if (compareRcMode) {
    setRcRightRange((prev) => prev ?? getDefaultRightYearRange());
  } else {
    setRcRightRange(null);
  }
}, [compareRcMode]);

/* ====== 9) FILTERS ====== */
const [filtersRcLeft, setFiltersRcLeft] = useState({
  label: { value: null, matchMode: "equals" }, // dropdown na root cause
  internal: { value: null, matchMode: "contains" },
  external: { value: null, matchMode: "contains" },
  complaints: { value: null, matchMode: "contains" },
  total: { value: null, matchMode: "contains" },
});

const [filtersRcRight, setFiltersRcRight] = useState({
  label: { value: null, matchMode: "equals" },
  internal: { value: null, matchMode: "contains" },
  external: { value: null, matchMode: "contains" },
  complaints: { value: null, matchMode: "contains" },
  total: { value: null, matchMode: "contains" },
});

/* ====== 10) SOURCE RECORDS (obdobie) ====== */
const rcLeftSourceRecords = useMemo(
  () => filterRecordsByRangeMonths(baseFilteredRecords, rcLeftRange),
  [baseFilteredRecords, rcLeftRange]
);

const rcRightSourceRecords = useMemo(
  () => filterRecordsByRangeMonths(baseFilteredRecords, rcRightRange),
  [baseFilteredRecords, rcRightRange]
);

/* ====== 11) AGG (ľavá/pravá) ====== */
const rcLeftAgg = useMemo(
  () => aggregateByRootCauseDeptEmp(rcLeftSourceRecords),
  [rcLeftSourceRecords, rcIdToLabel] // rcIdToLabel kvôli názvom (nech je fresh)
);

const rcRightAgg = useMemo(
  () => aggregateByRootCauseDeptEmp(rcRightSourceRecords),
  [rcRightSourceRecords, rcIdToLabel]
);

/* ====== 12) ROWS ====== */
const rcLeftRows = useMemo(
  () =>
    [...rcLeftAgg.values()].map((r, i) => ({
      _key: i,
      rootCauseId: r.rootCause?.id ?? 0,
      rootCauseName: r.rootCauseName,
      internal: r.counts["Interná chyba"],
      external: r.counts["Externá chyba"],
      complaints: r.counts["Reklamácia"],
      total: r.counts.total,
      departments: r.departments,
    })),
  [rcLeftAgg]
);

const rcRightRows = useMemo(
  () =>
    [...rcRightAgg.values()].map((r, i) => {
      const internal = r.counts["Interná chyba"];
      const external = r.counts["Externá chyba"];
      const complaints = r.counts["Reklamácia"];
      return {
        _key: i,
        rootCauseId: r.rootCause?.id ?? 0,
        rootCauseName: r.rootCauseName,
        internal,
        external,
        complaints,
        total: internal + external + complaints,
        departments: r.departments,
      };
    }),
  [rcRightAgg]
);

/* ====== 13) TREE ====== */
const rcLeftTree = useMemo(() => toTreeRootCauseCounts(rcLeftRows, "L"), [rcLeftRows]);
const rcRightTree = useMemo(() => toTreeRootCauseCounts(rcRightRows, "R"), [rcRightRows]);

// ak máš vlastný helper na t('OTKEvaluation.ParentOstaneKedMatchneChild'), tu ho napoj.
// inak nechaj identity:
const rcLeftFilteredTree = useMemo(() => rcLeftTree, [rcLeftTree]);
const rcRightFilteredTree = useMemo(() => rcRightTree, [rcRightTree]);

/* ====== 14) TOTALS (footer) ====== */
const rcTotalsLeft = useMemo(() => {
  const t = { internal: 0, external: 0, complaints: 0, total: 0 };
  for (const r of rcLeftRows) {
    t.internal += num(r.internal);
    t.external += num(r.external);
    t.complaints += num(r.complaints);
    t.total += num(r.total);
  }
  return t;
}, [rcLeftRows]);

const rcTotalsRight = useMemo(() => {
  const t = { internal: 0, external: 0, complaints: 0, total: 0 };
  for (const r of rcRightRows) {
    t.internal += num(r.internal);
    t.external += num(r.external);
    t.complaints += num(r.complaints);
    t.total += num(r.total);
  }
  return t;
}, [rcRightRows]);

const handleOpenOutput_RootCauseCounts = React.useCallback(
  ({ node, side, errTypeKey }) => {
    openOutputFromTreeNode({
      node,
      side,
      compareMode: compareRcMode,
      leftRecords: rcLeftSourceRecords,
      rightRecords: rcRightSourceRecords,
      errTypeKey,
      dispatch,
    });
  },
  [compareRcMode, rcLeftSourceRecords, rcRightSourceRecords, dispatch]
);

const RC_CLICKABLE_LEVELS = ["rootCause", "department", "employee"];

const rcErrTypeFromColKey = (k) => {
  if (!k) return null;
  if (k === "internal") return "Interná chyba";
  if (k === "external") return "Externá chyba";
  if (k === "complaints") return "Reklamácia";
  return k; // ak už posielaš priamo "Interná chyba" atď.
};

const renderClickableCountCellRC = (field, errTypeKeyOrNull, side) => (node) => {
  const value = node?.data?.[field] ?? 0;
  const rowType = node?.data?.type;

  if (!value || !RC_CLICKABLE_LEVELS.includes(rowType)) {
    return FORMAT_INT(value);
  }

  const errTypeKey = rcErrTypeFromColKey(errTypeKeyOrNull);

  const onClick = (e) => {
    e.stopPropagation();
    handleOpenOutput_RootCauseCounts({ node, side, errTypeKey });
  };

  return (
    <span
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={{
        color: "#2563eb",
        cursor: "pointer",
        fontWeight: 600,
        textDecoration: "underline",
      }}
      title={t('OTKEvaluation.OtvoritVystupPrefilter')}
    >
      {FORMAT_INT(value)}
    </span>
  );
};

/* ====== 16) ROW CLASS + NAME TEMPLATE ====== */
const rcRowClassName = (node) => {
  const type = node?.data?.type;
  if (type === "employee") return ROW_STYLE_LEAF;
  if (type === "department") return ROW_STYLE_MID;
  return ROW_STYLE_ROOT;
};

const rcNameBodyTemplate = (node) => {
  const type = node?.data?.type;

  let marginLeft = "0.5rem"; // root cause
  if (type === "department") marginLeft = "1.5rem";
  if (type === "employee") marginLeft = "2.5rem";

  return (
    <div
      className="tt-name-cell"
      style={{ display: "inline-block", marginLeft, whiteSpace: "nowrap" }}
    >
      {node?.data?.label ?? ""}
    </div>
  );
};

 /* ======= GRAFY ====== */
const chartColors = {
  lightBlue: "#93C5FD",   // dodané množstvo
  blue: "#1D4ED8",        // náklady
  lightOrange: "#FCD34D", // dodané kusy
  orange: "#CA8A04",      // rek kusy
  lightRed: "#FCA5A5",    // dodané zákazky
  red: "#B91C1C",         // rek zákazky
};

const getDepName = (r) =>
  r.departmentName ||
  (r.department && r.department.name) ||
  r.dept_name ||
  null;

/* ====== 17) DROPDOWN options pre filter v 1. stĺpci ====== */
const rootCauseFilterOptions = useMemo(() => {
  const fromMaster = rootCauses
    .slice()
    .map((rc) => localizeRootCauseName(rc.name))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ label, value: label }));

  if (fromMaster.length) return [{ label: t('Common.All'), value: null }, ...fromMaster];

  // fallback z rows
  const fromRows = rcLeftRows
    .slice()
    .map((x) => x.rootCauseName)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ label, value: label }));

  return [{ label: t('Common.All'), value: null }, ...fromRows];
}, [rootCauses, rcLeftRows, RC_LANG]);

/* =========================================================
   GRAF: Koreňové príčiny / Spolu (Spolu alebo Po mesiacoch)
   - Multiselect 1: Koreňová príčina
   - Multiselect 2: Oddelenie
   ========================================================= */

/* ====== 18) TABUĽKA / GRAF ====== */
const [rcTableMode, setRcTableMode] = useState(true); // true=tabuľka, false=graf

/* ====== 19) GRAF FILTRE ====== */
const [selectedRcRootCauseIds, setSelectedRcRootCauseIds] = useState([]); // hodnoty = rootCause.id
const [selectedRcDepartmentIds, setSelectedRcDepartmentIds] = useState([]); // hodnoty = department.id
const [selectedRcDepartments, setSelectedRcDepartments] = useState([]);
const [rcChartViewMode, setRcChartViewMode] = useState("total"); // "total" | "months"

/* ====== 20) OPTIONS pre multiselect (root cause) ====== */
const rcRootCauseOptions = useMemo(() => {
  const list = rootCauses
    .slice()
    .sort((a, b) => localizeRootCauseName(a.name).localeCompare(localizeRootCauseName(b.name)))
    .map((rc) => ({ label: localizeRootCauseName(rc.name), value: rc.id }));

  if (list.length) return list;

  // fallback z rows
  return rcLeftRows
    .slice()
    .sort((a, b) => (a.rootCauseName || "").localeCompare(b.rootCauseName || ""))
    .map((x) => ({ label: x.rootCauseName, value: x.rootCauseId }));
}, [rootCauses, rcLeftRows, RC_LANG]);

const rcDepartmentOptions = useMemo(() => {
  const list = (departments || [])
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map((d) => ({ label: d.name, value: d.name })); // 🔥 value = názov

  if (list.length) return list;

  // fallback z recordov
  const set = new Set();
  for (const r of rcLeftSourceRecords) {
    const dn = getDepName(r);
    if (dn) set.add(dn);
  }
  return [...set].sort().map((x) => ({ label: x, value: x }));
}, [departments, rcLeftSourceRecords]);

/* ====== 21) CHART SOURCE RECORDS (vždy z ĽAVÉHO obdobia) ====== */
const rcChartSourceRecords = useMemo(() => {
  let rows = rcLeftSourceRecords;

  if (selectedRcDepartments?.length) {
    const depSet = new Set(selectedRcDepartments); // názvy
    rows = rows.filter((r) => {
      const dn = getDepName(r);
      return dn && depSet.has(dn);
    });
  }

  if (selectedRcRootCauseIds?.length) {
    const rcSet = new Set(selectedRcRootCauseIds); // idčka
    rows = rows.filter((r) => rcSet.has(getRootCauseFromRecord(r).id));
  }

  return rows;
}, [rcLeftSourceRecords, selectedRcDepartments, selectedRcRootCauseIds, rcIdToLabel]);

const rcChartRows = useMemo(() => {
  const map = new Map(); // rcId -> row

  for (const r of rcChartSourceRecords) {
    const errType = r.type;
    if (!RC_TYPES.includes(errType)) continue;

    const rc = getRootCauseFromRecord(r);

    if (!map.has(rc.id)) {
      map.set(rc.id, {
        rootCauseId: rc.id,
        rootCauseName: rc.name,
        internal: 0,
        external: 0,
        complaints: 0,
        total: 0,
      });
    }

    const row = map.get(rc.id);

    if (errType === "Interná chyba") row.internal += 1;
    else if (errType === "Externá chyba") row.external += 1;
    else if (errType === "Reklamácia") row.complaints += 1;

    row.total += 1;
  }

  return [...map.values()].sort((a, b) => (a.rootCauseName || "").localeCompare(b.rootCauseName || ""));
}, [rcChartSourceRecords]);

/* ====== 23) MESIACE pre graf (z rcLeftRange) ====== */
const rcChartMonthDates = useMemo(() => {
  const [s0, e0] = getRangeEnds(rcLeftRange);
  if (!s0 && !e0) return [];

  const s = s0 ?? e0;
  const e = e0 ?? s0;
  const start = s <= e ? s : e;
  const end = s <= e ? e : s;

  let cur = monthDate(start.getFullYear(), start.getMonth());
  const last = monthDate(end.getFullYear(), end.getMonth());

  const out = [];
  while (cur <= last) {
    out.push(cur);
    cur = addMonths(cur, 1);
  }
  return out;
}, [rcLeftRange]);

const rcChartMonthKeys = useMemo(() => rcChartMonthDates.map((d) => getYMKey(d)), [rcChartMonthDates]);

const rcChartMonthLabels = useMemo(
  () =>
    rcChartMonthDates.map((d) => {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = String(d.getFullYear());
      return `${mm}/${yy}`;
    }),
  [rcChartMonthDates]
);

/* ====== 24) MONTHLY matrix: rcId -> (monthKey -> count) ====== */
const rcChartMonthlyMatrix = useMemo(() => {
  const monthSet = new Set(rcChartMonthKeys);
  const matrix = new Map(); // rcId -> Map(monthKeyInt -> count)

  for (const r of rcChartSourceRecords) {
    const errType = r.type;
    if (!RC_TYPES.includes(errType)) continue;

    const dt = r?.created_at ? parseDbTimestamp(r.created_at) : null;
    if (!dt) continue;

    const mk = getYMKey(dt);
    if (!monthSet.has(mk)) continue;

    const rc = getRootCauseFromRecord(r);

    if (!matrix.has(rc.id)) matrix.set(rc.id, new Map());
    const row = matrix.get(rc.id);
    row.set(mk, (row.get(mk) ?? 0) + 1);
  }

  return matrix;
}, [rcChartSourceRecords, rcChartMonthKeys, rcIdToLabel]);

const rcChartMonthlySeries = useMemo(() => {
  const monthSet = new Set(rcChartMonthKeys);

  const sums = new Map(); // mk -> {internal, external, complaints, total}
  for (const mk of rcChartMonthKeys) {
    sums.set(mk, { internal: 0, external: 0, complaints: 0, total: 0 });
  }

  for (const r of rcChartSourceRecords) {
    const errType = r.type;
    if (!RC_TYPES.includes(errType)) continue;

    const dt = r?.created_at ? parseDbTimestamp(r.created_at) : null;
    if (!dt) continue;

    const mk = getYMKey(dt);
    if (!monthSet.has(mk)) continue;

    const bucket = sums.get(mk);

    if (errType === "Interná chyba") bucket.internal += 1;
    else if (errType === "Externá chyba") bucket.external += 1;
    else if (errType === "Reklamácia") bucket.complaints += 1;

    bucket.total += 1;
  }

  return {
    internal: rcChartMonthKeys.map((mk) => sums.get(mk)?.internal ?? 0),
    external: rcChartMonthKeys.map((mk) => sums.get(mk)?.external ?? 0),
    complaints: rcChartMonthKeys.map((mk) => sums.get(mk)?.complaints ?? 0),
    total: rcChartMonthKeys.map((mk) => sums.get(mk)?.total ?? 0),
  };
}, [rcChartSourceRecords, rcChartMonthKeys]);

/* ====== 25) ktoré root causes zobraziť v monthly (ak nič vybraté, top 8) ====== */
const rcMonthlyShownIds = useMemo(() => {
  if (selectedRcRootCauseIds?.length) return selectedRcRootCauseIds;

  // top 8 podľa totals
  return rcChartRows.slice(0, 8).map((x) => x.rootCauseId);
}, [selectedRcRootCauseIds, rcChartRows]);

/* ====== 26) paleta farieb pre multi dataset ====== */
const rcPalette = [
  chartColors?.blue,
  chartColors?.lightBlue,
  chartColors?.orange,
  chartColors?.lightOrange,
  chartColors?.red,
  chartColors?.lightRed,
  chartColors?.green,
  chartColors?.purple,
  chartColors?.teal,
  chartColors?.gray,
  chartColors?.yellow,
].filter(Boolean);

const pickRcColor = (i) => rcPalette[i % rcPalette.length];


  /* ====== 3) DRUH CHYBY VOČI SKUPINE ====== */
  // pre graf – z 4-úrovňovej štruktúry spravíme riadky (Druh + Skupina)
const flattenTypeGroupRows = (aggMap) => {
  const rows = [];
  let i = 0;

  if (!aggMap) return rows;

  const values = aggMap instanceof Map ? aggMap.values() : aggMap;

  for (const et of values) {
    const errorTypeName =
      et.errorType?.name?.sk ||
      et.errorType?.name?.en ||
      t('OTKEvaluation.NeznamyDruh');

    for (const grp of et.groups.values()) {
      const groupName =
        grp.group?.name?.sk ||
        grp.group?.name?.en ||
        t('OTKEvaluation.NeznamaSkupina');

      rows.push({
        _key: i++,
        errorTypeName,
        groupName,
        count:  grp.count,
        cost:   grp.cost,
        pieces: grp.pieces,
      });
    }
  }

  return rows;
};

const [compareTypeGroupMode, setCompareTypeGroupMode] = useState(false);
const [tgLeftRange, setTgLeftRange] = useState(() => getDefaultLeftRange());
const [tgRightRange, setTgRightRange] = useState(null);

useEffect(() => {
  if (compareTypeGroupMode) {
    setTgRightRange((prev) => prev ?? getDefaultRightYearRange());
  } else {
    setTgRightRange(null);
  }
}, [compareTypeGroupMode]);

const [tgTableMode, setTgTableMode] = useState(true);

const [filtersTypeGroupLeft, setFiltersTypeGroupLeft] = useState({
  errorTypeName: { value: null, matchMode: "equals" },
  groupName:     { value: null, matchMode: "equals" },   // môže zostať, aj keď nemáme stĺpec
  count:         { value: null, matchMode: "contains" },
  cost:          { value: null, matchMode: "contains" },
  pieces:        { value: null, matchMode: "contains" },
});

const [filtersTypeGroupRight, setFiltersTypeGroupRight] = useState({
  errorTypeName: { value: null, matchMode: "equals" },
  groupName:     { value: null, matchMode: "equals" },
  count:         { value: null, matchMode: "contains" },
  cost:          { value: null, matchMode: "contains" },
  pieces:        { value: null, matchMode: "contains" },
});

// nové expandedKeys pre túto konkrétnu tabuľku
const [expandedTypeGroupLeft,  setExpandedTypeGroupLeft]  = useState({});
const [expandedTypeGroupRight, setExpandedTypeGroupRight] = useState({});
const [expandTypeGroupDialogVisible, setExpandTypeGroupDialogVisible] = useState(false);

// ľavá strana
const tgLeftAgg = useMemo(
  () => aggregateTypeGroup(filterRecordsByRangeMonths(baseFilteredRecords, tgLeftRange)),
  [baseFilteredRecords, tgLeftRange]
);

const tgLeftRows = useMemo(
  () => flattenTypeGroupRows(tgLeftAgg),
  [tgLeftAgg]
);

// pravá strana
const [tgRightStart, tgRightEnd] = getRangeEnds(tgRightRange);

const tgRightAgg = useMemo(
  () => aggregateTypeGroup(filterRecordsByRangeMonths(baseFilteredRecords, tgRightRange)),
  [baseFilteredRecords, tgRightRange]
);

const tgRightRows = useMemo(
  () => flattenTypeGroupRows(tgRightAgg),
  [tgRightAgg]
);

// 4-úrovňový strom pre TreeTable
const tgLeftTree = useMemo(
  () => toTreeTypeGroup(tgLeftAgg, "L"),
  [tgLeftAgg]
);

const tgRightTree = useMemo(
  () => toTreeTypeGroup(tgRightAgg, "R"),
  [tgRightAgg]
);

const tgLeftFilteredTree = React.useMemo(
  () => {
    const base = filterTreeByDepartments(tgLeftTree, selectedDepartments);
    return filterTree(tgLeftTree, filtersTypeGroupLeft);
  },
  [tgLeftTree, filtersTypeGroupLeft, selectedDepartments]
);

const tgRightFilteredTree = React.useMemo(
  () => {
    const base = filterTreeByDepartments(tgRightTree, selectedDepartments);
    return filterTree(tgRightTree, filtersTypeGroupRight);
  },
  [tgRightTree, filtersTypeGroupRight, selectedDepartments]
);

// súčty pre footer
const sumTopLevelTypeGroup = (nodes = []) => {
  const acc = { count: 0, cost: 0, pieces: 0 };
  for (const n of nodes) {
    const d = n?.data || {};
    acc.count  += Number(d.count  || 0);
    acc.cost   += Number(d.cost   || 0);
    acc.pieces += Number(d.pieces || 0);
  }
  return { count: acc.count, cost: roundEur(acc.cost), pieces: acc.pieces };
};

const totalsTypeGroupLeft = useMemo(
  () => sumTopLevelTypeGroup(tgLeftFilteredTree),
  [tgLeftFilteredTree]
);

const totalsTypeGroupRight = useMemo(
  () => sumTopLevelTypeGroup(tgRightFilteredTree),
  [tgRightFilteredTree]
);

const sortedTgLeftRows = useMemo(
  () =>
    [...tgLeftRows].sort((a, b) => (b.count || 0) - (a.count || 0)),
  [tgLeftRows]
);

/* ====== 4) SKUPINA CHÝB VOČI DRUHU CHÝB ====== */
const flattenGroupTypeRows = (aggMap) => {
  const rows = [];
  if (!aggMap) return rows;

  const values = aggMap instanceof Map ? aggMap.values() : aggMap;

  let i = 0;

  for (const gBucket of values) {
    const groupName =
      gBucket.group?.name?.sk ||
      gBucket.group?.name?.en ||
      gBucket.group?.name ||
      gBucket.groupName ||
      t('OTKEvaluation.NeznamaSkupina');

    // 2. úroveň – errorTypes je Map(errorTypeId -> bucket)
    const errorTypeValues =
      gBucket.errorTypes && gBucket.errorTypes.values
        ? [...gBucket.errorTypes.values()]
        : [];

    for (const etBucket of errorTypeValues) {
      const errorTypeName =
        etBucket.errorType?.name?.sk ||
        etBucket.errorType?.name?.en ||
        etBucket.errorTypeName ||
        t('OTKEvaluation.NeznamyDruh');

      rows.push({
        _key: i++,

        groupId: gBucket.groupId,
        groupName,

        errorTypeId: etBucket.errorTypeId,
        errorTypeName,

        count:  etBucket.count  ?? 0,
        cost:   etBucket.cost   ?? 0,
        pieces: etBucket.pieces ?? 0,
      });
    }
  }

  return rows;
};

const [compareGroupTypeMode, setCompareGroupTypeMode] = useState(false);
const [gtLeftRange,  setGtLeftRange]  = useState(() => getDefaultLeftRange());
const [gtRightRange, setGtRightRange] = useState(null);

useEffect(() => {
  if (compareGroupTypeMode) {
    setGtRightRange((prev) => prev ?? getDefaultRightYearRange());
  } else {
    setGtRightRange(null);
  }
}, [compareGroupTypeMode]);

const [filtersGroupTypeLeft, setFiltersGroupTypeLeft] = useState({
  groupName:     { value: null, matchMode: "equals" },
  errorTypeName: { value: null, matchMode: "equals" },
  count:         { value: null, matchMode: "contains" },
  pieces:        { value: null, matchMode: "contains" },
  cost:          { value: null, matchMode: "contains" },
});

const [filtersGroupTypeRight, setFiltersGroupTypeRight] = useState({
  groupName:     { value: null, matchMode: "equals" },
  errorTypeName: { value: null, matchMode: "equals" },
  count:         { value: null, matchMode: "contains" },
  pieces:        { value: null, matchMode: "contains" },
  cost:          { value: null, matchMode: "contains" },
});

const [gtTableMode, setGtTableMode] = useState(true);

// LEFT agg (mesiac)
const gtLeftAgg = useMemo(
  () => aggregateGroupType(filterRecordsByRangeMonths(baseFilteredRecords, gtLeftRange)),
  [baseFilteredRecords, gtLeftRange]
);

// RIGHT agg (od–do)
const [gtRightStart, gtRightEnd] = getRangeEnds(gtRightRange);
const gtRightAgg = useMemo(
  () => aggregateGroupType(filterRecordsByRangeMonths(baseFilteredRecords, gtRightRange)),
  [baseFilteredRecords, gtRightRange]
);

// stromy
const gtLeftTree  = useMemo(() => toTreeGroupType(gtLeftAgg,  "L"), [gtLeftAgg]);
const gtRightTree = useMemo(() => toTreeGroupType(gtRightAgg, "R"), [gtRightAgg]);

const gtLeftFilteredTree = React.useMemo(
  () => {
    const base = filterTreeByDepartments(gtLeftTree, selectedDepartments);
    return filterTree(gtLeftTree, filtersGroupTypeLeft);
  },
  [gtLeftTree, filtersGroupTypeLeft, selectedDepartments]
);

const gtRightFilteredTree = React.useMemo(
  () => {
    const base = filterTreeByDepartments(gtRightTree, selectedDepartments);
    return filterTree(gtRightTree, filtersGroupTypeRight);
  },
  [gtRightTree, filtersGroupTypeRight, selectedDepartments]
);

// sumár pre footer – môžeš použiť existujúci sumTopLevelTypeGroup
const totalsGroupTypeLeft = useMemo(
  () => sumTopLevelTypeGroup(gtLeftFilteredTree),
  [gtLeftFilteredTree]
);

const totalsGroupTypeRight = useMemo(
  () => sumTopLevelTypeGroup(gtRightFilteredTree),
  [gtRightFilteredTree]
);

const gtLeftRows = useMemo(
  () => flattenGroupTypeRows(gtLeftAgg),
  [gtLeftAgg]
);

const gtRightRows = useMemo(
  () =>
    [...gtRightAgg.values()].map((r, i) => ({
      ...r,
      _key: i,
      groupName:
        r.group?.name?.sk ||
        r.group?.name?.en ||
        r.groupName ||
        t('OTKEvaluation.NeznamaSkupina'),
      errorTypeName:
        r.errorType?.name?.sk ||
        r.errorType?.name?.en ||
        r.errorTypeName ||
        t('OTKEvaluation.NeznamyDruh'),
      count:  r.count  ?? 0,
      cost:   r.cost   ?? 0,
      pieces: r.pieces ?? 0,
    })),
  [gtRightAgg]
);

  /* ====== 5) DRUH CHYBY ZA ODD. ====== */
  const [compareTypeDeptMode, setCompareTypeDeptMode] = useState(false);
  const [tdLeftRange, setTdLeftRange] = useState(() => getDefaultLeftRange());
  const [tdRightRange, setTdRightRange] = useState(null);

  useEffect(() => {
    if (compareTypeDeptMode) {
      setTdRightRange((prev) => prev ?? getDefaultRightYearRange());
    } else {
      setTdRightRange(null);
    }
  }, [compareTypeDeptMode]);

  const [filtersTypeDeptLeft, setFiltersTypeDeptLeft] = useState({
    errorTypeName:  { value: null, matchMode: "equals" },
    departmentName: { value: null, matchMode: "equals" },
    count:   { value: null, matchMode: "contains" },
    pieces:  { value: null, matchMode: "contains" },
    cost:    { value: null, matchMode: "contains" },
  });

  const [filtersTypeDeptRight, setFiltersTypeDeptRight] = useState({
    errorTypeName:  { value: null, matchMode: "equals" },
    departmentName: { value: null, matchMode: "equals" },
    count:   { value: null, matchMode: "contains" },
    pieces:  { value: null, matchMode: "contains" },
    cost:    { value: null, matchMode: "contains" },
  });
  const [tdTableMode, setTdTableMode] = useState(true);
  const [expandedTypeDeptLeft, setExpandedTypeDeptLeft] = useState([]);
  const [expandedTypeDeptRight, setExpandedTypeDeptRight] = useState([]);

  const tdLeftAgg = useMemo(
  () => aggregateTypeDept(filterRecordsByRangeMonths(baseFilteredRecords, tdLeftRange)),
  [baseFilteredRecords, tdLeftRange]
  );  

const tdLeftRows = useMemo(
  () =>
    [...(tdLeftAgg?.values?.() ?? [])].map((r, i) => ({
      ...r, // nechávame employees atď. pre TreeTable
      _key: i,
      departmentName:
        r.department?.name ||
        r.departmentName ||
        t('OTKEvaluation.Nezaradene'),
      errorTypeName:
        r.errorType?.name?.sk ||
        r.errorType?.name?.en ||
        r.errorTypeName ||
        t('OTKEvaluation.NeznamyDruh'),
      count:  r.count  ?? 0,
      pieces: r.pieces ?? 0,
      cost:   r.cost   ?? 0,
    })),
  [tdLeftAgg]
);

  const [tdRightStart, tdRightEnd] = getRangeEnds(tdRightRange);

  const tdRightAgg = useMemo(
    () => aggregateTypeDept(filterRecordsByRangeMonths(baseFilteredRecords, tdRightRange)),
    [baseFilteredRecords, tdRightRange]
  );

  const tdRightRows = useMemo(
    () =>
      [...tdRightAgg.values()].map((r, i) => ({
        ...r,
        _key: i,
        errorTypeName:
          r.errorType?.name?.sk ||
          r.errorType?.name?.en ||
          t('OTKEvaluation.NeznamyDruh'),
        departmentName: r.department?.name || t('OTKEvaluation.Nezaradene'),
      })),
    [tdRightAgg]
  );

  const tdLeftTree = useMemo(
  () => toTreeTypeDept(tdLeftAgg, "L"),
  [tdLeftAgg]
  );

  const tdRightTree = useMemo(
    () => toTreeTypeDept(tdRightAgg, "R"),
    [tdRightAgg]
  );

  // filtrované stromy
const tdLeftFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(tdLeftTree, selectedDepartments);
    return filterTree(base, filtersTypeDeptLeft);
  },
  [tdLeftTree, selectedDepartments, filtersTypeDeptLeft]
);

const tdRightFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(tdRightTree, selectedDepartments);
    return filterTree(base, filtersTypeDeptRight);
  },
  [tdRightTree, selectedDepartments, filtersTypeDeptRight]
);

  // súčty do footra
const sumTopLevelTypeDept = (nodes = []) => {
  const acc = { count: 0, pieces: 0, cost: 0 };

  for (const n of nodes) {
    const d = n?.data || {};
    acc.count  += Number(d.count  || 0);
    acc.pieces += Number(d.pieces || 0);
    acc.cost   += Number(d.cost   || 0);
  }

  return acc;
};

  const totalsTypeDeptLeft = useMemo(
    () => sumTopLevelTypeDept(tdLeftFilteredTree),
    [tdLeftFilteredTree]
  );

  const totalsTypeDeptRight = useMemo(
    () => sumTopLevelTypeDept(tdRightFilteredTree),
    [tdRightFilteredTree]
  );

  const rowExpansionTypeDept = (row) => {
    const employees = [...(row.employees?.values?.() || [])];
    return (
      <div className="p-3 bg-surface-50 rounded-md">
        <DataTable value={employees} size="small" stripedRows>
          <Column field="employee.name" header={t('OTKEvaluation.Zamestnanec')} />
          <Column header={t('OTKEvaluation.PocetZakaziek')} body={(r) => FORMAT_INT(r.count)} />
          <Column header={t('OTKEvaluation.NakladyNaOpravy')} body={(r) => FORMAT_CURRENCY(r.cost)} />
        </DataTable>
      </div>
    );
  };

  /* ====== 6) ODDELENIE VOČI SKUPINE CHÝB ====== */
  // 🧩 Oddelenie -> Skupina: flatten pre graf
const flattenDeptGroupRows = (aggInput) => {
  const rows = [];
  if (!aggInput) return rows;

  const depBuckets = Array.isArray(aggInput)
    ? aggInput
    : [...(aggInput?.values?.() ?? [])];

  let i = 0;

  depBuckets.forEach((depBucket, dIndex) => {
    const departmentName =
      depBucket.departmentName ??
      depBucket.department?.name ??
      t('OTKEvaluation.Nezaradene');

    const groupValues = [...(depBucket.groups?.values?.() ?? [])];

    groupValues.forEach((grpBucket, gIndex) => {
      const groupName =
        grpBucket.groupName ??
        grpBucket.group?.name?.sk ??
        grpBucket.group?.name?.en ??
        t('OTKEvaluation.NeznamaSkupina');

      rows.push({
        _key: i++,

        departmentName,
        groupId: grpBucket.groupId ?? gIndex,
        groupName,

        count:  Number(grpBucket.count  ?? 0),
        pieces: Number(grpBucket.pieces ?? 0),
        cost:   Number(grpBucket.cost   ?? 0),
      });
    });
  });

  return rows;
};

const [compareDeptGroupMode, setCompareDeptGroupMode] = useState(false);
const [dgLeftRange, setDgLeftRange] = useState(() => getDefaultLeftRange());
const [dgRightRange, setDgRightRange] = useState(null);

useEffect(() => {
    if (compareDeptGroupMode) {
      setDgRightRange((prev) => prev ?? getDefaultRightYearRange());
    } else {
      setDgRightRange(null);
    }
  }, [compareDeptGroupMode]);

const [filtersDeptGroupLeft, setFiltersDeptGroupLeft] = useState({
  departmentName: { value: null, matchMode: "equals" },
  count:         { value: null, matchMode: "contains" },
  pieces:        { value: null, matchMode: "contains" },
  cost:          { value: null, matchMode: "contains" },
});

const [filtersDeptGroupRight, setFiltersDeptGroupRight] = useState({
  departmentName: { value: null, matchMode: "equals" },
  count:         { value: null, matchMode: "contains" },
  pieces:        { value: null, matchMode: "contains" },
  cost:          { value: null, matchMode: "contains" },
});

const [dgTableMode, setDgTableMode] = useState(true);

// rozbalovanie stromu
const [expandedDeptGroupLeft,  setExpandedDeptGroupLeft]  = useState({});
const [expandedDeptGroupRight, setExpandedDeptGroupRight] = useState({});
const [expandDeptGroupDialogVisible, setExpandDeptGroupDialogVisible] = useState(false);

// agregácie – ĽAVÁ (single mesiac)
const dgLeftAgg = useMemo(
  () => aggregateDeptGroup(filterRecordsByRangeMonths(baseFilteredRecords, dgLeftRange)),
  [baseFilteredRecords, dgLeftRange]
);

// agregácie – PRAVÁ (range mesiacov) pre compare mód
const [dgRightStart, dgRightEnd] = getRangeEnds(dgRightRange);
const dgRightAgg = useMemo(
  () => aggregateDeptGroup(filterRecordsByRangeMonths(baseFilteredRecords, dgRightRange)),
  [baseFilteredRecords, dgRightRange]
);

// riadky pre graf – flatten (oddelenie + skupina)
// rows pre graf Oddelenie vs Skupina
const dgLeftRows = useMemo(
  () => flattenDeptGroupRows(dgLeftAgg),
  [dgLeftAgg]
);


const dgRightRows = useMemo(
  () =>
    [...dgRightAgg.values()].flatMap((depBucket, i) => {
      const departmentName =
        depBucket.departmentName ??
        depBucket.department?.name ??
        t('OTKEvaluation.Nezaradene');

      const grpArray = [...(depBucket.groups?.values?.() ?? [])];

      return grpArray.map((grpBucket, gi) => ({
        _key: `${depBucket.department?.id ?? i}-${grpBucket.groupId ?? gi}`,
        departmentName,
        groupName:
          grpBucket.groupName ??
          grpBucket.group?.name?.sk ??
          grpBucket.group?.name?.en ??
          t('OTKEvaluation.NeznamaSkupina'),
        count:  grpBucket.count  ?? 0,
        pieces: grpBucket.pieces ?? 0,
        cost:   grpBucket.cost   ?? 0,
      }));
    }),
  [dgRightAgg]
);

// stromy pre TreeTable
const dgLeftTree = useMemo(
  () => toTreeDeptGroup(dgLeftAgg, "L"),
  [dgLeftAgg]
);

const dgRightTree = useMemo(
  () => toTreeDeptGroup(dgRightAgg, "R"),
  [dgRightAgg]
);

// filtrované stromy – len na výpočet footra
const dgLeftFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(dgLeftTree, selectedDepartments);
    return filterTree(base, filtersDeptGroupLeft);
  },
  [dgLeftTree, selectedDepartments, filtersDeptGroupLeft]
);

const dgRightFilteredTree = useMemo(
  () => {
    const base = filterTreeByDepartments(dgRightTree, selectedDepartments);
    return filterTree(base, filtersDeptGroupRight);
  },
  [dgRightTree, selectedDepartments, filtersDeptGroupRight]
);

// súčty top-level (oddelenia) do footra
const sumTopLevelDeptGroup = (nodes = []) => {
  const acc = { count: 0, pieces: 0, cost: 0 };
  for (const n of nodes) {
    const d = n?.data || {};
    acc.count  += Number(d.count  || 0);
    acc.pieces += Number(d.pieces || 0);
    acc.cost   += Number(d.cost   || 0);
  }
  return acc;
};

// súčty „Celkovo“
const totalsDeptGroupLeft = useMemo(
  () => sumTopLevelDeptGroup(dgLeftFilteredTree),
  [dgLeftFilteredTree]
);

const totalsDeptGroupRight = useMemo(
  () => sumTopLevelDeptGroup(dgRightFilteredTree),
  [dgRightFilteredTree]
);

const handleOpenList_TypeGroup = React.useCallback(
  ({ node, side }) =>
    openOTKListFromTree({
      node,
      side,
      compareMode: compareTypeGroupMode,
      leftRange: tgLeftRange,
      rightRange: tgRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareTypeGroupMode, tgLeftRange, tgRightRange,
    dispatch, baseFilteredRecords, filterRecordsByRangeMonths
  ]
);

const handleOpenList_GroupType = React.useCallback(
  ({ node, side }) =>
    openOTKListFromTree({
      node,
      side,
      compareMode: compareGroupTypeMode,
      leftRange: gtLeftRange,
      rightRange: gtRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareGroupTypeMode, gtLeftRange, gtRightRange,
    dispatch, baseFilteredRecords, filterRecordsByRangeMonths
  ]
);

const handleOpenList_TypeDept = React.useCallback(
  ({ node, side }) =>
    openOTKListFromTree({
      node,
      side,
      compareMode: compareTypeDeptMode,
      leftRange: tdLeftRange,
      rightRange: tdRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareTypeDeptMode, tdLeftRange, tdRightRange,
    dispatch, baseFilteredRecords, filterRecordsByRangeMonths
  ]
);

const handleOpenList_DeptGroup = React.useCallback(
  ({ node, side }) =>
    openOTKListFromTree({
      node,
      side,
      compareMode: compareDeptGroupMode,
      leftRange: dgLeftRange,
      rightRange: dgRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareDeptGroupMode, dgLeftRange, dgRightRange,
    dispatch, baseFilteredRecords, filterRecordsByRangeMonths
  ]
);


const makeClickableTreeCell = (formatFn) => (field, side, openFn) => (node) => {
  const value = node?.data?.[field] ?? 0;
  if (!value) return formatFn(value);
  const onClick = (e) => {
    e.stopPropagation();
    openFn({ node, side });
  };
  return (
    <span
      onClick={onClick}
      style={{ color: "#2563eb", cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
      title={t('OTKEvaluation.OtvoritZoznamPrefilter')}
    >
      {formatFn(value)}
    </span>
  );
};
const renderClickableTreeIntCell = makeClickableTreeCell(FORMAT_INT);
const renderClickableTreeCurrencyCell = makeClickableTreeCell(FORMAT_CURRENCY);


/***** GRAF: Druh chýb voči skupine chýb *****/
// výbery v filtroch
const [selectedErrorTypes, setSelectedErrorTypes] = useState(
  (state?.error_types || []).map((t) =>
    (t?.name && (t.name.sk || t.name.en)) ||
    t('OTKEvaluation.NeznamyDruh')
  )
);
const [selectedGroups, setSelectedGroups] = useState([]);

// možnosti pre MultiSelect z error_types v state
const errorTypeOptions = useMemo(() => {
  // 1) všetky typy, ktoré sa reálne vyskytujú v tgLeftRows
  const usedNames = new Set(
    (tgLeftRows || [])
      .map((r) => r.errorTypeName)
      .filter(Boolean)
  );

  // 2) premapuj state.error_types na { label, value }
  return (state?.error_types || [])
    .map((t) => {
      const name =
        (t?.name && (t.name.sk || t.name.en)) ||
        t('OTKEvaluation.NeznamyDruh');

      return { label: name, value: name };
    })
    .filter((opt) => usedNames.has(opt.value))
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
}, [tgLeftRows, state?.error_types]);

// možnosti pre MultiSelect zo groups v state
const groupOptions = useMemo(() => {
  if (!tgLeftRows || tgLeftRows.length === 0) return [];

  const filterByTypes =
    selectedErrorTypes && selectedErrorTypes.length > 0
      ? selectedErrorTypes
      : null;

  const rowsForGroups = tgLeftRows.filter((r) =>
    filterByTypes
      ? filterByTypes.includes(r.errorTypeName || "")
      : true
  );

  const names = Array.from(
    new Set(
      rowsForGroups
        .map((r) => r.groupName)
        .filter(Boolean)
    )
  ).sort((a, b) => (a || "").localeCompare(b || ""));

  return names.map((name) => ({ label: name, value: name }));
}, [tgLeftRows, selectedErrorTypes]);


const tgChartRows = useMemo(() => {
  if (!tgLeftRows || tgLeftRows.length === 0) return [];

  // 👇 Ak nie je vybratý žiadny druh chyby → nechceme nič v grafe
  if (!selectedErrorTypes || selectedErrorTypes.length === 0) {
    return [];
  }

  // filter podľa vybraných druhov chýb
  let rows = tgLeftRows.filter((row) =>
    selectedErrorTypes.includes(row.errorTypeName || "")
  );

  // 2a) bez vybratej skupiny → agregácia len podľa druhu chyby
  if (!selectedGroups || selectedGroups.length === 0) {
    const map = new Map();

    rows.forEach((row) => {
      const typeName = row.errorTypeName || t('OTKEvaluation.NeznamyDruh');
      const prev =
        map.get(typeName) || {
          errorTypeName: typeName,
          count: 0,
          pieces: 0,
          cost: 0,
        };

      prev.count += row.count ?? 0;
      prev.pieces += row.pieces ?? 0;
      prev.cost += row.cost ?? 0;

      map.set(typeName, prev);
    });

    const result = Array.from(map.values());
    result.sort((a, b) => (a.errorTypeName || "").localeCompare(b.errorTypeName || ""));
    return result;
  }

  // 2b) máme vybrané skupiny → agregácia podľa (typ, skupina)
  rows = rows.filter((row) =>
    selectedGroups.includes(row.groupName || "")
  );

  const map = new Map();

  rows.forEach((row) => {
    const typeName = row.errorTypeName || t('OTKEvaluation.NeznamyDruh');
    const groupName = row.groupName || "(Bez skupiny)";
    const key = typeName + "||" + groupName;

    const prev =
      map.get(key) || {
        errorTypeName: typeName,
        groupName: groupName,
        count: 0,
        pieces: 0,
        cost: 0,
      };

    prev.count += row.count ?? 0;
    prev.pieces += row.pieces ?? 0;
    prev.cost += row.cost ?? 0;

    map.set(key, prev);
  });

  const result = Array.from(map.values());
  result.sort((a, b) => {
    const c = (a.errorTypeName || "").localeCompare(b.errorTypeName || "");
    return c !== 0 ? c : (a.groupName || "").localeCompare(b.groupName || "");
  });
  return result;
}, [tgLeftRows, selectedErrorTypes, selectedGroups]);

/***** GRAF: Skupina chýb voči druhu chýb *****/
// výbery v multiselectoch (pre tento graf)
const [gtSelectedGroups, setGtSelectedGroups] = useState([]);
const [gtSelectedErrorTypes, setGtSelectedErrorTypes] = useState([]);

// MultiSelect – SKUPINY (prvý select)
// berieme len skupiny, ktoré sa reálne vyskytujú v gtLeftRows
const gtGroupOptions = useMemo(() => {
  const usedNames = new Set(
    (gtLeftRows || [])
      .map((r) => r.groupName)
      .filter(Boolean)
  );

  return (state?.groups || [])
    .map((g) => {
      const name =
        (g?.name && (g.name.sk || g.name.en)) ||
        t('OTKEvaluation.NeznamaSkupina');
      return { label: name, value: name };
    })
    .filter((opt) => usedNames.has(opt.value))
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
}, [gtLeftRows, state?.groups]);

// MultiSelect – DRUHY CHÝB (druhý select)
// berieme len tie druhy, ktoré majú dáta v gtLeftRows a zároveň v aktuálne vybraných skupinách
const gtErrorTypeOptions = useMemo(() => {
  if (!gtLeftRows || gtLeftRows.length === 0) return [];

  const filterByGroups =
    gtSelectedGroups && gtSelectedGroups.length > 0
      ? new Set(gtSelectedGroups)
      : null;

  const names = Array.from(
    new Set(
      gtLeftRows
        .filter((r) =>
          filterByGroups ? filterByGroups.has(r.groupName || "") : true
        )
        .map((r) => r.errorTypeName)
        .filter((name) => name && name !== "(Neznámy druh)")
    )
  ).sort((a, b) => (a || "").localeCompare(b || ""));

  return names.map((name) => ({ label: name, value: name }));
}, [gtLeftRows, gtSelectedGroups]);

// dátové riadky pre graf (zoradené od najväčšieho počtu)
const gtChartRows = useMemo(() => {
  if (!gtLeftRows || gtLeftRows.length === 0) return [];

  // bez vybraných skupín nechceme nič v grafe
  if (!gtSelectedGroups || gtSelectedGroups.length === 0) {
    return [];
  }

  // 1) filtrovanie podľa vybraných skupín
  let rows = gtLeftRows.filter((row) =>
    gtSelectedGroups.includes(row.groupName || "")
  );

  // 2a) ak nie sú vybrané žiadne druhy chýb → agregácia len podľa SKUPINY
  if (!gtSelectedErrorTypes || gtSelectedErrorTypes.length === 0) {
    const map = new Map();

    rows.forEach((row) => {
      const groupName = row.groupName || "(Bez skupiny)";
      const prev =
        map.get(groupName) || {
          groupName,
          count: 0,
          pieces: 0,
          cost: 0,
        };

      prev.count += row.count ?? 0;
      prev.pieces += row.pieces ?? 0;
      prev.cost += row.cost ?? 0;

      map.set(groupName, prev);
    });

    const result = Array.from(map.values());
    result.sort((a, b) => (a.groupName || "").localeCompare(b.groupName || ""));
    return result;
  }

  // 2b) máme vybrané druhy chýb → agregácia podľa (SKUPINA, DRUH)
  rows = rows.filter((row) =>
    gtSelectedErrorTypes.includes(row.errorTypeName || "")
  );

  const map = new Map();

  rows.forEach((row) => {
    const groupName = row.groupName || "(Bez skupiny)";
    const typeName = row.errorTypeName || t('OTKEvaluation.NeznamyDruh');
    const key = groupName + "||" + typeName;

    const prev =
      map.get(key) || {
        groupName,
        errorTypeName: typeName,
        count: 0,
        pieces: 0,
        cost: 0,
      };

    prev.count += row.count ?? 0;
    prev.pieces += row.pieces ?? 0;
    prev.cost += row.cost ?? 0;

    map.set(key, prev);
  });

  const result = Array.from(map.values());
  result.sort((a, b) => {
    const c = (a.groupName || "").localeCompare(b.groupName || "");
    return c !== 0 ? c : (a.errorTypeName || "").localeCompare(b.errorTypeName || "");
  });
  return result;
}, [gtLeftRows, gtSelectedGroups, gtSelectedErrorTypes]);

// === GRAF: Oddelenie voči druhu chýb ===
const [tdSelectedDepartments, setTdSelectedDepartments] = useState([]);
const [tdSelectedErrorTypes, setTdSelectedErrorTypes] = useState([]);

// možnosti pre MultiSelect – ODDelenia (iba tie, ktoré majú záznam)
const tdDepartmentOptions = useMemo(() => {
  if (!tdLeftRows || tdLeftRows.length === 0) return [];

  const names = Array.from(
    new Set(
      tdLeftRows
        .map((r) => r.departmentName)
        .filter(Boolean)
    )
  ).sort((a, b) => (a || "").localeCompare(b || ""));

  return names.map((name) => ({ label: name, value: name }));
}, [tdLeftRows]);

// možnosti pre MultiSelect – Druhy chýb (len tie, ktoré sa vyskytujú v zvolených oddeleniach)
const tdErrorTypeOptions = useMemo(() => {
  if (!tdLeftRows || tdLeftRows.length === 0) return [];

  const filterByDepartments =
    tdSelectedDepartments && tdSelectedDepartments.length > 0
      ? new Set(tdSelectedDepartments)
      : null;

  const names = Array.from(
    new Set(
      tdLeftRows
        .filter((r) =>
          filterByDepartments
            ? filterByDepartments.has(r.departmentName || "")
            : true
        )
        .map((r) => r.errorTypeName)
        .filter(
          (name) => name && name !== "(Neznámy druh)"
        )
    )
  ).sort((a, b) => (a || "").localeCompare(b || ""));

  return names.map((name) => ({ label: name, value: name }));
}, [tdLeftRows, tdSelectedDepartments]);

const tdChartRows = useMemo(() => {
  if (!tdLeftRows || tdLeftRows.length === 0) return [];

  // bez vybraných oddelení nechceme nič v grafe
  if (!tdSelectedDepartments || tdSelectedDepartments.length === 0) {
    return [];
  }

  // 1) filtrovanie podľa vybraných oddelení
  let rows = tdLeftRows.filter((row) =>
    tdSelectedDepartments.includes(row.departmentName || "")
  );

  // 2a) ak nie sú vybrané žiadne druhy chýb → agregácia len podľa ODDelenia
  if (!tdSelectedErrorTypes || tdSelectedErrorTypes.length === 0) {
    const map = new Map();

    rows.forEach((row) => {
      const depName = row.departmentName || t('OTKEvaluation.Nezaradene');
      const prev =
        map.get(depName) || {
          departmentName: depName,
          count: 0,
          pieces: 0,
          cost: 0,
        };

      prev.count += row.count ?? 0;
      prev.pieces += row.pieces ?? 0;
      prev.cost += row.cost ?? 0;

      map.set(depName, prev);
    });

    const result = Array.from(map.values());
    result.sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""));
    return result;
  }

  // 2b) máme vybrané druhy chýb → agregácia podľa (ODDelenie, DRUH)
  rows = rows.filter((row) =>
    tdSelectedErrorTypes.includes(row.errorTypeName || "")
  );

  const map = new Map();

  rows.forEach((row) => {
    const depName = row.departmentName || t('OTKEvaluation.Nezaradene');
    const typeName = row.errorTypeName || t('OTKEvaluation.NeznamyDruh');
    const key = depName + "||" + typeName;

    const prev =
      map.get(key) || {
        departmentName: depName,
        errorTypeName: typeName,
        count: 0,
        pieces: 0,
        cost: 0,
      };

    prev.count += row.count ?? 0;
    prev.pieces += row.pieces ?? 0;
    prev.cost += row.cost ?? 0;

    map.set(key, prev);
  });

  const result = Array.from(map.values());
  result.sort((a, b) => {
    const c = (a.departmentName || "").localeCompare(b.departmentName || "");
    return c !== 0 ? c : (a.errorTypeName || "").localeCompare(b.errorTypeName || "");
  });
  return result;
}, [tdLeftRows, tdSelectedDepartments, tdSelectedErrorTypes]);

// === GRAF: Oddelenie voči skupine chýb ===
const [dgSelectedDepartments, setDgSelectedDepartments] = useState([]);
const [dgSelectedGroups, setDgSelectedGroups] = useState([]);

// MultiSelect – Oddelenia (iba tie, ktoré majú záznam)
const dgDepartmentOptions = useMemo(() => {
  if (!dgLeftRows || dgLeftRows.length === 0) return [];

  const names = Array.from(
    new Set(
      dgLeftRows
        .map((r) => r.departmentName)
        .filter(Boolean)
    )
  ).sort((a, b) => (a || "").localeCompare(b || ""));

  return names.map((name) => ({ label: name, value: name }));
}, [dgLeftRows]);

// MultiSelect – Skupiny (len tie, ktoré sa vyskytujú v zvolených oddeleniach)
const dgGroupOptions = useMemo(() => {
  if (!dgLeftRows || dgLeftRows.length === 0) return [];

  const filterByDepartments =
    dgSelectedDepartments && dgSelectedDepartments.length > 0
      ? new Set(dgSelectedDepartments)
      : null;

  const names = Array.from(
    new Set(
      dgLeftRows
        .filter((r) =>
          filterByDepartments
            ? filterByDepartments.has(r.departmentName || "")
            : true
        )
        .map((r) => r.groupName)
        .filter(Boolean)
    )
  ).sort((a, b) => (a || "").localeCompare(b || ""));

  return names.map((name) => ({ label: name, value: name }));
}, [dgLeftRows, dgSelectedDepartments]);

const dgChartRows = useMemo(() => {
  if (!dgLeftRows || dgLeftRows.length === 0) return [];

  // bez vybraných oddelení nechceme nič v grafe
  if (!dgSelectedDepartments || dgSelectedDepartments.length === 0) {
    return [];
  }

  // 1) filtrovanie podľa vybraných oddelení
  let rows = dgLeftRows.filter((row) =>
    dgSelectedDepartments.includes(row.departmentName || "")
  );

  // 2a) ak nie sú vybrané žiadne skupiny → agregácia len podľa ODDelenia
  if (!dgSelectedGroups || dgSelectedGroups.length === 0) {
    const map = new Map();

    rows.forEach((row) => {
      const depName = row.departmentName || t('OTKEvaluation.Nezaradene');
      const prev =
        map.get(depName) || {
          departmentName: depName,
          count: 0,
          pieces: 0,
          cost: 0,
        };

      prev.count += row.count ?? 0;
      prev.pieces += row.pieces ?? 0;
      prev.cost += row.cost ?? 0;

      map.set(depName, prev);
    });

    const result = Array.from(map.values());
    result.sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""));
    return result;
  }

  // 2b) máme vybrané skupiny → agregácia podľa (ODDelenie, SKUPINA)
  rows = rows.filter((row) =>
    dgSelectedGroups.includes(row.groupName || "")
  );

  const map = new Map();

  rows.forEach((row) => {
    const depName = row.departmentName || t('OTKEvaluation.Nezaradene');
    const groupName = row.groupName || t('OTKEvaluation.NeznamaSkupina');
    const key = depName + "||" + groupName;

    const prev =
      map.get(key) || {
        departmentName: depName,
        groupName,
        count: 0,
        pieces: 0,
        cost: 0,
      };

    prev.count += row.count ?? 0;
    prev.pieces += row.pieces ?? 0;
    prev.cost += row.cost ?? 0;

    map.set(key, prev);
  });

  const result = Array.from(map.values());
  result.sort((a, b) => {
    const c = (a.departmentName || "").localeCompare(b.departmentName || "");
    return c !== 0 ? c : (a.groupName || "").localeCompare(b.groupName || "");
  });
  return result;
}, [dgLeftRows, dgSelectedDepartments, dgSelectedGroups]);

const renderErrorTypeLabel = () => {
  if (!selectedErrorTypes || selectedErrorTypes.length === 0) {
    // keď nie je nič vybraté
    return t('OTKEvaluation.ZiadnyVyber');
  }

  if (selectedErrorTypes.length === errorTypeOptions.length) {
    // všetky typy sú vybraté
    return t('Common.All');
  }

  // inak vypíš konkrétne typy (alebo len počet, ak chceš)
  if (selectedErrorTypes.length <= 2) {
    return selectedErrorTypes.join(', ');
  }

  return t('OTKEvaluation.SelectedCount', { count: selectedErrorTypes.length });
};

const safeDiv = (a, b) => (b > 0 ? a / b : 0);
const safePct = (a, b) => safeDiv(a, b) * 100;

const FORMAT_PCT = (v) => `${(Number(v) || 0).toFixed(2)} %`;

// súčty pre footer tabuľky
  const customerOptions = useMemo(
  () => (customers || []).map((c) => ({ label: c.name, value: c.name })),
  [customers]
  );

  // --- robustné čítanie ID z "companies" ---
  const getCompanyIdFromCompany = (c) =>
  c?.id ?? c?.company_id ?? c?.companyId ?? c?.customer_id ?? c?.customerId ?? null;

  const getCompanyNameFromCompany = (c) =>
  c?.name ?? c?.company_name ?? c?.companyName ?? c?.customer_name ?? c?.customerName ?? null;

  // Map: id -> name (companies)
  const companyNameById = useMemo(() => {
     const m = new Map(); 
     for (const c of companies || []) { 
      const id = getCompanyIdFromCompany(c); 
      const nm = getCompanyNameFromCompany(c); 
      if (id == null) continue; if (!nm) continue; 
      m.set(String(id), String(nm)); 
    } 
    return m; 
  }, [companies]);

// Map: id -> name (CUSTOMERS)  ✅ toto ti chýbalo
const customerNameById = useMemo(() => {
  const m = new Map();
  for (const c of customers || []) {
    const id = c?.id ?? c?.customer_id ?? c?.customerId ?? null;
    const nm = c?.name ?? c?.customer_name ?? c?.customerName ?? null;
    if (id == null || !nm) continue;
    m.set(String(id), String(nm));
  }
  return m;
}, [customers]);

// --- robustné čítanie ID z "customerOptions" (Dropdown/MultiSelect) ---
const getOptId = (o) => {
  const v = o?.value;
  if (v && typeof v === "object") return v.id ?? v.company_id ?? v.companyId ?? v.customer_id ?? v.customerId ?? null;
  return v ?? null;
};
const getOptLabel = (o) => o?.label ?? o?.name ?? (typeof o?.value === "object" ? (o.value?.name ?? null) : null);

// Map: id -> label (customerOptions)
const customerOptionLabelById = useMemo(() => {
  const m = new Map();
  for (const o of customerOptions || []) {
    const id = getOptId(o);
    const nm = getOptLabel(o);
    if (id == null) continue;
    if (!nm) continue;
    m.set(String(id), String(nm));
  }
  return m;
}, [customerOptions]);

// univerzálny extractor company/customer id pre Trend (records, stats, rows)
const getTrendCompanyId = (x) =>
  x?.customerId ??
  x?.customer_id ??
  x?.company_id ??
  x?.companyId ??
  x?.company_id_fk ??
  x?.company?.id ??
  x?.customer?.id ??
  null;

// finálny lookup (priorita: customers -> companies -> customerOptions)
const nameByCompanyId = (id) => {
  if (id == null || id === 0 || String(id) === "0") return t('OTKEvaluation.Neznamy');
  const k = String(id);

  return (
    customerNameById.get(k) ||              // ✅ NOVÉ (najdôležitejšie)
    companyNameById.get(k) ||               // existujúce
    customerOptionLabelById.get(k) ||       // existujúce (ale tu máš key=meno)
    t('OTKEvaluation.Neznamy')
  );
};

const TREND_PERIOD_MODE_OPTIONS = [
  { label: t('OTKEvaluation.Mesacne'), value: "month" },
  { label: t('OTKEvaluation.Rocne'), value: "year" },
];

const [trendPeriodMode, setTrendPeriodMode] = useState("month");
const [trendTableMode, setTrendTableMode] = useState("abs");

const trendSortMeta = trendTableMode === "abs" ? trendSortMetaAbs : trendSortMetaPct;
const setTrendSortMeta = (val) => {
  if (trendTableMode === "abs") setTrendSortMetaAbs(val);
  else setTrendSortMetaPct(val);
};

const getDefaultTrendRangeMonth = () => {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return [prevMonth, prevMonth];
};

const getDefaultTrendRangeYear = () => {
  const now = new Date();
  const y = now.getFullYear();
  const d = new Date(y, 0, 1);
  return [d, d];
};

const normalizeRange = (v) => {
  if (Array.isArray(v)) return v;
  if (v instanceof Date) return [v, null];
  return [null, null];
};

const [trendRangeUI, setTrendRangeUI] = useState(() => getDefaultTrendRangeMonth());
const [trendRangeApplied, setTrendRangeApplied] = useState(() => {
  const v = getDefaultTrendRangeMonth();
  return v?.[0] ? [v[0], v[1] ?? v[0]] : null;
});

const [isPending, startTransition] = useTransition();

useEffect(() => {
  const def = trendPeriodMode === "year" ? getDefaultTrendRangeYear() : getDefaultTrendRangeMonth();
  setTrendRangeUI(def);
  startTransition(() => setTrendRangeApplied(def?.[0] ? [def[0], def[1] ?? def[0]] : null));
}, [trendPeriodMode, startTransition]);

const handleTrendRangeChange = useCallback(
  (e) => {
    const ui = normalizeRange(e.value);
    setTrendRangeUI(ui);

    if (ui?.[0]) {
      const applied = [ui[0], ui[1] ?? ui[0]];
      startTransition(() => setTrendRangeApplied(applied));
    } else {
      startTransition(() => setTrendRangeApplied(null));
    }
  },
  [startTransition]
);

const trendRangeForFiltering = useMemo(() => {
  if (!trendRangeApplied?.[0]) return null;

  const s = trendRangeApplied[0];
  const e = trendRangeApplied[1] ?? trendRangeApplied[0];

  if (trendPeriodMode === "year") {
    const sy = s.getFullYear();
    const ey = e.getFullYear();
    return [new Date(sy, 0, 1), new Date(ey, 11, 1)];
  }

  return [new Date(s.getFullYear(), s.getMonth(), 1), new Date(e.getFullYear(), e.getMonth(), 1)];
}, [trendRangeApplied, trendPeriodMode]);

const trendClaimsRecordsBase = useMemo(
  () => filterRecordsByRangeMonths(claimsBaseRecords, trendRangeForFiltering),
  [claimsBaseRecords, trendRangeForFiltering]
);

const trendCompanyStatsInRange = useMemo(
  () => filterCompanyStatsByRangeMonths(companyStats, trendRangeForFiltering),
  [companyStats, trendRangeForFiltering]
);

const trendCompanyStatsFiltered = useMemo(() => {
  if (customersTouched && (!selectedCustomers || selectedCustomers.length === 0)) return [];

  const set = new Set((selectedCustomers || []).map(String));
  const isAll = set.size > 0 && customers?.length > 0 && set.size === customers.length;

  if (isAll || set.size === 0) return trendCompanyStatsInRange;

  return (trendCompanyStatsInRange || []).filter((r) => {
    const id = getTrendCompanyId(r);
    return id && set.has(String(id));
  });
}, [trendCompanyStatsInRange, selectedCustomers, customersTouched, customers]);

const trendClaimsRecords = useMemo(() => {
  if (customersTouched && (!selectedCustomers || selectedCustomers.length === 0)) return [];

  const effective =
    !customersTouched && selectedCustomers.length === 0 ? allCustomerIds : selectedCustomers;

  const set = effective?.length ? new Set(effective.map(String)) : null;
  if (!set) return trendClaimsRecordsBase;

  return (trendClaimsRecordsBase || []).filter((r) => {
    const id = getTrendCompanyId(r);
    return id && set.has(String(id));
  });
}, [trendClaimsRecordsBase, customersTouched, selectedCustomers, allCustomerIds]);

const buildPeriods = (range, mode) => {
  if (!range?.[0]) return [];
  const s = range[0];
  const e = range[1] ?? range[0];

  if (mode === "year") {
    const sy = s.getFullYear();
    const ey = e.getFullYear();
    const out = [];
    for (let y = sy; y <= ey; y++) out.push({ periodKey: String(y), periodLabel: String(y) });
    return out;
  }

  const start = new Date(s.getFullYear(), s.getMonth(), 1);
  const end = new Date(e.getFullYear(), e.getMonth(), 1);

  const out = [];
  let cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = cur.getMonth() + 1;
    const periodKey = `${y}-${String(m).padStart(2, "0")}`;
    const periodLabel = `${String(m).padStart(2, "0")}/${String(y).slice(-2)}`;
    out.push({ periodKey, periodLabel });
    cur = new Date(y, cur.getMonth() + 1, 1);
  }
  return out;
};

const trendPeriods = useMemo(
  () => buildPeriods(trendRangeApplied, trendPeriodMode),
  [trendRangeApplied, trendPeriodMode]
);

const totalsByMonth = useMemo(() => {
  const m = new Map();
  for (const r of trendCompanyStatsFiltered || []) {
    const d = statToMonthDate(r);
    if (!d) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const prev = m.get(key) || { revenue: 0, ordersTotal: 0, partsTotal: 0 };
    prev.revenue += num(r.monthly_revenue);
    prev.ordersTotal += num(r.orders_count);
    prev.partsTotal += num(r.items_count);
    m.set(key, prev);
  }
  return m;
}, [trendCompanyStatsFiltered]);

const totalsByCompanyMonth = useMemo(() => {
  const m = new Map();
  for (const r of trendCompanyStatsFiltered || []) {
    const id = getTrendCompanyId(r);
    if (!id) continue;
    const d = statToMonthDate(r);
    if (!d) continue;
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const key = `${id}|${ym}`;
    const prev = m.get(key) || { revenue: 0, ordersTotal: 0, partsTotal: 0 };
    prev.revenue += num(r.monthly_revenue);
    prev.ordersTotal += num(r.orders_count);
    prev.partsTotal += num(r.items_count);
    m.set(key, prev);
  }
  return m;
}, [trendCompanyStatsFiltered]);

const totalsByYear = useMemo(() => {
  const m = new Map();
  for (const r of trendCompanyStatsFiltered || []) {
    const y = Number(r.year ?? statToMonthDate(r)?.getFullYear?.());
    if (!y) continue;
    const key = String(y);
    const prev = m.get(key) || { revenue: 0, ordersTotal: 0, partsTotal: 0 };
    prev.revenue += num(r.monthly_revenue);
    prev.ordersTotal += num(r.orders_count);
    prev.partsTotal += num(r.items_count);
    m.set(key, prev);
  }
  return m;
}, [trendCompanyStatsFiltered]);

const totalsByCompanyYear = useMemo(() => {
  const m = new Map();
  for (const r of trendCompanyStatsFiltered || []) {
    const id = getTrendCompanyId(r);
    if (!id) continue;
    const y = Number(r.year ?? statToMonthDate(r)?.getFullYear?.());
    if (!y) continue;
    const key = `${id}|${y}`;
    const prev = m.get(key) || { revenue: 0, ordersTotal: 0, partsTotal: 0 };
    prev.revenue += num(r.monthly_revenue);
    prev.ordersTotal += num(r.orders_count);
    prev.partsTotal += num(r.items_count);
    m.set(key, prev);
  }
  return m;
}, [trendCompanyStatsFiltered]);

const computeTrendLine = (values = []) => {
  // Do výpočtu zahrnieme aj nulové hodnoty (len vyradíme NaN)
  const pts = values
    .map((y, i) => ({ x: i, y: Number(y) || 0 }))
    .filter((p) => !Number.isNaN(p.y));

  if (pts.length < 2) return values.map(() => null);

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  for (const p of pts) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumXX += p.x * p.x;
  }

  const n = pts.length;
  const denom = n * sumXX - sumX * sumX;
  if (!denom) return values.map(() => null);

  const a = (n * sumXY - sumX * sumY) / denom;
  const b = (sumY - a * sumX) / n;

  // Trendová čiara pre všetky body (vrátane pozícií s nulovou hodnotou)
  return values.map((_, i) => a * i + b);
};

function aggregateTrend(records = [], totalsMap = new Map(), options = {}) {
  const { groupByCustomer = false, nameByCompanyId, periodMode = "month" } = options;

  const map = new Map();

  for (const r of records) {
    if (r.type !== "Reklamácia") continue;
    if (selectedStates.length<2){
      if(selectedStates.length==1){
        if(selectedStates[0]?.label=="Uznaná" && r.ack_error !== "Uznaná")continue;
        else if(selectedStates[0]?.label=="Neuznaná" && r.ack_error !== "Neuznaná")continue;
      }
      else{
        if (r.ack_error !== "") continue;
      }
    }
    const dt = r.created_at ? parseDbTimestamp(r.created_at) : null;
    if (!dt) continue;

    const year = USE_UTC ? dt.getUTCFullYear() : dt.getFullYear();
    const month = (USE_UTC ? dt.getUTCMonth() : dt.getMonth()) + 1;

    const periodKey =
      periodMode === "year" ? String(year) : `${year}-${String(month).padStart(2, "0")}`;

    const periodLabel =
      periodMode === "year" ? String(year) : `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;

    const companyId = getTrendCompanyId(r);
    if (groupByCustomer && !companyId) continue; // nech nerobí customer=0 => (Neznámy)

    const key = groupByCustomer ? `${companyId}|${periodKey}` : periodKey;

    if (!map.has(key)) {
      map.set(key, {
        _key: key,
        periodKey,
        periodLabel,

        customerId: groupByCustomer ? companyId : null,
        customer: groupByCustomer ? nameByCompanyId(companyId) : null,

        ordersCount: 0,
        partsCount: 0,
        claimsCost: 0,
      });
    }

    const it = map.get(key);

    it.ordersCount += 1;
    it.partsCount += num(r.defective_pieces);
    it.claimsCost += num(r.repair_cost ?? r.repair_costs ?? r.claim_cost ?? 0);
  }

  const out = [...map.values()].map((it) => {
    const denomKey = groupByCustomer ? it._key : it.periodKey;

    const totals = totalsMap.get(denomKey) || {
      revenue: 0,
      ordersTotal: 0,
      partsTotal: 0,
    };

    const ordersCount = it.ordersCount;
    const partsCount = it.partsCount;
    const claimsCost = it.claimsCost;

    return {
      _key: it._key,
      customerId: it.customerId,
      customer: it.customer,

      periodLabel: it.periodLabel,
      periodKey: it.periodKey,

      ordersCount,
      partsCount,
      claimsCost,

      revenue: totals.revenue,
      ordersTotal: totals.ordersTotal,
      partsTotal: totals.partsTotal,

      costPct: safePct(claimsCost, totals.revenue),
      ordersPct: safePct(ordersCount, totals.ordersTotal),
      partsPct: safePct(partsCount, totals.partsTotal),
    };
  });

  out.sort((a, b) => {
    const c = String(a.periodKey).localeCompare(String(b.periodKey));
    if (c !== 0) return c;
    return String(a.customer ?? "").localeCompare(String(b.customer ?? ""));
  });

  return out;
}

const fillOverall = (rows, totalsMap, periods) => {
  const byKey = new Map((rows || []).map((r) => [String(r.periodKey), r]));
  return (periods || []).map((p) => {
    const existing = byKey.get(String(p.periodKey));
    if (existing) return existing;

    const totals = totalsMap.get(String(p.periodKey)) || { revenue: 0, ordersTotal: 0, partsTotal: 0 };

    const claimsCost = 0;
    const ordersCount = 0;
    const partsCount = 0;

    return {
      _key: p.periodKey,
      customerId: null,
      customer: null,

      periodKey: p.periodKey,
      periodLabel: p.periodLabel,

      ordersCount,
      partsCount,
      claimsCost,

      revenue: totals.revenue,
      ordersTotal: totals.ordersTotal,
      partsTotal: totals.partsTotal,

      costPct: safePct(claimsCost, totals.revenue),
      ordersPct: safePct(ordersCount, totals.ordersTotal),
      partsPct: safePct(partsCount, totals.partsTotal),
    };
  });
};

const fillByCustomer = (rows, totalsMap, periods, customerIds) => {
  const byKey = new Map((rows || []).map((r) => [String(r._key), r]));
  const out = [];

  for (const cid of customerIds || []) {
    for (const p of periods || []) {
      const k = `${cid}|${p.periodKey}`;
      const existing = byKey.get(String(k));
      if (existing) {
        out.push(existing);
        continue;
      }

      const totals = totalsMap.get(String(k)) || { revenue: 0, ordersTotal: 0, partsTotal: 0 };

      const claimsCost = 0;
      const ordersCount = 0;
      const partsCount = 0;

      out.push({
        _key: k,
        customerId: cid,
        customer: nameByCompanyId(cid),

        periodKey: p.periodKey,
        periodLabel: p.periodLabel,

        ordersCount,
        partsCount,
        claimsCost,

        revenue: totals.revenue,
        ordersTotal: totals.ordersTotal,
        partsTotal: totals.partsTotal,

        costPct: safePct(claimsCost, totals.revenue),
        ordersPct: safePct(ordersCount, totals.ordersTotal),
        partsPct: safePct(partsCount, totals.partsTotal),
      });
    }
  }

  out.sort((a, b) => {
    const c = String(a.customer ?? "").localeCompare(String(b.customer ?? ""));
    if (c !== 0) return c;
    return String(a.periodKey).localeCompare(String(b.periodKey));
  });

  return out;
};

const trendTotalsMap = useMemo(
  () => (trendPeriodMode === "year" ? totalsByYear : totalsByMonth),
  [trendPeriodMode, totalsByYear, totalsByMonth]
);

const trendTotalsByCustomerMap = useMemo(
  () => (trendPeriodMode === "year" ? totalsByCompanyYear : totalsByCompanyMonth),
  [trendPeriodMode, totalsByCompanyYear, totalsByCompanyMonth]
);

const trendRowsOverallRaw = useMemo(
  () => aggregateTrend(trendClaimsRecords, trendTotalsMap, { periodMode: trendPeriodMode }),
  [trendClaimsRecords, trendTotalsMap, trendPeriodMode]
);

const trendRowsOverall = useMemo(
  () => fillOverall(trendRowsOverallRaw, trendTotalsMap, trendPeriods),
  [trendRowsOverallRaw, trendTotalsMap, trendPeriods]
);

const trendCustomerIds = useMemo(() => {
  const ids = new Set();
  for (const r of trendCompanyStatsFiltered || []) {
    const id = getTrendCompanyId(r);
    if (id) ids.add(String(id));
  }
  return [...ids];
}, [trendCompanyStatsFiltered]);

const trendRowsTableRaw = useMemo(
  () =>
    aggregateTrend(trendClaimsRecords, trendTotalsByCustomerMap, {
      groupByCustomer: true,
      nameByCompanyId,
      periodMode: trendPeriodMode,
    }),
  [trendClaimsRecords, trendTotalsByCustomerMap, nameByCompanyId, trendPeriodMode]
);

const trendRowsTable = useMemo(
  () => fillByCustomer(trendRowsTableRaw, trendTotalsByCustomerMap, trendPeriods, trendCustomerIds),
  [trendRowsTableRaw, trendTotalsByCustomerMap, trendPeriods, trendCustomerIds]
);

// 9 metrík (bez delenia podľa Zobrazenia); trendové čiary sú len v legende grafu, nie v Selecte
const TREND_METRICS = [
  { label: t('OTKEvaluation.DodaneZakazkyPc'), value: "ordersTotal", kind: "count" },
  { label: t('OTKEvaluation.ReklamovaneZakazkyPc'), value: "ordersCount", kind: "count" },
  { label: t('OTKEvaluation.ReklamovaneZakazky'), value: "ordersPct", kind: "pct" },
  { label: t('OTKEvaluation.DodaneKusyPc'), value: "partsTotal", kind: "count" },
  { label: t('OTKEvaluation.ReklamovaneKusyPc'), value: "partsCount", kind: "count" },
  { label: t('OTKEvaluation.ReklamovaneKusy'), value: "partsPct", kind: "pct" },
  { label: t('OTKEvaluation.DeliveredAmountCurrency', { currency: currencySymbol }), value: "revenue", kind: "eur" },
  { label: t('OTKEvaluation.RepairCostsCurrency', { currency: currencySymbol }), value: "claimsCost", kind: "eur" },
  { label: t('OTKEvaluation.NakladyNaOpravy2'), value: "costPct", kind: "pct" },
];

const TREND_DISPLAY_OPTIONS = [
  { label: t('OTKEvaluation.Pocty'), value: "count" },
  { label: currencySymbol, value: "eur" },
  { label: t('OTKEvaluation.Percenta'), value: "pct" },
  { label: t('OTKEvaluation.CountsPlusCurrency', { currency: currencySymbol }), value: "count_eur" },
  { label: t('OTKEvaluation.Pocty2'), value: "count_pct" },
  { label: `${currencySymbol} + %`, value: "eur_pct" },
];

const TREND_DISPLAY_KINDS = {
  count: ["count"],
  eur: ["eur"],
  pct: ["pct"],
  count_eur: ["count", "eur"],
  count_pct: ["count", "pct"],
  eur_pct: ["eur", "pct"],
};

const getMetricsForDisplayType = (displayType) => {
  if (!displayType) return [];
  const kinds = TREND_DISPLAY_KINDS[displayType];
  if (!kinds) return [];
  return TREND_METRICS.filter((m) => kinds.includes(m.kind)).map((m) => ({ label: m.label, value: m.value }));
};

const getMetricKind = (value) => TREND_METRICS.find((m) => m.value === value)?.kind ?? null;

const trendChartRows = useMemo(() => {
  if (customersTouched && selectedCustomers?.length === 1) {
    const id = String(selectedCustomers[0]);
    return (trendRowsTable || [])
      .filter((r) => String(r.customerId) === id)
      .sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)));
  }
  return trendRowsOverall;
}, [trendRowsTable, trendRowsOverall, customersTouched, selectedCustomers]);

const [filtersTrend, setFiltersTrend] = useState({
  departmentName: { value: null, matchMode: "equals" },

  ordersTotal: { value: null, matchMode: "contains" },
  ordersCount: { value: null, matchMode: "contains" },
  ordersPct: { value: null, matchMode: "contains" },

  partsTotal: { value: null, matchMode: "contains" },
  partsCount: { value: null, matchMode: "contains" },
  partsPct: { value: null, matchMode: "contains" },

  revenue: { value: null, matchMode: "contains" },
  claimsCost: { value: null, matchMode: "contains" },
  costPct: { value: null, matchMode: "contains" },
});

const [expandedTrend, setExpandedTrend] = useState({});
const [expandTrendDialogVisible, setExpandTrendDialogVisible] = useState(false);
const [trendActiveNode, setTrendActiveNode] = useState(null);

const trendScope = useMemo(() => {
  const n = trendActiveNode?.data;

  if (n?.type === "customer") {
    return {
      type: "customer",
      periodKey: null,
      customerId: String(n.customerId ?? ""),
      label: n.departmentName ?? n.customer ?? "",
    };
  }

  if (n?.type === "period") {
    return {
      type: "period",
      periodKey: String(n.periodKey ?? ""),
      customerId: null,
      label: n.periodLabel ?? String(n.periodKey ?? ""),
    };
  }

  return { type: "overall", periodKey: null, customerId: null, label: "Celkovo" };
}, [trendActiveNode]);

const toTrendTree = (rows = []) => {
  const byPeriod = new Map();

  for (const r of rows || []) {
    const pk = String(r.periodKey ?? "");
    if (!pk) continue;

    if (!byPeriod.has(pk)) {
      byPeriod.set(pk, {
        periodKey: pk,
        periodLabel: r.periodLabel ?? pk,
        rows: [],
        sums: {
          ordersTotal: 0,
          ordersCount: 0,
          partsTotal: 0,
          partsCount: 0,
          revenue: 0,
          claimsCost: 0,
        },
      });
    }

    const g = byPeriod.get(pk);
    g.rows.push(r);

    g.sums.ordersTotal += num(r.ordersTotal);
    g.sums.ordersCount += num(r.ordersCount);
    g.sums.partsTotal += num(r.partsTotal);
    g.sums.partsCount += num(r.partsCount);
    g.sums.revenue += num(r.revenue);
    g.sums.claimsCost += num(r.claimsCost);
  }

  const periods = [...byPeriod.values()].sort((a, b) =>
    String(a.periodKey).localeCompare(String(b.periodKey))
  );

  return periods.map((p) => {
    const ordersPct = safePct(p.sums.ordersCount, p.sums.ordersTotal);
    const partsPct = safePct(p.sums.partsCount, p.sums.partsTotal);
    const costPct = safePct(p.sums.claimsCost, p.sums.revenue);

    const periodKey = `P-${p.periodKey}`;
    return {
      key: periodKey,
      data: {
        key: periodKey,
        type: "period",
        periodKey: p.periodKey,
        periodLabel: p.periodLabel,
        departmentName: p.periodLabel,

        ordersTotal: p.sums.ordersTotal,
        ordersCount: p.sums.ordersCount,
        partsTotal: p.sums.partsTotal,
        partsCount: p.sums.partsCount,
        revenue: p.sums.revenue,
        claimsCost: p.sums.claimsCost,

        ordersPct,
        partsPct,
        costPct,
      },
      children: (p.rows || [])
        .slice()
        .map((r) => {
          // 1) robustné customerId
          const cid =
            r.customerId ??
            r.customer_id ??
            r.company_id ??
            r.companyId ??
            // fallback: keď je _key "cid|YYYY-MM"
            (typeof r._key === "string" && r._key.includes("|") ? r._key.split("|")[0] : null);

          // 2) robustné customerName
          const cname = r.customer ?? nameByCompanyId(cid);

          const customerKey = `C-${p.periodKey}-${String(cid ?? cname ?? "x")}`;
          return {
            key: customerKey,
            data: {
              key: customerKey,
              type: "customer",
              periodKey: r.periodKey,
              periodLabel: r.periodLabel,

              customerId: cid,
              customer: cname,
              departmentName: cname,

              ordersTotal: r.ordersTotal,
              ordersCount: r.ordersCount,
              partsTotal: r.partsTotal,
              partsCount: r.partsCount,
              revenue: r.revenue,
              claimsCost: r.claimsCost,

              ordersPct: r.ordersPct,
              partsPct: r.partsPct,
              costPct: r.costPct,
            },
            leaf: true,
          };
        })
        .sort((a, b) =>
          String(a?.data?.departmentName ?? "").localeCompare(String(b?.data?.departmentName ?? ""))
        ),
    };
  });
};

const nodeMatchesTrendFilters = (node, filters) => {
  const d = node?.data || {};

  const fName = filters?.nodeName?.value;

  const fOrdersTotal = filters?.ordersTotal?.value;
  const fOrdersCount = filters?.ordersCount?.value;
  const fOrdersPct = filters?.ordersPct?.value;

  const fPartsTotal = filters?.partsTotal?.value;
  const fPartsCount = filters?.partsCount?.value;
  const fPartsPct = filters?.partsPct?.value;

  const fRevenue = filters?.revenue?.value;
  const fClaimsCost = filters?.claimsCost?.value;
  const fCostPct = filters?.costPct?.value;

  if (!matchEquals(d.nodeName ?? "", fName)) return false;

  if (!matchContains(d.ordersTotal ?? 0, fOrdersTotal)) return false;
  if (!matchContains(d.ordersCount ?? 0, fOrdersCount)) return false;
  if (!matchContains(d.ordersPct ?? 0, fOrdersPct)) return false;

  if (!matchContains(d.partsTotal ?? 0, fPartsTotal)) return false;
  if (!matchContains(d.partsCount ?? 0, fPartsCount)) return false;
  if (!matchContains(d.partsPct ?? 0, fPartsPct)) return false;

  if (!matchContains(d.revenue ?? 0, fRevenue)) return false;
  if (!matchContains(d.claimsCost ?? 0, fClaimsCost)) return false;
  if (!matchContains(d.costPct ?? 0, fCostPct)) return false;

  return true;
};

const aggregatePeriodFromChildren = (periodNode, children = []) => {
  const acc = {
    ordersTotal: 0,
    ordersCount: 0,
    partsTotal: 0,
    partsCount: 0,
    revenue: 0,
    claimsCost: 0,
  };

  for (const ch of children) {
    const d = ch?.data || {};
    acc.ordersTotal += num(d.ordersTotal);
    acc.ordersCount += num(d.ordersCount);
    acc.partsTotal += num(d.partsTotal);
    acc.partsCount += num(d.partsCount);
    acc.revenue += num(d.revenue);
    acc.claimsCost += num(d.claimsCost);
  }

  return {
    ...periodNode,
    data: {
      ...periodNode.data,
      ordersTotal: acc.ordersTotal,
      ordersCount: acc.ordersCount,
      ordersPct: safePct(acc.ordersCount, acc.ordersTotal),

      partsTotal: acc.partsTotal,
      partsCount: acc.partsCount,
      partsPct: safePct(acc.partsCount, acc.partsTotal),

      revenue: roundEur(acc.revenue),
      claimsCost: roundEur(acc.claimsCost),
      costPct: safePct(acc.claimsCost, acc.revenue),
    },
  };
};

const filterTrendTree = (nodes = [], filters) => {
  const out = [];
  for (const n of nodes) {
    const childrenRaw = Array.isArray(n.children) ? n.children : [];
    const childrenFiltered = filterTrendTree(childrenRaw, filters);
    const selfOk = nodeMatchesTrendFilters(n, filters);

    if (selfOk || childrenFiltered.length > 0) {
      out.push({
        ...n,
        children: childrenFiltered.length > 0 ? childrenFiltered : n.children,
      });
    }
  }
  return out;
};

const sumTrendTopLevel = (nodes = []) => {
  const acc = {
    ordersTotal: 0,
    ordersCount: 0,
    partsTotal: 0,
    partsCount: 0,
    revenue: 0,
    claimsCost: 0,
  };

  for (const n of nodes || []) {
    const d = n?.data || {};
    acc.ordersTotal += num(d.ordersTotal);
    acc.ordersCount += num(d.ordersCount);
    acc.partsTotal  += num(d.partsTotal);
    acc.partsCount  += num(d.partsCount);
    acc.revenue     += num(d.revenue);
    acc.claimsCost  += num(d.claimsCost);
  }

  return {
    ordersTotal: acc.ordersTotal,
    ordersCount: acc.ordersCount,
    partsTotal: acc.partsTotal,
    partsCount: acc.partsCount,
    revenue: roundEur(acc.revenue),
    claimsCost: roundEur(acc.claimsCost),
    ordersPct: safePct(acc.ordersCount, acc.ordersTotal),
    partsPct:  safePct(acc.partsCount,  acc.partsTotal),
    costPct:   safePct(acc.claimsCost,  acc.revenue),
  };
};

const trendTree = useMemo(
  () => toTrendTree(trendRowsTable),
  [trendRowsTable]
);

const trendFilteredTree = useMemo(
  () => filterTrendTree(trendTree, filtersTrend),
  [trendTree, filtersTrend]
);

const totalsTrend = useMemo(
  () => sumTrendTopLevel(trendFilteredTree),
  [trendFilteredTree]
);

const totalsTrendTree = useMemo(
  () => sumTrendTopLevel(trendFilteredTree),
  [trendFilteredTree]
);

const TREND_MAX_CHARTS = 10;

const TREND_ALL_METRIC_VALUES = TREND_METRICS.map((m) => m.value);

const TREND_CHART_TYPE_OPTIONS = [
  { label: t('OTKEvaluation.Stlpcovy'), value: "bar" },
  { label: t('OTKEvaluation.Linearny'), value: "line" },
];

/** Pri tlači: čierny text a zväčšené písmo v grafoch (tab 2 – Reklamované zákazky/kusy, Náklady). fontSize napr. 18 ako pri trendoch. */
const getChartOptionsForPrint = (fontSize) => ({
  plugins: { legend: { labels: { color: "#000", font: { size: fontSize ?? 11 } } } },
  scales: {
    x: { ticks: { color: "#000", font: { size: fontSize ?? 11 } }, title: { color: "#000", font: { size: fontSize ?? 11 } } },
    y: { ticks: { color: "#000", font: { size: fontSize ?? 11 } }, title: { color: "#000", font: { size: fontSize ?? 11 } } },
  },
});

const makeTrendChart = (displayType, seriesOverride, chartType = "bar") => ({
  id: `trend-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  displayType: displayType ?? "pct",
  series: Array.isArray(seriesOverride) ? seriesOverride : [],
  chartType: chartType === "line" ? "line" : "bar",
});

const TREND_DEFAULT_FIRST_SERIES = ["costPct", "ordersPct", "partsPct"];

const [trendCharts, setTrendCharts] = useState(() => [
  makeTrendChart("pct", TREND_DEFAULT_FIRST_SERIES),
]);

const trendChartMode = trendTableMode;

const ensureSeriesAllowedByDisplayType = (displayType, series) => {
  const allowed = new Set(getMetricsForDisplayType(displayType).map((o) => o.value));
  return (series || []).filter((v) => allowed.has(v));
};

const addTrendChart = () => {
  setTrendCharts((prev) => {
    const curr = Array.isArray(prev) ? prev : [];
    if (curr.length >= TREND_MAX_CHARTS) return curr;

    // nový graf default = jedna metrika (ďalšia nepoužitá), aby sa dali rýchlo spraviť “6 single”
    return [...curr, makeTrendChart("count", [])];
  });
};

const removeTrendChart = (id) => {
  setTrendCharts((prev) => {
    const curr = Array.isArray(prev) ? prev : [];
    // nechaj minimálne 1 graf
    if (curr.length <= 1) return curr;
    return curr.filter((c) => c.id !== id);
  });
};

const setTrendChartDisplayType = (id, displayType) => {
  setTrendCharts((prev) =>
    prev.map((c) =>
      c.id === id ? { ...c, displayType, series: [] } : c
    )
  );
};

const setTrendChartSeries = (id, series) => {
  setTrendCharts((prev) =>
    prev.map((c) => {
      if (c.id !== id) return c;
      const allowed = new Set(getMetricsForDisplayType(c.displayType).map((o) => o.value));
      const filtered = (series || []).filter((v) => allowed.has(v));
      return { ...c, series: filtered };
    })
  );
};

const setTrendChartChartType = (id, chartType) => {
  setTrendCharts((prev) =>
    prev.map((c) => (c.id === id ? { ...c, chartType: chartType === "line" ? "line" : "bar" } : c))
  );
};

const TREND_METRIC_LABELS = Object.fromEntries(TREND_METRICS.map((m) => [m.value, m.label]));

// ===== build chartData pre konkrétny graf (podľa chart.displayType, chart.series, chart.chartType) =====
// Graf je buď stĺpcový alebo lineárny (nie oboje naraz). Trendové čiary sú pre všetkých 9 metrík.
const buildTrendChartDataForChart = (chart) => {
  const rows = trendChartRows || [];
  const labels = rows.map((r) => r.periodLabel);
  const sel = Array.isArray(chart.series) ? chart.series : [];
  const displayType = chart.displayType || "pct";
  const ct = chart.chartType === "line" ? "line" : "bar";

  const baseLineProps = { tension: 0.3, pointRadius: 3 };
  const baseBarProps = { borderWidth: 1 };
  const trendLineProps = { borderWidth: 1.5, borderDash: [6, 4], pointRadius: 0, spanGaps: true };

  const mkDataset = (type, label, data, yAxisID, color, bgColor) => {
    const isLine = type === "line";
    return {
      type,
      label,
      data,
      yAxisID,
      borderColor: color,
      backgroundColor: bgColor ?? color,
      ...(isLine ? baseLineProps : baseBarProps),
    };
  };
  const mkTrendDataset = (label, data, yAxisID, color) => ({
    type: "line",
    label,
    data,
    yAxisID,
    borderColor: color,
    borderWidth: trendLineProps.borderWidth,
    borderDash: trendLineProps.borderDash,
    pointRadius: trendLineProps.pointRadius,
    spanGaps: trendLineProps.spanGaps,
  });
  /** Prípustná hodnota: tenká plná čiara, farba ako príslušný trend (podľa diagramu). */
  const mkLimitDataset = (label, data, yAxisID, color) => ({
    type: "line",
    label,
    data,
    yAxisID,
    borderColor: color,
    borderWidth: 1,
    borderDash: [],
    pointRadius: 0,
    spanGaps: true,
  });

  const datasets = [];

  if (displayType === "pct") {
    const costPct = rows.map((r) => Number(r.costPct ?? 0));
    const ordersPct = rows.map((r) => Number(r.ordersPct ?? 0));
    const partsPct = rows.map((r) => Number(r.partsPct ?? 0));

    if (sel.includes("costPct")) {
      datasets.push(mkDataset(ct, t('OTKEvaluation.NakladyNaOpravy2'), costPct, "y", chartColors.blue));
      datasets.push(mkTrendDataset(t('OTKEvaluation.TrendNaklady'), computeTrendLine(costPct), "y", chartColors.blue));
      if (trendCostPctLimit > 0) {
        const line = labels.map(() => trendCostPctLimit);
        datasets.push(
          mkLimitDataset(
            t('OTKEvaluation.PripustnaHodnotaNaklady'),
            line,
            "y",
            chartColors.blue
          )
        );
      }
    }
    if (sel.includes("ordersPct")) {
      datasets.push(mkDataset(ct, t('OTKEvaluation.ReklamovaneZakazky'), ordersPct, "y", chartColors.red));
      datasets.push(mkTrendDataset(t('OTKEvaluation.TrendZakazky'), computeTrendLine(ordersPct), "y", chartColors.red));
      if (trendOrdersPctLimit > 0) {
        const line = labels.map(() => trendOrdersPctLimit);
        datasets.push(
          mkLimitDataset(
            t('OTKEvaluation.PripustnaHodnotaZakazky'),
            line,
            "y",
            chartColors.red
          )
        );
      }
    }
    if (sel.includes("partsPct")) {
      datasets.push(mkDataset(ct, t('OTKEvaluation.ReklamovaneKusy'), partsPct, "y", chartColors.orange));
      datasets.push(mkTrendDataset("Trend kusy", computeTrendLine(partsPct), "y", chartColors.orange));
      if (trendPartsPctLimit > 0) {
        const line = labels.map(() => trendPartsPctLimit);
        datasets.push(
          mkLimitDataset(
            t('OTKEvaluation.PripustnaHodnotaKusy'),
            line,
            "y",
            chartColors.orange
          )
        );
      }
    }

    return { labels, datasets };
  }

  const hasCount = ["count", "count_eur", "count_pct"].includes(displayType);
  const hasEur = ["eur", "count_eur", "eur_pct"].includes(displayType);
  const yCount = hasCount ? "y" : "y1";
  const yEur = hasEur && hasCount ? "y1" : "y";

  const ordersTotal = rows.map((r) => Number(r.ordersTotal ?? 0));
  const ordersCount = rows.map((r) => Number(r.ordersCount ?? 0));
  const partsTotal = rows.map((r) => Number(r.partsTotal ?? 0));
  const partsCount = rows.map((r) => Number(r.partsCount ?? 0));
  const revenue = rows.map((r) => Number(r.revenue ?? 0));
  const claimsCost = rows.map((r) => Number(r.claimsCost ?? 0));
  const ordersPct = rows.map((r) => Number(r.ordersPct ?? 0));
  const partsPct = rows.map((r) => Number(r.partsPct ?? 0));
  const costPct = rows.map((r) => Number(r.costPct ?? 0));

  if (hasCount && sel.includes("ordersTotal")) {
    datasets.push(mkDataset(ct, t('OTKEvaluation.DodaneZakazkyPc'), ordersTotal, yCount, chartColors.lightRed));
    datasets.push(mkTrendDataset(t('OTKEvaluation.TrendDodaneZakazky'), computeTrendLine(ordersTotal), yCount, chartColors.lightRed));
  }
  if (hasCount && sel.includes("ordersCount")) {
    datasets.push(mkDataset(ct, t('OTKEvaluation.ReklamovaneZakazkyPc'), ordersCount, yCount, chartColors.red));
    datasets.push(mkTrendDataset(t('OTKEvaluation.TrendReklamovaneZakazky'), computeTrendLine(ordersCount), yCount, chartColors.red));
    if (trendOrdersCountLimit > 0) {
      const line = labels.map(() => trendOrdersCountLimit);
      datasets.push(
        mkLimitDataset(
          t('OTKEvaluation.PripustnaHodnotaZakazkyPc'),
          line,
          yCount,
          chartColors.red
        )
      );
    }
  }
  if (hasCount && sel.includes("partsTotal")) {
    datasets.push(mkDataset(ct, t('OTKEvaluation.DodaneKusyPc'), partsTotal, yCount, chartColors.lightOrange));
    datasets.push(mkTrendDataset(t('OTKEvaluation.TrendDodaneKusy'), computeTrendLine(partsTotal), yCount, chartColors.lightOrange));
  }
  if (hasCount && sel.includes("partsCount")) {
    datasets.push(mkDataset(ct, t('OTKEvaluation.ReklamovaneKusyPc'), partsCount, yCount, chartColors.orange));
    datasets.push(mkTrendDataset(t('OTKEvaluation.TrendReklamovaneKusy'), computeTrendLine(partsCount), yCount, chartColors.orange));
    if (trendPartsCountLimit > 0) {
      const line = labels.map(() => trendPartsCountLimit);
      datasets.push(
        mkLimitDataset(
          t('OTKEvaluation.PripustnaHodnotaKusyPc'),
          line,
          yCount,
          chartColors.orange
        )
      );
    }
  }
  if (hasEur && sel.includes("revenue")) {
    datasets.push(mkDataset(ct, t('OTKEvaluation.DeliveredAmountCurrency', { currency: currencySymbol }), revenue, yEur, chartColors.lightBlue));
    datasets.push(mkTrendDataset(t('OTKEvaluation.TrendDodaneMnozstvo'), computeTrendLine(revenue), yEur, chartColors.lightBlue));
  }
  if (hasEur && sel.includes("claimsCost")) {
    datasets.push(mkDataset(ct, t('OTKEvaluation.RepairCostsCurrency', { currency: currencySymbol }), claimsCost, yEur, chartColors.blue));
    datasets.push(mkTrendDataset(t('OTKEvaluation.TrendNakladyNaOpravy'), computeTrendLine(claimsCost), yEur, chartColors.blue));
    if (trendCostValueLimit > 0) {
      const line = labels.map(() => trendCostValueLimit);
      datasets.push(
        mkLimitDataset(
          t('OTKEvaluation.AllowedCostValueCurrency', { currency: currencySymbol }),
          line,
          yEur,
          chartColors.blue
        )
      );
    }
  }

  const hasPct = ["count_pct", "eur_pct"].includes(displayType);
  const yPctMixed = "y1";
  if (hasPct) {
    if (sel.includes("ordersPct")) {
      datasets.push(mkDataset(ct, t('OTKEvaluation.ReklamovaneZakazky'), ordersPct, yPctMixed, chartColors.red));
      datasets.push(mkTrendDataset(t('OTKEvaluation.TrendZakazky2'), computeTrendLine(ordersPct), yPctMixed, chartColors.red));
      if (trendOrdersPctLimit > 0) {
        const line = labels.map(() => trendOrdersPctLimit);
        datasets.push(
          mkLimitDataset(
            t('OTKEvaluation.PripustnaHodnotaZakazky'),
            line,
            yPctMixed,
            chartColors.red
          )
        );
      }
    }
    if (sel.includes("partsPct")) {
      datasets.push(mkDataset(ct, t('OTKEvaluation.ReklamovaneKusy'), partsPct, yPctMixed, chartColors.orange));
      datasets.push(mkTrendDataset("Trend kusy (%)", computeTrendLine(partsPct), yPctMixed, chartColors.orange));
      if (trendPartsPctLimit > 0) {
        const line = labels.map(() => trendPartsPctLimit);
        datasets.push(
          mkLimitDataset(
            t('OTKEvaluation.PripustnaHodnotaKusy'),
            line,
            yPctMixed,
            chartColors.orange
          )
        );
      }
    }
    if (sel.includes("costPct")) {
      datasets.push(mkDataset(ct, t('OTKEvaluation.NakladyNaOpravy2'), costPct, yPctMixed, chartColors.blue));
      datasets.push(mkTrendDataset(t('OTKEvaluation.TrendNaklady2'), computeTrendLine(costPct), yPctMixed, chartColors.blue));
      if (trendCostPctLimit > 0) {
        const line = labels.map(() => trendCostPctLimit);
        datasets.push(
          mkLimitDataset(
            t('OTKEvaluation.PripustnaHodnotaNaklady'),
            line,
            yPctMixed,
            chartColors.blue
          )
        );
      }
    }
  }

  return { labels, datasets };
};

const isPeriodScope = trendScope.type === "period";

const getTrendChartOptions = (displayType, printChartFontSize = null) => {
  const isPrint = !!printChartFontSize;
  const font = { size: printChartFontSize ?? 11, ...(isPrint && { color: "#000" }) };
  const gridPrint = isPrint ? { color: "#000" } : {};
  const ticksPrint = isPrint ? { color: "#000" } : {};
  const titlePrint = isPrint ? { color: "#000" } : {};
  const base = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { position: "bottom", labels: { font, ...(isPrint && { color: "#000" }) } } },
    scales: {
      x: {
        ticks: { autoSkip: true, maxRotation: 45, minRotation: 0, font, ...ticksPrint },
        grid: gridPrint,
      },
    },
  };
  if (displayType === "pct") {
    base.scales.y = {
      type: "linear",
      position: "left",
      beginAtZero: true,
      ticks: { callback: (v) => `${v}%`, font, ...ticksPrint },
      title: { display: true, text: isPeriodScope ? "Podiel za obdobie (%)" : t('OTKEvaluation.PodielVCase'), font, ...titlePrint },
      grid: gridPrint,
    };
    return base;
  }
  if (displayType === "count" || displayType === "eur") {
    base.scales.y = {
      type: "linear",
      position: "left",
      beginAtZero: true,
      ticks: { font, ...ticksPrint },
      title: { display: true, text: displayType === "count" ? t('OTKEvaluation.CountsPcs') : currencySymbol, font, ...titlePrint },
      grid: gridPrint,
    };
    return base;
  }
  base.scales.y = {
    type: "linear",
    position: "left",
    beginAtZero: true,
    ticks: { font, ...ticksPrint },
    title: { display: true, text: t('OTKEvaluation.PoctyPc'), font, ...titlePrint },
    grid: gridPrint,
  };
  base.scales.y1 = {
    type: "linear",
    position: "right",
    beginAtZero: true,
    grid: { drawOnChartArea: false, ...gridPrint },
    ticks: { font, ...ticksPrint },
    title: { display: true, text: currencySymbol, font, ...titlePrint },
  };
  if (displayType === "count_pct" || displayType === "eur_pct") {
    base.scales.y1 = {
      type: "linear",
      position: "right",
      beginAtZero: true,
      grid: { drawOnChartArea: false, ...gridPrint },
      ticks: { callback: (v) => `${v}%`, font, ...ticksPrint },
      title: { display: true, text: "%", font, ...titlePrint },
    };
  }
  return base;
};

// Memoizované dáta a options pre trendové grafy – bráni zbytočnému prekresľovaniu (nové referencie každý render)
const trendChartsDataAndOptions = useMemo(
  () =>
    (trendCharts || []).map((ch) => ({
      data: buildTrendChartDataForChart(ch),
      options: getTrendChartOptions(ch.displayType, printChartFontSize),
    })),
  [trendCharts, trendChartRows, printChartFontSize, trendScope]
);

// ===== TREND TreeTable – UI/STYLE (copy-paste) =====

const WTrend = {
  nameMin: 260,   // Obdobie / Zákazník
  num: 140,       // Dodané zákazky, Dodané kusy
  numWide: 170,   // Reklamované (Pč/%), € stĺpce
};

const EXP_TREND = 0; // expander je v prvom stĺpci (nie samostatný)
const TREND_TT_MINW = EXP_TREND + WTrend.nameMin + 2 * WTrend.num + 4 * WTrend.numWide;

const TREND_HD = {
  background: "var(--surface-100)",
  borderBottom: "1px solid var(--surface-200)",
  width: WTrend.num,
  minWidth: WTrend.num,
  whiteSpace: "nowrap",
};

const TREND_FT = {
  background: "var(--surface-100)",
  borderTop: "1px solid var(--surface-200)",
  fontWeight: 600,
  width: WTrend.num,
  minWidth: WTrend.num,
  whiteSpace: "nowrap",
};

const TREND_HD_WIDE = {
  background: "var(--surface-100)",
  borderBottom: "1px solid var(--surface-200)",
  width: WTrend.numWide,
  minWidth: WTrend.numWide,
  whiteSpace: "nowrap",
};

const TREND_FT_WIDE = {
  background: "var(--surface-100)",
  borderTop: "1px solid var(--surface-200)",
  fontWeight: 600,
  width: WTrend.numWide,
  minWidth: WTrend.numWide,
  whiteSpace: "nowrap",
};

const TREND_HD_FIRST = {
  background: "var(--surface-100)",
  borderBottom: "1px solid var(--surface-200)",
  width: WTrend.nameMin,
  minWidth: WTrend.nameMin,
  whiteSpace: "nowrap",
};

const TREND_FT_FIRST = {
  background: "var(--surface-100)",
  borderTop: "1px solid var(--surface-200)",
  fontWeight: 600,
  width: WTrend.nameMin,
  minWidth: WTrend.nameMin,
  whiteSpace: "nowrap",
};

const TREND_PT_HEADER = {
  headerContent: { style: { display: "flex", alignItems: "center", gap: "0.25rem" } },
  headerTitle: { style: { whiteSpace: "nowrap" } },
  sortIcon: { className: "ml-1" },
};

// 1. úroveň = obdobie (bold), 2. úroveň = zákazník (medium), (3. úroveň nepoužívame, ale ponechané)
const trendRowClassName = (node) => {
  const type = node?.data?.type;

  if (type === "customer") {
    return {
      "font-medium": true,
      "text-slate-800": true,
      "bg-white": true,
      "border-b border-[var(--surface-100)]": true,
      "hover:bg-[var(--surface-50)]": true,
      "transition-colors": true,
    };
  }

  // period
  return {
    "font-semibold": true,
    "text-slate-900": true,
    "bg-[var(--surface-50)]": true,
    "border-y border-[var(--surface-200)]": true,
    "hover:bg-[var(--surface-100)]": true,
    "transition-colors": true,
  };
};

// zobrazenie prvej bunky + odsadenie podľa úrovne + skrátenie dlhých názvov

const trendNameBodyTemplate = (node) => {
  const d = node?.data || {};
  const type = d.type;

  const marginLeft = type === "customer" ? "1.5rem" : "0.5rem";
  const text =
    type === "customer"
      ? (d.departmentName ?? d.customer ?? t('OTKEvaluation.Neznamy'))
      : (d.periodLabel ?? d.periodKey ?? "");

  return (
    <span
      className="tt-name-cell"
      title={text}
      style={{
        display: "inline-block",     // <- dôležité: bude vedľa expander šípky
        verticalAlign: "middle",
        marginLeft,
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </span>
  );
};

// helpery pre body formatovanie (aby child neriadky nemali footery / aby period parent zobrazoval svoje sumy)
const trendIntBody = (field) => (node) => {
  const v = node?.data?.[field] ?? 0;
  return FORMAT_INT(v);
};

const trendCurrencyBody = (field) => (node) => {
  const v = node?.data?.[field] ?? 0;
  return FORMAT_CURRENCY(v);
};

const trendPctBody = (field) => (node) => {
  const v = Number(node?.data?.[field] ?? 0);
  return `${v.toFixed(2)}%`;
};

const trendOrdersField = trendChartMode === "pct" ? "ordersPct" : "ordersCount";
const trendOrdersHeader = trendChartMode === "pct" ? t('OTKEvaluation.ClaimedOrdersPct') : t('OTKEvaluation.ReklamovaneZakazkyPc');
const trendOrdersBody = trendChartMode === "pct" ? trendPctBody("ordersPct") : trendIntBody("ordersCount");

const trendPartsField = trendChartMode === "pct" ? "partsPct" : "partsCount";
const trendPartsHeader = trendChartMode === "pct" ? t('OTKEvaluation.ClaimedPiecesPct') : t('OTKEvaluation.ReklamovaneKusyPc');
const trendPartsBody = trendChartMode === "pct" ? trendPctBody("partsPct") : trendIntBody("partsCount");

const isComplaintRecord = (r) => {
  // primárne podľa range na Poradové číslo / specific_id
  const sidRaw = r?.specific_id ?? r?.specificId ?? r?.specific_id_number ?? null;
  const sid = typeof sidRaw === "string" ? parseInt(sidRaw, 10) : Number(sidRaw);

  if (Number.isFinite(sid)) {
    return sid >= 3000000 && sid < 4000000;
  }

  // fallback (ak by niekedy specific_id chýbalo)
  return r?.type === "Reklamácia";
};

const getPeriodRangeFromKey = (periodKey, trendPeriodMode) => {
  const pk = String(periodKey ?? "");
  if (!pk) return null;

  if (trendPeriodMode === "year") {
    const y = Number(pk);
    if (!Number.isFinite(y)) return null;
    const start = new Date(y, 0, 1);
    const end = new Date(y + 1, 0, 1); // exclusive
    return { start, end };
  }

  // month mode: "YYYY-MM"
  const [ys, ms] = pk.split("-");
  const y = Number(ys);
  const m = Number(ms);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;

  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 1); // exclusive
  return { start, end };
};

const getTrendRecDate = (r) => {
  // podľa tvojho sample to sedí na created_at
  const raw = r?.created_at ?? r?.updated_at ?? null;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const openTrendOutputFromNode = ({
  node,
  dispatch,
  baseFilteredRecords,
  trendPeriodMode,
}) => {
  if (!node?.data) return;
  if (!baseFilteredRecords?.length) return;

  const { periodKey, type, customerId } = node.data;

  const range = getPeriodRangeFromKey(periodKey, trendPeriodMode);
  if (!range) return;

  const matches = baseFilteredRecords.filter((r) => {
    // 0) LEN reklamácie
    if (!isComplaintRecord(r)) return false;

    // 1) obdobie
    const d = getTrendRecDate(r);
    if (!d) return false;
    if (!(d >= range.start && d < range.end)) return false;

    // 2) customer (iba ak klikneš na customer riadok)
    if (type === "customer") {
      const rcid = r?.customer_id ?? r?.customer?.id ?? r?.customerId ?? null;
      return String(rcid ?? "") === String(customerId ?? "");
    }

    // period row = všetci zákazníci v období
    return true;
  });

  if (!matches.length) return;

  dispatch({
    type: "create_new_tab",
    name: t('OTKEvaluation.VystupFiltrovane'), translationKey: 'OTKEvaluation.VystupFiltrovane', legacyLabel: t('OTKEvaluation.VystupFiltrovane'),
    page: "OTKStatistics",
    props: {
      drilldownRecords: matches,
      drilldownMeta: { ...node.data, source: "trend", only: "complaints" },
    },
  });
};

const handleOpenOutput_Trend = React.useCallback(
  ({ node }) =>
    openTrendOutputFromNode({
      node,
      dispatch,
      baseFilteredRecords,
      trendPeriodMode,
    }),
  [dispatch, baseFilteredRecords, trendPeriodMode]
);

const TREND_CLICKABLE_TYPES = ["period", "customer"];

const makeTrendClickableCell = ({ field, format }) => (node) => {
  const value = node?.data?.[field] ?? 0;
  const rowType = node?.data?.type;

  if (!value || !TREND_CLICKABLE_TYPES.includes(rowType)) return format(value);

  const onClick = (e) => {
    e.stopPropagation();
    handleOpenOutput_Trend({ node });
  };

  return (
    <span
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={{ color: "#2563eb", cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
      title={t('OTKEvaluation.OtvoritVystupLenReklamacie')}
    >
      {format(value)}
    </span>
  );
};

const renderTrendClickableInt = (f) => makeTrendClickableCell({ field: f, format: FORMAT_INT });
const renderTrendClickablePct = (f) => makeTrendClickableCell({ field: f, format: FORMAT_PCT });
const renderTrendClickableCurrency = (f) =>
  makeTrendClickableCell({ field: f, format: FORMAT_CURRENCY });


  /* ====== Vlastné tabuľky (ponechané komplet) ====== */
  const [customTables, setCustomTables] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem("customTablesOTK") || "[]");
    } catch {
      return [];
    }
  });  
  const [addTableDialogVisible, setAddTableDialogVisible] = useState(false);
  const SHOW_CUSTOM_TABLES = false; /* dočasne skryté: sekcia „Vlastná tabuľka“ + dialóg na vytvorenie */
  const [newTableConfig, setNewTableConfig] = useState({
    title: "",
    type: "table", // "table" | "chart"
    source: "byDept", // byDept | byTypeGroup | byTypeDept | error_records | companyStats
    columns: [],
  });

  function getDataBySource(source) {
    // berieme z ľavej "single" sady, nech je predvídateľné
    if (source === "byDept") {
      // defaultne použijeme počty (ľavá tabuľka). Ak chceš náklady, zmeň na costsLeftRows.
      return countsLeftRows;
    }
    if (source === "byTypeGroup") return tgLeftRows;
    if (source === "byTypeDept") return tdLeftRows;
    if (source === "error_records") return baseFilteredRecords;
    if (source === "companyStats") return companyStats;
    return [];
  }

  useEffect(() => {
    const saved = localStorage.getItem("customTablesOTK");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setCustomTables(parsed);
      } catch (e) {
        /* ignore */
      }
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("customTablesOTK", JSON.stringify(customTables));
  }, [customTables]);

  function handleSaveNewTable() {
    const updated = [newTableConfig, ...customTables];
    setCustomTables(updated);
    setAddTableDialogVisible(false);
    setNewTableConfig({ title: "", type: "table", source: "byDept", columns: [] });
    localStorage.setItem("customTablesOTK", JSON.stringify(updated));
  }
  function handleDeleteTable(index) {
    const updated = customTables.filter((_, i) => i !== index);
    setCustomTables(updated);
  }
  function handleEditTable(index) {
    const t = customTables[index];
    setNewTableConfig(t);
    setCustomTables((prev) => prev.filter((_, i) => i !== index));
    setAddTableDialogVisible(true);
  }

  // TAB 2 – Vyhodnotenie reklamácií (rovnaká logika ako TAB 1) - Tabuľka Reklamované zákazky 
  const [compareClaimedOrdersMode, setCompareClaimedOrdersMode] = useState(false);
  const [claimedOrdersLeftRange, setClaimedOrdersLeftRange] = useState(() => getDefaultLeftRange());
  const [claimedOrdersRightRange, setClaimedOrdersRightRange] = useState(null);
  useEffect(() => {
    if (compareClaimedOrdersMode) {
      setClaimedOrdersRightRange((prev) => prev ?? getDefaultRightYearRange());
    } else {
      setClaimedOrdersRightRange(null);
    }
  }, [compareClaimedOrdersMode]);

  const [claimedOrdersLeftStart, claimedOrdersLeftEnd] = getRangeEnds(claimedOrdersLeftRange);

const claimedOrdersLeftAgg = useMemo(() => {
  if (!claimedOrdersLeftStart) return [];
  const end = claimedOrdersLeftEnd ?? claimedOrdersLeftStart;

  return aggregateCompanyStats(
    filterCompanyStatsByRangeMonths(companyStats, claimedOrdersLeftRange),
    {
      mode: "range",
      rangeStart: claimedOrdersLeftStart,
      rangeEnd: end,
      claimsByCompanyMonth: claimsByCompanyMonthFiltered,
    }
  );
}, [
  companyStats,
  claimedOrdersLeftRange,
  claimedOrdersLeftStart,
  claimedOrdersLeftEnd,
  claimsByCompanyMonthFiltered,
]);

const [claimedOrdersRightStart, claimedOrdersRightEnd] = getRangeEnds(claimedOrdersRightRange);

const claimedOrdersRightAgg = useMemo(() => {
  if (!claimedOrdersRightStart) return [];
  const end = claimedOrdersRightEnd ?? claimedOrdersRightStart;

  return aggregateCompanyStats(
    filterCompanyStatsByRangeMonths(companyStats, claimedOrdersRightRange),
    {
      mode: "range",
      rangeStart: claimedOrdersRightStart,
      rangeEnd: end,
      claimsByCompanyMonth: claimsByCompanyMonthFiltered,
    }
  );
}, [
  companyStats,
  claimedOrdersRightRange,
  claimedOrdersRightStart,
  claimedOrdersRightEnd,
  claimsByCompanyMonthFiltered,
]);

const filterAggBySelectedCustomers = (agg = []) => {
  if (customersTouched && selectedCustomers.length === 0) return [];

  const effective = (!customersTouched && selectedCustomers.length === 0)
    ? allCustomerIds
    : selectedCustomers;

  const set = effective.length ? new Set(effective.map(String)) : null;

  return set
    ? (agg || []).filter(r => set.has(String(r.company_id)))
    : (agg || []);
};

  const toClaimedSummaryRows = (agg) =>
  (agg || []).map((r, i) => {
    const costEUR     = num(r.monthly_claim_costs);
    const revenue     = num(r.monthly_revenue);

    const ordersCount = num(r.claimOrders);
    const ordersTotal = num(r.orders_count);

    const partsCount  = num(r.claimPieces);
    const partsTotal  = num(r.items_count);

    return {
      _key: `${r.company_id}-${i}`,
      customer: nameByCompanyId(r.company_id),

      costEUR,
      revenue,
      costPct:   safePct(costEUR, revenue),

      ordersCount,
      ordersTotal,
      ordersPct: safePct(ordersCount, ordersTotal),

      partsCount,
      partsTotal,
      partsPct:  safePct(partsCount, partsTotal),
    };
  });

const claimedOrdersLeftRowsAll = useMemo(() => {
  const aggFiltered = filterAggBySelectedCustomers(claimedOrdersLeftAgg);
  return toClaimedSummaryRows(aggFiltered);
}, [claimedOrdersLeftAgg, companies, selectedCustomers, customersTouched, allCustomerIds]);

const claimedOrdersRightRowsAll = useMemo(() => {
  const aggFiltered = filterAggBySelectedCustomers(claimedOrdersRightAgg);
  return toClaimedSummaryRows(aggFiltered);
}, [claimedOrdersRightAgg, companies, selectedCustomers, customersTouched, allCustomerIds]);

const [filtersClaimedOrdersLeft, setFiltersClaimedOrdersLeft] = useState({
  customer:    { value: null, matchMode: "equals"   },
  ordersTotal: { value: null, matchMode: "contains" },
  ordersCount: { value: null, matchMode: "contains" },
  ordersPct:   { value: null, matchMode: "contains" },
});

const [filtersClaimedOrdersRight, setFiltersClaimedOrdersRight] = useState({
  customer:    { value: null, matchMode: "equals"   },
  ordersTotal: { value: null, matchMode: "contains" },
  ordersCount: { value: null, matchMode: "contains" },
  ordersPct:   { value: null, matchMode: "contains" },
});

const rowMatchesClaimedOrdersFilters = (row, filters) => {
  if (!matchEquals(row.customer ?? "", filters?.customer?.value)) return false;
  if (!matchContains(row.ordersTotal ?? 0, filters?.ordersTotal?.value)) return false;
  if (!matchContains(row.ordersCount ?? 0, filters?.ordersCount?.value)) return false;
  if (!matchContains(row.ordersPct   ?? 0, filters?.ordersPct?.value))   return false;
  return true;
};

const filterClaimedOrdersRows = (rows = [], filters) =>
  (rows || []).filter((r) => rowMatchesClaimedOrdersFilters(r, filters));

const sumClaimedSummaryTotals = (rows = []) => {
  const acc = {
    costEUR: 0,
    revenue: 0,
    ordersCount: 0,
    ordersTotal: 0,
    partsCount: 0,
    partsTotal: 0,
  };

  for (const r of rows) {
    acc.costEUR     += num(r.costEUR);
    acc.revenue     += num(r.revenue);
    acc.ordersCount += num(r.ordersCount);
    acc.ordersTotal += num(r.ordersTotal);
    acc.partsCount  += num(r.partsCount);
    acc.partsTotal  += num(r.partsTotal);
  }

  return {
    costEUR: roundEur(acc.costEUR),
    revenue: roundEur(acc.revenue),
    costPct: safePct(acc.costEUR, acc.revenue),

    ordersCount: acc.ordersCount,
    ordersTotal: acc.ordersTotal,
    ordersPct: safePct(acc.ordersCount, acc.ordersTotal),

    partsCount: acc.partsCount,
    partsTotal: acc.partsTotal,
    partsPct: safePct(acc.partsCount, acc.partsTotal),
  };
};

const totalsClaimedOrdersLeft = useMemo(
  () => sumClaimedSummaryTotals(filterClaimedOrdersRows(claimedOrdersLeftRowsAll, filtersClaimedOrdersLeft)),
  [claimedOrdersLeftRowsAll, filtersClaimedOrdersLeft]
);

const totalsClaimedOrdersRight = useMemo(
  () => sumClaimedSummaryTotals(filterClaimedOrdersRows(claimedOrdersRightRowsAll, filtersClaimedOrdersRight)),
  [claimedOrdersRightRowsAll, filtersClaimedOrdersRight]
);

//Tabuľka Reklamované kusy

const [compareClaimedPiecesMode, setCompareClaimedPiecesMode] = useState(false);
const [claimedPiecesLeftRange, setClaimedPiecesLeftRange] = useState(() => getDefaultLeftRange());
const [claimedPiecesRightRange, setClaimedPiecesRightRange] = useState(null);
useEffect(() => {
  if (compareClaimedPiecesMode) {
    setClaimedPiecesRightRange((prev) => prev ?? getDefaultRightYearRange());
  } else {
    setClaimedPiecesRightRange(null);
  }
}, [compareClaimedPiecesMode]);

const [claimedPiecesLeftStart, claimedPiecesLeftEnd] = getRangeEnds(claimedPiecesLeftRange);

const claimedPiecesLeftAgg = useMemo(() => {
  if (!claimedPiecesLeftStart) return [];
  const end = claimedPiecesLeftEnd ?? claimedPiecesLeftStart;

  return aggregateCompanyStats(
    filterCompanyStatsByRangeMonths(companyStats, claimedPiecesLeftRange),
    {
      mode: "range",
      rangeStart: claimedPiecesLeftStart,
      rangeEnd: end,
      claimsByCompanyMonth: claimsByCompanyMonthFiltered,
    }
  );
}, [
  companyStats,
  claimedPiecesLeftRange,
  claimedPiecesLeftStart,
  claimedPiecesLeftEnd,
  claimsByCompanyMonthFiltered,
]);

const [claimedPiecesRightStart, claimedPiecesRightEnd] = getRangeEnds(claimedPiecesRightRange);

const claimedPiecesRightAgg = useMemo(() => {
  if (!claimedPiecesRightStart) return [];
  const end = claimedPiecesRightEnd ?? claimedPiecesRightStart;

  return aggregateCompanyStats(
    filterCompanyStatsByRangeMonths(companyStats, claimedPiecesRightRange),
    {
      mode: "range",
      rangeStart: claimedPiecesRightStart,
      rangeEnd: end,
      claimsByCompanyMonth: claimsByCompanyMonthFiltered,
    }
  );
}, [
  companyStats,
  claimedPiecesRightRange,
  claimedPiecesRightStart,
  claimedPiecesRightEnd,
  claimsByCompanyMonthFiltered,
]);

const claimedPiecesLeftRowsAll = useMemo(() => {
  const aggFiltered = filterAggBySelectedCustomers(claimedPiecesLeftAgg);
  return toClaimedSummaryRows(aggFiltered);
}, [claimedPiecesLeftAgg, companies, selectedCustomers, customersTouched, allCustomerIds]);

const claimedPiecesRightRowsAll = useMemo(() => {
  const aggFiltered = filterAggBySelectedCustomers(claimedPiecesRightAgg);
  return toClaimedSummaryRows(aggFiltered);
}, [claimedPiecesRightAgg, companies, selectedCustomers, customersTouched, allCustomerIds]);

const [filtersClaimedPiecesLeft, setFiltersClaimedPiecesLeft] = useState({
  customer:   { value: null, matchMode: "equals"   },
  partsTotal: { value: null, matchMode: "contains" },
  partsCount: { value: null, matchMode: "contains" },
  partsPct:   { value: null, matchMode: "contains" },
});

const [filtersClaimedPiecesRight, setFiltersClaimedPiecesRight] = useState({
  customer:   { value: null, matchMode: "equals"   },
  partsTotal: { value: null, matchMode: "contains" },
  partsCount: { value: null, matchMode: "contains" },
  partsPct:   { value: null, matchMode: "contains" },
});

const rowMatchesClaimedPiecesFilters = (row, filters) => {
  if (!matchEquals(row.customer ?? "", filters?.customer?.value)) return false;
  if (!matchContains(row.partsTotal ?? 0, filters?.partsTotal?.value)) return false;
  if (!matchContains(row.partsCount ?? 0, filters?.partsCount?.value)) return false;
  if (!matchContains(row.partsPct   ?? 0, filters?.partsPct?.value))   return false;
  return true;
};

const filterClaimedPiecesRows = (rows = [], filters) =>
  (rows || []).filter((r) => rowMatchesClaimedPiecesFilters(r, filters));

const totalsClaimedPiecesLeft = useMemo(
  () => sumClaimedSummaryTotals(filterClaimedPiecesRows(claimedPiecesLeftRowsAll, filtersClaimedPiecesLeft)),
  [claimedPiecesLeftRowsAll, filtersClaimedPiecesLeft]
);

const totalsClaimedPiecesRight = useMemo(
  () => sumClaimedSummaryTotals(filterClaimedPiecesRows(claimedPiecesRightRowsAll, filtersClaimedPiecesRight)),
  [claimedPiecesRightRowsAll, filtersClaimedPiecesRight]
);

// Tabuľka Náklady na opravy
const [compareRepairCostMode, setCompareRepairCostMode] = useState(false);
const [repairCostLeftRange, setRepairCostLeftRange] = useState(() => getDefaultLeftRange());
const [repairCostRightRange, setRepairCostRightRange] = useState(null);
useEffect(() => {
  if (compareRepairCostMode) {
    setRepairCostRightRange((prev) => prev ?? getDefaultRightYearRange());
  } else {
    setRepairCostRightRange(null);
  }
}, [compareRepairCostMode]);

const [repairCostLeftStart, repairCostLeftEnd] = getRangeEnds(repairCostLeftRange);

const repairCostLeftAgg = useMemo(() => {
  if (!repairCostLeftStart) return [];
  const end = repairCostLeftEnd ?? repairCostLeftStart;

  return aggregateCompanyStats(
    filterCompanyStatsByRangeMonths(companyStats, repairCostLeftRange),
    {
      mode: "range",
      rangeStart: repairCostLeftStart,
      rangeEnd: end,
      claimsByCompanyMonth: claimsByCompanyMonthFiltered,
    }
  );
}, [
  companyStats,
  repairCostLeftRange,
  repairCostLeftStart,
  repairCostLeftEnd,
  claimsByCompanyMonthFiltered,
]);

const [repairCostRightStart, repairCostRightEnd] = getRangeEnds(repairCostRightRange);

const repairCostRightAgg = useMemo(() => {
  if (!repairCostRightStart) return [];
  const end = repairCostRightEnd ?? repairCostRightStart;

  return aggregateCompanyStats(
    filterCompanyStatsByRangeMonths(companyStats, repairCostRightRange),
    {
      mode: "range",
      rangeStart: repairCostRightStart,
      rangeEnd: end,
      claimsByCompanyMonth: claimsByCompanyMonthFiltered,
    }
  );
}, [
  companyStats,
  repairCostRightRange,
  repairCostRightStart,
  repairCostRightEnd,
  claimsByCompanyMonthFiltered,
]);

const repairCostLeftRowsAll = useMemo(() => {
  const aggFiltered = filterAggBySelectedCustomers(repairCostLeftAgg);
  return toClaimedSummaryRows(aggFiltered);
}, [repairCostLeftAgg, companies, selectedCustomers, customersTouched, allCustomerIds]);

const repairCostRightRowsAll = useMemo(() => {
  const aggFiltered = filterAggBySelectedCustomers(repairCostRightAgg);
  return toClaimedSummaryRows(aggFiltered);
}, [repairCostRightAgg, companies, selectedCustomers, customersTouched, allCustomerIds]);

const [filtersRepairCostLeft, setFiltersRepairCostLeft] = useState({
  customer: { value: null, matchMode: "equals"   },
  revenue:  { value: null, matchMode: "contains" },
  costEUR:  { value: null, matchMode: "contains" },
  costPct:  { value: null, matchMode: "contains" },
});

const [filtersRepairCostRight, setFiltersRepairCostRight] = useState({
  customer: { value: null, matchMode: "equals"   },
  revenue:  { value: null, matchMode: "contains" },
  costEUR:  { value: null, matchMode: "contains" },
  costPct:  { value: null, matchMode: "contains" },
});

const rowMatchesRepairCostFilters = (row, filters) => {
  if (!matchEquals(row.customer ?? "", filters?.customer?.value)) return false;
  if (!matchContains(row.revenue ?? 0, filters?.revenue?.value))  return false;
  if (!matchContains(row.costEUR ?? 0, filters?.costEUR?.value))  return false;
  if (!matchContains(row.costPct ?? 0, filters?.costPct?.value))  return false;
  return true;
};

const filterRepairCostRows = (rows = [], filters) =>
  (rows || []).filter((r) => rowMatchesRepairCostFilters(r, filters));

const totalsRepairCostLeft = useMemo(
  () => sumClaimedSummaryTotals(filterRepairCostRows(repairCostLeftRowsAll, filtersRepairCostLeft)),
  [repairCostLeftRowsAll, filtersRepairCostLeft]
);

const totalsRepairCostRight = useMemo(
  () => sumClaimedSummaryTotals(filterRepairCostRows(repairCostRightRowsAll, filtersRepairCostRight)),
  [repairCostRightRowsAll, filtersRepairCostRight]
);

  // Reklamácie podľa firma + rok + mesiac
  const claimsByCompanyMonth = useMemo(() => {
    const map = new Map();

    (state.error_records || [])
      .filter((r) => r.type === "Reklamácia" && r.customer_id && selectedStates.map(state => state.label).includes(r?.ack_error)) // len uznané reklamácie s firmou
      .forEach((r) => {
        const dt = r.created_at ? parseDbTimestamp(r.created_at) : null;
        if (!dt) return;
        const year  = USE_UTC ? dt.getUTCFullYear()  : dt.getFullYear();
        const month = (USE_UTC ? dt.getUTCMonth() : dt.getMonth()) + 1;
        const companyId = r.customer_id;
        const key = `${companyId}_${year}_${month}`;
        if (!map.has(key)) {
          map.set(key, {
            company_id: companyId,
            year,
            month,
            claimOrdersSet: new Set(), // aby sme vedeli unikátne objednávky
            claimPieces: 0,
          });
        }
        const it = map.get(key);
        //if (r.order_number) it.claimOrdersSet.add(r.order_number);
        if (r.specific_id) it.claimOrdersSet.add(r.specific_id);
        it.claimPieces += num(r.defective_pieces);
      });

    // pre jednoduchosť si set prepočítame na číslo
    const out = new Map();
    for (const [key, val] of map.entries()) {
      out.set(key, {
        company_id: val.company_id,
        year: val.year,
        month: val.month,
        claimOrders: val.claimOrdersSet.size,
        claimPieces: val.claimPieces,
      });
    }
    return out;
  }, [state.error_records]);

function aggregateCompanyStats(records, options = {}) {
  const { mode, singleMonth, rangeStart, rangeEnd } = options;

  // ✅ umožní injektovať mapu reklamácií (napr. prefiltrovanú toolbarom)
  // ak nepodáš nič, použije sa pôvodná globálna claimsByCompanyMonth
  const claimsMap = options.claimsByCompanyMonth ?? claimsByCompanyMonth;

  const map = new Map();

  // helper: či rok/mesiac patrí do zvoleného obdobia
  const inSelectedPeriod = (year, month) => {
    const y = Number(year);
    const m = Number(month);

    if (mode === "single" && singleMonth instanceof Date) {
      const yy = singleMonth.getFullYear();
      const mm = singleMonth.getMonth() + 1;
      return y === yy && m === mm;
    }

    if (mode === "range" && rangeStart && rangeEnd) {
      const d = new Date(y, m - 1, 1);
      const from = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
      const to   = new Date(rangeEnd.getFullYear(),   rangeEnd.getMonth(),   1);
      return d >= from && d <= to;
    }

    return true;
  };

  // 1) agregácia companyStats (bez reklamácií!)
  for (const r of records || []) {
    const id = r.company_id ?? r.companyId ?? 0;
    if (!id) continue;

    if (!map.has(id)) {
      map.set(id, {
        company_id: id,
        monthly_claim_costs: 0,
        monthly_revenue: 0,
        orders_count: 0,
        items_count: 0,
        claimOrders: 0,
        claimPieces: 0,
      });
    }

    const it = map.get(id);
    it.monthly_claim_costs += num(r.monthly_claim_costs);
    it.monthly_revenue     += num(r.monthly_revenue);
    it.orders_count        += num(r.orders_count);
    it.items_count         += num(r.items_count);
  }

  // 2) reklamácie prirátaj 1× za company+month (podľa výberu obdobia)
  // ✅ pozor: teraz iterujeme claimsMap, nie natvrdo claimsByCompanyMonth
  for (const extra of (claimsMap?.values?.() || [])) {
    const { company_id, year, month, claimOrders, claimPieces } = extra;
    if (!company_id) continue;
    if (!inSelectedPeriod(year, month)) continue;

    if (!map.has(company_id)) {
      map.set(company_id, {
        company_id,
        monthly_claim_costs: 0,
        monthly_revenue: 0,
        orders_count: 0,
        items_count: 0,
        claimOrders: 0,
        claimPieces: 0,
      });
    }

    const it = map.get(company_id);
    it.claimOrders += num(claimOrders);
    it.claimPieces += num(claimPieces);
  }

  return [...map.values()];
}


const getCustomerIdFromAggRow = (row) =>
  row?.customerId ??
  row?.customer_id ??
  row?.companyId ??
  row?.company_id ??
  row?.customer?.id ??
  null;

const getCustomerIdFromRecord = (r) =>
  r?.customer_id ?? r?.customer?.id ?? r?.customerId ?? null;

const getAggCompanyId = (row) =>
  row?.company_id ?? row?.companyId ?? row?.customer_id ?? row?.customerId ?? null;

const getAggCustomerName = (row) =>
  row?.customer ?? row?.customerName ?? row?.name ?? null;

const getRecCompanyId = (r) =>
  r?.customer_id ?? r?.customer?.id ?? r?.customerId ?? null;

const getRecCustomerName = (r) =>
  r?.customer?.name ?? r?.customer ?? null;

// otvorí Výstup pre “customer row” + vybraný range (+ side v compare)
const openComplaintsFromCustomerRow = ({
  row,
  side,                // "L" | "R"
  compareMode,
  leftRange,
  rightRange,
  dispatch,
  baseFilteredRecords,
  filterRecordsByRangeMonths,
}) => {
  if (!row) return;

  const range = !compareMode || side === "L" || !rightRange ? leftRange : rightRange;
  if (!range) return;

  const aggCid = getAggCompanyId(row);
  const aggName = getAggCustomerName(row);
  const aggNameNorm = norm(aggName);

  const periodRecords = filterRecordsByRangeMonths(baseFilteredRecords, range);

  const matches = periodRecords.filter((r) => {
    if (!isComplaintRecord(r)) return false;

    const recCid = getRecCompanyId(r);
    if (aggCid && recCid) return String(recCid) === String(aggCid);

    // fallback na meno (ak aggCid nemáš)
    return aggNameNorm && norm(getRecCustomerName(r)) === aggNameNorm;
  });

  if (!matches.length) return;

  dispatch({
    type: "create_new_tab",
    name: t('OTKEvaluation.VystupFiltrovane'), translationKey: 'OTKEvaluation.VystupFiltrovane', legacyLabel: t('OTKEvaluation.VystupFiltrovane'),
    page: "OTKStatistics",
    props: {
      drilldownRecords: matches,
      drilldownMeta: { source: "datatable", side, only: "complaints", aggCid, aggName },
    },
  });
};

const renderClickableDTCell = ({ field, format, side, openFn }) => (row) => {
  const value = row?.[field] ?? 0;
  if (!value) return format(value);

  const onClick = (e) => {
    e.stopPropagation();
    // (voliteľné) toto je ten “istý” log na overenie:
    // console.log("CLICK ROW:", row);
    openFn({ row, side });
  };

  return (
    <span
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={{ color: "#2563eb", cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
      title={t('OTKEvaluation.OtvoritVystupLenReklamacie')}
    >
      {format(value)}
    </span>
  );
};

const handleOpenClaimedOrders = React.useCallback(
  ({ row, side }) =>
    openComplaintsFromCustomerRow({
      row,
      side,
      compareMode: compareClaimedOrdersMode,
      leftRange: claimedOrdersLeftRange,
      rightRange: claimedOrdersRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareClaimedOrdersMode,
    claimedOrdersLeftRange,
    claimedOrdersRightRange,
    dispatch,
    baseFilteredRecords,
    filterRecordsByRangeMonths,
  ]
);

const handleOpenClaimedPieces = React.useCallback(
  ({ row, side }) =>
    openComplaintsFromCustomerRow({
      row,
      side,
      compareMode: compareClaimedPiecesMode,
      leftRange: claimedPiecesLeftRange,
      rightRange: claimedPiecesRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareClaimedPiecesMode,
    claimedPiecesLeftRange,
    claimedPiecesRightRange,
    dispatch,
    baseFilteredRecords,
    filterRecordsByRangeMonths,
  ]
);

const handleOpenRepairCost = React.useCallback(
  ({ row, side }) =>
    openComplaintsFromCustomerRow({
      row,
      side,
      compareMode: compareRepairCostMode,
      leftRange: repairCostLeftRange,
      rightRange: repairCostRightRange,
      dispatch,
      baseFilteredRecords,
      filterRecordsByRangeMonths,
    }),
  [
    compareRepairCostMode,
    repairCostLeftRange,
    repairCostRightRange,
    dispatch,
    baseFilteredRecords,
    filterRecordsByRangeMonths,
  ]
);

const fmtCount = (x) => {
  const v = Number(x) || 0;
  return Number.isInteger(v) ? v : v.toFixed(2);
};

  // či riadok spĺňa filtre tejto tabuľky
const rowMatchesClaimsFilters = (row, filters) => {
  const fCustomer    = filters?.customer?.value;
  const fCostEUR     = filters?.costEUR?.value;
  const fCostPct     = filters?.costPct?.value;
  const fOrdersCount = filters?.ordersCount?.value;
  const fOrdersPct   = filters?.ordersPct?.value;
  const fPartsCount  = filters?.partsCount?.value;
  const fPartsPct    = filters?.partsPct?.value;

  // customer: equals (Dropdown)
  if (!matchEquals(row.customer ?? "", fCustomer)) return false;

  // čísla: contains (rovnako ako inde)
  if (!matchContains(row.costEUR      ?? 0, fCostEUR))     return false;
  if (!matchContains(row.costPct      ?? 0, fCostPct))     return false;
  if (!matchContains(row.ordersCount  ?? 0, fOrdersCount)) return false;
  if (!matchContains(row.ordersPct    ?? 0, fOrdersPct))   return false;
  if (!matchContains(row.partsCount   ?? 0, fPartsCount))  return false;
  if (!matchContains(row.partsPct     ?? 0, fPartsPct))    return false;

  return true;
};

const [showClaimedOrdersCharts, setShowClaimedOrdersCharts] = useState(false);
const [showClaimedPiecesCharts, setShowClaimedPiecesCharts] = useState(false);
const [showRepairCostCharts, setShowRepairCostCharts] = useState(false);

const claimedOrdersLeftRowsVisible = useMemo(
  () => filterClaimedOrdersRows(claimedOrdersLeftRowsAll, filtersClaimedOrdersLeft),
  [claimedOrdersLeftRowsAll, filtersClaimedOrdersLeft]
);
const claimedOrdersRightRowsVisible = useMemo(
  () => filterClaimedOrdersRows(claimedOrdersRightRowsAll, filtersClaimedOrdersRight),
  [claimedOrdersRightRowsAll, filtersClaimedOrdersRight]
);

const claimedPiecesLeftRowsVisible = useMemo(
  () => filterClaimedPiecesRows(claimedPiecesLeftRowsAll, filtersClaimedPiecesLeft),
  [claimedPiecesLeftRowsAll, filtersClaimedPiecesLeft]
);
const claimedPiecesRightRowsVisible = useMemo(
  () => filterClaimedPiecesRows(claimedPiecesRightRowsAll, filtersClaimedPiecesRight),
  [claimedPiecesRightRowsAll, filtersClaimedPiecesRight]
);

const repairCostLeftRowsVisible = useMemo(
  () => filterRepairCostRows(repairCostLeftRowsAll, filtersRepairCostLeft),
  [repairCostLeftRowsAll, filtersRepairCostLeft]
);
const repairCostRightRowsVisible = useMemo(
  () => filterRepairCostRows(repairCostRightRowsAll, filtersRepairCostRight),
  [repairCostRightRowsAll, filtersRepairCostRight]
);


  useEffect(() => {
    const prev = document.body.style.overflowY;
    document.body.style.overflowY = "hidden";   // zakáž vonkajší scroll

    return () => {
      // pri odchode zo stránky vráť pôvodnú hodnotu
      document.body.style.overflowY = prev || "auto";
    };
  }, []);

  const trendCustomerNameOptions = useMemo(() => {
  const base = (customerOptions || [])
    .filter(o => o?.label)
    .map(o => ({ label: o.label, value: o.label })); // value = NAME
  return [{ label: t('Common.All'), value: null }, ...base];
}, [customerOptions]);

  //Label pre všetky označené v MultiSelect
 const renderSelectedMulti = (value, options, emptyLabel, allLabel) => {
  const selected = Array.isArray(value) ? value : [];
  const count = selected.length;
  const total = options?.length || 0;

  if (!count) {
    return <span className="text-gray-400">{emptyLabel}</span>;
  }

  if (total > 0 && count === total) {
    return allLabel || t('Common.All');
  }

  if (count === 1) {
    const opt = options.find((o) => o.value === selected[0]);
    return opt ? opt.label : selected[0];
  }

  return t('OTKEvaluation.SelectedCount', { count });
};

//sync obdobie - multiselecty 
const syncErrorTypesWithRows = useCallback(() => {
  const tableErrorType =
    filtersTypeGroupLeft &&
    filtersTypeGroupLeft.errorTypeName &&
    filtersTypeGroupLeft.errorTypeName.value;

  if (tableErrorType) {
    setSelectedErrorTypes([tableErrorType]);
  } else {
    const typesFromRows = Array.from(
      new Set(
        (tgLeftRows || [])
          .map((r) => r.errorTypeName)
          .filter(Boolean)
      )
    );
    setSelectedErrorTypes(typesFromRows);
  }
}, [tgLeftRows, filtersTypeGroupLeft]);

useEffect(() => {
  if (compareTypeGroupMode || tgTableMode) return;

  if (!tgLeftRows || tgLeftRows.length === 0) {
    setSelectedErrorTypes([]);
    setSelectedGroups([]);
    return;
  }

  syncErrorTypesWithRows(); // dopočíta druhy chýb
  setSelectedGroups([]);    // skupiny vždy prázdne pri zmene obdobia
}, [tgLeftRows, compareTypeGroupMode, tgTableMode, syncErrorTypesWithRows]);

const syncGtGroupsWithRows = useCallback(() => {
  const tableGroupName =
    filtersGroupTypeLeft &&
    filtersGroupTypeLeft.groupName &&
    filtersGroupTypeLeft.groupName.value;

  if (tableGroupName) {
    // v tabuľke je vybraná konkrétna skupina → graf má ukázať len túto
    setGtSelectedGroups([tableGroupName]);
  } else {
    // žiadny filter v tabuľke → všetky skupiny, ktoré majú dáta
    const groupsFromRows = Array.from(
      new Set(
        (gtLeftRows || [])
          .map((r) => r.groupName)
          .filter(Boolean)
      )
    );
    setGtSelectedGroups(groupsFromRows);
  }
}, [gtLeftRows, filtersGroupTypeLeft]);

// pri zmene dát (obdobie) v režime GRAF
useEffect(() => {
  if (compareGroupTypeMode || gtTableMode) return;

  if (!gtLeftRows || gtLeftRows.length === 0) {
    setGtSelectedGroups([]);
    setGtSelectedErrorTypes([]);
    return;
  }

  syncGtGroupsWithRows();
  setGtSelectedErrorTypes([]);
}, [gtLeftRows, compareGroupTypeMode, gtTableMode, syncGtGroupsWithRows]);

const syncTdDepartmentsWithRows = useCallback(() => {
  const tableDeptName =
    filtersTypeDeptLeft &&
    filtersTypeDeptLeft.departmentName &&
    filtersTypeDeptLeft.departmentName.value;

  if (tableDeptName) {
    // v tabuľke je vybrané konkrétne oddelenie → graf má ukazovať len toto
    setTdSelectedDepartments([tableDeptName]);
  } else {
    // žiadny filter v tabuľke → vyber všetky oddelenia, ktoré majú dáta
    const deptsFromRows = Array.from(
      new Set(
        (tdLeftRows || [])
          .map((r) => r.departmentName)
          .filter(Boolean)
      )
    );
    setTdSelectedDepartments(deptsFromRows);
  }
}, [tdLeftRows, filtersTypeDeptLeft]);

useEffect(() => {
  // v porovnávacom režime alebo pri tabuľke nič nesyncujeme
  if (compareTypeDeptMode || tdTableMode) return;

  // žiadne dáta → vyčistiť všetko
  if (!tdLeftRows || tdLeftRows.length === 0) {
    setTdSelectedDepartments([]);
    setTdSelectedErrorTypes([]);
    return;
  }

  // oddelenia podľa tabuľky / riadkov
  syncTdDepartmentsWithRows();

  // druhy chýb vždy reset na prázdno pri zmene obdobia
  setTdSelectedErrorTypes([]);
}, [tdLeftRows, compareTypeDeptMode, tdTableMode, syncTdDepartmentsWithRows]);

const syncDgDepartmentsWithRows = useCallback(() => {
  const tableDeptName =
    filtersDeptGroupLeft &&
    filtersDeptGroupLeft.departmentName &&
    filtersDeptGroupLeft.departmentName.value;

  if (tableDeptName) {
    // v tabuľke je vybrané konkrétne oddelenie
    setDgSelectedDepartments([tableDeptName]);
  } else {
    // žiadny filter -> všetky oddelenia, ktoré majú dáta
    const deptsFromRows = Array.from(
      new Set(
        (dgLeftRows || [])
          .map((r) => r.departmentName)
          .filter(Boolean)
      )
    );
    setDgSelectedDepartments(deptsFromRows);
  }
}, [dgLeftRows, filtersDeptGroupLeft]);

  useEffect(() => {
    // v porovnávacom režime alebo pri tabuľke nič nesyncujeme
    if (compareDeptGroupMode || dgTableMode) return;

    if (!dgLeftRows || dgLeftRows.length === 0) {
      setDgSelectedDepartments([]);
      setDgSelectedGroups([]);
      return;
    }

    syncDgDepartmentsWithRows();
    setDgSelectedGroups([]); // skupiny vždy reset pri zmene obdobia
  }, [dgLeftRows, compareDeptGroupMode, dgTableMode, syncDgDepartmentsWithRows]);

  /***********************
   * 1) HELPERS (generic)
   ***********************/
  const toObj = (val, fallback = null) => {
    if (val == null || val === "") return fallback;
    if (typeof val === "string") {
      try { return JSON.parse(val); } catch { return fallback; }
    }
    return val;
  };

  const packExpanded = (expandedKeys) =>
    Object.keys(expandedKeys || {}).filter((k) => (expandedKeys || {})[k]);

  /** Serialize Date | [Date,Date] | null for JSON storage */
  const serializePeriod = (val) => {
    if (val == null) return null;
    if (val instanceof Date) return val.toISOString();
    if (Array.isArray(val)) return val.map((d) => (d instanceof Date ? d.toISOString() : null));
    return null;
  };

  /** Deserialize stored period back to Date | [Date,Date] | null */
  const deserializePeriod = (val) => {
    if (val == null) return null;
    if (typeof val === "string") return new Date(val);
    if (Array.isArray(val)) return val.map((s) => (s ? new Date(s) : null)).filter(Boolean);
    return null;
  };

  const unpackExpanded = (keysArr) => {
    const arr = Array.isArray(keysArr) ? keysArr : [];
    return Object.fromEntries(arr.map((k) => [k, true]));
  };

  // merge dokumentov tak, aby si neprepisoval iné tabuľky
  function mergeDoc(base, patch) {
    const out = { ...(base || {}) };
    if (!patch) return out;

    if (patch.v != null) out.v = patch.v;

    if (patch.globals) out.globals = { ...(out.globals || {}), ...(patch.globals || {}) };
    if (patch.tables) out.tables = { ...(out.tables || {}), ...(patch.tables || {}) };
    if (patch.ui) out.ui = { ...(out.ui || {}), ...(patch.ui || {}) };
    if (patch.expanded) out.expanded = { ...(out.expanded || {}), ...(patch.expanded || {}) };
    if (patch.periods) out.periods = { ...(out.periods || {}), ...(patch.periods || {}) };

    return out;
  }

  /**
   * BUILD FormData (ready for your backend):
   * - columns    -> JSON (globals + optional tables)
   * - filters    -> JSON (tables[key] = filters)
   * - sorters    -> JSON (tables[key] = sortMeta)
   * - attributes -> JSON (ui flags + expanded per table key)
   *
   * snapshots: [{ key, filters, sorters, expandedKeys }, ...]
   * uiFlags: { compareCountsMode: true, ... }  // ľubovoľné UI veci
   * columnsGlobals: { ... }                   // globálne veci do DB stĺpca "columns"
   * removeTableKeys: [ "counts:main" ]        // voliteľne – uprace staré kľúče
   */
  function buildUserMaskFormData({
    page,
    type = "all",
    existingRow = null,
    snapshots = [],
    uiFlags = {},
    columnsGlobals = {},
    periods = {},
    removeTableKeys = [],
    extraFields = null,
  } = {}) {
    // existujúce doky z DB (aby si neprepísal iné tabuľky)
    let columnsDoc = toObj(existingRow?.columns, { v: 1, globals: {}, tables: {} });
    let filtersDoc = toObj(existingRow?.filters, { v: 1, tables: {} });
    let sortersDoc = toObj(existingRow?.sorters, { v: 1, tables: {} });
    let attrsDoc   = toObj(existingRow?.attributes, { v: 1, ui: {}, expanded: {}, periods: {} });

    // patch z aktuálneho stavu
    const columnsPatch = { v: 1, globals: { ...columnsGlobals } };
    const filtersPatch = { v: 1, tables: {} };
    const sortersPatch = { v: 1, tables: {} };
    const attrsPatch   = { v: 1, ui: { ...uiFlags }, expanded: {}, periods: { ...periods } };

    snapshots.forEach((s) => {
      if (!s || !s.key) return;
      if (s.filters != null) filtersPatch.tables[s.key] = s.filters;
      if (s.sorters != null) sortersPatch.tables[s.key] = s.sorters;
      if (s.expandedKeys != null) attrsPatch.expanded[s.key] = packExpanded(s.expandedKeys);
    });

    // merge
    columnsDoc = mergeDoc(columnsDoc, columnsPatch);
    filtersDoc = mergeDoc(filtersDoc, filtersPatch);
    sortersDoc = mergeDoc(sortersDoc, sortersPatch);
    attrsDoc   = mergeDoc(attrsDoc, attrsPatch);

    // cleanup starých kľúčov (typicky pri prepnutí compare)
    removeTableKeys.forEach((k) => {
      if (filtersDoc?.tables) delete filtersDoc.tables[k];
      if (sortersDoc?.tables) delete sortersDoc.tables[k];
      if (attrsDoc?.expanded) delete attrsDoc.expanded[k];
      if (columnsDoc?.tables) delete columnsDoc.tables[k];
      // periods sú keyed podľa logickej tabuľky (counts, pieces...), nie kľúča snapshot
    });

    // FormData pre backend
    const fd = new FormData();
    fd.append("page", page);
    fd.append("type", type);

    fd.append("columns", JSON.stringify(columnsDoc));
    fd.append("filters", JSON.stringify(filtersDoc));
    fd.append("sorters", JSON.stringify(sortersDoc));
    fd.append("attributes", JSON.stringify(attrsDoc));

    if (extraFields) {
      Object.entries(extraFields).forEach(([k, v]) => fd.append(k, String(v)));
    }

    return fd;
  }

  /**
   * APPLY z DB riadku do state premenných
   * tableMap = {
   *   "counts:main": { setFilters, setSorters, setExpanded, defaultFilters, defaultSorters },
   *   "counts:L":    { ... },
   *   "counts:R":    { ... },
   *   // ďalšie tabuľky len doplníš sem
   * }
   *
   * selectKeys(ui) -> ktoré keys sa majú aplikovať (napr. podľa compare)
   * clearUnused(ui) -> voliteľne vyčistí nepoužité state (napr. pravú stranu v non-compare)
   */
  function applyUserMaskFromRow(row, {
    uiSetters = {},        // napr. { compareCountsMode: setCompareCountsMode }
    tableMap = {},         // map key -> setters
    periodSetters = {},    // map tableName -> { setLeft, setRight } alebo { setValue, setPeriodMode? }
    setColumnsGlobals = null,  // voliteľne setter na globálne columns nastavenia
    selectKeys = null,     // (ui) => ["counts:main"] alebo ["counts:L","counts:R"]
    clearUnused = null,    // (ui) => void
  } = {}) {
    if (!row) return;

    const columnsDoc = toObj(row.columns, { globals: {} });
    const filtersDoc = toObj(row.filters, { tables: {} });
    const sortersDoc = toObj(row.sorters, { tables: {} });
    const attrsDoc   = toObj(row.attributes, { ui: {}, expanded: {}, periods: {} });

    const ui = attrsDoc?.ui || {};
    const expandedMap = attrsDoc?.expanded || {};
    const periodsMap = attrsDoc?.periods || {};
    const filtersTables = filtersDoc?.tables || {};
    const sortersTables = sortersDoc?.tables || {};

    // 1) nastav UI flagy (compare, iné switchre, inputy...)
    Object.entries(uiSetters).forEach(([uiKey, setter]) => {
      if (typeof setter !== "function") return;
      if (uiKey in ui) setter(ui[uiKey]);
    });

    // 2) nastav columns.globals (globálne veci pre všetky stĺpce/tabuľky)
    if (typeof setColumnsGlobals === "function") {
      setColumnsGlobals(columnsDoc?.globals || {});
    }

    // 3) nastav tabuľky po UI (aby sa to nebilo s resetmi)
    setTimeout(() => {
      const keysToApply = typeof selectKeys === "function"
        ? (selectKeys(ui) || [])
        : Object.keys(tableMap);

      keysToApply.forEach((key) => {
        const cfg = tableMap[key];
        if (!cfg) return;

        const mergeFilters = (loaded, def) => def ? ({ ...def, ...(loaded || {}) }) : (loaded || {});
        const loadedFilters = filtersTables[key];
        const loadedSorters = sortersTables[key];
        const loadedExpandedArr = expandedMap[key];

        if (typeof cfg.setFilters === "function") {
          cfg.setFilters(mergeFilters(loadedFilters, cfg.defaultFilters));
        }
        if (typeof cfg.setSorters === "function") {
          let sortersToApply = loadedSorters != null ? loadedSorters : (cfg.defaultSorters || []);
          if (key === "trendPeriod:main" && Array.isArray(sortersToApply)) {
            sortersToApply = sortersToApply.map((s) => (s && s.field === "departmentName" ? { ...s, field: "periodKey" } : s));
          }
          cfg.setSorters(sortersToApply);
        }
        if (typeof cfg.setExpanded === "function") {
          cfg.setExpanded(unpackExpanded(loadedExpandedArr));
        }
      });

      // 4) nastav obdobia (period) – spätná kompatibilita: ak periods chýbajú, nič sa neaplikuje
      Object.entries(periodSetters).forEach(([tableName, setters]) => {
        const stored = periodsMap[tableName];
        if (!stored) return;
        if (setters.setLeft && stored.left != null) {
          const v = deserializePeriod(stored.left);
          if (v != null) setters.setLeft(v);
        }
        if (setters.setRight && stored.right != null) {
          const v = deserializePeriod(stored.right);
          if (v != null) setters.setRight(v);
        }
        if (setters.setValue && stored.value != null) {
          const v = deserializePeriod(stored.value);
          if (v != null) setters.setValue(v);
        }
        if (setters.setPeriodMode && stored.periodMode) {
          setters.setPeriodMode(stored.periodMode);
        }
      });

      if (typeof clearUnused === "function") clearUnused(ui);
    }, 0);
  }

  /*********************************
   * 2) TVOJE SAVE (upravené)
   *********************************/
  function saveUserPreferences() {
    const existingRow = state.user_mask_filters?.find(m => m.page === "OTKEvaluation");

    // ---- KĽÚČE TABULIEK: vyber si aké chceš názvy (bez CARD_ID)
    // Tento blok je presne to, kde budeš dopĺňať ďalšie tabuľky:
    const snapshots = [];

    //TU DOPISOVAT TABULKY
    //POCET ZAKAZOK ZA ODD
    if (!compareCountsMode) {
      snapshots.push({
        key: "counts:main",
        filters: filtersDeptCountsLeft,
        sorters: countsSortMeta,
        expandedKeys: expandedCountsLeft,
      });
    } else {
      snapshots.push({
        key: "counts:L",
        filters: filtersDeptCountsLeft,
        sorters: countsSortMeta,
        expandedKeys: expandedCountsLeft,
      });
      snapshots.push({
        key: "counts:R",
        filters: filtersDeptCountsRight,
        sorters: countsSortMeta,
        expandedKeys: expandedCountsRight,
      });
    }

    //POCET KUSOV ZA ODD
    if (!comparePiecesMode) {
      snapshots.push({
        key: "pieces:main",
        filters: filtersDeptPiecesLeft,
        sorters: piecesSortMeta,
        expandedKeys: expandedPiecesLeft,
      });
    } else {
      snapshots.push({
        key: "pieces:L",
        filters: filtersDeptPiecesLeft,
        sorters: piecesSortMeta,
        expandedKeys: expandedPiecesLeft,
      });
      snapshots.push({
        key: "pieces:R",
        filters: filtersDeptPiecesRight,
        sorters: piecesSortMeta,
        expandedKeys: expandedPiecesRight,
      });
    }

    //NAKLADY ZA ODD
    if (!compareCostsMode) {
      snapshots.push({
        key: "costs:main",
        filters: filtersDeptCostsLeft,
        sorters: totalCostsSortMeta,
        expandedKeys: expandedCostsLeft,
      });
    } else {
      snapshots.push({
        key: "costs:L",
        filters: filtersDeptCostsLeft,
        sorters: totalCostsSortMeta,
        expandedKeys: expandedCostsLeft,
      });
      snapshots.push({
        key: "costs:R",
        filters: filtersDeptCostsRight,
        sorters: totalCostsSortMeta,
        expandedKeys: expandedCostsRight,
      });
    }

    //KORENOVA PRICINA
    if (!compareRcMode) {
      snapshots.push({
        key: "rootCause:main",
        filters: filtersRcLeft,
        sorters: rcSortMeta,
        expandedKeys: expandedRcLeft,
      });
    } else {
      snapshots.push({
        key: "rootCause:L",
        filters: filtersRcLeft,
        sorters: rcSortMeta,
        expandedKeys: expandedRcLeft,
      });
      snapshots.push({
        key: "rootCause:R",
        filters: filtersRcRight,
        sorters: rcSortMeta,
        expandedKeys: expandedRcRight,
      });
    }

    //Druh chyby voči skupine chýb
    if (!compareTypeGroupMode) {
      snapshots.push({
        key: "typeGroup:main",
        filters: filtersTypeGroupLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedTypeGroupLeft,
      });
    } else {
      snapshots.push({
        key: "typeGroup:L",
        filters: filtersTypeGroupLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedTypeGroupLeft,
      });
      snapshots.push({
        key: "typeGroup:R",
        filters: filtersTypeGroupRight,
        sorters: costsSortMeta,
        expandedKeys: expandedTypeGroupRight,
      });
    }
    
    //Skupina chýb voči druhu chýb
    if (!compareGroupTypeMode) {
      snapshots.push({
        key: "groupType:main",
        filters: filtersGroupTypeLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedGroupTypeLeft,
      });
    } else {
      snapshots.push({
        key: "groupType:L",
        filters: filtersGroupTypeLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedGroupTypeLeft,
      });
      snapshots.push({
        key: "groupType:R",
        filters: filtersGroupTypeRight,
        sorters: costsSortMeta,
        expandedKeys: expandedGroupTypeRight,
      });
    }

    //Oddelenie voči druhu chýb
    if (!compareTypeDeptMode) {
      snapshots.push({
        key: "typeDept:main",
        filters: filtersTypeDeptLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedTypeDeptLeft,
      });
    } else {
      snapshots.push({
        key: "typeDept:L",
        filters: filtersTypeDeptLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedTypeDeptLeft,
      });
      snapshots.push({
        key: "typeDept:R",
        filters: filtersTypeDeptRight,
        sorters: costsSortMeta,
        expandedKeys: expandedTypeDeptRight,
      });
    }

    //Oddelenie voči skupine chýb
    if (!compareDeptGroupMode) {
      snapshots.push({
        key: "deptGroup:main",
        filters: filtersDeptGroupLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedDeptGroupLeft,
      });
    } else {
      snapshots.push({
        key: "deptGroup:L",
        filters: filtersDeptGroupLeft,
        sorters: costsSortMeta,
        expandedKeys: expandedDeptGroupLeft,
      });
      snapshots.push({
        key: "deptGroup:R",
        filters: filtersDeptGroupRight,
        sorters: costsSortMeta,
        expandedKeys: expandedDeptGroupRight,
      });
    }

    //TAB 2

    //Reklamované zákazky za zvolené obdobie
    if (!compareClaimedOrdersMode) {
      snapshots.push({
        key: "claimedOrders:main",
        filters: filtersClaimedOrdersLeft,
        sorters: claimedOrdersSortMeta,
      });
    } else {
      snapshots.push({
        key: "claimedOrders:L",
        filters: filtersClaimedOrdersLeft,
        sorters: claimedOrdersSortMeta,
      });
      snapshots.push({
        key: "claimedOrders:R",
        filters: filtersClaimedOrdersRight,
        sorters: claimedOrdersSortMeta,
      });
    }

    //Reklamované kusy za zvolené obdobie
    if (!compareClaimedPiecesMode) {
      snapshots.push({
        key: "claimedPieces:main",
        filters: filtersClaimedPiecesLeft,
        sorters: claimedPiecesSortMeta,
      });
    } else {
      snapshots.push({
        key: "claimedPieces:L",
        filters: filtersClaimedPiecesLeft,
        sorters: claimedPiecesSortMeta,
      });
      snapshots.push({
        key: "claimedPieces:R",
        filters: filtersClaimedPiecesRight,
        sorters: claimedPiecesSortMeta,
      });
    }

    //Náklady na opravy za zvolené obdobie
    if (!compareRepairCostMode) {
      snapshots.push({
        key: "repairCost:main",
        filters: filtersRepairCostLeft,
        sorters: repairCostSortMeta,
      });
    } else {
      snapshots.push({
        key: "repairCost:L",
        filters: filtersRepairCostLeft,
        sorters: repairCostSortMeta,
      });
      snapshots.push({
        key: "repairCost:R",
        filters: filtersRepairCostRight,
        sorters: repairCostSortMeta,
      });
    }

    //Trend chybaovosti v čase
    snapshots.push({
      key: "trendPeriod:main",
      expandedKeys: expandedTrend,
      filters: filtersTrend,
      sorters: trendSortMeta,
    });

    // ✅ TU LEN DOPÍŠ ďalšie tabuľky:
    // snapshots.push({ key: "costs:main", filters: filtersDeptCosts, sorters: costsSortMeta, expandedKeys: expandedCosts });
    // snapshots.push({ key: "xxx", ... });

    // upratanie starých kľúčov podľa režimu
    const removeTableKeys = [
      ...(compareCountsMode ? ["counts:main"] : ["counts:L", "counts:R"]),
      ...(comparePiecesMode  ? ["pieces:main"]  : ["pieces:L", "pieces:R"]),
      ...(compareCostsMode  ? ["costs:main"]  : ["costs:L", "costs:R"]),
      ...(compareRcMode  ? ["rootCause:main"]  : ["rootCause:L", "rootCause:R"]),
      ...(compareTypeGroupMode  ? ["typeGroup:main"]  : ["typeGroup:L", "typeGroup:R"]),
      ...(compareGroupTypeMode  ? ["groupType:main"]  : ["groupType:L", "groupType:R"]),
      ...(compareTypeDeptMode  ? ["typeDept:main"]  : ["typeDept:L", "typeDept:R"]),
      ...(compareDeptGroupMode  ? ["deptGroup:main"]  : ["deptGroup:L", "deptGroup:R"]),
      //TAB 2
      ...(compareClaimedOrdersMode  ? ["claimedOrders:main"]  : ["claimedOrders:L", "claimedOrders:R"]),
      ...(compareClaimedPiecesMode  ? ["claimedPieces:main"]  : ["claimedPieces:L", "claimedPieces:R"]),
      ...(compareRepairCostMode  ? ["repairCost:main"]  : ["repairCost:L", "repairCost:R"]),
      ...(["trendPeriod:main"]),
    ];

    // globálne veci do DB stĺpca "columns" (platí pre všetky stĺpce/tabuľky na stránke)
    const columnsGlobals = {
      kinds: kinds,
      selectedDepartments: selectedDepartments,
      selectedStates: selectedStates,
      selectedCustomers: selectedCustomers,
      // sem si daj svoje global nastavenia:
      // density: "compact",
      // showZeros: true,
    };

    // UI flags do attributes.ui (môžeš si pomenovať ľubovoľne)
    const uiFlags = {
      compareCountsMode,
      comparePiecesMode,
      compareCostsMode,
      compareRcMode,
      compareTypeGroupMode,
      compareGroupTypeMode,
      compareTypeDeptMode,
      compareDeptGroupMode,
      compareClaimedOrdersMode,
      showClaimedOrdersCharts,
      compareClaimedPiecesMode,
      showClaimedPiecesCharts,
      compareRepairCostMode,
      showRepairCostCharts,
      // Tab1: zobrazenie tabuľka vs. graf pod tabuľkou
      rcTableMode,
      tgTableMode,
      gtTableMode,
      tdTableMode,
      dgTableMode,
      // Trend chybovosti: režim tabuľky (Počty+€ / Percentá) a grafy (počet, zobrazenie + metriky každého)
      trendTableMode,
      trendCharts: (trendCharts || []).map((ch) => ({ displayType: ch.displayType, series: Array.isArray(ch.series) ? ch.series : [], chartType: ch.chartType ?? "bar" })),
    };

    // Obdobia (period) per-tab a per-table – uloží sa do attributes.periods
    const periods = {
      counts: { left: serializePeriod(countsLeftRange), ...(compareCountsMode && countsRightRange ? { right: serializePeriod(countsRightRange) } : {}) },
      pieces: { left: serializePeriod(piecesLeftRange), ...(comparePiecesMode && piecesRightRange ? { right: serializePeriod(piecesRightRange) } : {}) },
      costs: { left: serializePeriod(costsLeftRange), ...(compareCostsMode && costsRightRange ? { right: serializePeriod(costsRightRange) } : {}) },
      rootCause: { left: serializePeriod(rcLeftRange), ...(compareRcMode && rcRightRange ? { right: serializePeriod(rcRightRange) } : {}) },
      typeGroup: { left: serializePeriod(tgLeftRange), ...(compareTypeGroupMode && tgRightRange ? { right: serializePeriod(tgRightRange) } : {}) },
      groupType: { left: serializePeriod(gtLeftRange), ...(compareGroupTypeMode && gtRightRange ? { right: serializePeriod(gtRightRange) } : {}) },
      typeDept: { left: serializePeriod(tdLeftRange), ...(compareTypeDeptMode && tdRightRange ? { right: serializePeriod(tdRightRange) } : {}) },
      deptGroup: { left: serializePeriod(dgLeftRange), ...(compareDeptGroupMode && dgRightRange ? { right: serializePeriod(dgRightRange) } : {}) },
      claimedOrders: { left: serializePeriod(claimedOrdersLeftRange), ...(compareClaimedOrdersMode && claimedOrdersRightRange ? { right: serializePeriod(claimedOrdersRightRange) } : {}) },
      claimedPieces: { left: serializePeriod(claimedPiecesLeftRange), ...(compareClaimedPiecesMode && claimedPiecesRightRange ? { right: serializePeriod(claimedPiecesRightRange) } : {}) },
      repairCost: { left: serializePeriod(repairCostLeftRange), ...(compareRepairCostMode && repairCostRightRange ? { right: serializePeriod(repairCostRightRange) } : {}) },
      trendPeriod: { value: serializePeriod(trendRangeApplied), periodMode: trendPeriodMode },
    };

    setDialogText("Nastavujem");
    setVisible(true);

    const fd = buildUserMaskFormData({
      page: "OTKEvaluation",
      type: "all",
      existingRow,
      snapshots,
      uiFlags,
      columnsGlobals,
      periods,
      removeTableKeys,
    });

    // debug
    //for (const [k, v] of fd.entries()) console.log("[sendFd]", k, v);

    postRequest(`/api/user_mask_filters`, fd, true)
      .then((response) => {
        if (response.status === 201) {
          dispatch({ type: "load_user_mask_filters", newUserMaskFilters: response.data });
          toast?.current?.show({
            severity: "success",
            summary: t('OTKEvaluation.Potvrdene'),
            life: 3000,
            detail: t('OTKEvaluation.NastaveniaBoliUlozene'),
          });
        } else {
          toast?.current?.show({
            severity: "warn",
            summary: "Upozornenie",
            life: 3000,
            detail: t('OTKEvaluation.OdpovedServeraNebolaOcakavana'),
          });
        }
        setVisible(false);
      })
      .catch((reason) => {
        setVisible(false);
        toast?.current?.show({
          severity: "error",
          summary: t('Common.Error'),
          life: 4000,
          detail: reason?.message || t('OTKEvaluation.NepodariloSaUlozitNastavenia'),
        });
      });
  }

  function resetUserPreferences() {
    dispatch({
      type: "load_user_mask_filters",
      newUserMaskFilters: (state.user_mask_filters || []).filter((m) => m.page !== "OTKEvaluation"),
    });

    setKinds(ERROR_TYPES.map((k) => k.value));
    setSelectedDepartments([]);
    setSelectedStates(["Uznaná"]);
    setSelectedCustomers([]);
    setDepartmentsTouched(false);
    setCustomersTouched(false);

    setCompareCountsMode(false);
    setComparePiecesMode(false);
    setCompareCostsMode(false);
    setCompareRcMode(false);
    setCompareTypeGroupMode(false);
    setCompareGroupTypeMode(false);
    setCompareTypeDeptMode(false);
    setCompareDeptGroupMode(false);
    setCompareClaimedOrdersMode(false);
    setCompareClaimedPiecesMode(false);
    setCompareRepairCostMode(false);

    setShowClaimedOrdersCharts(false);
    setShowClaimedPiecesCharts(false);
    setShowRepairCostCharts(false);

    setRcTableMode(true);
    setTgTableMode(true);
    setGtTableMode(true);
    setTdTableMode(true);
    setDgTableMode(true);

    setTrendTableMode("abs");
    setTrendCharts([makeTrendChart("pct", TREND_DEFAULT_FIRST_SERIES)]);
    setExpandedTrend({});
    setTrendPeriodMode("month");
    const defTrend = getDefaultTrendRangeMonth();
    setTrendRangeUI(defTrend);
    startTransition(() => setTrendRangeApplied(defTrend?.[0] ? [defTrend[0], defTrend[1] ?? defTrend[0]] : null));

    setCountsLeftRange(getDefaultLeftRange());
    setCountsRightRange(null);
    setPiecesLeftRange(getDefaultLeftRange());
    setPiecesRightRange(null);
    setCostsLeftRange(getDefaultLeftRange());
    setCostsRightRange(null);
    setRcLeftRange(getDefaultLeftRange());
    setRcRightRange(null);
    setTgLeftRange(getDefaultLeftRange());
    setTgRightRange(null);
    setGtLeftRange(getDefaultLeftRange());
    setGtRightRange(null);
    setTdLeftRange(getDefaultLeftRange());
    setTdRightRange(null);
    setDgLeftRange(getDefaultLeftRange());
    setDgRightRange(null);
    setClaimedOrdersLeftRange(getDefaultLeftRange());
    setClaimedOrdersRightRange(null);
    setClaimedPiecesLeftRange(getDefaultLeftRange());
    setClaimedPiecesRightRange(null);
    setRepairCostLeftRange(getDefaultLeftRange());
    setRepairCostRightRange(null);
    setTrendSortMetaAbs([{ field: "claimsCost", order: -1 }]);
    setTrendSortMetaPct([{ field: "costPct", order: -1 }]);

    toast?.current?.show({
      severity: "success",
      summary: t('OTKEvaluation.Potvrdene'),
      life: 3000,
      detail: t('OTKEvaluation.NastaveniaBoliResetovaneNaPredvolene'),
    });

    setCountsSortMeta([{ field: "total", order: -1 }]);
    setPiecesSortMeta([{ field: "pieces", order: -1 }]);
    setTotalCostsSortMeta([{ field: "totalCosts", order: -1 }]);
    setRcSortMeta([{ field: "total", order: -1 }]);
    setCostsSortMeta([{ field: "cost", order: -1 }]);
    setClaimedOrdersSortMeta([{ field: "ordersCount", order: -1 }]);
    setClaimedPiecesSortMeta([{ field: "partsCount", order: -1 }]);
    setRepairCostSortMeta([{ field: "costEUR", order: -1 }]);

    setExpandedCountsLeft({});
    setExpandedCountsRight({});
    setExpandedPiecesLeft({});
    setExpandedPiecesRight({});
    setExpandedCostsLeft({});
    setExpandedCostsRight({});
    setExpandedRcLeft({});
    setExpandedRcRight({});
    setExpandedTypeGroupLeft({});
    setExpandedTypeGroupRight({});
    setExpandedGroupTypeLeft({});
    setExpandedGroupTypeRight({});
    setExpandedTypeDeptLeft({});
    setExpandedTypeDeptRight({});
    setExpandedDeptGroupLeft({});
    setExpandedDeptGroupRight({});

    const countsFilter = { departmentName: { value: null, matchMode: "equals" }, internal: { value: null, matchMode: "contains" }, external: { value: null, matchMode: "contains" }, complaints: { value: null, matchMode: "contains" }, total: { value: null, matchMode: "contains" } };
    const piecesFilter = { departmentName: { value: null, matchMode: "equals" }, internal: { value: null, matchMode: "contains" }, external: { value: null, matchMode: "contains" }, complaints: { value: null, matchMode: "contains" }, pieces: { value: null, matchMode: "contains" } };
    const costsFilter = { departmentName: { value: null, matchMode: "equals" }, internalCost: { value: null, matchMode: "contains" }, externalCost: { value: null, matchMode: "contains" }, complaintsCost: { value: null, matchMode: "contains" }, totalCosts: { value: null, matchMode: "contains" } };
    setFiltersDeptCountsLeft(countsFilter);
    setFiltersDeptCountsRight({ ...countsFilter });
    setFiltersDeptPiecesLeft(piecesFilter);
    setFiltersDeptPiecesRight({ ...piecesFilter });
    setFiltersDeptCostsLeft(costsFilter);
    setFiltersDeptCostsRight({ ...costsFilter });
    const rcFilter = { label: { value: null, matchMode: "equals" }, internal: { value: null, matchMode: "contains" }, external: { value: null, matchMode: "contains" }, complaints: { value: null, matchMode: "contains" }, total: { value: null, matchMode: "contains" } };
    setFiltersRcLeft(rcFilter);
    setFiltersRcRight({ ...rcFilter });
    const typeGroupFilter = { errorTypeName: { value: null, matchMode: "equals" }, groupName: { value: null, matchMode: "equals" }, count: { value: null, matchMode: "contains" }, cost: { value: null, matchMode: "contains" }, pieces: { value: null, matchMode: "contains" } };
    setFiltersTypeGroupLeft(typeGroupFilter);
    setFiltersTypeGroupRight({ ...typeGroupFilter });
    const groupTypeFilter = { groupName: { value: null, matchMode: "equals" }, errorTypeName: { value: null, matchMode: "equals" }, count: { value: null, matchMode: "contains" }, pieces: { value: null, matchMode: "contains" }, cost: { value: null, matchMode: "contains" } };
    setFiltersGroupTypeLeft(groupTypeFilter);
    setFiltersGroupTypeRight({ ...groupTypeFilter });
    const typeDeptFilter = { errorTypeName: { value: null, matchMode: "equals" }, departmentName: { value: null, matchMode: "equals" }, count: { value: null, matchMode: "contains" }, pieces: { value: null, matchMode: "contains" }, cost: { value: null, matchMode: "contains" } };
    setFiltersTypeDeptLeft(typeDeptFilter);
    setFiltersTypeDeptRight({ ...typeDeptFilter });
    const deptGroupFilter = { departmentName: { value: null, matchMode: "equals" }, count: { value: null, matchMode: "contains" }, pieces: { value: null, matchMode: "contains" }, cost: { value: null, matchMode: "contains" } };
    setFiltersDeptGroupLeft(deptGroupFilter);
    setFiltersDeptGroupRight({ ...deptGroupFilter });
    const claimedOrdersFilter = { customer: { value: null, matchMode: "equals" }, ordersTotal: { value: null, matchMode: "contains" }, ordersCount: { value: null, matchMode: "contains" }, ordersPct: { value: null, matchMode: "contains" } };
    setFiltersClaimedOrdersLeft(claimedOrdersFilter);
    setFiltersClaimedOrdersRight({ ...claimedOrdersFilter });
    const claimedPiecesFilter = { customer: { value: null, matchMode: "equals" }, partsTotal: { value: null, matchMode: "contains" }, partsCount: { value: null, matchMode: "contains" }, partsPct: { value: null, matchMode: "contains" } };
    setFiltersClaimedPiecesLeft(claimedPiecesFilter);
    setFiltersClaimedPiecesRight({ ...claimedPiecesFilter });
    const repairCostFilter = { customer: { value: null, matchMode: "equals" }, revenue: { value: null, matchMode: "contains" }, costEUR: { value: null, matchMode: "contains" }, costPct: { value: null, matchMode: "contains" } };
    setFiltersRepairCostLeft(repairCostFilter);
    setFiltersRepairCostRight({ ...repairCostFilter });
    setFiltersTrend({
      departmentName: { value: null, matchMode: "equals" },
      ordersTotal: { value: null, matchMode: "contains" },
      ordersCount: { value: null, matchMode: "contains" },
      ordersPct: { value: null, matchMode: "contains" },
      partsTotal: { value: null, matchMode: "contains" },
      partsCount: { value: null, matchMode: "contains" },
      partsPct: { value: null, matchMode: "contains" },
      revenue: { value: null, matchMode: "contains" },
      claimsCost: { value: null, matchMode: "contains" },
      costPct: { value: null, matchMode: "contains" },
    });

    toast?.current?.show({
      severity: "info",
      summary: t('OTKEvaluation.Resetovane'),
      life: 3000,
      detail: t('OTKEvaluation.NastaveniaBoliResetovaneNaPredvolene'),
    });
  }

  const [columnsGlobals, setColumnsGlobals] = useState({});

  // Stabilná závislosť: allCustomerIds je nové pole každý render (customers z .filter), takže by spúšťalo slučku
  const allCustomerIdsKey = (allCustomerIds || []).slice().sort().join(",");
  useEffect(() => {
    if (!columnsGlobals) return;
    if (columnsGlobals.kinds != null) setKinds(columnsGlobals.kinds);
    if (columnsGlobals.selectedDepartments != null) setSelectedDepartments(columnsGlobals.selectedDepartments);
    if (columnsGlobals.selectedStates != null) setSelectedStates(columnsGlobals.selectedStates);
    // Aplikovať uložených zákazníkov len ak user ešte nezmenil výber (inak by sme prepísali jeho voľbu pri refetchi)
    if (columnsGlobals.selectedCustomers != null && !customersTouched) {
      const saved = columnsGlobals.selectedCustomers || [];
      const savedSet = new Set(saved.map(String));
      (allCustomerIds || []).forEach((id) => savedSet.add(String(id)));
      const merged = Array.from(savedSet);
      setSelectedCustomers((prev) => {
        if (prev.length === merged.length && merged.every((id, i) => String(prev[i]) === String(id))) return prev;
        return merged;
      });
    }
  }, [columnsGlobals, allCustomerIdsKey, customersTouched]);

  /*********************************
   * 3) TVOJE LOAD z DB (upravené)
   *********************************/
  useEffect(() => {
    const row = state.user_mask_filters?.find(m => m.page === "OTKEvaluation");
    if (!row) return;

    const selectKeysFn = (ui) => [
        ...(ui?.compareCountsMode ? ["counts:L","counts:R"] : ["counts:main"]),
        ...(ui?.compareCostsMode  ? ["costs:L","costs:R"]  : ["costs:main"]),
        ...(ui?.comparePiecesMode ? ["pieces:L","pieces:R"] : ["pieces:main"]),
        ...(ui?.compareRcMode ? ["rootCause:L","rootCause:R"] : ["rootCause:main"]),
        ...(ui?.compareTypeGroupMode ? ["typeGroup:L","typeGroup:R"] : ["typeGroup:main"]),
        ...(ui?.compareGroupTypeMode ? ["groupType:L","groupType:R"] : ["groupType:main"]),
        ...(ui?.compareTypeDeptMode ? ["typeDept:L","typeDept:R"] : ["typeDept:main"]),
        ...(ui?.compareDeptGroupMode ? ["deptGroup:L","deptGroup:R"] : ["deptGroup:main"]),
        //TAB 2 (single = main, compare = L+R)
        ...(ui?.compareClaimedOrdersMode  ? ["claimedOrders:L", "claimedOrders:R"]  : ["claimedOrders:main"]),
        ...(ui?.compareClaimedPiecesMode  ? ["claimedPieces:L", "claimedPieces:R"]  : ["claimedPieces:main"]),
        ...(ui?.compareRepairCostMode  ? ["repairCost:L", "repairCost:R"]  : ["repairCost:main"]),
        ...(["trendPeriod:main"]),
      ];

    const tableMapObj = {
        "counts:main": {
          setFilters: setFiltersDeptCountsLeft,
          setSorters: setCountsSortMeta,
          setExpanded: setExpandedCountsLeft,
          defaultFilters: filtersDeptCountsLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "counts:L": {
          setFilters: setFiltersDeptCountsLeft,
          setSorters: setCountsSortMeta,
          setExpanded: setExpandedCountsLeft,
          defaultFilters: filtersDeptCountsLeft,
          defaultSorters: [],
        },
        "counts:R": {
          setFilters: setFiltersDeptCountsRight,
          setSorters: setCountsSortMeta,
          setExpanded: setExpandedCountsRight,
          defaultFilters: filtersDeptCountsRight,
          defaultSorters: [],
          reset: () => {
            setFiltersDeptCountsRight(filtersDeptCountsRight /* idealne DEFAULT */);
            setExpandedCountsRight({});
          },
        },
         "pieces:main": {
          setFilters: setFiltersDeptPiecesLeft,
          setSorters: setPiecesSortMeta,
          setExpanded: setExpandedPiecesLeft,
          defaultFilters: filtersDeptPiecesLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "pieces:L": {
          setFilters: setFiltersDeptPiecesLeft,
          setSorters: setPiecesSortMeta,
          setExpanded: setExpandedPiecesLeft,
          defaultFilters: filtersDeptPiecesLeft,
          defaultSorters: [],
        },
        "pieces:R": {
          setFilters: setFiltersDeptPiecesRight,
          setSorters: setPiecesSortMeta,
          setExpanded: setExpandedPiecesRight,
          defaultFilters: filtersDeptPiecesRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersDeptPiecesRight(filtersDeptPiecesRight /* idealne DEFAULT */);
            setExpandedPiecesRight({});
          },
        },
        "costs:main": {
          setFilters: setFiltersDeptCostsLeft,
          setSorters: setTotalCostsSortMeta,
          setExpanded: setExpandedCostsLeft,
          defaultFilters: filtersDeptCostsLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "costs:L": {
          setFilters: setFiltersDeptCostsLeft,
          setSorters: setTotalCostsSortMeta,
          setExpanded: setExpandedCostsLeft,
          defaultFilters: filtersDeptCostsLeft,
          defaultSorters: [],
        },
        "costs:R": {
          setFilters: setFiltersDeptCostsRight,
          setSorters: setTotalCostsSortMeta,
          setExpanded: setExpandedCostsRight,
          defaultFilters: filtersDeptCostsRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersDeptCostsRight(filtersDeptCostsRight /* idealne DEFAULT */);
            setExpandedCostsRight({});
          },
        },
        "rootCause:main": {
          setFilters: setFiltersRcLeft,
          setSorters: setRcSortMeta,
          setExpanded: setExpandedRcLeft,
          defaultFilters: filtersRcLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "rootCause:L": {
          setFilters: setFiltersRcLeft,
          setSorters: setRcSortMeta,
          setExpanded: setExpandedRcLeft,
          defaultFilters: filtersRcLeft,
          defaultSorters: [],
        },
        "rootCause:R": {
          setFilters: setFiltersRcRight,
          setSorters: setRcSortMeta,
          setExpanded: setExpandedRcRight,
          defaultFilters: filtersRcRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersRcRight(filtersRcRight /* idealne DEFAULT */);
            setExpandedRcRight({});
          },
        },
        "typeGroup:main": {
          setFilters: setFiltersTypeGroupLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedTypeGroupLeft,
          defaultFilters: filtersTypeGroupLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "typeGroup:L": {
          setFilters: setFiltersTypeGroupLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedTypeGroupLeft,
          defaultFilters: filtersTypeGroupLeft,
          defaultSorters: [],
        },
        "typeGroup:R": {
          setFilters: setFiltersTypeGroupRight,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedTypeGroupRight,
          defaultFilters: filtersTypeGroupRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersTypeGroupRight(filtersTypeGroupRight /* idealne DEFAULT */);
            setExpandedTypeGroupRight({});
          },
        },
        "groupType:main": {
          setFilters: setFiltersGroupTypeLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedGroupTypeLeft,
          defaultFilters: filtersGroupTypeLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "groupType:L": {
          setFilters: setFiltersGroupTypeLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedGroupTypeLeft,
          defaultFilters: filtersGroupTypeLeft,
          defaultSorters: [],
        },
        "groupType:R": {
          setFilters: setFiltersGroupTypeRight,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedGroupTypeRight,
          defaultFilters: filtersGroupTypeRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersGroupTypeRight(filtersGroupTypeRight /* idealne DEFAULT */);
            setExpandedGroupTypeRight({});
          },
        },
        "typeDept:main": {
          setFilters: setFiltersTypeDeptLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedTypeDeptLeft,
          defaultFilters: filtersTypeDeptLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "typeDept:L": {
          setFilters: setFiltersTypeDeptLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedTypeDeptLeft,
          defaultFilters: filtersTypeDeptLeft,
          defaultSorters: [],
        },
        "typeDept:R": {
          setFilters: setFiltersTypeDeptRight,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedTypeDeptRight,
          defaultFilters: filtersTypeDeptRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersTypeDeptRight(filtersTypeDeptRight /* idealne DEFAULT */);
            setExpandedTypeDeptRight({});
          },
        },
        "deptGroup:main": {
          setFilters: setFiltersDeptGroupLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedDeptGroupLeft,
          defaultFilters: filtersDeptGroupLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "deptGroup:L": {
          setFilters: setFiltersDeptGroupLeft,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedDeptGroupLeft,
          defaultFilters: filtersDeptGroupLeft,
          defaultSorters: [],
        },
        "deptGroup:R": {
          setFilters: setFiltersDeptGroupRight,
          setSorters: setCostsSortMeta,
          setExpanded: setExpandedDeptGroupRight,
          defaultFilters: filtersDeptGroupRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersDeptGroupRight(filtersDeptGroupRight /* idealne DEFAULT */);
            setExpandedDeptGroupRight({});
          },
        },

        //TAB 2
        "claimedOrders:main": {
          setFilters: setFiltersClaimedOrdersLeft,
          setSorters: setClaimedOrdersSortMeta,
          defaultFilters: filtersClaimedOrdersLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "claimedOrders:L": {
          setFilters: setFiltersClaimedOrdersLeft,
          setSorters: setClaimedOrdersSortMeta,
          defaultFilters: filtersClaimedOrdersLeft,
          defaultSorters: [],
        },
        "claimedOrders:R": {
          setFilters: setFiltersClaimedOrdersRight,
          setSorters: setClaimedOrdersSortMeta,
          defaultFilters: filtersClaimedOrdersRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersClaimedOrdersRight(filtersClaimedOrdersRight /* idealne DEFAULT */);
          },
        },
        "claimedPieces:main": {
          setFilters: setFiltersClaimedPiecesLeft,
          setSorters: setClaimedPiecesSortMeta,
          defaultFilters: filtersClaimedPiecesLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "claimedPieces:L": {
          setFilters: setFiltersClaimedPiecesLeft,
          setSorters: setClaimedPiecesSortMeta,
          defaultFilters: filtersClaimedPiecesLeft,
          defaultSorters: [],
        },
        "claimedPieces:R": {
          setFilters: setFiltersClaimedPiecesRight,
          setSorters: setClaimedPiecesSortMeta,
          defaultFilters: filtersClaimedPiecesRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersClaimedPiecesRight(filtersClaimedPiecesRight /* idealne DEFAULT */);
          },
        },
        "repairCost:main": {
          setFilters: setFiltersRepairCostLeft,
          setSorters: setRepairCostSortMeta,
          defaultFilters: filtersRepairCostLeft, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
        "repairCost:L": {
          setFilters: setFiltersRepairCostLeft,
          setSorters: setRepairCostSortMeta,
          defaultFilters: filtersRepairCostLeft,
          defaultSorters: [],
        },
        "repairCost:R": {
          setFilters: setFiltersRepairCostRight,
          setSorters: setRepairCostSortMeta,
          defaultFilters: filtersRepairCostRight, 
          defaultSorters: [],
          reset: () => {
            setFiltersRepairCostRight(filtersRepairCostRight /* idealne DEFAULT */);
          },
        },
        "trendPeriod:main": {
          setFilters: setFiltersTrend,
          setSorters: setTrendSortMeta,
          setExpanded: setExpandedTrend,
          defaultFilters: filtersTrend, // ideálne konštanta DEFAULT_..., nie aktuálny state
          defaultSorters: [],
        },
    }

    applyUserMaskFromRow(row, {
    uiSetters: {
      compareCountsMode: setCompareCountsMode,
      comparePiecesMode: setComparePiecesMode,
      compareCostsMode: setCompareCostsMode,
      compareRcMode: setCompareRcMode,
      compareTypeGroupMode: setCompareTypeGroupMode,
      compareGroupTypeMode: setCompareGroupTypeMode,
      compareTypeDeptMode: setCompareTypeDeptMode,
      compareDeptGroupMode: setCompareDeptGroupMode,
      compareClaimedOrdersMode: setCompareClaimedOrdersMode,
      showClaimedOrdersCharts: setShowClaimedOrdersCharts,
      compareClaimedPiecesMode: setCompareClaimedPiecesMode,
      showClaimedPiecesCharts: setShowClaimedPiecesCharts,
      compareRepairCostMode: setCompareRepairCostMode,
      showRepairCostCharts: setShowRepairCostCharts,
      rcTableMode: setRcTableMode,
      tgTableMode: setTgTableMode,
      gtTableMode: setGtTableMode,
      tdTableMode: setTdTableMode,
      dgTableMode: setDgTableMode,
      trendTableMode: setTrendTableMode,
      trendCharts: (val) => {
        if (Array.isArray(val) && val.length > 0) {
          const max = typeof TREND_MAX_CHARTS !== "undefined" ? TREND_MAX_CHARTS : 9;
          setTrendCharts(val.slice(0, max).map((item) => makeTrendChart(item?.displayType, item?.series, item?.chartType)));
        }
      },
    },

    tableMap: tableMapObj,
    setColumnsGlobals,

    periodSetters: {
      counts: { setLeft: setCountsLeftRange, setRight: setCountsRightRange },
      pieces: { setLeft: setPiecesLeftRange, setRight: setPiecesRightRange },
      costs: { setLeft: setCostsLeftRange, setRight: setCostsRightRange },
      rootCause: { setLeft: setRcLeftRange, setRight: setRcRightRange },
      typeGroup: { setLeft: setTgLeftRange, setRight: setTgRightRange },
      groupType: { setLeft: setGtLeftRange, setRight: setGtRightRange },
      typeDept: { setLeft: setTdLeftRange, setRight: setTdRightRange },
      deptGroup: { setLeft: setDgLeftRange, setRight: setDgRightRange },
      claimedOrders: { setLeft: setClaimedOrdersLeftRange, setRight: setClaimedOrdersRightRange },
      claimedPieces: { setLeft: setClaimedPiecesLeftRange, setRight: setClaimedPiecesRightRange },
      repairCost: { setLeft: setRepairCostLeftRange, setRight: setRepairCostRightRange },
      trendPeriod: {
        setValue: (v) => {
          const arr = Array.isArray(v) ? v : (v instanceof Date ? [v, v] : null);
          setTrendRangeUI(arr);
          startTransition(() => setTrendRangeApplied(arr?.[0] ? [arr[0], arr[1] ?? arr[0]] : null));
        },
        setPeriodMode: setTrendPeriodMode,
      },
    },

    selectKeys: selectKeysFn,

    clearUnused: (ui) => {
      const active = new Set(selectKeysFn(ui));
      Object.entries(tableMapObj).forEach(([key, cfg]) => {
        if (!active.has(key)) cfg.reset?.();
      });
    },
  });  
  }, [state.user_mask_filters]);

  /* ====== RENDER ====== */
  return (
    <div className="otk-evaluation-root flex flex-col h-full min-h-0 overflow-hidden">
      <Toolbar start={startToolbar} end={rightToolbar} className="shadow-1" />
      {evaluationLoading && (
        <div className="flex-1 flex items-center justify-center p-8 text-gray-500">
          <i className="pi pi-spin pi-spinner mr-2" style={{ fontSize: "1.5rem" }} />
          {t('Common.LoadingData')}
        </div>
      )}
      {evaluationError && !evaluationLoading && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-red-600">
            <i className="pi pi-exclamation-triangle mb-2" style={{ fontSize: "1.5rem" }} />
            <p>{evaluationError}</p>
          </div>
        </div>
      )}
      {!evaluationLoading && !evaluationError && (
      <>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6 pb-10">
        <TabView 
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
          renderActiveOnly={false}
          >
          {/* ===== TAB 1 ===== */}
          <TabPanel header={t('OTKEvaluation.VyhodnotenieOddeleni')}>
            <div ref={refAllTablesTab1} className="flex flex-col gap-4">
              {/* === 1) POČTY ZA ODD === */}
              <div ref={refDeptCounts} className="print-full">
              <Card
                title={
                  <div className="flex justify-between items-center w-full">
                    <span>{t('Ui.R67.Pocet_chybnych_zakazok_za_oddelenie')}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                      <InputSwitch
                        className="no-print"
                        checked={compareCountsMode}
                        onChange={(e) => {
                          setCompareCountsMode(e.value);
                          setExpandedCountsLeft({});
                          setExpandedCountsRight({});
                        }}
                      />
                      <Button
                        icon={
                          compareCountsMode
                            ? Object.keys(expandedCountsLeft).length > 0 &&
                              Object.keys(expandedCountsRight).length > 0
                              ? 'pi pi-angle-down'
                              : 'pi pi-angle-right'
                            : Object.keys(expandedCountsLeft).length > 0
                              ? 'pi pi-angle-down'
                              : 'pi pi-angle-right'
                        }
                        className="p-button-text p-button-sm no-print"
                        tooltip={
                          compareCountsMode
                            ? Object.keys(expandedCountsLeft).length > 0 &&
                              Object.keys(expandedCountsRight).length > 0
                              ? t('OTKEvaluation.ZbalitVsetky')
                              : t('OTKEvaluation.RozbalitVsetky')
                            : Object.keys(expandedCountsLeft).length > 0
                              ? t('OTKEvaluation.ZbalitVsetky')
                              : t('OTKEvaluation.RozbalitVsetky')
                        }
                        tooltipOptions={{ position: 'left' }}
                        onClick={() => {
                          if (!compareCountsMode) {
                            const hasExpandedLeft = Object.keys(expandedCountsLeft).length > 0;

                            if (hasExpandedLeft) {
                              // zbal všetko
                              setExpandedCountsLeft({});
                            } else {
                              // otvor dialog pre "counts"
                              setExpandDialogTarget('counts');
                              setExpandDialogVisible(true);
                            }
                          } else {
                            const hasExpandedLeft  = Object.keys(expandedCountsLeft).length > 0;
                            const hasExpandedRight = Object.keys(expandedCountsRight).length > 0;
                            const bothExpanded     = hasExpandedLeft && hasExpandedRight;

                            if (bothExpanded) {
                              setExpandedCountsLeft({});
                              setExpandedCountsRight({});
                            } else {
                              setExpandDialogTarget('counts');
                              setExpandDialogVisible(true);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                }
                subTitle={t('OTKEvaluation.KliknutimRozbalteZamestnancovVPorovnaniMesiacVsOd')}
              >
                {!compareCountsMode ? (
                    <div className="flex items-center gap-3 mb-4 no-print">
                      <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                      <Calendar
                        value={countsLeftRange || null}
                        onChange={(e) => {
                          setCountsLeftRange(e.value);
                          setExpandedCountsLeft({});
                        }}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                    ) : (
                    <div className="flex items-center justify-between gap-6 mb-4 no-print">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka')}</span>
                        <Calendar
                          value={countsLeftRange || null}
                          onChange={(e) => {
                            setCountsLeftRange(e.value);
                            setExpandedCountsLeft({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex items-center gap-2 no-print">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                        <Calendar
                          value={countsRightRange || null}
                          onChange={(e) => {
                            setCountsRightRange(e.value);
                            setExpandedDeptCostsRight([]);
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          readOnlyInput
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                    </div>
                  )}
                    {!compareCountsMode ? (
                    <TreeTable
                      value={countsLeftFilteredTree}
                      dataKey="key"
                      expandedKeys={expandedCountsLeft}
                      onToggle={(e) => setExpandedCountsLeft(e.value)}
                      filters={filtersDeptCountsLeft}
                      onFilter={(e) => setFiltersDeptCountsLeft(e.filters)}
                      filterMode="lenient"
                      resizableColumns
                      columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto', width:"100%" }}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}
                      sortMode="multiple"
                      multiSortMeta={countsSortMeta}
                      onSort={(e) => setCountsSortMeta(e.multiSortMeta)}
                      removableSort
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterMatchMode="equals"                 // bežný equals
                              filterElement={
                                <Dropdown
                                  value={filtersDeptCountsLeft.departmentName?.value ?? null}
                                  options={departmentFilterOptions}    // viz nižšie
                                  onChange={(e) =>
                                    setFiltersDeptCountsLeft(prev => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: 'equals' }
                                    }))
                                  }
                                  className="w-full h-[42px]"
                                  optionDisabled="disabled"
                                  showClear
                                  placeholder={t('Common.SelectOption')}
                                />
                              }
                      />
                      {showInternal && <Column field="internal" header={t('OTKEvaluation.Interne')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('internal', 'internal', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.internal)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.internal.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, internal: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showExternal && <Column field="external" header={t('OTKEvaluation.Externe')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('external', 'external', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.external)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.external.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, external: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showComplaints && <Column field="complaints" header={t('OTKEvaluation.Reklamacie')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('complaints', 'complaints', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.complaints)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.complaints.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, complaints: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {/* Spolu = súčet chýb (nie kusy) */}
                      <Column field="total" header="Spolu"
                              style={{ minWidth: 120, width: 120 }}          // zväčšený stĺpec
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell("total", null, "L")}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.total)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.total.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, total: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />
                    </TreeTable>
                ) : (
                  <div className="grid grid-cols-2 gap-4 print-compare-grid">
                    <div className="overflow-x-auto print-compare-cell">
                    {/* ĽAVÁ (porovnanie) */}
                    <TreeTable
                      value={countsLeftFilteredTree}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}      // <- horizontálny aj vertikálny
                      filters={filtersDeptCountsLeft}
                      onFilter={(e) => setFiltersDeptCountsLeft(e.filters)}
                      resizableColumns
                      expandedKeys={expandedCountsLeft}
                      onToggle={(e) => setExpandedCountsLeft(e.value)}
                      sortMode="multiple"
                      multiSortMeta={countsSortMeta}
                      onSort={(e) => setCountsSortMeta(e.multiSortMeta)}
                      removableSort
                      //columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterElement={
                                <Dropdown
                                  value={filtersDeptCountsLeft.departmentName.value}
                                  options={departmentFilterOptions}
                                  onChange={(e) =>
                                    setFiltersDeptCountsLeft((prev) => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: "equals" },
                                    }))
                                  }
                                  placeholder={t('Common.SelectOption')}
                                  className="w-full h-[42px]"
                                  showClear
                                />
                              }
                      />
                      {showInternal && <Column field="internal" header={t('OTKEvaluation.Interne')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('internal', 'internal', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.internal)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.internal.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, internal: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showExternal && <Column field="external" header={t('OTKEvaluation.Externe')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('external', 'external', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.external)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.external.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, external: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showComplaints && <Column field="complaints" header={t('OTKEvaluation.Reklamacie')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('complaints', 'complaints', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.complaints)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.complaints.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, complaints: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {/* Spolu = súčet chýb (nie kusy) */}
                      <Column field="total" header="Spolu"
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell("total", null, "L")}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsLeft.total)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsLeft.total.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsLeft((p) => ({ ...p, total: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />
                    </TreeTable>
                    </div>
                    <div className="overflow-x-auto print-compare-cell">
                    {/* PRAVÁ (porovnanie) */}
                    <TreeTable
                      value={countsRightFilteredTree}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}
                      filters={filtersDeptCountsRight}
                      onFilter={(e) => setFiltersDeptCountsRight(e.filters)}
                      resizableColumns
                      expandedKeys={expandedCountsRight}
                      onToggle={(e) => setExpandedCountsRight(e.value)}
                      sortMode="multiple"
                      multiSortMeta={countsSortMeta}
                      onSort={(e) => setCountsSortMeta(e.multiSortMeta)}
                      removableSort
                      //columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterElement={
                                <Dropdown
                                  value={filtersDeptCountsRight.departmentName.value}
                                  options={departmentFilterOptions}
                                  onChange={(e) =>
                                    setFiltersDeptCountsRight((prev) => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: "equals" },
                                    }))
                                  }
                                  placeholder={t('Common.SelectOption')}
                                  className="w-full h-[42px]"
                                  showClear
                                />
                              }
                      />
                      {showInternal && <Column field="internal" header={t('OTKEvaluation.Interne')} 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('internal', 'internal', 'R')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsRight.internal)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsRight.internal.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsRight((p) => ({ ...p, internal: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showExternal && <Column field="external" header={t('OTKEvaluation.Externe')} 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('external', 'external', 'R')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsRight.external)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsRight.external.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsRight((p) => ({ ...p, external: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showComplaints && <Column field="complaints" header={t('OTKEvaluation.Reklamacie')} 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell('complaints', 'complaints', 'R')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsRight.complaints)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsRight.complaints.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsRight((p) => ({ ...p, complaints: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {/* Spolu = súčet chýb (nie kusy) */}
                      <Column field="total" header="Spolu" 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCell("total", null, "R")}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalsRight.total)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptCountsRight.total.value}
                                  onChange={(val) =>
                                    setFiltersDeptCountsRight((p) => ({ ...p, total: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />
                    </TreeTable>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            <div ref={refDeptCountPieces} className="print-full">
              <Card
                title={
                  <div className="flex justify-between items-center w-full">
                    <span>{t('Ui.R67.Pocet_chybnych_kusov_za_oddelenie')}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                      <InputSwitch
                        className="no-print"
                        checked={comparePiecesMode}
                        onChange={(e) => {
                          setComparePiecesMode(e.value);
                          setExpandedPiecesLeft({});
                          setExpandedPiecesRight({});
                        }}
                      />
                      <Button
                        icon={
                          comparePiecesMode
                            ? Object.keys(expandedPiecesLeft).length > 0 &&
                              Object.keys(expandedPiecesRight).length > 0
                              ? 'pi pi-angle-down'
                              : 'pi pi-angle-right'
                            : Object.keys(expandedPiecesLeft).length > 0
                              ? 'pi pi-angle-down'
                              : 'pi pi-angle-right'
                        }
                        className="p-button-text p-button-sm no-print"
                        tooltip={
                          comparePiecesMode
                            ? Object.keys(expandedPiecesLeft).length > 0 &&
                              Object.keys(expandedPiecesRight).length > 0
                              ? t('OTKEvaluation.ZbalitVsetky')
                              : t('OTKEvaluation.RozbalitVsetky')
                            : Object.keys(expandedPiecesLeft).length > 0
                              ? t('OTKEvaluation.ZbalitVsetky')
                              : t('OTKEvaluation.RozbalitVsetky')
                        }
                        tooltipOptions={{ position: 'left' }}
                        onClick={() => {
                          if (!comparePiecesMode) {
                            const hasExpandedLeft = Object.keys(expandedPiecesLeft).length > 0;

                            if (hasExpandedLeft) {
                              setExpandedPiecesLeft({});
                            } else {
                              setExpandDialogTarget('pieces');
                              setExpandDialogVisible(true);
                            }
                          } else {
                            const hasExpandedLeft  = Object.keys(expandedPiecesLeft).length > 0;
                            const hasExpandedRight = Object.keys(expandedPiecesRight).length > 0;
                            const bothExpanded     = hasExpandedLeft && hasExpandedRight;

                            if (bothExpanded) {
                              setExpandedPiecesLeft({});
                              setExpandedPiecesRight({});
                            } else {
                              setExpandDialogTarget('pieces');
                              setExpandDialogVisible(true);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                }
                subTitle={t('OTKEvaluation.KliknutimRozbalteZamestnancovVPorovnaniMesiacVsOd')}
              >
                {!comparePiecesMode ? (
                    <div className="flex items-center gap-3 mb-4 no-print">
                      <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                      <Calendar
                        value={piecesLeftRange || null}
                        onChange={(e) => {
                          setPiecesLeftRange(e.value);
                          setExpandedPiecesLeft({});
                        }}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                    ) : (
                    <div className="flex items-center justify-between gap-6 mb-4 no-print">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka')}</span>
                        <Calendar
                          value={piecesLeftRange || null}
                          onChange={(e) => {
                            setPiecesLeftRange(e.value);
                            setExpandedPiecesLeft({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex items-center gap-2 no-print">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                        <Calendar
                          value={piecesRightRange || null}
                          onChange={(e) => {
                            setPiecesRightRange(e.value);
                            setExpandedPiecesRight([]);
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          readOnlyInput
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                    </div>
                  )}
                    {!comparePiecesMode ? (
                    <TreeTable
                      value={piecesLeftFilteredTree}
                      dataKey="key"
                      expandedKeys={expandedPiecesLeft}
                      onToggle={(e) => setExpandedPiecesLeft(e.value)}
                      filters={filtersDeptPiecesLeft}
                      onFilter={(e) => setFiltersDeptPiecesLeft(e.filters)}
                      filterMode="lenient"
                      resizableColumns
                      columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}
                      sortMode="multiple"
                      multiSortMeta={piecesSortMeta}
                      onSort={(e) => setPiecesSortMeta(e.multiSortMeta)}
                      removableSort
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterMatchMode="equals"                 // bežný equals
                              filterElement={
                                <Dropdown
                                  value={filtersDeptPiecesLeft.departmentName?.value ?? null}
                                  options={departmentFilterOptions}    // viz nižšie
                                  onChange={(e) =>
                                    setFiltersDeptPiecesLeft(prev => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: 'equals' }
                                    }))
                                  }
                                  className="w-full h-[42px]"
                                  optionDisabled="disabled"
                                  showClear
                                  placeholder={t('Common.SelectOption')}
                                />
                              }
                      />
                      {showInternal && <Column field="internal" header={t('OTKEvaluation.Interne')}
                              style={{ minWidth: 120, width: 120 }}          // zväčšený stĺpec
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('internal', 'internal', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.internal)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.internal.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, internal: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showExternal && <Column field="external" header={t('OTKEvaluation.Externe')}
                              style={{ minWidth: 120, width: 120 }}          // zväčšený stĺpec
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('external', 'external', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.external)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.external.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, external: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showComplaints && <Column field="complaints" header={t('OTKEvaluation.Reklamacie')}
                              style={{ minWidth: 120, width: 120 }}          // zväčšený stĺpec
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('complaints', 'complaints', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.complaints)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.complaints.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, complaints: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {/* Spolu = súčet chýb (nie kusy) */}
                      <Column field="pieces" header="Spolu"
                              style={{ minWidth: 120, width: 120 }}          // zväčšený stĺpec
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell("pieces", null, "L")}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.pieces)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.pieces.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, pieces: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />
                    </TreeTable>
                ) : (
                  <div className="grid grid-cols-2 gap-4 print-compare-grid">
                    <div className="overflow-x-auto print-compare-cell">
                    {/* ĽAVÁ (porovnanie) */}
                    <TreeTable
                      value={piecesLeftFilteredTree}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}      // <- horizontálny aj vertikálny
                      filters={filtersDeptPiecesLeft}
                      onFilter={(e) => setFiltersDeptPiecesLeft(e.filters)}
                      resizableColumns
                      expandedKeys={expandedPiecesLeft}
                      onToggle={(e) => setExpandedPiecesLeft(e.value)}
                      sortMode="multiple"
                      multiSortMeta={piecesSortMeta}
                      onSort={(e) => setPiecesSortMeta(e.multiSortMeta)}
                      removableSort
                      //columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterElement={
                                <Dropdown
                                  value={filtersDeptPiecesLeft.departmentName.value}
                                  options={departmentFilterOptions}
                                  onChange={(e) =>
                                    setFiltersDeptPiecesLeft((prev) => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: "equals" },
                                    }))
                                  }
                                  placeholder={t('Common.SelectOption')}
                                  className="w-full h-[42px]"
                                  showClear
                                />
                              }
                      />
                      {showInternal && <Column field="internal" header={t('OTKEvaluation.Interne')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('internal', 'internal', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.internal)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.internal.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, internal: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showExternal && <Column field="external" header={t('OTKEvaluation.Externe')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('external', 'external', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.external)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.external.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, external: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showComplaints && <Column field="complaints" header={t('OTKEvaluation.Reklamacie')}
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('complaints', 'complaints', 'L')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.complaints)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.complaints.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, complaints: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {/* Spolu = súčet chýb (nie kusy) */}
                      <Column field="pieces" header="Spolu"
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell("pieces", null, "L")}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesLeft.pieces)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesLeft.pieces.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesLeft((p) => ({ ...p, pieces: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />
                    </TreeTable>
                    </div>
                    <div className="overflow-x-auto print-compare-cell">
                    {/* PRAVÁ (porovnanie) */}
                    <TreeTable
                      value={piecesRightFilteredTree}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}
                      filters={filtersDeptPiecesRight}
                      onFilter={(e) => setFiltersDeptPiecesRight(e.filters)}
                      resizableColumns
                      expandedKeys={expandedPiecesRight}
                      onToggle={(e) => setExpandedPiecesRight(e.value)}
                      sortMode="multiple"
                      multiSortMeta={piecesSortMeta}
                      onSort={(e) => setPiecesSortMeta(e.multiSortMeta)}
                      removableSort
                      //columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterElement={
                                <Dropdown
                                  value={filtersDeptPiecesRight.departmentName.value}
                                  options={departmentFilterOptions}
                                  onChange={(e) =>
                                    setFiltersDeptPiecesRight((prev) => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: "equals" },
                                    }))
                                  }
                                  placeholder={t('Common.SelectOption')}
                                  className="w-full h-[42px]"
                                  showClear
                                />
                              }
                      />
                      {showInternal && <Column field="internal" header={t('OTKEvaluation.Interne')} 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('internal', 'internal', 'R')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesRight.internal)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesRight.internal.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesRight((p) => ({ ...p, internal: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showExternal && <Column field="external" header={t('OTKEvaluation.Externe')} 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('external', 'external', 'R')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesRight.external)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesRight.external.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesRight((p) => ({ ...p, external: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {showComplaints && <Column field="complaints" header={t('OTKEvaluation.Reklamacie')} 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell('complaints', 'complaints', 'R')}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesRight.complaints)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesRight.complaints.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesRight((p) => ({ ...p, complaints: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />}
                      {/* Spolu = súčet chýb (nie kusy) */}
                      <Column field="pieces" header="Spolu" 
                              style={{ width: 120, minWidth: 120}}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickablePiecesCell("pieces", null, "R")}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(totalPiecesRight.pieces)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersDeptPiecesRight.pieces.value}
                                  onChange={(val) =>
                                    setFiltersDeptPiecesRight((p) => ({ ...p, pieces: { value: val, matchMode: 'contains' } }))
                                  }
                                />
                              }
                      />
                    </TreeTable>
                    </div>
                  </div>
                )}
              </Card>
            </div>

              {/* === 2) NÁKLADY ZA ODD === */}
              <div ref={refDeptCosts} className="print-full">
                <Card
                  title={
                    <div className="flex justify-between items-center w-full">
                      <span>{t('Ui.R67.Naklady_na_odstranenie_chyb_za_oddelenie')}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                        <InputSwitch
                          className="no-print"
                          checked={compareCostsMode}
                          onChange={(e) => {
                            setCompareCostsMode(e.value);
                            setExpandedDeptCostsLeft([]);
                            setExpandedDeptCostsRight([]);
                          }}
                        />
                        <Button
                          icon={
                            compareCostsMode
                              ? Object.keys(expandedCostsLeft).length > 0 &&
                                Object.keys(expandedCostsRight).length > 0
                                ? 'pi pi-angle-down'
                                : 'pi pi-angle-right'
                              : Object.keys(expandedCostsLeft).length > 0
                                ? 'pi pi-angle-down'
                                : 'pi pi-angle-right'
                          }
                          className="p-button-text p-button-sm no-print"
                          tooltip={
                            compareCostsMode
                              ? Object.keys(expandedCostsLeft).length > 0 &&
                                Object.keys(expandedCostsRight).length > 0
                                ? t('OTKEvaluation.ZbalitVsetky')
                                : t('OTKEvaluation.RozbalitVsetky')
                              : Object.keys(expandedCostsLeft).length > 0
                                ? t('OTKEvaluation.ZbalitVsetky')
                                : t('OTKEvaluation.RozbalitVsetky')
                          }
                          tooltipOptions={{ position: 'left' }}
                          onClick={() => {
                            if (!compareCostsMode) {
                              const hasExpandedLeft = Object.keys(expandedCostsLeft).length > 0;

                              if (hasExpandedLeft) {
                                setExpandedCostsLeft({});
                              } else {
                                setExpandDialogTarget('costs');
                                setExpandDialogVisible(true);
                              }
                            } else {
                              const hasExpandedLeft  = Object.keys(expandedCostsLeft).length > 0;
                              const hasExpandedRight = Object.keys(expandedCostsRight).length > 0;
                              const bothExpanded     = hasExpandedLeft && hasExpandedRight;

                              if (bothExpanded) {
                                setExpandedCostsLeft({});
                                setExpandedCostsRight({});
                              } else {
                                setExpandDialogTarget('costs');
                                setExpandDialogVisible(true);
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  }
                  subTitle=""
                >
                  {!compareCostsMode ? (
                    <div className="flex items-center gap-3 mb-4 no-print">
                      <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                      <Calendar
                        value={costsLeftRange || null}
                        onChange={(e) => {
                          setCostsLeftRange(e.value);
                          setExpandedDeptCostsLeft([]);
                        }}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-6 mb-4 no-print">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka')}</span>
                        <Calendar
                          value={costsLeftRange || null}
                          onChange={(e) => {
                            setCostsLeftRange(e.value);
                            setExpandedDeptCostsLeft([]);
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex items-center gap-2 no-print">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                        <Calendar
                          value={costsRightRange || null}
                          onChange={(e) => {
                            setCostsRightRange(e.value);
                            setExpandedDeptCostsRight([]);
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          readOnlyInput
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                    </div>
                  )}

                  {!compareCostsMode ? (
                    <TreeTable
                      value={costsLeftFilteredTree}
                      dataKey="key"
                      expandedKeys={expandedCostsLeft}
                      onToggle={(e) => setExpandedCostsLeft(e.value)}
                      filters={filtersDeptCostsLeft}
                      onFilter={(e) => setFiltersDeptCostsLeft(e.filters)}
                      filterMode="lenient"
                      resizableColumns
                      columnResizeMode="expand"
                      rowClassName={rowClassName}
                      tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                      scrollable={!isPrintMode}
                      scrollHeight={isPrintMode ? undefined : "500px"}
                      scrollDirection={isPrintMode ? undefined : "both"}
                      removableSort
                      sortMode="multiple"
                      multiSortMeta={totalCostsSortMeta}
                      onSort={(e) => setTotalCostsSortMeta(e.multiSortMeta)}
                    >
                      <Column expander field="departmentName" header={t('Common.Department')}
                              style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                              body={deptBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter sortable pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterMatchMode="equals"                 // bežný equals
                              filterElement={
                                <Dropdown
                                  value={filtersDeptCostsLeft.departmentName?.value ?? null}
                                  options={departmentFilterOptions}    // viz nižšie
                                  onChange={(e) =>
                                    setFiltersDeptCostsLeft(prev => ({
                                      ...prev,
                                      departmentName: { value: e.value, matchMode: 'equals' }
                                    }))
                                  }
                                  className="w-full h-[42px]"
                                  optionDisabled="disabled"
                                  showClear
                                  placeholder={t('Common.SelectOption')}
                                />
                              }
                      />
                      {showInternal && <Column
                        field="internalCost"
                        header={t('OTKEvaluation.InternalCurrency', { currency: currencySymbol })}
                        style={{ width: 120, minWidth: 120}}
                        headerStyle={HD}
                        filterHeaderStyle={HD}
                        footerStyle={FT}
                        body={renderClickableCostCell("internalCost", "internal", "L")}
                        footer={FORMAT_CURRENCY(totalsCostsLeft.internalCost)}
                        filter sortable pt={PT_HEADER}
                        showFilterMenu={false}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersDeptCostsLeft.internalCost.value}
                            onChange={(val) =>
                              setFiltersDeptCostsLeft((prev) => ({
                                ...prev,
                                internalCost: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />}
                      {showExternal && <Column
                        field="externalCost"
                        header={t('OTKEvaluation.ExternalCurrency', { currency: currencySymbol })}
                        style={{ width: 120, minWidth: 120}}
                        headerStyle={HD}
                        filterHeaderStyle={HD}
                        footerStyle={FT}
                        body={renderClickableCostCell("externalCost", "external", "L")}
                        footer={FORMAT_CURRENCY(totalsCostsLeft.externalCost)}
                        filter sortable pt={PT_HEADER}
                        showFilterMenu={false}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersDeptCostsLeft.externalCost.value}
                            onChange={(val) =>
                              setFiltersDeptCostsLeft((prev) => ({
                                ...prev,
                                externalCost: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />}
                      {showComplaints && <Column
                        field="complaintsCost"
                        header={t('OTKEvaluation.ClaimsCurrency', { currency: currencySymbol })}
                        style={{ width: 120, minWidth: 120}}
                        headerStyle={HD}
                        filterHeaderStyle={HD}
                        footerStyle={FT}
                        body={renderClickableCostCell("complaintsCost", "complaints", "L")}
                        footer={FORMAT_CURRENCY(totalsCostsLeft.complaintsCost)}
                        filter sortable pt={PT_HEADER}
                        showFilterMenu={false}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersDeptCostsLeft.complaintsCost.value}
                            onChange={(val) =>
                              setFiltersDeptCostsLeft((prev) => ({
                                ...prev,
                                complaintsCost: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />}
                      <Column
                        field="totalCosts"
                        header="Spolu"
                        style={{ width: 120, minWidth: 120}}
                        headerStyle={HD}
                        filterHeaderStyle={HD}
                        footerStyle={FT}
                        body={renderClickableCostCell("totalCosts", null, "L")}
                        footer={FORMAT_CURRENCY(totalsCostsLeft.totalCosts)}
                        filter sortable pt={PT_HEADER}
                        showFilterMenu={false}
                        filterElement={
                          <SearchFilterInput
                            value={filtersDeptCostsLeft.totalCosts.value}
                            onChange={(val) =>
                              setFiltersDeptCostsLeft((prev) => ({
                                ...prev,
                                totalCosts: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </TreeTable>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 print-compare-grid">
                      <div className="print-compare-cell">
                      {/* ĽAVÁ */}
                      <TreeTable
                        value={costsLeftFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedCostsLeft}
                        onToggle={(e) => setExpandedCostsLeft(e.value)}
                        filters={filtersDeptCostsLeft}
                        onFilter={(e) => setFiltersDeptCostsLeft(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassName}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={totalCostsSortMeta}
                        onSort={(e) => setTotalCostsSortMeta(e.multiSortMeta)}
                      >
                        <Column
                          expander
                          field="departmentName"
                          header={t('Common.Department')}
                          style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                          body={deptBodyTemplate}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer="Celkovo"
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersDeptCostsLeft.departmentName.value}
                              options={[{ label: t('Common.All'), value: null }, ...departments.map((d) => ({ label: d.name, value: d.name }))]}
                              onChange={(e) =>
                                setFiltersDeptCostsLeft((prev) => ({
                                  ...prev,
                                  departmentName: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberOddelenie')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                        />
                        {showInternal && <Column
                          field="internalCost"
                          header={t('OTKEvaluation.InternalCurrency', { currency: currencySymbol })}
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("internalCost", "internal", "L")}
                          footer={FORMAT_CURRENCY(totalsCostsLeft.internalCost)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersDeptCostsLeft.internalCost.value}
                              onChange={(val) =>
                                setFiltersDeptCostsLeft((prev) => ({
                                  ...prev,
                                  internalCost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />}
                        {showExternal && <Column
                          field="externalCost"
                          header={t('OTKEvaluation.ExternalCurrency', { currency: currencySymbol })}
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("externalCost", "external", "L")}
                          footer={FORMAT_CURRENCY(totalsCostsLeft.externalCost)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersDeptCostsLeft.externalCost.value}
                              onChange={(val) =>
                                setFiltersDeptCostsLeft((prev) => ({
                                  ...prev,
                                  externalCost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />}
                        {showComplaints && <Column
                          field="complaintsCost"
                          header={t('OTKEvaluation.ClaimsCurrency', { currency: currencySymbol })}
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("complaintsCost", "complaints", "L")}
                          footer={FORMAT_CURRENCY(totalsCostsLeft.complaintsCost)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersDeptCostsLeft.complaintsCost.value}
                              onChange={(val) =>
                                setFiltersDeptCostsLeft((prev) => ({
                                  ...prev,
                                  complaintsCost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />}
                        <Column
                          field="totalCosts"
                          header="Spolu"
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("totalCosts", null, "L")}
                          footer={FORMAT_CURRENCY(totalsCostsLeft.totalCosts)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersDeptCostsLeft.totalCosts.value}
                              onChange={(val) =>
                                setFiltersDeptCostsLeft((prev) => ({
                                  ...prev,
                                  totalCosts: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      </div>

                      <div className="print-compare-cell">
                      {/* PRAVÁ */}
                      <TreeTable
                        value={costsRightFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedCostsRight}
                        onToggle={(e) => setExpandedCostsRight(e.value)}
                        filters={filtersDeptCostsRight}
                        onFilter={(e) => setFiltersDeptCostsRight(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassName}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: 'auto' }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={totalCostsSortMeta}
                        onSort={(e) => setTotalCostsSortMeta(e.multiSortMeta)}
                      >
                        <Column
                          expander
                          field="departmentName"
                          header={t('Common.Department')}
                          style={{ minWidth: 260, width: 260 }}          // zväčšený stĺpec
                          body={deptBodyTemplate}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer="Celkovo"
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersDeptCostsRight.departmentName.value}
                              options={[{ label: t('Common.All'), value: null }, ...departments.map((d) => ({ label: d.name, value: d.name }))]}
                              onChange={(e) =>
                                setFiltersDeptCostsRight((prev) => ({
                                  ...prev,
                                  departmentName: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberOddelenie')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                        />
                        {showInternal && <Column
                          field="internalCost"
                          header={t('OTKEvaluation.InternalCurrency', { currency: currencySymbol })}
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("internalCost", "internal", "R")}
                          footer={FORMAT_CURRENCY(totalsCostsRight.internalCost)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersDeptCostsRight.internalCost.value}
                              onChange={(val) =>
                                setFiltersDeptCostsRight((prev) => ({
                                  ...prev,
                                  internalCost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />}
                        {showExternal && <Column
                          field="externalCost"
                          header={t('OTKEvaluation.ExternalCurrency', { currency: currencySymbol })}
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("externalCost", "external", "R")}
                          footer={FORMAT_CURRENCY(totalsCostsRight.externalCost)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersDeptCostsRight.externalCost.value}
                              onChange={(val) =>
                                setFiltersDeptCostsRight((prev) => ({
                                  ...prev,
                                  externalCost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />}
                        {showComplaints &&<Column
                          field="complaintsCost"
                          header={t('OTKEvaluation.ClaimsCurrency', { currency: currencySymbol })}
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("complaintsCost", "complaints", "R")}
                          footer={FORMAT_CURRENCY(totalsCostsRight.complaintsCost)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersDeptCostsRight.complaintsCost.value}
                              onChange={(val) =>
                                setFiltersDeptCostsRight((prev) => ({
                                  ...prev,
                                  complaintsCost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />}
                        <Column
                          field="totalCosts"
                          header="Spolu"
                          style={{ width: 120, minWidth: 120}}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCostCell("totalCosts", null, "R")}
                          footer={FORMAT_CURRENCY(totalsCostsRight.totalCosts)}
                          filter sortable pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersDeptCostsRight.totalCosts.value}
                              onChange={(val) =>
                                setFiltersDeptCostsRight((prev) => ({
                                  ...prev,
                                  totalCosts: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
              <div ref={refRootCauseCounts} className="print-full">
                <Card
                  title={
                    <>
                    <div className="flex justify-between items-center w-full">
                      <span>{t('Ui.R67.Korenova_pricina_voci_oddeleniu_a_zamestnancovi')}</span>

                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                        <InputSwitch
                          className="no-print"
                          checked={compareRcMode}
                          onChange={(e) => {
                            setCompareRcMode(e.value);
                            setExpandedRcLeft({});
                            setExpandedRcRight({});
                            if (e.value) setRcTableMode(true);
                          }}
                        />

                        <Button
                          icon={
                            compareRcMode
                              ? Object.keys(expandedRcLeft).length > 0 &&
                                Object.keys(expandedRcRight).length > 0
                                ? "pi pi-angle-down"
                                : "pi pi-angle-right"
                              : Object.keys(expandedRcLeft).length > 0
                                ? "pi pi-angle-down"
                                : "pi pi-angle-right"
                          }
                          className="p-button-text p-button-sm no-print"
                          tooltip={
                            compareRcMode
                              ? Object.keys(expandedRcLeft).length > 0 &&
                                Object.keys(expandedRcRight).length > 0
                                ? t('OTKEvaluation.ZbalitVsetky')
                                : t('OTKEvaluation.RozbalitVsetky')
                              : Object.keys(expandedRcLeft).length > 0
                                ? t('OTKEvaluation.ZbalitVsetky')
                                : t('OTKEvaluation.RozbalitVsetky')
                          }
                          tooltipOptions={{ position: "left" }}
                          onClick={() => {
                            if (!compareRcMode) {
                              const hasExpandedLeft = Object.keys(expandedRcLeft).length > 0;
                              if (hasExpandedLeft) {
                                setExpandedRcLeft({});
                              } else {
                                setExpandRootCauseDialogVisible(true);
                              }
                            } else {
                              const hasExpandedLeft = Object.keys(expandedRcLeft).length > 0;
                              const hasExpandedRight = Object.keys(expandedRcRight).length > 0;
                              const bothExpanded = hasExpandedLeft && hasExpandedRight;

                              if (bothExpanded) {
                                setExpandedRcLeft({});
                                setExpandedRcRight({});
                              } else {
                                setExpandRootCauseDialogVisible(true);
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                  <div className="mt-2">
                    {!compareRcMode && (
                      <div className="flex items-center gap-2">
                        <span className="text-base">{t('OTKEvaluation.Tabulka')}</span>
                        <InputSwitch
                          checked={!rcTableMode}
                          onChange={(e) => {
                            const showGraph = e.value;
                            setRcTableMode(!e.value);

                            if (showGraph && !compareRcMode) {
                              setSelectedRcRootCauseIds([]);
                              setSelectedRcDepartments([]);
                              setRcChartViewMode("total");
                            }
                          }}
                        />
                        <span className="text-base">Graf</span>
                      </div>
                    )}
                  </div>
                  </>
                  }
                >

                  {!compareRcMode ? (
                    <div className="flex items-center gap-3 mb-4 flex-wrap no-print">
                      <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                      <Calendar
                        value={rcLeftRange || null}
                        onChange={(e) => {
                          setRcLeftRange(e.value);
                          setExpandedRcLeft({});
                        }}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />

                      {!rcTableMode && (
                        <>
                          <span className="text-sm text-gray-700 font-medium ml-4">{t('Ui.R67.Korenova_pricina')}</span>
                          <MultiSelect
                            value={selectedRcRootCauseIds}
                            options={rcRootCauseOptions}
                            onChange={(e) => setSelectedRcRootCauseIds(e.value)}
                            placeholder={t('OTKEvaluation.VsetkyPriciny')}
                            maxSelectedLabels={2}
                            selectedItemsLabel={t('OTKEvaluation.0Pricin')}
                            selectAllLabel={t('Common.All')}
                            className="w-[260px] h-[38px] items-center border border-gray-400 rounded-md"
                            valueTemplate={(value) =>
                              renderSelectedMulti(
                                value,
                                rcRootCauseOptions,
                                t('OTKEvaluation.VyberPricinu'),
                                t('OTKEvaluation.VsetkyPriciny')
                              )
                            }
                          />

                          <span className="text-sm text-gray-700 font-medium">Oddelenie:</span>
                          <MultiSelect
                            value={selectedRcDepartments}
                            options={rcDepartmentOptions}
                            onChange={(e) => setSelectedRcDepartments(e.value)}
                            placeholder={t('OTKEvaluation.VsetkyOdd')}
                            maxSelectedLabels={2}
                            selectedItemsLabel="{0} odd."
                            selectAllLabel={t('Common.All')}
                            className="w-[240px] h-[38px] items-center border border-gray-400 rounded-md"
                            valueTemplate={(value) =>
                              renderSelectedMulti(
                                value,
                                rcDepartmentOptions,
                                t('OTKEvaluation.VyberOddelenie'),
                                t('OTKEvaluation.VsetkyOddelenia')
                              )
                            }
                          />

                          <span className="text-sm text-gray-700 font-medium">Zobrazenie:</span>
                          <Dropdown
                            value={rcChartViewMode}
                            options={[
                              { label: "Spolu", value: "total" },
                              { label: "Po mesiacoch", value: "months" },
                            ]}
                            onChange={(e) => setRcChartViewMode(e.value)}
                            className="w-[180px] h-[38px] items-center border border-gray-400 rounded-md"
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-6 mb-4 no-print">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka')}</span>
                        <Calendar
                          value={rcLeftRange || null}
                          onChange={(e) => {
                            setRcLeftRange(e.value);
                            setExpandedRcLeft({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>

                      <div className="flex items-center gap-2 no-print">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                        <Calendar
                          value={rcRightRange || null}
                          onChange={(e) => {
                            setRcRightRange(e.value);
                            setExpandedRcRight({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          readOnlyInput
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                    </div>
                  )}

                  {rcTableMode || compareRcMode ? (
                    !compareRcMode ? (
                      <TreeTable
                        value={rcLeftFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedRcLeft}
                        onToggle={(e) => setExpandedRcLeft(e.value)}
                        filters={filtersRcLeft}
                        onFilter={(e) => setFiltersRcLeft(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rcRowClassName}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto", width: "100%" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        sortMode="multiple"
                        multiSortMeta={rcSortMeta}
                        onSort={(e) => setRcSortMeta(e.multiSortMeta)}
                        removableSort
                      >
                        <Column
                          expander
                          field="label"
                          header={t('OTKEvaluation.KorenovaPricina')}
                          style={{ minWidth: 260, width: 260 }}
                          body={rcNameBodyTemplate}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          footer="Celkovo"
                          filterMatchMode="equals"
                          filterElement={
                            <Dropdown
                              value={filtersRcLeft.label?.value ?? null}
                              options={rootCauseFilterOptions}
                              onChange={(e) =>
                                setFiltersRcLeft((prev) => ({
                                  ...prev,
                                  label: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              className="w-full h-[42px]"
                              showClear
                              placeholder={t('OTKEvaluation.VyberPricinu')}
                            />
                          }
                        />

                        {showInternal && (
                          <Column
                            field="internal"
                            header={t('OTKEvaluation.Interne')}
                            style={{ width: 120, minWidth: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableCountCellRC("internal", "internal", "L")}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            footer={FORMAT_INT(rcTotalsLeft.internal)}
                            filterElement={
                              <SearchFilterInput
                                value={filtersRcLeft.internal.value}
                                onChange={(val) =>
                                  setFiltersRcLeft((p) => ({
                                    ...p,
                                    internal: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        )}

                        {showExternal && (
                          <Column
                            field="external"
                            header={t('OTKEvaluation.Externe')}
                            style={{ width: 120, minWidth: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableCountCellRC("external", "external", "L")}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            footer={FORMAT_INT(rcTotalsLeft.external)}
                            filterElement={
                              <SearchFilterInput
                                value={filtersRcLeft.external.value}
                                onChange={(val) =>
                                  setFiltersRcLeft((p) => ({
                                    ...p,
                                    external: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        )}

                        {showComplaints && (
                          <Column
                            field="complaints"
                            header={t('OTKEvaluation.Reklamacie')}
                            style={{ width: 120, minWidth: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableCountCellRC("complaints", "complaints", "L")}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            footer={FORMAT_INT(rcTotalsLeft.complaints)}
                            filterElement={
                              <SearchFilterInput
                                value={filtersRcLeft.complaints.value}
                                onChange={(val) =>
                                  setFiltersRcLeft((p) => ({
                                    ...p,
                                    complaints: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        )}

                        <Column
                          field="total"
                          header="Spolu"
                          style={{ width: 120, minWidth: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableCountCellRC("total", null, "L")}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          footer={FORMAT_INT(rcTotalsLeft.total)}
                          filterElement={
                            <SearchFilterInput
                              value={filtersRcLeft.total.value}
                              onChange={(val) =>
                                setFiltersRcLeft((p) => ({
                                  ...p,
                                  total: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 print-compare-grid">
                        <div className="overflow-x-auto print-compare-cell">
                          <TreeTable
                            value={rcLeftFilteredTree}
                            scrollable={!isPrintMode}
                            scrollHeight={isPrintMode ? undefined : "500px"}
                            scrollDirection={isPrintMode ? undefined : "both"}
                            filters={filtersRcLeft}
                            onFilter={(e) => setFiltersRcLeft(e.filters)}
                            resizableColumns
                            expandedKeys={expandedRcLeft}
                            onToggle={(e) => setExpandedRcLeft(e.value)}
                            sortMode="multiple"
                            multiSortMeta={rcSortMeta}
                            onSort={(e) => setRcSortMeta(e.multiSortMeta)}
                            removableSort
                            rowClassName={rcRowClassName}
                            tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          >
                            <Column
                              expander
                              field="label"
                              header={t('OTKEvaluation.KorenovaPricina')}
                              style={{ minWidth: 260, width: 260 }}
                              body={rcNameBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterMatchMode="equals"
                              filterElement={
                                <Dropdown
                                  value={filtersRcLeft.label?.value ?? null}
                                  options={rootCauseFilterOptions}
                                  onChange={(e) =>
                                    setFiltersRcLeft((prev) => ({
                                      ...prev,
                                      label: { value: e.value, matchMode: "equals" },
                                    }))
                                  }
                                  className="w-full h-[42px]"
                                  showClear
                                  placeholder={t('OTKEvaluation.VyberPricinu')}
                                />
                              }
                            />

                            {showInternal && (
                              <Column
                                field="internal"
                                header={t('OTKEvaluation.Interne')}
                                style={{ width: 120, minWidth: 120 }}
                                headerStyle={HD}
                                filterHeaderStyle={HD}
                                footerStyle={FT}
                                body={renderClickableCountCellRC("internal", "internal", "L")}
                                filter
                                sortable
                                pt={PT_HEADER}
                                showFilterMenu={false}
                                footer={FORMAT_INT(rcTotalsLeft.internal)}
                                filterElement={
                                  <SearchFilterInput
                                    value={filtersRcLeft.internal.value}
                                    onChange={(val) =>
                                      setFiltersRcLeft((p) => ({
                                        ...p,
                                        internal: { value: val, matchMode: "contains" },
                                      }))
                                    }
                                  />
                                }
                              />
                            )}

                            {showExternal && (
                              <Column
                                field="external"
                                header={t('OTKEvaluation.Externe')}
                                style={{ width: 120, minWidth: 120 }}
                                headerStyle={HD}
                                filterHeaderStyle={HD}
                                footerStyle={FT}
                                body={renderClickableCountCellRC("external", "external", "L")}
                                filter
                                sortable
                                pt={PT_HEADER}
                                showFilterMenu={false}
                                footer={FORMAT_INT(rcTotalsLeft.external)}
                                filterElement={
                                  <SearchFilterInput
                                    value={filtersRcLeft.external.value}
                                    onChange={(val) =>
                                      setFiltersRcLeft((p) => ({
                                        ...p,
                                        external: { value: val, matchMode: "contains" },
                                      }))
                                    }
                                  />
                                }
                              />
                            )}

                            {showComplaints && (
                              <Column
                                field="complaints"
                                header={t('OTKEvaluation.Reklamacie')}
                                style={{ width: 120, minWidth: 120 }}
                                headerStyle={HD}
                                filterHeaderStyle={HD}
                                footerStyle={FT}
                                body={renderClickableCountCellRC("complaints", "complaints", "L")}
                                filter
                                sortable
                                pt={PT_HEADER}
                                showFilterMenu={false}
                                footer={FORMAT_INT(rcTotalsLeft.complaints)}
                                filterElement={
                                  <SearchFilterInput
                                    value={filtersRcLeft.complaints.value}
                                    onChange={(val) =>
                                      setFiltersRcLeft((p) => ({
                                        ...p,
                                        complaints: { value: val, matchMode: "contains" },
                                      }))
                                    }
                                  />
                                }
                              />
                            )}

                            <Column
                              field="total"
                              header="Spolu"
                              style={{ width: 120, minWidth: 120 }}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCellRC("total", null, "L")}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(rcTotalsLeft.total)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersRcLeft.total.value}
                                  onChange={(val) =>
                                    setFiltersRcLeft((p) => ({
                                      ...p,
                                      total: { value: val, matchMode: "contains" },
                                    }))
                                  }
                                />
                              }
                            />
                          </TreeTable>
                        </div>

                        <div className="overflow-x-auto print-compare-cell">
                          <TreeTable
                            value={rcRightFilteredTree}
                            scrollable={!isPrintMode}
                            scrollHeight={isPrintMode ? undefined : "500px"}
                            scrollDirection={isPrintMode ? undefined : "both"}
                            filters={filtersRcRight}
                            onFilter={(e) => setFiltersRcRight(e.filters)}
                            resizableColumns
                            expandedKeys={expandedRcRight}
                            onToggle={(e) => setExpandedRcRight(e.value)}
                            sortMode="multiple"
                            multiSortMeta={rcSortMeta}
                            onSort={(e) => setRcSortMeta(e.multiSortMeta)}
                            removableSort
                            rowClassName={rcRowClassName}
                            tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          >
                            <Column
                              expander
                              field="label"
                              header={t('OTKEvaluation.KorenovaPricina')}
                              style={{ minWidth: 260, width: 260 }}
                              body={rcNameBodyTemplate}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              footer="Celkovo"
                              filterMatchMode="equals"
                              filterElement={
                                <Dropdown
                                  value={filtersRcRight.label?.value ?? null}
                                  options={rootCauseFilterOptions}
                                  onChange={(e) =>
                                    setFiltersRcRight((prev) => ({
                                      ...prev,
                                      label: { value: e.value, matchMode: "equals" },
                                    }))
                                  }
                                  className="w-full h-[42px]"
                                  showClear
                                  placeholder={t('OTKEvaluation.VyberPricinu')}
                                />
                              }
                            />

                            {showInternal && (
                              <Column
                                field="internal"
                                header={t('OTKEvaluation.Interne')}
                                style={{ width: 120, minWidth: 120 }}
                                headerStyle={HD}
                                filterHeaderStyle={HD}
                                footerStyle={FT}
                                body={renderClickableCountCellRC("internal", "internal", "R")}
                                filter
                                sortable
                                pt={PT_HEADER}
                                showFilterMenu={false}
                                footer={FORMAT_INT(rcTotalsRight.internal)}
                                filterElement={
                                  <SearchFilterInput
                                    value={filtersRcRight.internal.value}
                                    onChange={(val) =>
                                      setFiltersRcRight((p) => ({
                                        ...p,
                                        internal: { value: val, matchMode: "contains" },
                                      }))
                                    }
                                  />
                                }
                              />
                            )}

                            {showExternal && (
                              <Column
                                field="external"
                                header={t('OTKEvaluation.Externe')}
                                style={{ width: 120, minWidth: 120 }}
                                headerStyle={HD}
                                filterHeaderStyle={HD}
                                footerStyle={FT}
                                body={renderClickableCountCellRC("external", "external", "R")}
                                filter
                                sortable
                                pt={PT_HEADER}
                                showFilterMenu={false}
                                footer={FORMAT_INT(rcTotalsRight.external)}
                                filterElement={
                                  <SearchFilterInput
                                    value={filtersRcRight.external.value}
                                    onChange={(val) =>
                                      setFiltersRcRight((p) => ({
                                        ...p,
                                        external: { value: val, matchMode: "contains" },
                                      }))
                                    }
                                  />
                                }
                              />
                            )}

                            {showComplaints && (
                              <Column
                                field="complaints"
                                header={t('OTKEvaluation.Reklamacie')}
                                style={{ width: 120, minWidth: 120 }}
                                headerStyle={HD}
                                filterHeaderStyle={HD}
                                footerStyle={FT}
                                body={renderClickableCountCellRC("complaints", "complaints", "R")}
                                filter
                                sortable
                                pt={PT_HEADER}
                                showFilterMenu={false}
                                footer={FORMAT_INT(rcTotalsRight.complaints)}
                                filterElement={
                                  <SearchFilterInput
                                    value={filtersRcRight.complaints.value}
                                    onChange={(val) =>
                                      setFiltersRcRight((p) => ({
                                        ...p,
                                        complaints: { value: val, matchMode: "contains" },
                                      }))
                                    }
                                  />
                                }
                              />
                            )}

                            <Column
                              field="total"
                              header="Spolu"
                              style={{ width: 120, minWidth: 120 }}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableCountCellRC("total", null, "R")}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              footer={FORMAT_INT(rcTotalsRight.total)}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersRcRight.total.value}
                                  onChange={(val) =>
                                    setFiltersRcRight((p) => ({
                                      ...p,
                                      total: { value: val, matchMode: "contains" },
                                    }))
                                  }
                                />
                              }
                            />
                          </TreeTable>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                    <div className="h-[500px]">
                      {rcChartViewMode === "total" ? (
                        <Chart
                          type="bar"
                          style={{ width: "100%", height: "100%" }}
                          data={{
                            labels: rcChartRows.map((v) => v.rootCauseName),
                            datasets: [
                              {
                                type: "bar",
                                label: t('OTKEvaluation.Interne'),
                                data: rcChartRows.map((v) => v.internal),
                                yAxisID: "y",
                                backgroundColor: chartColors.blue,
                                borderColor: chartColors.blue,
                                borderWidth: 1,
                              },
                              {
                                type: "bar",
                                label: t('OTKEvaluation.Externe'),
                                data: rcChartRows.map((v) => v.external),
                                yAxisID: "y",
                                backgroundColor: chartColors.orange,
                                borderColor: chartColors.orange,
                                borderWidth: 1,
                              },
                              {
                                type: "bar",
                                label: t('OTKEvaluation.Reklamacie'),
                                data: rcChartRows.map((v) => v.complaints),
                                yAxisID: "y",
                                backgroundColor: chartColors.red,
                                borderColor: chartColors.red,
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: { mode: "index", intersect: false },
                            plugins: { legend: { position: "bottom", labels: { font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) } } },
                            scales: {
                              x: {
                                stacked: false,
                                ticks: { autoSkip: true, maxRotation: 45, minRotation: 0, font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) }, ...(printChartFontSize && { color: "#000" }),
                                ...(printChartFontSize && { grid: { color: "#000" } }),
                              },
                              y: {
                                stacked: false,
                                beginAtZero: true,
                                title: { display: true, text: t('OTKEvaluation.PocetZakaziek'), font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) }, ...(printChartFontSize && { color: "#000" }),
                                ...(printChartFontSize && { grid: { color: "#000" } }),
                              },
                            },
                          }}
                        />
                      ) : (
                        <Chart
                          type="bar"
                          style={{ width: "100%", height: "100%" }}
                          data={{
                            labels: rcChartMonthLabels,
                            datasets: [
                              {
                                type: "bar",
                                label: t('OTKEvaluation.Interne'),
                                data: rcChartMonthlySeries.internal,
                                yAxisID: "y",
                                backgroundColor: chartColors.blue,
                                borderColor: chartColors.blue,
                                borderWidth: 1,
                              },
                              {
                                type: "bar",
                                label: t('OTKEvaluation.Externe'),
                                data: rcChartMonthlySeries.external,
                                yAxisID: "y",
                                backgroundColor: chartColors.orange,
                                borderColor: chartColors.orange,
                                borderWidth: 1,
                              },
                              {
                                type: "bar",
                                label: t('OTKEvaluation.Reklamacie'),
                                data: rcChartMonthlySeries.complaints,
                                yAxisID: "y",
                                backgroundColor: chartColors.red,
                                borderColor: chartColors.red,
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: { mode: "index", intersect: false },
                            plugins: { legend: { position: "bottom", labels: { font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) } } },
                            scales: {
                              x: {
                                stacked: false,
                                ticks: { autoSkip: true, maxRotation: 45, minRotation: 0, font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) }, ...(printChartFontSize && { color: "#000" }),
                                ...(printChartFontSize && { grid: { color: "#000" } }),
                              },
                              y: {
                                stacked: false,
                                beginAtZero: true,
                                title: { display: true, text: t('OTKEvaluation.PocetZakaziek'), font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) }, ...(printChartFontSize && { color: "#000" }),
                                ...(printChartFontSize && { grid: { color: "#000" } }),
                              },
                            },
                          }}
                        />
                      )}
                    </div>
                  </div>
                  )}
                </Card>
              </div>

              {/* === 3) DRUH CHYBY VOČI SKUPINE === */}
              <div ref={refTypeGroup} className="print-full">
                <Card
                  title={
                    <>
                      <div className="flex justify-between items-center w-full">
                        <span>{t('Ui.R67.Druh_chyby_voci_skupine_chyb')}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                          <InputSwitch
                            className="no-print"
                            checked={compareTypeGroupMode}
                            onChange={(e) => {
                              setCompareTypeGroupMode(e.value);
                              setExpandedTypeGroupLeft({});
                              setExpandedTypeGroupRight({});
                            }}
                          />

                          {/* Rozbaliť / zbaliť všetky */}
                          <Button
                            icon={
                              compareTypeGroupMode
                                ? Object.keys(expandedTypeGroupLeft).length > 0 &&
                                  Object.keys(expandedTypeGroupRight).length > 0
                                  ? "pi pi-angle-down"
                                  : "pi pi-angle-right"
                                : Object.keys(expandedTypeGroupLeft).length > 0
                                ? "pi pi-angle-down"
                                : "pi pi-angle-right"
                            }
                            className="p-button-text p-button-sm no-print"
                            tooltip={
                              compareTypeGroupMode
                                ? Object.keys(expandedTypeGroupLeft).length > 0 &&
                                  Object.keys(expandedTypeGroupRight).length > 0
                                  ? t('OTKEvaluation.ZbalitVsetky')
                                  : t('OTKEvaluation.RozbalitVsetky')
                                : Object.keys(expandedTypeGroupLeft).length > 0
                                ? t('OTKEvaluation.ZbalitVsetky')
                                : t('OTKEvaluation.RozbalitVsetky')
                            }
                            tooltipOptions={{ position: "left" }}
                            onClick={() => {
                              if (!compareTypeGroupMode) {
                                // single tabuľka
                                if (Object.keys(expandedTypeGroupLeft).length > 0) {
                                  setExpandedTypeGroupLeft({});
                                } else {
                                  setExpandTypeGroupDialogVisible(true);
                                }
                              } else {
                                // porovnávací režim
                                const anyExpanded =
                                  Object.keys(expandedTypeGroupLeft).length > 0 ||
                                  Object.keys(expandedTypeGroupRight).length > 0;

                                if (anyExpanded) {
                                  setExpandedTypeGroupLeft({});
                                  setExpandedTypeGroupRight({});
                                } else {
                                  setExpandTypeGroupDialogVisible(true);
                                }
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-2">
                        {!compareTypeGroupMode && (
                          <div className="flex items-center gap-2">
                            <span className="text-base">{t('OTKEvaluation.Tabulka')}</span>
                            <InputSwitch
                              checked={!tgTableMode}
                              onChange={(e) => {
                                const showGraph = e.value; // true = idem na GRAF
                                setTgTableMode(!e.value);

                                if (showGraph && !compareTypeGroupMode) {
                                  syncErrorTypesWithRows(); // nastav druhy chýb
                                  setSelectedGroups([]);    // skupiny prázdne pri prvom zobrazení grafu
                                }
                              }}
                            />
                            <span className="text-base">Graf</span>
                          </div>
                        )}
                      </div>
                    </>
                  }
                >
                  {/* Výber obdobia */}
                  {!compareTypeGroupMode ? (
                    <div className="flex items-center gap-3 mb-4 no-print">
                      <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                      <Calendar
                        value={tgLeftRange || null}
                        onChange={(e) => {
                          setTgLeftRange(e.value);
                          setExpandedTypeGroupLeft({});
                        }}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md "
                      />
                    {/* Filtre len pri GRAFE (tgTableMode === false) */}
                    {!tgTableMode && (
                      <>
                        {/* Vyber druh chyby */}
                        <span className="text-sm text-gray-700 font-medium ml-4">
                          Druh chyby:
                        </span>
                        <MultiSelect
                          value={selectedErrorTypes}
                          options={errorTypeOptions}
                          onChange={(e) => setSelectedErrorTypes(e.value)}
                          placeholder={t('OTKEvaluation.VsetkyDruhy')}
                          //display="chip"
                          maxSelectedLabels={2}
                          selectedItemsLabel={t('OTKEvaluation.0DruhovChyb')}
                          selectAllLabel={t('Common.All')}
                          className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md " 
                          valueTemplate={(value) =>
                            renderSelectedMulti(value, errorTypeOptions, t('OTKEvaluation.VyberDruhChyby'), t('OTKEvaluation.VsetkyDruhy'))
                          }
                        />

                        {/* Vyber skupinu */}
                        <span className="text-sm text-gray-700 font-medium">
                          Skupina:
                        </span>
                        <MultiSelect
                          value={selectedGroups}
                          options={groupOptions}
                          onChange={(e) => setSelectedGroups(e.value)}
                          placeholder={t('OTKEvaluation.VyberSkupinu')}
                          //display="chip"
                          maxSelectedLabels={2}
                          selectedItemsLabel="{0} skupiny"
                          selectAllLabel={t('Common.All')}
                          className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md "
                          valueTemplate={(value) =>
                            renderSelectedMulti(value, groupOptions, t('OTKEvaluation.VyberSkupinu'), t('OTKEvaluation.VsetkySkupiny'))
                          }
                        />
                      </>
                    )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-6 mb-4 no-print">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                        <Calendar
                          value={tgLeftRange || null}
                          onChange={(e) => {
                            setTgLeftRange(e.value);
                            setExpandedTypeGroupLeft({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex items-center gap-2 no-print">
                        <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                        <Calendar
                          value={tgRightRange || null}
                          onChange={(e) => {
                            setTgRightRange(e.value);
                            setExpandedTypeGroupRight({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          readOnlyInput
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                      </div>
                    </div>
                  )}

                  {/* REŽIM: NEPOROVNÁVAM */}
                  {!compareTypeGroupMode ? (
                    tgTableMode ? (
                      <TreeTable
                        value={tgLeftFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedTypeGroupLeft}
                        onToggle={(e) => setExpandedTypeGroupLeft(e.value)}
                        filters={filtersTypeGroupLeft}
                        onFilter={(e) => setFiltersTypeGroupLeft(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassNameTypeGroup}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={costsSortMeta}
                        onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                      >
                        {/* 1. stĺpec – hierarchia (4 úrovne) */}
                        <Column
                          expander
                          field="errorTypeName"
                          header="Druh chyby / skupina / oddelenie / zamestnanec"
                          style={{ minWidth: 260, width: 260 }}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer={
                            <div
                              className="tt-name-cell inline-block truncate max-w-[240px]"
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Celkovo
                            </div>
                          }
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersTypeGroupLeft.errorTypeName.value}
                              options={[
                                { label: t('Common.All'), value: null },
                                ...errorTypes.map((t) => ({
                                  label: t.name?.sk || t.name?.en || t('OTKEvaluation.NeznamyDruh'),
                                  value: t.name?.sk || t.name?.en || t('OTKEvaluation.NeznamyDruh'),
                                })),
                              ]}
                              onChange={(e) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  errorTypeName: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberDruhChyby')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                          body={tgNameBodyTemplate}
                        />

                        {/* Počet */}
                        <Column
                          field="count"
                          header={t('OTKEvaluation.PocetZakaziek')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("count", "L", handleOpenList_TypeGroup)}
                          footer={FORMAT_INT(totalsTypeGroupLeft.count)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeGroupLeft.count.value}
                              onChange={(val) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  count: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        {/* Kusy s chybou */}
                        <Column
                          field="pieces"
                          header={t('OTKEvaluation.PocetKusov')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("pieces","L", handleOpenList_TypeGroup)}
                          footer={FORMAT_INT(totalsTypeGroupLeft.pieces)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeGroupLeft.pieces.value}
                              onChange={(val) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  pieces: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        {/* Náklady na opravy */}
                        <Column
                          field="cost"
                          header={t('OTKEvaluation.NakladyNaOpravy')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_TypeGroup)}
                          footer={FORMAT_CURRENCY(totalsTypeGroupLeft.cost)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersTypeGroupLeft.cost.value}
                              onChange={(val) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  cost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                    ) : (
                      // 👇 TU MUSÍ OSTAŤ graf s tgLeftRows
                    <div className="grid grid-cols-1 gap-4">
                      <div className="h-[500px]">
                        <Chart
                          type="bar"
                          style={{ width: "100%", height: "100%" }}
                          data={{
                            labels: tgChartRows.map((v) =>
                              v.groupName
                                ? (v.errorTypeName || "") + " / " + (v.groupName || "")
                                : v.errorTypeName || ""
                            ),
                            datasets: [
                              {
                                type: "bar",
                                label: t('OTKEvaluation.PocetZakaziek'),
                                data: tgChartRows.map((v) => v.count),
                                yAxisID: "y",
                                backgroundColor: chartColors.blue,
                                borderColor: chartColors.blue,
                                borderWidth: 1,
                              },
                              {
                                type: "bar",
                                label: t('OTKEvaluation.PocetKusov'),
                                data: tgChartRows.map((v) => v.pieces),
                                yAxisID: "y",
                                backgroundColor: chartColors.orange,
                                borderColor: chartColors.orange,
                                borderWidth: 1,
                              },
                              {
                                type: "bar",
                                label: t('OTKEvaluation.NakladyNaOpravy'),
                                data: tgChartRows.map((v) => v.cost),
                                yAxisID: "y1",
                                backgroundColor: chartColors.red,
                                borderColor: chartColors.red,
                                borderWidth: 2,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: {
                              mode: "index",
                              intersect: false,
                            },
                            plugins: {
                              legend: {
                                position: "bottom",
                                labels: { font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) },
                              },
                            },
                            scales: {
                              x: {
                                ticks: {
                                  autoSkip: true,
                                  maxRotation: 45,
                                  minRotation: 0,
                                  font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                  ...(printChartFontSize && { color: "#000" }),
                                },
                                ...(printChartFontSize && { grid: { color: "#000" } }),
                              },
                              y: {
                                type: "linear",
                                position: "left",
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: t('OTKEvaluation.PocetZakaziekPocetKusov'),
                                  font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                  ...(printChartFontSize && { color: "#000" }),
                                },
                                ...(printChartFontSize && { grid: { color: "#000" } }),
                              },
                              y1: {
                                type: "linear",
                                position: "right",
                                beginAtZero: true,
                                grid: {
                                  drawOnChartArea: false, ...(printChartFontSize && { color: "#000" }),
                                },
                                title: {
                                  display: true,
                                  text: t('OTKEvaluation.CostsCurrency', { currency: currencySymbol }),
                                  font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                  ...(printChartFontSize && { color: "#000" }),
                                },
                                ...(printChartFontSize && { ticks: { color: "#000" } }),
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                    )
                  ) : (
                    // REŽIM: POROVNÁVAM – dve TreeTable vedľa seba
                    <div className="grid grid-cols-2 gap-4 print-compare-grid">
                      <div className="print-compare-cell">
                      <TreeTable
                        value={tgLeftFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedTypeGroupLeft}
                        onToggle={(e) => setExpandedTypeGroupLeft(e.value)}
                        filters={filtersTypeGroupLeft}
                        onFilter={(e) => setFiltersTypeGroupLeft(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassNameTypeGroup}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={costsSortMeta}
                        onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                      >
                        <Column
                          expander
                          field="errorTypeName"
                          header="Druh chyby / skupina / oddelenie / zamestnanec"
                          style={{ minWidth: 260, width: 260 }}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer={
                            <div
                              className="tt-name-cell inline-block truncate max-w-[240px]"
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Celkovo
                            </div>
                          }
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersTypeGroupLeft.errorTypeName.value}
                              options={[
                                { label: t('Common.All'), value: null },
                                ...errorTypes.map((t) => ({
                                  label: t.name?.sk || t.name?.en || t('OTKEvaluation.NeznamyDruh'),
                                  value: t.name?.sk || t.name?.en || t('OTKEvaluation.NeznamyDruh'),
                                })),
                              ]}
                              onChange={(e) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  errorTypeName: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberDruhChyby')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                          body={tgNameBodyTemplate}
                        />

                        <Column
                          field="count"
                          header={t('OTKEvaluation.PocetZakaziek')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("count", "L", handleOpenList_TypeGroup)} 
                          footer={FORMAT_INT(totalsTypeGroupLeft.count)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeGroupLeft.count.value}
                              onChange={(val) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  count: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        <Column
                          field="pieces"
                          header={t('OTKEvaluation.PocetKusov')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("pieces","L", handleOpenList_TypeGroup)}
                          footer={FORMAT_INT(totalsTypeGroupLeft.pieces)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeGroupLeft.pieces.value}
                              onChange={(val) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  pieces: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        <Column
                          field="cost"
                          header={t('OTKEvaluation.NakladyNaOpravy')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_TypeGroup)}
                          footer={FORMAT_CURRENCY(totalsTypeGroupLeft.cost)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersTypeGroupLeft.cost.value}
                              onChange={(val) =>
                                setFiltersTypeGroupLeft((prev) => ({
                                  ...prev,
                                  cost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      </div>

                      <div className="print-compare-cell">
                      {/* PRAVÁ */}
                      <TreeTable
                        value={tgRightFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedTypeGroupRight}
                        onToggle={(e) => setExpandedTypeGroupRight(e.value)}
                        filters={filtersTypeGroupRight}
                        onFilter={(e) => setFiltersTypeGroupRight(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassNameTypeGroup}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={costsSortMeta}
                        onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                      >
                        <Column
                          expander
                          field="errorTypeName"
                          header="Druh chyby / skupina / oddelenie / zamestnanec"
                          style={{ minWidth: 260, width: 260 }}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer={
                            <div
                              className="tt-name-cell inline-block truncate max-w-[240px]"
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Celkovo
                            </div>
                          }
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersTypeGroupRight.errorTypeName.value}
                              options={[
                                { label: t('Common.All'), value: null },
                                ...errorTypes.map((t) => ({
                                  label: t.name?.sk || t.name?.en || t('OTKEvaluation.NeznamyDruh'),
                                  value: t.name?.sk || t.name?.en || t('OTKEvaluation.NeznamyDruh'),
                                })),
                              ]}
                              onChange={(e) =>
                                setFiltersTypeGroupRight((prev) => ({
                                  ...prev,
                                  errorTypeName: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberDruhChyby')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                          body={tgNameBodyTemplate}
                        />

                        <Column
                          field="count"
                          header={t('OTKEvaluation.PocetZakaziek')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("count", "R", handleOpenList_TypeGroup)}
                          footer={FORMAT_INT(totalsTypeGroupRight.count)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeGroupRight.count.value}
                              onChange={(val) =>
                                setFiltersTypeGroupRight((prev) => ({
                                  ...prev,
                                  count: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        <Column
                          field="pieces"
                          header={t('OTKEvaluation.PocetKusov')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("pieces","R", handleOpenList_TypeGroup)}
                          footer={FORMAT_INT(totalsTypeGroupRight.pieces)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeGroupRight.pieces.value}
                              onChange={(val) =>
                                setFiltersTypeGroupRight((prev) => ({
                                  ...prev,
                                  pieces: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        <Column
                          field="cost"
                          header={t('OTKEvaluation.NakladyNaOpravy')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeCurrencyCell("cost","R", handleOpenList_TypeGroup)}
                          footer={FORMAT_CURRENCY(totalsTypeGroupRight.cost)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersTypeGroupRight.cost.value}
                              onChange={(val) =>
                                setFiltersTypeGroupRight((prev) => ({
                                  ...prev,
                                  cost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              {/* === 5) SKUPINA CHÝB VOČI DRUHU CHÝB === */}
                <div ref={refGroupType} className="print-full">
                  <Card
                    title={
                      <>
                        <div className="flex justify-between items-center w-full">
                          <span>{t('Ui.R67.Skupina_chyb_voci_druhu_chyb')}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                            <InputSwitch
                              className="no-print"
                              checked={compareGroupTypeMode}
                              onChange={(e) => {
                                setCompareGroupTypeMode(e.value);
                                setExpandedGroupTypeLeft({});
                                setExpandedGroupTypeRight({});
                              }}
                            />

                            {/* tlačidlo Rozbaliť / Zbaliť – otvorí DIALOG pre Skupina vs. druh chyby */}
                           <Button
                            icon={
                              compareGroupTypeMode
                                ? Object.keys(expandedGroupTypeLeft).length > 0 &&
                                  Object.keys(expandedGroupTypeRight).length > 0
                                  ? "pi pi-angle-down"
                                  : "pi pi-angle-right"
                                : Object.keys(expandedGroupTypeLeft).length > 0
                                ? "pi pi-angle-down"
                                : "pi pi-angle-right"
                            }
                            className="p-button-text p-button-sm no-print"
                            tooltip={
                              compareGroupTypeMode
                                ? Object.keys(expandedGroupTypeLeft).length > 0 &&
                                  Object.keys(expandedGroupTypeRight).length > 0
                                  ? t('OTKEvaluation.ZbalitVsetky')
                                  : t('OTKEvaluation.RozbalitVsetky')
                                : Object.keys(expandedGroupTypeLeft).length > 0
                                ? t('OTKEvaluation.ZbalitVsetky')
                                : t('OTKEvaluation.RozbalitVsetky')
                            }
                            tooltipOptions={{ position: "left" }}
                            onClick={() => {
                              const leftHas = Object.keys(expandedGroupTypeLeft).length > 0;
                              const rightHas = Object.keys(expandedGroupTypeRight).length > 0;

                              if (!compareGroupTypeMode) {
                                // 🔹 single tabuľka
                                if (leftHas) {
                                  // už je rozbalená → zbal
                                  setExpandedGroupTypeLeft({});
                                } else {
                                  // nie je rozbalená → ukáž dialóg s voľbou úrovne
                                  setExpandGroupTypeDialogVisible(true);
                                }
                              } else {
                                // 🔹 porovnávací režim (ľavá + pravá)
                                const bothExpanded = leftHas && rightHas;

                                if (bothExpanded) {
                                  // obe rozbalené → zbaľ obe
                                  setExpandedGroupTypeLeft({});
                                  setExpandedGroupTypeRight({});
                                } else {
                                  // aspoň jedna nie je rozbalená → ukáž dialóg
                                  setExpandGroupTypeDialogVisible(true);
                                }
                              }
                            }}
                          />
                          </div>
                        </div>

                        <div className="mt-2">
                        {!compareGroupTypeMode && (
                          <div className="flex items-center gap-2">
                            <span className="text-base">{t('OTKEvaluation.Tabulka')}</span>
                            <InputSwitch
                              checked={!gtTableMode}
                              onChange={(e) => {
                                const showGraph = e.value; // true = idem na GRAF
                                setGtTableMode(!e.value);

                                if (showGraph && !compareGroupTypeMode) {
                                  syncGtGroupsWithRows();
                                  setGtSelectedErrorTypes([]);
                                }
                              }}
                            />
                            <span className="text-base">Graf</span>
                          </div>
                        )}
                      </div>
                      </>
                    }
                  >
                    {/* výber obdobia */}
                    {!compareGroupTypeMode ? (
                      <div className="flex items-center gap-3 mb-4 no-print">
                        <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                        <Calendar
                          value={gtLeftRange || null}
                          onChange={(e) => {
                            setGtLeftRange(e.value);
                            setExpandedGroupTypeLeft({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />

                        {/* filtry len pri GRAFE */}
                        {!gtTableMode && (
                          <>
                            {/* Skupina chýb (primárny filter) */}
                            <span className="text-sm text-gray-700 font-medium ml-4">
                              Skupina:
                            </span>
                            <MultiSelect
                              value={gtSelectedGroups}
                              options={gtGroupOptions}
                              onChange={(e) => setGtSelectedGroups(e.value)}
                              placeholder={t('OTKEvaluation.VsetkySkupiny')}
                              maxSelectedLabels={2}
                              selectedItemsLabel="{0} skupiny"
                              selectAllLabel={t('Common.All')}
                              className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md "
                              valueTemplate={(value) =>
                                renderSelectedMulti(value, gtGroupOptions, t('OTKEvaluation.VyberSkupinu'), t('OTKEvaluation.VsetkySkupiny') )
                              }
                            />

                            {/* Druh chyby (sekundárny filter) */}
                            <span className="text-sm text-gray-700 font-medium">
                              Druh chyby:
                            </span>
                            <MultiSelect
                              value={gtSelectedErrorTypes}
                              options={gtErrorTypeOptions}
                              onChange={(e) => setGtSelectedErrorTypes(e.value)}
                              placeholder={t('OTKEvaluation.VyberDruhChyby')}
                              maxSelectedLabels={2}
                              selectedItemsLabel={t('OTKEvaluation.0DruhovChyb')}
                              selectAllLabel={t('Common.All')}
                              className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md " 
                              valueTemplate={(value) =>
                                renderSelectedMulti(value, gtErrorTypeOptions, t('OTKEvaluation.VyberDruhChyby'), t('OTKEvaluation.VsetkyDruhy'))
                              }
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-6 mb-4 no-print">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                          <Calendar
                            value={gtLeftRange || null}
                            onChange={(e) => {
                              setGtLeftRange(e.value);
                              setExpandedGroupTypeLeft({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                        </div>
                        <div className="flex items-center gap-2 no-print">
                          <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                          <Calendar
                            value={gtRightRange || null}
                            onChange={(e) => {
                              setGtRightRange(e.value);
                              setExpandedGroupTypeRight({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            readOnlyInput
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                        </div>
                      </div>
                    )}

                    {/* NEPOROVNÁM vs POROVNÁM */}
                    {!compareGroupTypeMode ? (
                      gtTableMode ? (
                      <TreeTable
                        value={gtLeftFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedGroupTypeLeft}
                        onToggle={(e) => setExpandedGroupTypeLeft(e.value)}
                        filters={filtersGroupTypeLeft}
                        onFilter={(e) => setFiltersGroupTypeLeft(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassNameGroupType}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={costsSortMeta}
                        onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                      >
                        {/* 1. stĺpec – hierarchia */}
                        <Column
                          expander
                          field="groupName"
                          header="Skupina / druh / oddelenie / zamestnanec"
                          style={{ minWidth: 260, width: 260 }}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer={
                            <div
                              className="tt-name-cell inline-block truncate max-w-[240px]"
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Celkovo
                            </div>
                          }
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersGroupTypeLeft.groupName.value}
                              options={[
                                { label: t('Common.All'), value: null },
                                ...groups.map((g) => ({
                                  label: g.name?.sk || g.name?.en || t('OTKEvaluation.NeznamaSkupina'),
                                  value: g.name?.sk || g.name?.en || t('OTKEvaluation.NeznamaSkupina'),
                                })),
                              ]}
                              onChange={(e) =>
                                setFiltersGroupTypeLeft((prev) => ({
                                  ...prev,
                                  groupName: { value: e.value, matchMode: "equals" },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberSkupinu')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                          body={gtNameBodyTemplate}
                        />

                        {/* Počet */}
                        <Column
                          field="count"
                          header={t('OTKEvaluation.PocetZakaziek')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("count", "L", handleOpenList_GroupType)}
                          footer={FORMAT_INT(totalsGroupTypeLeft.count)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersGroupTypeLeft.count.value}
                              onChange={(val) =>
                                setFiltersGroupTypeLeft((prev) => ({
                                  ...prev,
                                  count: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        {/* Kusy s chybou */}
                        <Column
                          field="pieces"
                          header={t('OTKEvaluation.PocetKusov')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("pieces","L", handleOpenList_GroupType)}
                          footer={FORMAT_INT(totalsGroupTypeLeft.pieces)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersGroupTypeLeft.pieces.value}
                              onChange={(val) =>
                                setFiltersGroupTypeLeft((prev) => ({
                                  ...prev,
                                  pieces: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />

                        {/* Náklady na opravy */}
                        <Column
                          field="cost"
                          header={t('OTKEvaluation.NakladyNaOpravy')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_GroupType)}
                          footer={FORMAT_CURRENCY(totalsGroupTypeLeft.cost)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersGroupTypeLeft.cost.value}
                              onChange={(val) =>
                                setFiltersGroupTypeLeft((prev) => ({
                                  ...prev,
                                  cost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      ) : (
                        // 👇 SEM patrí ten snippet s gtLeftRows
                        <div className="grid grid-cols-1 gap-4">
                          <div className="h-[500px]">
                            <Chart
                              type="bar"
                              style={{ width: "100%", height: "100%" }}
                              data={{
                                labels: gtChartRows.map((v) =>
                                  v.errorTypeName
                                    ? (v.groupName || "") + " / " + (v.errorTypeName || "")
                                    : v.groupName || ""
                                ),
                                datasets: [
                                  {
                                    type: "bar",
                                    label: t('OTKEvaluation.PocetZakaziek'),
                                    data: gtChartRows.map((v) => v.count),
                                    yAxisID: "y",
                                    backgroundColor: chartColors.blue,
                                    borderColor: chartColors.blue,
                                    borderWidth: 1,
                                  },
                                  {
                                    type: "bar",
                                    label: t('OTKEvaluation.PocetKusov'),
                                    data: gtChartRows.map((v) => v.pieces),
                                    yAxisID: "y",
                                    backgroundColor: chartColors.orange,
                                    borderColor: chartColors.orange,
                                    borderWidth: 1,
                                  },
                                  {
                                    type: "bar",
                                    label: t('OTKEvaluation.NakladyNaOpravy'),
                                    data: gtChartRows.map((v) => v.cost),
                                    yAxisID: "y1",
                                    backgroundColor: chartColors.red,
                                    borderColor: chartColors.red,
                                    borderWidth: 2,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                  mode: "index",
                                  intersect: false,
                                },
                                plugins: {
                                  legend: {
                                    position: "bottom",
                                    labels: { font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) },
                                  },
                                },
                                scales: {
                                  x: {
                                    ticks: {
                                      autoSkip: true,
                                      maxRotation: 45,
                                      minRotation: 0,
                                      font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                      ...(printChartFontSize && { color: "#000" }),
                                    },
                                    ...(printChartFontSize && { grid: { color: "#000" } }),
                                  },
                                  y: {
                                    type: "linear",
                                    position: "left",
                                    beginAtZero: true,
                                    title: {
                                      display: true,
                                      text: t('OTKEvaluation.PocetZakaziekPocetKusov'),
                                      font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                      ...(printChartFontSize && { color: "#000" }),
                                    },
                                    ...(printChartFontSize && { grid: { color: "#000" } }),
                                  },
                                  y1: {
                                    type: "linear",
                                    position: "right",
                                    beginAtZero: true,
                                    grid: {
                                      drawOnChartArea: false, ...(printChartFontSize && { color: "#000" }),
                                    },
                                    title: {
                                      display: true,
                                      text: t('OTKEvaluation.CostsCurrency', { currency: currencySymbol }),
                                      font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                      ...(printChartFontSize && { color: "#000" }),
                                    },
                                    ...(printChartFontSize && { ticks: { color: "#000" } }),
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="grid grid-cols-2 gap-4 print-compare-grid">
                        <div className="print-compare-cell">
                        <TreeTable
                          value={gtLeftFilteredTree}
                          dataKey="key"
                          expandedKeys={expandedGroupTypeLeft}
                          onToggle={(e) => setExpandedGroupTypeLeft(e.value)}
                          filters={filtersGroupTypeLeft}
                          onFilter={(e) => setFiltersGroupTypeLeft(e.filters)}
                          filterMode="lenient"
                          resizableColumns
                          columnResizeMode="expand"
                          rowClassName={rowClassNameGroupType}
                          tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          scrollable={!isPrintMode}
                          scrollHeight={isPrintMode ? undefined : "500px"}
                          scrollDirection={isPrintMode ? undefined : "both"}
                          removableSort
                          sortMode="multiple"
                          multiSortMeta={costsSortMeta}
                          onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                        >
                          <Column
                            expander
                            field="groupName"
                            header="Skupina / druh / oddelenie / zamestnanec"
                            style={{ minWidth: 260, width: 260 }}
                            headerStyle={HD_DN}
                            filterHeaderStyle={HD_DN}
                            footerStyle={FT_DN}
                            footer={
                              <div
                                className="tt-name-cell inline-block truncate max-w-[240px]"
                                style={{ marginLeft: "0.5rem" }}
                              >
                                Celkovo
                              </div>
                            }
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <Dropdown
                                value={filtersGroupTypeLeft.groupName.value}
                                options={[
                                  { label: t('Common.All'), value: null },
                                  ...groups.map((g) => ({
                                    label: g.name?.sk || g.name?.en || t('OTKEvaluation.NeznamaSkupina'),
                                    value: g.name?.sk || g.name?.en || t('OTKEvaluation.NeznamaSkupina'),
                                  })),
                                ]}
                                onChange={(e) =>
                                  setFiltersGroupTypeLeft((prev) => ({
                                    ...prev,
                                    groupName: { value: e.value, matchMode: "equals" },
                                  }))
                                }
                                placeholder={t('OTKEvaluation.VyberSkupinu')}
                                className="w-full h-[42px]"
                                showClear
                              />
                            }
                            body={gtNameBodyTemplate}
                          />

                          <Column
                            field="count"
                            header={t('OTKEvaluation.PocetZakaziek')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("count", "L", handleOpenList_GroupType)}
                            footer={FORMAT_INT(totalsGroupTypeLeft.count)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersGroupTypeLeft.count.value}
                                onChange={(val) =>
                                  setFiltersGroupTypeLeft((prev) => ({
                                    ...prev,
                                    count: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />

                          <Column
                            field="pieces"
                            header={t('OTKEvaluation.PocetKusov')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("pieces","L", handleOpenList_GroupType)}
                            footer={FORMAT_INT(totalsGroupTypeLeft.pieces)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersGroupTypeLeft.pieces.value}
                                onChange={(val) =>
                                  setFiltersGroupTypeLeft((prev) => ({
                                    ...prev,
                                    pieces: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />

                          <Column
                            field="cost"
                            header={t('OTKEvaluation.NakladyNaOpravy')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_GroupType)}
                            footer={FORMAT_CURRENCY(totalsGroupTypeLeft.cost)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <CurrencyFilterInput
                                value={filtersGroupTypeLeft.cost.value}
                                onChange={(val) =>
                                  setFiltersGroupTypeLeft((prev) => ({
                                    ...prev,
                                    cost: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        </TreeTable>
                        </div>

                        <div className="print-compare-cell">
                        <TreeTable
                          value={gtRightFilteredTree}
                          dataKey="key"
                          expandedKeys={expandedGroupTypeRight}
                          onToggle={(e) => setExpandedGroupTypeRight(e.value)}
                          filters={filtersGroupTypeRight}
                          onFilter={(e) => setFiltersGroupTypeRight(e.filters)}
                          filterMode="lenient"
                          resizableColumns
                          columnResizeMode="expand"
                          rowClassName={rowClassNameGroupType}
                          tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          scrollable={!isPrintMode}
                          scrollHeight={isPrintMode ? undefined : "500px"}
                          scrollDirection={isPrintMode ? undefined : "both"}
                          removableSort
                          sortMode="multiple"
                          multiSortMeta={costsSortMeta}
                          onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                        >
                          <Column
                            expander
                            field="groupName"
                            header="Skupina / druh / oddelenie / zamestnanec"
                            style={{ minWidth: 260, width: 260 }}
                            headerStyle={HD_DN}
                            filterHeaderStyle={HD_DN}
                            footerStyle={FT_DN}
                            footer={
                              <div
                                className="tt-name-cell inline-block truncate max-w-[240px]"
                                style={{ marginLeft: "0.5rem" }}
                              >
                                Celkovo
                              </div>
                            }
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <Dropdown
                                value={filtersGroupTypeRight.groupName.value}
                                options={[
                                  { label: t('Common.All'), value: null },
                                  ...groups.map((g) => ({
                                    label: g.name?.sk || g.name?.en || t('OTKEvaluation.NeznamaSkupina'),
                                    value: g.name?.sk || g.name?.en || t('OTKEvaluation.NeznamaSkupina'),
                                  })),
                                ]}
                                onChange={(e) =>
                                  setFiltersGroupTypeRight((prev) => ({
                                    ...prev,
                                    groupName: { value: e.value, matchMode: "equals" },
                                  }))
                                }
                                placeholder={t('OTKEvaluation.VyberSkupinu')}
                                className="w-full h-[42px]"
                                showClear
                              />
                            }
                            body={gtNameBodyTemplate}
                          />

                          <Column
                            field="count"
                            header={t('OTKEvaluation.PocetZakaziek')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("count","R", handleOpenList_GroupType)}
                            footer={FORMAT_INT(totalsGroupTypeRight.count)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersGroupTypeRight.count.value}
                                onChange={(val) =>
                                  setFiltersGroupTypeRight((prev) => ({
                                    ...prev,
                                    count: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />

                          <Column
                            field="pieces"
                            header={t('OTKEvaluation.PocetKusov')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("pieces","R", handleOpenList_GroupType)}
                            footer={FORMAT_INT(totalsGroupTypeRight.pieces)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersGroupTypeRight.pieces.value}
                                onChange={(val) =>
                                  setFiltersGroupTypeRight((prev) => ({
                                    ...prev,
                                    pieces: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />

                          <Column
                            field="cost"
                            header={t('OTKEvaluation.NakladyNaOpravy')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeCurrencyCell("cost","R", handleOpenList_GroupType)}
                            footer={FORMAT_CURRENCY(totalsGroupTypeRight.cost)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <CurrencyFilterInput
                                value={filtersGroupTypeRight.cost.value}
                                onChange={(val) =>
                                  setFiltersGroupTypeRight((prev) => ({
                                    ...prev,
                                    cost: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        </TreeTable>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>

              {/* === 6) DRUH CHYBY ZA ODD === */}
                <div ref={refTypeDept} className="print-full">
                  <Card
                    title={
                      <>
                        <div className="flex items-center justify-between w-full ">
                          <span>{t('Ui.R67.Oddelenie_voci_druhu_chyb')}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                            <InputSwitch
                              className="no-print"
                              checked={compareTypeDeptMode}
                              onChange={(e) => {
                                setCompareTypeDeptMode(e.value);
                                setExpandedTypeDeptLeft({});
                                setExpandedTypeDeptRight({});
                              }}
                            />
                            {tdTableMode && (
                              <Button
                                icon={
                                  compareTypeDeptMode
                                    ? Object.keys(expandedTypeDeptLeft).length > 0 &&
                                      Object.keys(expandedTypeDeptRight).length > 0
                                      ? "pi pi-angle-down"
                                      : "pi pi-angle-right"
                                    : Object.keys(expandedTypeDeptLeft).length > 0
                                    ? "pi pi-angle-down"
                                    : "pi pi-angle-right"
                                }
                                className="p-button-text p-button-sm no-print"
                                tooltip={
                                  compareTypeDeptMode
                                    ? Object.keys(expandedTypeDeptLeft).length > 0 &&
                                      Object.keys(expandedTypeDeptRight).length > 0
                                      ? t('OTKEvaluation.ZbalitVsetky')
                                      : t('OTKEvaluation.RozbalitVsetky')
                                    : Object.keys(expandedTypeDeptLeft).length > 0
                                    ? t('OTKEvaluation.ZbalitVsetky')
                                    : t('OTKEvaluation.RozbalitVsetky')
                                }
                                tooltipOptions={{ position: "left" }}
                                onClick={() => {
                                  const anyLeft  = Object.keys(expandedTypeDeptLeft).length  > 0;
                                  const anyRight = Object.keys(expandedTypeDeptRight).length > 0;

                                  if (!compareTypeDeptMode) {
                                    // SINGLE režim – ak už je niečo rozbalené, len zbaľ všetko
                                    if (anyLeft) {
                                      setExpandedTypeDeptLeft({});
                                    } else {
                                      // nič nie je rozbalené -> otvor dialóg na výber úrovne
                                      setExpandTypeDeptDialogVisible(true);
                                    }
                                  } else {
                                    // POROVNÁVACÍ režim – ak sú obe strany rozbalené, zbaľ obidve
                                    if (anyLeft && anyRight) {
                                      setExpandedTypeDeptLeft({});
                                      setExpandedTypeDeptRight({});
                                    } else {
                                      // aspoň jedna strana nie je rozbalená -> otvor dialóg
                                      setExpandTypeDeptDialogVisible(true);
                                    }
                                  }
                                }}
                              />
                            )}
                          </div>
                        </div>
                        <div className="mt-2">
                          {!compareTypeDeptMode && (
                            <div className="flex items-center gap-2">
                              <span className="text-base">{t('OTKEvaluation.Tabulka')}</span>
                              <InputSwitch
                                checked={!tdTableMode}
                                onChange={(e) => {
                                  const showGraph = e.value; // true = idem na GRAF
                                  setTdTableMode(!e.value);

                                  if (showGraph) {
                                    // pri prepnuti z Tabuľka -> Graf zosynchronizuj podľa tabuľky
                                    syncTdDepartmentsWithRows();
                                    setTdSelectedErrorTypes([]); // druhy chýb nech sú na začiatku prázdne
                                  }
                                }}
                              />
                              <span className="text-base">Graf</span>
                            </div>
                          )}
                        </div>
                      </>
                    }
                    subTitle={t('OTKEvaluation.KliknutimRozbalteOddelenieAleboPrepinajteGraf')}
                  >
                    {!compareTypeDeptMode ? (
                        <div className="flex items-center gap-3 mb-4 no-print">
                          <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                          <Calendar
                            value={tdLeftRange || null}
                            onChange={(e) => {
                              setTdLeftRange(e.value);
                              setExpandedTypeDeptLeft({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                          {/* Filtre len pri GRAFE */}
                          {!tdTableMode && (
                            <>
                              <span className="text-sm text-gray-700 font-medium ml-4">
                                Oddelenie:
                              </span>
                              <MultiSelect
                                value={tdSelectedDepartments}
                                options={tdDepartmentOptions}
                                onChange={(e) => setTdSelectedDepartments(e.value)}
                                placeholder={t('OTKEvaluation.VsetkyOddelenia')}
                                maxSelectedLabels={2}
                                selectedItemsLabel={t('OTKEvaluation.0Oddeleni')}
                                selectAllLabel={t('Common.All')}
                                className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md"
                                valueTemplate={(value) =>
                                  renderSelectedMulti(
                                    value,
                                    tdDepartmentOptions,
                                    t('OTKEvaluation.VsetkyOddelenia'),   // keď nie je nič vybraté
                                    t('OTKEvaluation.VsetkyOddelenia')    // keď sú vybraté všetky
                                  )
                                }
                              />

                              <span className="text-sm text-gray-700 font-medium">
                                Druh chyby:
                              </span>
                              <MultiSelect
                                value={tdSelectedErrorTypes}
                                options={tdErrorTypeOptions}
                                onChange={(e) => setTdSelectedErrorTypes(e.value)}
                                placeholder={t('OTKEvaluation.VyberDruhChyby')}
                                maxSelectedLabels={2}
                                selectedItemsLabel={t('OTKEvaluation.0DruhovChyb')}
                                selectAllLabel={t('Common.All')}
                                className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md"
                                valueTemplate={(value) =>
                                  renderSelectedMulti(
                                    value,
                                    tdErrorTypeOptions,
                                    t('OTKEvaluation.VyberDruhChyby'),   // keď je prázdne
                                    t('OTKEvaluation.VsetkyDruhy')        // keď sú vybraté všetky dostupné
                                  )
                                }
                              />
                            </>
                          )}
                        </div>
                        ) : (
                      <div className="flex items-center justify-between gap-6 mb-4 no-print">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                          <Calendar
                            value={tdLeftRange || null}
                            onChange={(e) => {
                              setTdLeftRange(e.value);
                              setExpandedTypeDeptLeft({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                        </div>
                        <div className="flex items-center gap-2 no-print">
                          <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                          <Calendar
                            value={tdRightRange || null}
                            onChange={(e) => {
                              setTdRightRange(e.value);
                              setExpandedTypeDeptRight({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            readOnlyInput
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                        </div>
                      </div>
                    )}
                      {!compareTypeDeptMode ? (
                        tdTableMode ? (
                          <TreeTable
                            value={tdLeftFilteredTree}
                            dataKey="key"
                            expandedKeys={expandedTypeDeptLeft}
                            onToggle={(e) => setExpandedTypeDeptLeft(e.value)}
                            filters={filtersTypeDeptLeft}
                            onFilter={(e) => setFiltersTypeDeptLeft(e.filters)}
                            filterMode="lenient"
                            resizableColumns
                            columnResizeMode="expand"
                            rowClassName={rowClassName}
                            tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                            scrollable={!isPrintMode}
                            scrollHeight={isPrintMode ? undefined : "500px"}
                            scrollDirection={isPrintMode ? undefined : "both"}
                            removableSort
                            sortMode="multiple"
                            multiSortMeta={costsSortMeta}
                            onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                          >
                            {/* 1. stĺpec – Oddelenie / druh / zamestnanec / artikel */}
                            <Column
                              expander
                              field="departmentName"
                              header={t('OTKEvaluation.OddelenieDruhZamestnanecArtikel')}
                              style={{ minWidth: 260, width: 260 }}
                              headerStyle={HD_DN}
                              filterHeaderStyle={HD_DN}
                              footerStyle={FT_DN}
                              footer={
                                <div
                                  className="tt-name-cell inline-block truncate max-w-[240px]"
                                  style={{ marginLeft: "0.5rem" }}
                                >
                                  Celkovo
                                </div>
                              }
                              body={tdNameBodyTemplate}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              filterElement={
                                <Dropdown
                                  value={filtersTypeDeptLeft.departmentName.value}
                                  options={[
                                    { label: t('Common.All'), value: null },
                                    ...departments.map((d) => ({
                                      label: d.name,
                                      value: d.name,
                                    })),
                                  ]}
                                  onChange={(e) =>
                                    setFiltersTypeDeptLeft((prev) => ({
                                      ...prev,
                                      departmentName: {
                                        value: e.value,
                                        matchMode: "equals",
                                      },
                                    }))
                                  }
                                  placeholder={t('OTKEvaluation.VyberOddelenie')}
                                  className="w-full h-[42px]"
                                  showClear
                                />
                              }
                            />

                            {/* Počet */}
                            <Column
                              field="count"
                              header={t('OTKEvaluation.PocetZakaziek')}
                              style={{ minWidth: 120, width: 120 }}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableTreeIntCell("count", "L", handleOpenList_TypeDept)}
                              footer={FORMAT_INT(totalsTypeDeptLeft.count)}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersTypeDeptLeft.count.value}
                                  onChange={(val) =>
                                    setFiltersTypeDeptLeft((prev) => ({
                                      ...prev,
                                      count: { value: val, matchMode: "contains" },
                                    }))
                                  }
                                />
                              }
                            />

                            {/* Kusy */}
                            <Column
                              field="pieces"
                              header={t('OTKEvaluation.PocetKusov')}
                              style={{ minWidth: 120, width: 120 }}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableTreeIntCell("pieces", "L", handleOpenList_TypeDept)}
                              footer={FORMAT_INT(totalsTypeDeptLeft.pieces)}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              filterElement={
                                <SearchFilterInput
                                  value={filtersTypeDeptLeft.pieces.value}
                                  onChange={(val) =>
                                    setFiltersTypeDeptLeft((prev) => ({
                                      ...prev,
                                      pieces: { value: val, matchMode: "contains" },
                                    }))
                                  }
                                />
                              }
                            />

                            {/* Náklady na opravy */}
                            <Column
                              field="cost"
                              header={t('OTKEvaluation.NakladyNaOpravy')}
                              style={{ minWidth: 120, width: 120 }}
                              headerStyle={HD}
                              filterHeaderStyle={HD}
                              footerStyle={FT}
                              body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_TypeDept)}
                              footer={FORMAT_CURRENCY(totalsTypeDeptLeft.cost)}
                              filter
                              sortable
                              pt={PT_HEADER}
                              showFilterMenu={false}
                              filterElement={
                                <CurrencyFilterInput
                                  value={filtersTypeDeptLeft.cost.value}
                                  onChange={(val) =>
                                    setFiltersTypeDeptLeft((prev) => ({
                                      ...prev,
                                      cost: { value: val, matchMode: "contains" },
                                    }))
                                  }
                                />
                              }
                            />
                          </TreeTable>
                        ) : (
                          // GRAF – nechávam pôvodný (Počet + Náklady)
                          <div className="grid grid-cols-1 gap-4">
                            <div className="h-[500px]">
                              <Chart
                                type="bar"
                                style={{ width: "100%", height: "100%" }}
                                data={{
                                  labels: tdChartRows.map((v) =>
                                    v.errorTypeName
                                      ? (v.departmentName || "") +
                                        " / " +
                                        (v.errorTypeName || "")
                                      : v.departmentName || ""
                                  ),
                                  datasets: [
                                    {
                                      type: "bar",
                                      label: t('OTKEvaluation.PocetZakaziek'),
                                      data: tdChartRows.map((v) => v.count),
                                      yAxisID: "y",
                                      backgroundColor: chartColors.blue,
                                      borderColor: chartColors.blue,
                                      borderWidth: 1,
                                    },
                                    {
                                      type: "bar",
                                      label: t('OTKEvaluation.PocetKusov'),
                                      data: tdChartRows.map((v) => v.pieces),
                                      yAxisID: "y",
                                      backgroundColor: chartColors.orange,
                                      borderColor: chartColors.orange,
                                      borderWidth: 1,
                                    },
                                    {
                                      type: "bar",
                                      label: t('OTKEvaluation.NakladyNaOpravy'),
                                      data: tdChartRows.map((v) => v.cost),
                                      yAxisID: "y1",
                                      backgroundColor: chartColors.red,
                                      borderColor: chartColors.red,
                                      borderWidth: 2,
                                    },
                                  ],
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  interaction: {
                                    mode: "index",
                                    intersect: false,
                                  },
                                  plugins: {
                                    legend: {
                                      position: "bottom",
                                      labels: { font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) },
                                    },
                                  },
                                  scales: {
                                    x: {
                                      ticks: {
                                        autoSkip: true,
                                        maxRotation: 45,
                                        minRotation: 0,
                                        font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                        ...(printChartFontSize && { color: "#000" }),
                                      },
                                      ...(printChartFontSize && { grid: { color: "#000" } }),
                                    },
                                    y: {
                                      type: "linear",
                                      position: "left",
                                      beginAtZero: true,
                                      title: {
                                        display: true,
                                        text: t('OTKEvaluation.PocetZakaziekPocetKusov'),
                                        font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                        ...(printChartFontSize && { color: "#000" }),
                                      },
                                      ...(printChartFontSize && { grid: { color: "#000" } }),
                                    },
                                    y1: {
                                      type: "linear",
                                      position: "right",
                                      beginAtZero: true,
                                      grid: {
                                        drawOnChartArea: false, ...(printChartFontSize && { color: "#000" }),
                                      },
                                      title: {
                                        display: true,
                                        text: t('OTKEvaluation.CostsCurrency', { currency: currencySymbol }),
                                        font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                        ...(printChartFontSize && { color: "#000" }),
                                      },
                                      ...(printChartFontSize && { ticks: { color: "#000" } }),
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>
                        )
                    ) : (
                    <div className="grid grid-cols-2 gap-4 print-compare-grid">
                      <div className="print-compare-cell">
                      <TreeTable
                        value={tdLeftFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedTypeDeptLeft}
                        onToggle={(e) => setExpandedTypeDeptLeft(e.value)}
                        filters={filtersTypeDeptLeft}
                        onFilter={(e) => setFiltersTypeDeptLeft(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassName}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={costsSortMeta}
                        onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                      >
                        <Column
                          expander
                          field="departmentName"
                          header={t('OTKEvaluation.OddelenieDruhZamestnanecArtikel')}
                          style={{ minWidth: 260, width: 260 }}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer={
                            <div
                              className="tt-name-cell inline-block truncate max-w-[240px]"
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Celkovo
                            </div>
                          }
                          body={tdNameBodyTemplate}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersTypeDeptLeft.departmentName.value}
                              options={[
                                { label: t('Common.All'), value: null },
                                ...departments.map((d) => ({
                                  label: d.name,
                                  value: d.name,
                                })),
                              ]}
                              onChange={(e) =>
                                setFiltersTypeDeptLeft((prev) => ({
                                  ...prev,
                                  departmentName: {
                                    value: e.value,
                                    matchMode: "equals",
                                  },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberOddelenie')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                        />
                        <Column
                          field="count"
                          header={t('OTKEvaluation.PocetZakaziek')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("count", "L", handleOpenList_TypeDept)}
                          footer={FORMAT_INT(totalsTypeDeptLeft.count)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeDeptLeft.count.value}
                              onChange={(val) =>
                                setFiltersTypeDeptLeft((prev) => ({
                                  ...prev,
                                  count: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                        <Column
                          field="pieces"
                          header={t('OTKEvaluation.PocetKusov')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("pieces", "L", handleOpenList_TypeDept)}
                          footer={FORMAT_INT(totalsTypeDeptLeft.pieces)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeDeptLeft.pieces.value}
                              onChange={(val) =>
                                setFiltersTypeDeptLeft((prev) => ({
                                  ...prev,
                                  pieces: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                        <Column
                          field="cost"
                          header={t('OTKEvaluation.NakladyNaOpravy')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_TypeDept)}
                          footer={FORMAT_CURRENCY(totalsTypeDeptLeft.cost)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersTypeDeptLeft.cost.value}
                              onChange={(val) =>
                                setFiltersTypeDeptLeft((prev) => ({
                                  ...prev,
                                  cost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      </div>

                      <div className="print-compare-cell">
                      <TreeTable
                        value={tdRightFilteredTree}
                        dataKey="key"
                        expandedKeys={expandedTypeDeptRight}
                        onToggle={(e) => setExpandedTypeDeptRight(e.value)}
                        filters={filtersTypeDeptRight}
                        onFilter={(e) => setFiltersTypeDeptRight(e.filters)}
                        filterMode="lenient"
                        resizableColumns
                        columnResizeMode="expand"
                        rowClassName={rowClassName}
                        tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                        scrollable={!isPrintMode}
                        scrollHeight={isPrintMode ? undefined : "500px"}
                        scrollDirection={isPrintMode ? undefined : "both"}
                        removableSort
                        sortMode="multiple"
                        multiSortMeta={costsSortMeta}
                        onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                      >
                        <Column
                          expander
                          field="departmentName"
                          header={t('OTKEvaluation.OddelenieDruhZamestnanecArtikel')}
                          style={{ minWidth: 260, width: 260 }}
                          headerStyle={HD_DN}
                          filterHeaderStyle={HD_DN}
                          footerStyle={FT_DN}
                          footer={
                            <div
                              className="tt-name-cell inline-block truncate max-w-[240px]"
                              style={{ marginLeft: "0.5rem" }}
                            >
                              Celkovo
                            </div>
                          }
                          body={tdNameBodyTemplate}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <Dropdown
                              value={filtersTypeDeptRight.departmentName.value}
                              options={[
                                { label: t('Common.All'), value: null },
                                ...departments.map((d) => ({
                                  label: d.name,
                                  value: d.name,
                                })),
                              ]}
                              onChange={(e) =>
                                setFiltersTypeDeptRight((prev) => ({
                                  ...prev,
                                  departmentName: {
                                    value: e.value,
                                    matchMode: "equals",
                                  },
                                }))
                              }
                              placeholder={t('OTKEvaluation.VyberOddelenie')}
                              className="w-full h-[42px]"
                              showClear
                            />
                          }
                        />
                        <Column
                          field="count"
                          header={t('OTKEvaluation.PocetZakaziek')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("count", "R", handleOpenList_TypeDept)}
                          footer={FORMAT_INT(totalsTypeDeptRight.count)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeDeptRight.count.value}
                              onChange={(val) =>
                                setFiltersTypeDeptRight((prev) => ({
                                  ...prev,
                                  count: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                        <Column
                          field="pieces"
                          header={t('OTKEvaluation.PocetKusov')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeIntCell("pieces", "R", handleOpenList_TypeDept)}
                          footer={FORMAT_INT(totalsTypeDeptRight.pieces)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <SearchFilterInput
                              value={filtersTypeDeptRight.pieces.value}
                              onChange={(val) =>
                                setFiltersTypeDeptRight((prev) => ({
                                  ...prev,
                                  pieces: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                        <Column
                          field="cost"
                          header={t('OTKEvaluation.NakladyNaOpravy')}
                          style={{ minWidth: 120, width: 120 }}
                          headerStyle={HD}
                          filterHeaderStyle={HD}
                          footerStyle={FT}
                          body={renderClickableTreeCurrencyCell("cost","R", handleOpenList_TypeDept)}
                          footer={FORMAT_CURRENCY(totalsTypeDeptRight.cost)}
                          filter
                          sortable
                          pt={PT_HEADER}
                          showFilterMenu={false}
                          filterElement={
                            <CurrencyFilterInput
                              value={filtersTypeDeptRight.cost.value}
                              onChange={(val) =>
                                setFiltersTypeDeptRight((prev) => ({
                                  ...prev,
                                  cost: { value: val, matchMode: "contains" },
                                }))
                              }
                            />
                          }
                        />
                      </TreeTable>
                      </div>
                    </div>
                  )}
                </Card>
                </div>
                {/* === 6) ODDELENIE VOČI SKUPINE CHÝB === */}
                <div ref={refDeptGroup} className="print-full">
                  <Card
                    title={
                      <>
                        <div className="flex items-center justify-between w-full">
                          <span>{t('Ui.R67.Oddelenie_voci_skupine_chyb')}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                            <InputSwitch
                              className="no-print"
                              checked={compareDeptGroupMode}
                              onChange={(e) => {
                                setCompareDeptGroupMode(e.value);
                                setExpandedDeptGroupLeft({});
                                setExpandedDeptGroupRight({});
                              }}
                            />
                            {dgTableMode && (
                              <Button
                                icon={
                                  compareDeptGroupMode
                                    ? Object.keys(expandedDeptGroupLeft).length > 0 &&
                                      Object.keys(expandedDeptGroupRight).length > 0
                                      ? "pi pi-angle-down"
                                      : "pi pi-angle-right"
                                    : Object.keys(expandedDeptGroupLeft).length > 0
                                    ? "pi pi-angle-down"
                                    : "pi pi-angle-right"
                                }
                                className="p-button-text p-button-sm no-print"
                                tooltip={
                                  compareDeptGroupMode
                                    ? Object.keys(expandedDeptGroupLeft).length > 0 &&
                                      Object.keys(expandedDeptGroupRight).length > 0
                                      ? t('OTKEvaluation.ZbalitVsetky')
                                      : t('OTKEvaluation.RozbalitVsetky')
                                    : Object.keys(expandedDeptGroupLeft).length > 0
                                    ? t('OTKEvaluation.ZbalitVsetky')
                                    : t('OTKEvaluation.RozbalitVsetky')
                                }
                                tooltipOptions={{ position: "left" }}
                                onClick={() => {
                                  const anyLeft  = Object.keys(expandedDeptGroupLeft).length  > 0;
                                  const anyRight = Object.keys(expandedDeptGroupRight).length > 0;

                                  if (!compareDeptGroupMode) {
                                    if (anyLeft) {
                                      setExpandedDeptGroupLeft({});
                                    } else {
                                      setExpandDeptGroupDialogVisible(true);
                                    }
                                  } else {
                                    if (anyLeft && anyRight) {
                                      setExpandedDeptGroupLeft({});
                                      setExpandedDeptGroupRight({});
                                    } else {
                                      setExpandDeptGroupDialogVisible(true);
                                    }
                                  }
                                }}
                              />
                            )}
                          </div>
                        </div>
                        <div className="mt-2">
                          {!compareDeptGroupMode && (
                            <div className="flex items-center gap-2">
                              <span className="text-base">{t('OTKEvaluation.Tabulka')}</span>
                              <InputSwitch
                                checked={!dgTableMode}
                                onChange={(e) => {
                                  const showGraph = e.value; // true = idem na GRAF
                                  setDgTableMode(!e.value);

                                  if (showGraph) {
                                    // pri prepnuti z Tabuľka -> Graf zosynchronizuj podľa tabuľky
                                    syncDgDepartmentsWithRows();
                                    setDgSelectedGroups([]); // skupiny nech sú na začiatku prázdne
                                  }
                                }}
                              />
                              <span className="text-base">Graf</span>
                            </div>
                          )}
                        </div>
                      </>
                    }
                    subTitle={t('OTKEvaluation.KliknutimRozbalteOddelenieAleboPrepinajteGraf')}
                  >
                    {/* Výber období */}
                    {!compareDeptGroupMode ? (
                      <div className="flex items-center gap-3 mb-4 no-print">
                        <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                        <Calendar
                          value={dgLeftRange || null}
                          onChange={(e) => {
                            setDgLeftRange(e.value);
                            setExpandedDeptGroupLeft({});
                          }}
                          selectionMode="range"
                          view="month"
                          dateFormat="mm/yy"
                          showIcon
                          className="w-[220px] border border-gray-400 rounded-md"
                        />
                        {/* Filtre len pri GRAFE */}
                        {!dgTableMode && (
                          <>
                            <span className="text-sm text-gray-700 font-medium ml-4">
                              Oddelenie:
                            </span>
                            <MultiSelect
                              value={dgSelectedDepartments}
                              options={dgDepartmentOptions}
                              onChange={(e) => setDgSelectedDepartments(e.value)}
                              placeholder={t('OTKEvaluation.VsetkyOddelenia')}
                              maxSelectedLabels={2}
                              selectedItemsLabel={t('OTKEvaluation.0Oddeleni')}
                              selectAllLabel={t('Common.All')}
                              className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md " 
                              valueTemplate={(value) =>
                                renderSelectedMulti(
                                  value,
                                  dgDepartmentOptions,
                                  t('OTKEvaluation.VsetkyOddelenia'),
                                  t('OTKEvaluation.VsetkyOddelenia')
                                )
                              }
                            />

                            <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Skupina_chyb')}</span>
                            <MultiSelect
                              value={dgSelectedGroups}
                              options={dgGroupOptions}
                              onChange={(e) => setDgSelectedGroups(e.value)}
                              placeholder={t('OTKEvaluation.VyberSkupinu')}
                              maxSelectedLabels={2}
                              selectedItemsLabel="{0} skupiny"
                              selectAllLabel={t('Common.All')}
                              className="w-[220px] h-[38px] items-center border border-gray-400 rounded-md " 
                              valueTemplate={(value) =>
                                renderSelectedMulti(
                                  value,
                                  dgGroupOptions,
                                  t('OTKEvaluation.VyberSkupinu'),
                                  t('OTKEvaluation.VsetkySkupiny')
                                )
                              }
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-6 mb-4 no-print">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                          <Calendar
                            value={dgLeftRange || null}
                            onChange={(e) => {
                              setDgLeftRange(e.value);
                              setExpandedDeptGroupLeft({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                        </div>
                        <div className="flex items-center gap-2 no-print">
                          <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                          <Calendar
                            value={dgRightRange || null}
                            onChange={(e) => {
                              setDgRightRange(e.value);
                              setExpandedDeptGroupRight({});
                            }}
                            selectionMode="range"
                            view="month"
                            dateFormat="mm/yy"
                            readOnlyInput
                            showIcon
                            className="w-[220px] border border-gray-400 rounded-md"
                          />
                        </div>
                      </div>
                    )}

                    {/* SINGLE režim (len ľavá tabuľka / graf) */}
                    {!compareDeptGroupMode ? (
                      dgTableMode ? (
                        <TreeTable
                          value={dgLeftFilteredTree}
                          dataKey="key"
                          expandedKeys={expandedDeptGroupLeft}
                          onToggle={(e) => setExpandedDeptGroupLeft(e.value)}
                          filters={filtersDeptGroupLeft}
                          onFilter={(e) => setFiltersDeptGroupLeft(e.filters)}
                          filterMode="lenient"
                          resizableColumns
                          columnResizeMode="expand"
                          rowClassName={rowClassName}
                          tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          scrollable={!isPrintMode}
                          scrollHeight={isPrintMode ? undefined : "500px"}
                          scrollDirection={isPrintMode ? undefined : "both"}
                          removableSort
                          sortMode="multiple"
                          multiSortMeta={costsSortMeta}
                          onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                        >
                          {/* 1. stĺpec – Oddelenie / Skupina / Zamestnanec / Zákazka */}
                          <Column
                            expander
                            field="departmentName"
                            header={t('OTKEvaluation.OddelenieSkupinaZamestnanecArtikel')}
                            style={{ minWidth: 260, width: 260 }}
                            headerStyle={HD_DN}
                            filterHeaderStyle={HD_DN}
                            footerStyle={FT_DN}
                            footer={
                              <div
                                className="tt-name-cell inline-block truncate max-w-[240px]"
                                style={{ marginLeft: "0.5rem" }}
                              >
                                Celkovo
                              </div>
                            }
                            body={dgNameBodyTemplate}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <Dropdown
                                value={filtersDeptGroupLeft.departmentName.value}
                                options={[
                                  { label: t('Common.All'), value: null },
                                  ...departments.map((d) => ({
                                    label: d.name,
                                    value: d.name,
                                  })),
                                ]}
                                onChange={(e) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    departmentName: {
                                      value: e.value,
                                      matchMode: "equals",
                                    },
                                  }))
                                }
                                placeholder={t('OTKEvaluation.VyberOddelenie')}
                                className="w-full h-[42px]"
                                showClear
                              />
                            }
                          />

                          {/* Počet */}
                          <Column
                            field="count"
                            header={t('OTKEvaluation.PocetZakaziek')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("count", "L", handleOpenList_DeptGroup)}
                            footer={FORMAT_INT(totalsDeptGroupLeft.count)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersDeptGroupLeft.count.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    count: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />

                          {/* Kusy */}
                          <Column
                            field="pieces"
                            header={t('OTKEvaluation.PocetKusov')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("pieces", "L", handleOpenList_DeptGroup)}
                            footer={FORMAT_INT(totalsDeptGroupLeft.pieces)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersDeptGroupLeft.pieces.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    pieces: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />

                          {/* Náklady na opravy */}
                          <Column
                            field="cost"
                            header={t('OTKEvaluation.NakladyNaOpravy')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_DeptGroup)}
                            footer={FORMAT_CURRENCY(totalsDeptGroupLeft.cost)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <CurrencyFilterInput
                                value={filtersDeptGroupLeft.cost.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    cost: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        </TreeTable>
                      ) : (
                        // GRAF mód – single
                        <div className="grid grid-cols-1 gap-4">
                          <div className="h-[500px]">
                            <Chart
                              type="bar"
                              style={{ width: "100%", height: "100%" }}
                              data={{
                                labels: dgChartRows.map((v) =>
                                  v.groupName
                                    ? (v.departmentName || "") +
                                      " / " +
                                      (v.groupName || "")
                                    : v.departmentName || ""
                                ),
                                datasets: [
                                  {
                                    type: "bar",
                                    label: t('OTKEvaluation.PocetZakaziek'),
                                    data: dgChartRows.map((v) => v.count),
                                    yAxisID: "y",
                                    backgroundColor: chartColors.blue,
                                    borderColor: chartColors.blue,
                                    borderWidth: 1,
                                  },
                                  {
                                    type: "bar",
                                    label: t('OTKEvaluation.PocetKusov'),
                                    data: dgChartRows.map((v) => v.pieces),
                                    yAxisID: "y",
                                    backgroundColor: chartColors.orange,
                                    borderColor: chartColors.orange,
                                    borderWidth: 1,
                                  },
                                  {
                                    type: "bar",
                                    label: t('OTKEvaluation.NakladyNaOpravy'),
                                    data: dgChartRows.map((v) => v.cost),
                                    yAxisID: "y1",
                                    backgroundColor: chartColors.red,
                                    borderColor: chartColors.red,
                                    borderWidth: 2,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                  mode: "index",
                                  intersect: false,
                                },
                                plugins: {
                                  legend: {
                                    position: "bottom",
                                    labels: { font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }) },
                                  },
                                },
                                scales: {
                                  x: {
                                    ticks: {
                                      autoSkip: true,
                                      maxRotation: 45,
                                      minRotation: 0,
                                      font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                      ...(printChartFontSize && { color: "#000" }),
                                    },
                                    ...(printChartFontSize && { grid: { color: "#000" } }),
                                  },
                                  y: {
                                    type: "linear",
                                    position: "left",
                                    beginAtZero: true,
                                    title: {
                                      display: true,
                                      text: t('OTKEvaluation.PocetZakaziekPocetKusov'),
                                      font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                      ...(printChartFontSize && { color: "#000" }),
                                    },
                                    ...(printChartFontSize && { grid: { color: "#000" } }),
                                  },
                                  y1: {
                                    type: "linear",
                                    position: "right",
                                    beginAtZero: true,
                                    grid: {
                                      drawOnChartArea: false, ...(printChartFontSize && { color: "#000" }),
                                    },
                                    title: {
                                      display: true,
                                      text: t('OTKEvaluation.CostsCurrency', { currency: currencySymbol }),
                                      font: { size: printChartFontSize ?? 11 }, ...(printChartFontSize && { color: "#000" }),
                                      ...(printChartFontSize && { color: "#000" }),
                                    },
                                    ...(printChartFontSize && { ticks: { color: "#000" } }),
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      )
                    ) : (
                      // POROVNÁVACÍ režim – dve TreeTable vedľa seba
                      <div className="grid grid-cols-2 gap-4 print-compare-grid">
                        <div className="print-compare-cell">
                        <TreeTable
                          value={dgLeftFilteredTree}
                          dataKey="key"
                          expandedKeys={expandedDeptGroupLeft}
                          onToggle={(e) => setExpandedDeptGroupLeft(e.value)}
                          filters={filtersDeptGroupLeft}
                          onFilter={(e) => setFiltersDeptGroupLeft(e.filters)}
                          filterMode="lenient"
                          resizableColumns
                          columnResizeMode="expand"
                          rowClassName={rowClassName}
                          tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          scrollable={!isPrintMode}
                          scrollHeight={isPrintMode ? undefined : "500px"}
                          scrollDirection={isPrintMode ? undefined : "both"}
                          removableSort
                          sortMode="multiple"
                          multiSortMeta={costsSortMeta}
                          onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                        >
                          <Column
                            expander
                            field="departmentName"
                            header={t('OTKEvaluation.OddelenieSkupinaZamestnanecArtikel')}
                            style={{ minWidth: 260, width: 260 }}
                            headerStyle={HD_DN}
                            filterHeaderStyle={HD_DN}
                            footerStyle={FT_DN}
                            footer={
                              <div
                                className="tt-name-cell inline-block truncate max-w-[240px]"
                                style={{ marginLeft: "0.5rem" }}
                              >
                                Celkovo
                              </div>
                            }
                            body={dgNameBodyTemplate}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <Dropdown
                                value={filtersDeptGroupLeft.departmentName.value}
                                options={[
                                  { label: t('Common.All'), value: null },
                                  ...departments.map((d) => ({
                                    label: d.name,
                                    value: d.name,
                                  })),
                                ]}
                                onChange={(e) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    departmentName: {
                                      value: e.value,
                                      matchMode: "equals",
                                    },
                                  }))
                                }
                                placeholder={t('OTKEvaluation.VyberOddelenie')}
                                className="w-full h-[42px]"
                                showClear
                              />
                            }
                          />
                          <Column
                            field="count"
                            header={t('OTKEvaluation.PocetZakaziek')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("count", "L", handleOpenList_DeptGroup)}
                            footer={FORMAT_INT(totalsDeptGroupLeft.count)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersDeptGroupLeft.count.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    count: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                          <Column
                            field="pieces"
                            header={t('OTKEvaluation.PocetKusov')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("pieces", "L", handleOpenList_DeptGroup)}
                            footer={FORMAT_INT(totalsDeptGroupLeft.pieces)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersDeptGroupLeft.pieces.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    pieces: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                          <Column
                            field="cost"
                            header={t('OTKEvaluation.NakladyNaOpravy')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeCurrencyCell("cost","L", handleOpenList_DeptGroup)}
                            footer={FORMAT_CURRENCY(totalsDeptGroupLeft.cost)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <CurrencyFilterInput
                                value={filtersDeptGroupLeft.cost.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupLeft((prev) => ({
                                    ...prev,
                                    cost: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        </TreeTable>
                        </div>

                        <div className="print-compare-cell">
                        <TreeTable
                          value={dgRightFilteredTree}
                          dataKey="key"
                          expandedKeys={expandedDeptGroupRight}
                          onToggle={(e) => setExpandedDeptGroupRight(e.value)}
                          filters={filtersDeptGroupRight}
                          onFilter={(e) => setFiltersDeptGroupRight(e.filters)}
                          filterMode="lenient"
                          resizableColumns
                          columnResizeMode="expand"
                          rowClassName={rowClassName}
                          tableStyle={{ minWidth: TT_MINW, tableLayout: "auto" }}
                          scrollable={!isPrintMode}
                          scrollHeight={isPrintMode ? undefined : "500px"}
                          scrollDirection={isPrintMode ? undefined : "both"}
                          removableSort
                          sortMode="multiple"
                          multiSortMeta={costsSortMeta}
                          onSort={(e) => setCostsSortMeta(e.multiSortMeta)}
                        >
                          <Column
                            expander
                            field="departmentName"
                            header={t('OTKEvaluation.OddelenieSkupinaZamestnanecArtikel')}
                            style={{ minWidth: 260, width: 260 }}
                            headerStyle={HD_DN}
                            filterHeaderStyle={HD_DN}
                            footerStyle={FT_DN}
                            footer={
                              <div
                                className="tt-name-cell inline-block truncate max-w-[240px]"
                                style={{ marginLeft: "0.5rem" }}
                              >
                                Celkovo
                              </div>
                            }
                            body={dgNameBodyTemplate}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <Dropdown
                                value={filtersDeptGroupRight.departmentName.value}
                                options={[
                                  { label: t('Common.All'), value: null },
                                  ...departments.map((d) => ({
                                    label: d.name,
                                    value: d.name,
                                  })),
                                ]}
                                onChange={(e) =>
                                  setFiltersDeptGroupRight((prev) => ({
                                    ...prev,
                                    departmentName: {
                                      value: e.value,
                                      matchMode: "equals",
                                    },
                                  }))
                                }
                                placeholder={t('OTKEvaluation.VyberOddelenie')}
                                className="w-full h-[42px]"
                                showClear
                              />
                            }
                          />
                          <Column
                            field="count"
                            header={t('OTKEvaluation.PocetZakaziek')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("count", "R", handleOpenList_DeptGroup)}
                            footer={FORMAT_INT(totalsDeptGroupRight.count)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersDeptGroupRight.count.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupRight((prev) => ({
                                    ...prev,
                                    count: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                          <Column
                            field="pieces"
                            header={t('OTKEvaluation.PocetKusov')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeIntCell("pieces", "R", handleOpenList_DeptGroup)}
                            footer={FORMAT_INT(totalsDeptGroupRight.pieces)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <SearchFilterInput
                                value={filtersDeptGroupRight.pieces.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupRight((prev) => ({
                                    ...prev,
                                    pieces: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                          <Column
                            field="cost"
                            header={t('OTKEvaluation.NakladyNaOpravy')}
                            style={{ minWidth: 120, width: 120 }}
                            headerStyle={HD}
                            filterHeaderStyle={HD}
                            footerStyle={FT}
                            body={renderClickableTreeCurrencyCell("cost","R", handleOpenList_DeptGroup)}
                            footer={FORMAT_CURRENCY(totalsDeptGroupRight.cost)}
                            filter
                            sortable
                            pt={PT_HEADER}
                            showFilterMenu={false}
                            filterElement={
                              <CurrencyFilterInput
                                value={filtersDeptGroupRight.cost.value}
                                onChange={(val) =>
                                  setFiltersDeptGroupRight((prev) => ({
                                    ...prev,
                                    cost: { value: val, matchMode: "contains" },
                                  }))
                                }
                              />
                            }
                          />
                        </TreeTable>
                        </div>
                      </div>
                    )}
                  </Card>


                {/* === 5) VLASTNÁ TABUĽKA === – dočasne skryté */}
                {SHOW_CUSTOM_TABLES && (
                <div className="mt-5 flex flex-col gap-4">
                  {customTables.map((t, i) => (
                    <Card
                      key={i}
                      title={
                        <div className="flex justify-between items-center w-full">
                          <span>{t.title}</span>
                          <div className="flex gap-2">
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-text p-button-sm"
                              tooltip={t('OTKEvaluation.UpravitTabulku')}
                              onClick={() => handleEditTable(i)}
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-text p-button-sm text-red-500"
                              tooltip={t('OTKEvaluation.OdstranitTabulku')}
                              onClick={() => handleDeleteTable(i)}
                            />
                          </div>
                        </div>
                      }
                    >
                      {t.type === "table" ? (
                        <DataTable value={getDataBySource(t.source)} scrollable scrollHeight="500px" stripedRows responsiveLayout="scroll" removableSort sortMode="multiple">
                          {t.columns.map((col) => (
                            <Column
                              key={col}
                              field={col}
                              header={col}
                              sortable
                              body={(row) => {
                                const val = row[col];
                                if (val == null || val === "") return "";
                                if (typeof val === "object") {
                                  if (Array.isArray(val)) {
                                    return val
                                      .map((v) =>
                                        typeof v === "object"
                                          ? v.name?.sk || v.name?.en || v.name || v.title || v.label || JSON.stringify(v)
                                          : String(v)
                                      )
                                      .join(", ");
                                  }
                                  return (
                                    val.name?.sk ||
                                    val.name?.en ||
                                    val.name ||
                                    val.title ||
                                    val.label ||
                                    val.code ||
                                    val.symbol ||
                                    JSON.stringify(val)
                                  );
                                }
                                if (typeof val === "number")
                                  return Number.isInteger(val)
                                    ? val.toLocaleString("sk-SK")
                                    : val.toLocaleString("sk-SK", { minimumFractionDigits: 2 });
                                return String(val);
                              }}
                            />
                          ))}
                        </DataTable>
                      ) : (
                        <Chart
                          type="bar"
                          data={{
                            labels: getDataBySource(t.source).map(
                              (row, idx) =>
                                row.departmentName ||
                                row.errorTypeName ||
                                row.groupName ||
                                row.customer ||
                                t('OTKEvaluation.ItemN', { n: idx + 1 })
                            ),
                            datasets: t.columns.map((col) => ({
                              label: col,
                              data: getDataBySource(t.source).map((r) => {
                                const val = r[col];
                                if (typeof val === "number") return val;
                                if (typeof val === "object") return 0;
                                const parsed = parseFloat(val);
                                return isNaN(parsed) ? 0 : parsed;
                              }),
                            })),
                          }}
                        />
                      )}
                    </Card>
                  ))}
                  <div
                    className="border-2 border-dashed border-gray-300 mt-3 rounded-xl flex justify-center items-center h-64 hover:bg-surface-50 cursor-pointer transition-all"
                    onClick={() => setAddTableDialogVisible(true)}
                  >
                    <i className="pi pi-plus text-4xl text-gray-400"></i>
                  </div>
                </div>
                )}
              </div>
            </div>
          </TabPanel>

          {/* ===== TAB 2 ===== */}
          <TabPanel header={t('OTKEvaluation.VyhodnotenieReklamacii')}>
            <div ref={refAllTablesTab2} className="flex flex-col gap-4">
            <div ref={refClaimedOrders} className="print-full print-table-section">
            <Card
              title={
                <div className="flex justify-between items-center w-full">
                  <span>{t('Ui.R67.Reklamovane_zakazky_za_zvolene_obdobie')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                    <InputSwitch
                      className="no-print"
                      checked={compareClaimedOrdersMode}
                      onChange={(e) => setCompareClaimedOrdersMode(e.value)}
                    />
                  </div>
                </div>
              }
              subTitle=""
            >
              {!compareClaimedOrdersMode ? (
                <>
                  <div className="flex items-center gap-3 mb-4 no-print">
                    <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                    <Calendar
                      value={claimedOrdersLeftRange || null}
                      onChange={(e) => setClaimedOrdersLeftRange(e.value)}
                      selectionMode="range"
                      view="month"
                      dateFormat="mm/yy"
                      showIcon
                      className="w-[220px] border border-gray-400 rounded-md"
                    />
                  </div>

                  <DataTable
                    value={claimedOrdersLeftRowsAll}
                    dataKey="_key"
                    scrollable
                    scrollHeight="500px"
                    removableSort
                    multiSortMeta={claimedOrdersSortMeta}
                    onSort={(e) => setClaimedOrdersSortMeta(e.multiSortMeta)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filters={filtersClaimedOrdersLeft}
                    onFilter={(e) => setFiltersClaimedOrdersLeft(e.filters)}
                  >
                    <Column
                      field="customer"
                      header={t('Common.Customer')}
                      frozen
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      footer="Celkovo"
                      filter
                      filterElement={
                        <Dropdown
                          value={filtersClaimedOrdersLeft.customer.value}
                          options={customerOptions}
                          onChange={(e) =>
                            setFiltersClaimedOrdersLeft((prev) => ({
                              ...prev,
                              customer: { value: e.value, matchMode: "equals" },
                            }))
                          }
                          placeholder={t('OTKEvaluation.VyberZakaznika')}
                          className="w-full h-[42px]"
                        />
                      }
                    />

                    <Column
                      field="ordersTotal"
                      header={t('OTKEvaluation.DodaneZakazkyPc')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={(r) => fmtCount(r.ordersTotal)}
                      footer={fmtCount(totalsClaimedOrdersLeft.ordersTotal)}
                      filterElement={
                        <SearchFilterInput
                          value={filtersClaimedOrdersLeft.ordersTotal.value}
                          onChange={(val) =>
                            setFiltersClaimedOrdersLeft((prev) => ({
                              ...prev,
                              ordersTotal: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />

                    <Column
                      field="ordersCount"
                      header={t('OTKEvaluation.ReklamovaneZakazkyPc')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={renderClickableDTCell({
                        field: "ordersCount",
                        format: fmtCount,
                        side: "L",
                        openFn: handleOpenClaimedOrders,
                      })}
                      footer={fmtCount(totalsClaimedOrdersLeft.ordersCount)}
                      filterElement={
                        <SearchFilterInput
                          value={filtersClaimedOrdersLeft.ordersCount.value}
                          onChange={(val) =>
                            setFiltersClaimedOrdersLeft((prev) => ({
                              ...prev,
                              ordersCount: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />

                    <Column
                      field="ordersPct"
                      header={t('OTKEvaluation.ReklamovaneZakazky')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={(r) => `${r.ordersPct.toFixed(2)} %`}
                      footer={`${totalsClaimedOrdersLeft.ordersPct.toFixed(2)} %`}
                      filterElement={
                        <SearchFilterInput
                          value={filtersClaimedOrdersLeft.ordersPct.value}
                          onChange={(val) =>
                            setFiltersClaimedOrdersLeft((prev) => ({
                              ...prev,
                              ordersPct: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />
                  </DataTable>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-6 mb-4 no-print">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                      <Calendar
                        value={claimedOrdersLeftRange || null}
                        onChange={(e) => setClaimedOrdersLeftRange(e.value)}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex items-center gap-2 no-print">
                      <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                      <Calendar
                        value={claimedOrdersRightRange || null}
                        onChange={(e) => setClaimedOrdersRightRange(e.value)}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        readOnlyInput
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 print-compare-grid">
                    <div className="print-compare-cell">
                    <DataTable
                      value={claimedOrdersLeftRowsAll}
                      dataKey="_key"
                      scrollable
                      scrollHeight="500px"
                      removableSort
                      multiSortMeta={claimedOrdersSortMeta}
                      onSort={(e) => setClaimedOrdersSortMeta(e.multiSortMeta)}
                      sortMode="multiple"
                      filterDisplay="row"
                      filters={filtersClaimedOrdersLeft}
                      onFilter={(e) => setFiltersClaimedOrdersLeft(e.filters)}
                    >
                      <Column
                        field="customer"
                        header={t('Common.Customer')}
                        frozen
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        footer="Celkovo"
                        filter
                        filterElement={
                          <Dropdown
                            value={filtersClaimedOrdersLeft.customer.value}
                            options={customerOptions}
                            onChange={(e) =>
                              setFiltersClaimedOrdersLeft((prev) => ({
                                ...prev,
                                customer: { value: e.value, matchMode: "equals" },
                              }))
                            }
                            placeholder={t('OTKEvaluation.VyberZakaznika')}
                            className="w-full h-[42px]"
                          />
                        }
                      />

                      <Column
                        field="ordersTotal"
                        header={t('OTKEvaluation.DodaneZakazkyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => fmtCount(r.ordersTotal)}
                        footer={fmtCount(totalsClaimedOrdersLeft.ordersTotal)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedOrdersLeft.ordersTotal.value}
                            onChange={(val) =>
                              setFiltersClaimedOrdersLeft((prev) => ({
                                ...prev,
                                ordersTotal: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="ordersCount"
                        header={t('OTKEvaluation.ReklamovaneZakazkyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={renderClickableDTCell({
                          field: "ordersCount",
                          format: fmtCount,
                          side: "L",
                          openFn: handleOpenClaimedOrders,
                        })}
                        footer={fmtCount(totalsClaimedOrdersLeft.ordersCount)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedOrdersLeft.ordersCount.value}
                            onChange={(val) =>
                              setFiltersClaimedOrdersLeft((prev) => ({
                                ...prev,
                                ordersCount: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="ordersPct"
                        header={t('OTKEvaluation.ReklamovaneZakazky')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => `${r.ordersPct.toFixed(2)} %`}
                        footer={`${totalsClaimedOrdersLeft.ordersPct.toFixed(2)} %`}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedOrdersLeft.ordersPct.value}
                            onChange={(val) =>
                              setFiltersClaimedOrdersLeft((prev) => ({
                                ...prev,
                                ordersPct: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </DataTable>
                    </div>
                    <div className="print-compare-cell">
                    <DataTable
                      value={claimedOrdersRightRowsAll}
                      dataKey="_key"
                      scrollable
                      scrollHeight="500px"
                      removableSort
                      multiSortMeta={claimedOrdersSortMeta}
                      onSort={(e) => setClaimedOrdersSortMeta(e.multiSortMeta)}
                      sortMode="multiple"
                      filterDisplay="row"
                      filters={filtersClaimedOrdersRight}
                      onFilter={(e) => setFiltersClaimedOrdersRight(e.filters)}
                    >
                      <Column
                        field="customer"
                        header={t('Common.Customer')}
                        frozen
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        footer="Celkovo"
                        filter
                        filterElement={
                          <Dropdown
                            value={filtersClaimedOrdersRight.customer.value}
                            options={customerOptions}
                            onChange={(e) =>
                              setFiltersClaimedOrdersRight((prev) => ({
                                ...prev,
                                customer: { value: e.value, matchMode: "equals" },
                              }))
                            }
                            placeholder={t('OTKEvaluation.VyberZakaznika')}
                            className="w-full h-[42px]"
                          />
                        }
                      />

                      <Column
                        field="ordersTotal"
                        header={t('OTKEvaluation.DodaneZakazkyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => fmtCount(r.ordersTotal)}
                        footer={fmtCount(totalsClaimedOrdersRight.ordersTotal)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedOrdersRight.ordersTotal.value}
                            onChange={(val) =>
                              setFiltersClaimedOrdersRight((prev) => ({
                                ...prev,
                                ordersTotal: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="ordersCount"
                        header={t('OTKEvaluation.ReklamovaneZakazkyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={renderClickableDTCell({
                          field: "ordersCount",
                          format: fmtCount,
                          side: "R",
                          openFn: handleOpenClaimedOrders,
                        })}
                        footer={fmtCount(totalsClaimedOrdersRight.ordersCount)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedOrdersRight.ordersCount.value}
                            onChange={(val) =>
                              setFiltersClaimedOrdersRight((prev) => ({
                                ...prev,
                                ordersCount: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="ordersPct"
                        header={t('OTKEvaluation.ReklamovaneZakazky')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => `${r.ordersPct.toFixed(2)} %`}
                        footer={`${totalsClaimedOrdersRight.ordersPct.toFixed(2)} %`}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedOrdersRight.ordersPct.value}
                            onChange={(val) =>
                              setFiltersClaimedOrdersRight((prev) => ({
                                ...prev,
                                ordersPct: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </DataTable>
                    </div>
                  </div>
                </>
              )}

              <div className={`mt-4 print-charts-section ${compareClaimedOrdersMode ? 'print-charts-compare' : ''}`}>
                <div className="flex items-center gap-2 w-full no-print">
                  <span className="font-bold">Grafy</span>
                  <InputSwitch
                    checked={showClaimedOrdersCharts}
                    onChange={(e) => setShowClaimedOrdersCharts(e.value)}
                  />
                </div>

                {showClaimedOrdersCharts ? (
                  <div className="mt-3 grid grid-cols-1 gap-4 print-chart-row">
                    {!compareClaimedOrdersMode ? (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Card title={t('OTKEvaluation.ClaimedOrdersPctRange', { range: fmtMonthRange(claimedOrdersLeftRange) })} className="print-chart-item">
                          <Chart
                            type="bar"
                            data={{
                              labels: claimedOrdersLeftRowsVisible.map((r) => r.customer),
                              datasets: [
                                {
                                  label: t('OTKEvaluation.ReklamovaneZakazky'),
                                  data: claimedOrdersLeftRowsVisible.map((r) => num(r.ordersPct)),
                                },
                              ],
                            }}
                            options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                          />
                        </Card>

                        <Card title={t('OTKEvaluation.ClaimedOrdersQtyLeft', { range: fmtMonthRange(claimedOrdersLeftRange) })} className="print-chart-item">
                          <Chart
                            type="bar"
                            data={{
                              labels: claimedOrdersLeftRowsVisible.map((r) => r.customer),
                              datasets: [
                                {
                                  label: t('OTKEvaluation.ReklamovaneZakazkyPc'),
                                  data: claimedOrdersLeftRowsVisible.map((r) => num(r.ordersCount)),
                                },
                              ],
                            }}
                            options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                          />
                        </Card>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <Card title={t('OTKEvaluation.ClaimedOrdersPctRange', { range: fmtMonthRange(claimedOrdersLeftRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedOrdersLeftRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneZakazky'),
                                    data: claimedOrdersLeftRowsVisible.map((r) => num(r.ordersPct)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>

                          <Card title={t('OTKEvaluation.ClaimedOrdersPctRight', { range: fmtMonthRange(claimedOrdersRightRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedOrdersRightRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneZakazky'),
                                    data: claimedOrdersRightRowsVisible.map((r) => num(r.ordersPct)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <Card title={t('OTKEvaluation.ClaimedOrdersQtyLeft', { range: fmtMonthRange(claimedOrdersLeftRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedOrdersLeftRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneZakazkyPc'),
                                    data: claimedOrdersLeftRowsVisible.map((r) => num(r.ordersCount)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>

                          <Card title={t('OTKEvaluation.ClaimedOrdersQtyRight', { range: fmtMonthRange(claimedOrdersRightRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedOrdersRightRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneZakazkyPc'),
                                    data: claimedOrdersRightRowsVisible.map((r) => num(r.ordersCount)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </Card>
            </div>

            <div ref={refClaimedPieces} className="print-full print-table-section print-section-break">
            <Card
              title={
                <div className="flex justify-between items-center w-full">
                  <span>{t('CrmEval.Section.ClaimedPieces')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                    <InputSwitch
                      className="no-print"
                      checked={compareClaimedPiecesMode}
                      onChange={(e) => setCompareClaimedPiecesMode(e.value)}
                    />
                  </div>
                </div>
              }
              subTitle=""
            >
              {!compareClaimedPiecesMode ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                    <Calendar
                      value={claimedPiecesLeftRange || null}
                      onChange={(e) => setClaimedPiecesLeftRange(e.value)}
                      selectionMode="range"
                      view="month"
                      dateFormat="mm/yy"
                      showIcon
                      className="w-[220px] border border-gray-400 rounded-md"
                    />
                  </div>

                  <DataTable
                    value={claimedPiecesLeftRowsAll}
                    dataKey="_key"
                    scrollable
                    scrollHeight="500px"
                    removableSort
                    multiSortMeta={claimedPiecesSortMeta}
                    onSort={(e) => setClaimedPiecesSortMeta(e.multiSortMeta)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filters={filtersClaimedPiecesLeft}
                    onFilter={(e) => setFiltersClaimedPiecesLeft(e.filters)}
                  >
                    <Column
                      field="customer"
                      header={t('Common.Customer')}
                      frozen
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      footer="Celkovo"
                      filter
                      filterElement={
                        <Dropdown
                          value={filtersClaimedPiecesLeft.customer.value}
                          options={customerOptions}
                          onChange={(e) =>
                            setFiltersClaimedPiecesLeft((prev) => ({
                              ...prev,
                              customer: { value: e.value, matchMode: "equals" },
                            }))
                          }
                          placeholder={t('OTKEvaluation.VyberZakaznika')}
                          className="w-full h-[42px]"
                        />
                      }
                    />

                    <Column
                      field="partsTotal"
                      header={t('OTKEvaluation.DodaneKusyPc')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={(r) => fmtCount(r.partsTotal)}
                      footer={fmtCount(totalsClaimedPiecesLeft.partsTotal)}
                      filterElement={
                        <SearchFilterInput
                          value={filtersClaimedPiecesLeft.partsTotal.value}
                          onChange={(val) =>
                            setFiltersClaimedPiecesLeft((prev) => ({
                              ...prev,
                              partsTotal: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />

                    <Column
                      field="partsCount"
                      header={t('OTKEvaluation.ReklamovaneKusyPc')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={renderClickableDTCell({
                        field: "partsCount",
                        format: fmtCount,
                        side: "L",
                        openFn: handleOpenClaimedPieces,
                      })}
                      footer={fmtCount(totalsClaimedPiecesLeft.partsCount)}
                      filterElement={
                        <SearchFilterInput
                          value={filtersClaimedPiecesLeft.partsCount.value}
                          onChange={(val) =>
                            setFiltersClaimedPiecesLeft((prev) => ({
                              ...prev,
                              partsCount: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />

                    <Column
                      field="partsPct"
                      header={t('OTKEvaluation.ReklamovaneKusy')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={(r) => `${r.partsPct.toFixed(2)} %`}
                      footer={`${totalsClaimedPiecesLeft.partsPct.toFixed(2)} %`}
                      filterElement={
                        <SearchFilterInput
                          value={filtersClaimedPiecesLeft.partsPct.value}
                          onChange={(val) =>
                            setFiltersClaimedPiecesLeft((prev) => ({
                              ...prev,
                              partsPct: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />
                  </DataTable>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-6 mb-4 no-print">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                      <Calendar
                        value={claimedPiecesLeftRange || null}
                        onChange={(e) => setClaimedPiecesLeftRange(e.value)}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex items-center gap-2 no-print">
                      <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                      <Calendar
                        value={claimedPiecesRightRange || null}
                        onChange={(e) => setClaimedPiecesRightRange(e.value)}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        readOnlyInput
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 print-compare-grid">
                    <div className="print-compare-cell">
                    <DataTable
                      value={claimedPiecesLeftRowsAll}
                      dataKey="_key"
                      scrollable
                      scrollHeight="500px"
                      removableSort
                      multiSortMeta={claimedPiecesSortMeta}
                      onSort={(e) => setClaimedPiecesSortMeta(e.multiSortMeta)}
                      sortMode="multiple"
                      filterDisplay="row"
                      filters={filtersClaimedPiecesLeft}
                      onFilter={(e) => setFiltersClaimedPiecesLeft(e.filters)}
                    >
                      <Column
                        field="customer"
                        header={t('Common.Customer')}
                        frozen
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        footer="Celkovo"
                        filter
                        filterElement={
                          <Dropdown
                            value={filtersClaimedPiecesLeft.customer.value}
                            options={customerOptions}
                            onChange={(e) =>
                              setFiltersClaimedPiecesLeft((prev) => ({
                                ...prev,
                                customer: { value: e.value, matchMode: "equals" },
                              }))
                            }
                            placeholder={t('OTKEvaluation.VyberZakaznika')}
                            className="w-full h-[42px]"
                          />
                        }
                      />

                      <Column
                        field="partsTotal"
                        header={t('OTKEvaluation.DodaneKusyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => fmtCount(r.partsTotal)}
                        footer={fmtCount(totalsClaimedPiecesLeft.partsTotal)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedPiecesLeft.partsTotal.value}
                            onChange={(val) =>
                              setFiltersClaimedPiecesLeft((prev) => ({
                                ...prev,
                                partsTotal: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="partsCount"
                        header={t('OTKEvaluation.ReklamovaneKusyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={renderClickableDTCell({
                          field: "partsCount",
                          format: fmtCount,
                          side: "L",
                          openFn: handleOpenClaimedPieces,
                        })}
                        footer={fmtCount(totalsClaimedPiecesLeft.partsCount)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedPiecesLeft.partsCount.value}
                            onChange={(val) =>
                              setFiltersClaimedPiecesLeft((prev) => ({
                                ...prev,
                                partsCount: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="partsPct"
                        header={t('OTKEvaluation.ReklamovaneKusy')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => `${r.partsPct.toFixed(2)} %`}
                        footer={`${totalsClaimedPiecesLeft.partsPct.toFixed(2)} %`}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedPiecesLeft.partsPct.value}
                            onChange={(val) =>
                              setFiltersClaimedPiecesLeft((prev) => ({
                                ...prev,
                                partsPct: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </DataTable>
                    </div>

                    <div className="print-compare-cell">
                    <DataTable
                      value={claimedPiecesRightRowsAll}
                      dataKey="_key"
                      scrollable
                      scrollHeight="500px"
                      removableSort
                      multiSortMeta={claimedPiecesSortMeta}
                      onSort={(e) => setClaimedPiecesSortMeta(e.multiSortMeta)}
                      sortMode="multiple"
                      filterDisplay="row"
                      filters={filtersClaimedPiecesRight}
                      onFilter={(e) => setFiltersClaimedPiecesRight(e.filters)}
                    >
                      <Column
                        field="customer"
                        header={t('Common.Customer')}
                        frozen
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        footer="Celkovo"
                        filter
                        filterElement={
                          <Dropdown
                            value={filtersClaimedPiecesRight.customer.value}
                            options={customerOptions}
                            onChange={(e) =>
                              setFiltersClaimedPiecesRight((prev) => ({
                                ...prev,
                                customer: { value: e.value, matchMode: "equals" },
                              }))
                            }
                            placeholder={t('OTKEvaluation.VyberZakaznika')}
                            className="w-full h-[42px]"
                          />
                        }
                      />

                      <Column
                        field="partsTotal"
                        header={t('OTKEvaluation.DodaneKusyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => fmtCount(r.partsTotal)}
                        footer={fmtCount(totalsClaimedPiecesRight.partsTotal)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedPiecesRight.partsTotal.value}
                            onChange={(val) =>
                              setFiltersClaimedPiecesRight((prev) => ({
                                ...prev,
                                partsTotal: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="partsCount"
                        header={t('OTKEvaluation.ReklamovaneKusyPc')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={renderClickableDTCell({
                          field: "partsCount",
                          format: fmtCount,
                          side: "R",
                          openFn: handleOpenClaimedPieces,
                        })}
                        footer={fmtCount(totalsClaimedPiecesRight.partsCount)}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedPiecesRight.partsCount.value}
                            onChange={(val) =>
                              setFiltersClaimedPiecesRight((prev) => ({
                                ...prev,
                                partsCount: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="partsPct"
                        header={t('OTKEvaluation.ReklamovaneKusy')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => `${r.partsPct.toFixed(2)} %`}
                        footer={`${totalsClaimedPiecesRight.partsPct.toFixed(2)} %`}
                        filterElement={
                          <SearchFilterInput
                            value={filtersClaimedPiecesRight.partsPct.value}
                            onChange={(val) =>
                              setFiltersClaimedPiecesRight((prev) => ({
                                ...prev,
                                partsPct: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </DataTable>
                    </div>
                  </div>
                </>
              )}
              <div className={`mt-4 print-charts-section ${compareClaimedPiecesMode ? 'print-charts-compare' : ''}`}>
                <div className="flex items-center gap-2 w-full no-print">
                  <span className="font-bold">Grafy</span>
                  <InputSwitch
                    checked={showClaimedPiecesCharts}
                    onChange={(e) => setShowClaimedPiecesCharts(e.value)}
                  />
                </div>

                {showClaimedPiecesCharts ? (
                  <div className="mt-3 grid grid-cols-1 gap-4 print-chart-row">
                    {!compareClaimedPiecesMode ? (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Card title={t('OTKEvaluation.ClaimedPiecesPctLeft', { range: fmtMonthRange(claimedPiecesLeftRange) })} className="print-chart-item">
                          <Chart
                            type="bar"
                            data={{
                              labels: claimedPiecesLeftRowsVisible.map((r) => r.customer),
                              datasets: [
                                {
                                  label: t('OTKEvaluation.ReklamovaneKusy'),
                                  data: claimedPiecesLeftRowsVisible.map((r) => num(r.partsPct)),
                                },
                              ],
                            }}
                            options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                          />
                        </Card>

                        <Card title={t('OTKEvaluation.ClaimedPiecesQtyLeft', { range: fmtMonthRange(claimedPiecesLeftRange) })} className="print-chart-item">
                          <Chart
                            type="bar"
                            data={{
                              labels: claimedPiecesLeftRowsVisible.map((r) => r.customer),
                              datasets: [
                                {
                                  label: t('OTKEvaluation.ReklamovaneKusyPc'),
                                  data: claimedPiecesLeftRowsVisible.map((r) => num(r.partsCount)),
                                },
                              ],
                            }}
                            options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                          />
                        </Card>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <Card title={t('OTKEvaluation.ClaimedPiecesPctLeft', { range: fmtMonthRange(claimedPiecesLeftRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedPiecesLeftRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneKusy'),
                                    data: claimedPiecesLeftRowsVisible.map((r) => num(r.partsPct)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>

                          <Card title={t('OTKEvaluation.ClaimedPiecesPctRight', { range: fmtMonthRange(claimedPiecesRightRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedPiecesRightRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneKusy'),
                                    data: claimedPiecesRightRowsVisible.map((r) => num(r.partsPct)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <Card title={t('OTKEvaluation.ClaimedPiecesQtyLeft', { range: fmtMonthRange(claimedPiecesLeftRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedPiecesLeftRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneKusyPc'),
                                    data: claimedPiecesLeftRowsVisible.map((r) => num(r.partsCount)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>

                          <Card title={t('OTKEvaluation.ClaimedPiecesQtyRight', { range: fmtMonthRange(claimedPiecesRightRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: claimedPiecesRightRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.ReklamovaneKusyPc'),
                                    data: claimedPiecesRightRowsVisible.map((r) => num(r.partsCount)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </Card>
            </div>

            <div ref={refRepairCost} className="print-full print-table-section print-section-break">
            <Card
              title={
                <div className="flex justify-between items-center w-full">
                  <span>{t('CrmEval.Section.RepairCost')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 no-print">{t('OTKEvaluation.PorovnatSObdobim')}</span>
                    <InputSwitch
                      className="no-print"
                      checked={compareRepairCostMode}
                      onChange={(e) => setCompareRepairCostMode(e.value)}
                    />
                  </div>
                </div>
              }
              subTitle=""
            >
              {!compareRepairCostMode ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                    <Calendar
                      value={repairCostLeftRange || null}
                      onChange={(e) => setRepairCostLeftRange(e.value)}
                      selectionMode="range"
                      view="month"
                      dateFormat="mm/yy"
                      showIcon
                      className="w-[220px] border border-gray-400 rounded-md"
                    />
                  </div>

                  <DataTable
                    value={repairCostLeftRowsAll}
                    dataKey="_key"
                    scrollable
                    scrollHeight="500px"
                    removableSort
                    multiSortMeta={repairCostSortMeta}
                    onSort={(e) => setRepairCostSortMeta(e.multiSortMeta)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filters={filtersRepairCostLeft}
                    onFilter={(e) => setFiltersRepairCostLeft(e.filters)}
                  >
                    <Column
                      field="customer"
                      header={t('Common.Customer')}
                      frozen
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      footer="Celkovo"
                      filter
                      filterElement={
                        <Dropdown
                          value={filtersRepairCostLeft.customer.value}
                          options={customerOptions}
                          onChange={(e) =>
                            setFiltersRepairCostLeft((prev) => ({
                              ...prev,
                              customer: { value: e.value, matchMode: "equals" },
                            }))
                          }
                          placeholder={t('OTKEvaluation.VyberZakaznika')}
                          className="w-full h-[42px]"
                        />
                      }
                    />

                    <Column
                      field="revenue"
                      header={t('OTKEvaluation.DeliveredAmountCurrency', { currency: currencySymbol })}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={(r) => FORMAT_CURRENCY(r.revenue)}
                      footer={FORMAT_CURRENCY(totalsRepairCostLeft.revenue)}
                      filterElement={
                        <CurrencyFilterInput
                          value={filtersRepairCostLeft.revenue.value}
                          onChange={(val) =>
                            setFiltersRepairCostLeft((prev) => ({
                              ...prev,
                              revenue: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />

                    <Column
                      field="costEUR"
                      header={t('OTKEvaluation.RepairCostsCurrency', { currency: currencySymbol })}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={renderClickableDTCell({
                        field: "costEUR",
                        format: FORMAT_CURRENCY,
                        side: "L",
                        openFn: handleOpenRepairCost,
                      })}
                      footer={FORMAT_CURRENCY(totalsRepairCostLeft.costEUR)}
                      filterElement={
                        <CurrencyFilterInput
                          value={filtersRepairCostLeft.costEUR.value}
                          onChange={(val) =>
                            setFiltersRepairCostLeft((prev) => ({
                              ...prev,
                              costEUR: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />

                    <Column
                      field="costPct"
                      header={t('OTKEvaluation.NakladyNaOpravy2')}
                      sortable
                      showFilterMenu={false}
                      headerStyle={{ background: "var(--surface-100)" }}
                      footerStyle={{ background: "var(--surface-100)" }}
                      filterHeaderStyle={{ background: "var(--surface-100)" }}
                      filter
                      body={(r) => `${r.costPct.toFixed(2)} %`}
                      footer={`${totalsRepairCostLeft.costPct.toFixed(2)} %`}
                      filterElement={
                        <SearchFilterInput
                          value={filtersRepairCostLeft.costPct.value}
                          onChange={(val) =>
                            setFiltersRepairCostLeft((prev) => ({
                              ...prev,
                              costPct: { value: val, matchMode: "contains" },
                            }))
                          }
                        />
                      }
                    />
                  </DataTable>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-6 mb-4 no-print">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                      <Calendar
                        value={repairCostLeftRange || null}
                        onChange={(e) => setRepairCostLeftRange(e.value)}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex items-center gap-2 no-print">
                      <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov_2')}</span>
                      <Calendar
                        value={repairCostRightRange || null}
                        onChange={(e) => setRepairCostRightRange(e.value)}
                        selectionMode="range"
                        view="month"
                        dateFormat="mm/yy"
                        readOnlyInput
                        showIcon
                        className="w-[220px] border border-gray-400 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 print-compare-grid">
                    <div className="print-compare-cell">
                    <DataTable
                      value={repairCostLeftRowsAll}
                      dataKey="_key"
                      scrollable
                      scrollHeight="500px"
                      removableSort
                      multiSortMeta={repairCostSortMeta}
                      onSort={(e) => setRepairCostSortMeta(e.multiSortMeta)}
                      sortMode="multiple"
                      filterDisplay="row"
                      filters={filtersRepairCostLeft}
                      onFilter={(e) => setFiltersRepairCostLeft(e.filters)}
                    >
                      <Column
                        field="customer"
                        header={t('Common.Customer')}
                        frozen
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        footer="Celkovo"
                        filter
                        filterElement={
                          <Dropdown
                            value={filtersRepairCostLeft.customer.value}
                            options={customerOptions}
                            onChange={(e) =>
                              setFiltersRepairCostLeft((prev) => ({
                                ...prev,
                                customer: { value: e.value, matchMode: "equals" },
                              }))
                            }
                            placeholder={t('OTKEvaluation.VyberZakaznika')}
                            className="w-full h-[42px]"
                          />
                        }
                      />

                      <Column
                        field="revenue"
                        header={t('OTKEvaluation.DeliveredAmountCurrency', { currency: currencySymbol })}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => FORMAT_CURRENCY(r.revenue)}
                        footer={FORMAT_CURRENCY(totalsRepairCostLeft.revenue)}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersRepairCostLeft.revenue.value}
                            onChange={(val) =>
                              setFiltersRepairCostLeft((prev) => ({
                                ...prev,
                                revenue: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="costEUR"
                        header={t('OTKEvaluation.RepairCostsCurrency', { currency: currencySymbol })}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={renderClickableDTCell({
                          field: "costEUR",
                          format: FORMAT_CURRENCY,
                          side: "L",
                          openFn: handleOpenRepairCost,
                        })}
                        footer={FORMAT_CURRENCY(totalsRepairCostLeft.costEUR)}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersRepairCostLeft.costEUR.value}
                            onChange={(val) =>
                              setFiltersRepairCostLeft((prev) => ({
                                ...prev,
                                costEUR: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="costPct"
                        header={t('OTKEvaluation.NakladyNaOpravy2')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => `${r.costPct.toFixed(2)} %`}
                        footer={`${totalsRepairCostLeft.costPct.toFixed(2)} %`}
                        filterElement={
                          <SearchFilterInput
                            value={filtersRepairCostLeft.costPct.value}
                            onChange={(val) =>
                              setFiltersRepairCostLeft((prev) => ({
                                ...prev,
                                costPct: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </DataTable>
                    </div>

                    <div className="print-compare-cell">
                    <DataTable
                      value={repairCostRightRowsAll}
                      dataKey="_key"
                      scrollable
                      scrollHeight="500px"
                      removableSort
                      multiSortMeta={repairCostSortMeta}
                      onSort={(e) => setRepairCostSortMeta(e.multiSortMeta)}
                      sortMode="multiple"
                      filterDisplay="row"
                      filters={filtersRepairCostRight}
                      onFilter={(e) => setFiltersRepairCostRight(e.filters)}
                    >
                      <Column
                        field="customer"
                        header={t('Common.Customer')}
                        frozen
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        footer="Celkovo"
                        filter
                        filterElement={
                          <Dropdown
                            value={filtersRepairCostRight.customer.value}
                            options={customerOptions}
                            onChange={(e) =>
                              setFiltersRepairCostRight((prev) => ({
                                ...prev,
                                customer: { value: e.value, matchMode: "equals" },
                              }))
                            }
                            placeholder={t('OTKEvaluation.VyberZakaznika')}
                            className="w-full h-[42px]"
                          />
                        }
                      />

                      <Column
                        field="revenue"
                        header={t('OTKEvaluation.DeliveredAmountCurrency', { currency: currencySymbol })}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => FORMAT_CURRENCY(r.revenue)}
                        footer={FORMAT_CURRENCY(totalsRepairCostRight.revenue)}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersRepairCostRight.revenue.value}
                            onChange={(val) =>
                              setFiltersRepairCostRight((prev) => ({
                                ...prev,
                                revenue: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="costEUR"
                        header={t('OTKEvaluation.RepairCostsCurrency', { currency: currencySymbol })}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={renderClickableDTCell({
                          field: "costEUR",
                          format: FORMAT_CURRENCY,
                          side: "R",
                          openFn: handleOpenRepairCost,
                        })}
                        footer={FORMAT_CURRENCY(totalsRepairCostRight.costEUR)}
                        filterElement={
                          <CurrencyFilterInput
                            value={filtersRepairCostRight.costEUR.value}
                            onChange={(val) =>
                              setFiltersRepairCostRight((prev) => ({
                                ...prev,
                                costEUR: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />

                      <Column
                        field="costPct"
                        header={t('OTKEvaluation.NakladyNaOpravy2')}
                        sortable
                        showFilterMenu={false}
                        headerStyle={{ background: "var(--surface-100)" }}
                        footerStyle={{ background: "var(--surface-100)" }}
                        filterHeaderStyle={{ background: "var(--surface-100)" }}
                        filter
                        body={(r) => `${r.costPct.toFixed(2)} %`}
                        footer={`${totalsRepairCostRight.costPct.toFixed(2)} %`}
                        filterElement={
                          <SearchFilterInput
                            value={filtersRepairCostRight.costPct.value}
                            onChange={(val) =>
                              setFiltersRepairCostRight((prev) => ({
                                ...prev,
                                costPct: { value: val, matchMode: "contains" },
                              }))
                            }
                          />
                        }
                      />
                    </DataTable>
                    </div>
                  </div>
                </>
              )}
              <div className={`mt-4 print-charts-section ${compareRepairCostMode ? 'print-charts-compare' : ''}`}>
                <div className="flex items-center gap-2 w-full no-print">
                  <span className="font-bold">Grafy</span>
                  <InputSwitch
                    checked={showRepairCostCharts}
                    onChange={(e) => setShowRepairCostCharts(e.value)}
                  />
                </div>

                {showRepairCostCharts ? (
                  <div className="mt-3 grid grid-cols-1 gap-4 print-chart-row">
                    {!compareRepairCostMode ? (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Card title={t('OTKEvaluation.RepairCostCurrencyLeft', { currency: currencySymbol, range: fmtMonthRange(repairCostLeftRange) })} className="print-chart-item">
                          <Chart
                            type="bar"
                            data={{
                              labels: repairCostLeftRowsVisible.map((r) => r.customer),
                              datasets: [
                                {
                                  label: t('OTKEvaluation.NakladyNaOpravy'),
                                  data: repairCostLeftRowsVisible.map((r) => num(r.costEUR)),
                                },
                              ],
                            }}
                            options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                          />
                        </Card>

                        <Card title={t('OTKEvaluation.RepairCostPctLeft', { range: fmtMonthRange(repairCostLeftRange) })} className="print-chart-item">
                          <Chart
                            type="bar"
                            data={{
                              labels: repairCostLeftRowsVisible.map((r) => r.customer),
                              datasets: [
                                {
                                  label: t('OTKEvaluation.Naklady'),
                                  data: repairCostLeftRowsVisible.map((r) => num(r.costPct)),
                                },
                              ],
                            }}
                            options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                          />
                        </Card>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <Card title={t('OTKEvaluation.RepairCostCurrencyLeft', { currency: currencySymbol, range: fmtMonthRange(repairCostLeftRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: repairCostLeftRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.NakladyNaOpravy'),
                                    data: repairCostLeftRowsVisible.map((r) => num(r.costEUR)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>

                          <Card title={t('OTKEvaluation.RepairCostCurrencyRight', { currency: currencySymbol, range: fmtMonthRange(repairCostRightRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: repairCostRightRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.NakladyNaOpravy'),
                                    data: repairCostRightRowsVisible.map((r) => num(r.costEUR)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <Card title={t('OTKEvaluation.RepairCostPctLeft', { range: fmtMonthRange(repairCostLeftRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: repairCostLeftRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.Naklady'),
                                    data: repairCostLeftRowsVisible.map((r) => num(r.costPct)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>

                          <Card title={t('OTKEvaluation.RepairCostPctRight', { range: fmtMonthRange(repairCostRightRange) })} className="print-chart-item">
                            <Chart
                              type="bar"
                              data={{
                                labels: repairCostRightRowsVisible.map((r) => r.customer),
                                datasets: [
                                  {
                                    label: t('OTKEvaluation.Naklady'),
                                    data: repairCostRightRowsVisible.map((r) => num(r.costPct)),
                                  },
                                ],
                              }}
                              options={printChartFontSize ? getChartOptionsForPrint(printChartFontSize) : undefined}
                            />
                          </Card>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </Card>
            </div>

            <Divider/>
            <div ref={refTrendPeriod} className="print-full">
           <Card
            title={
              <div className="flex justify-between items-center w-full">
                <span>{t('CrmEval.Section.Trend')}</span>

                <Button
                  icon={
                    Object.keys(expandedTrend).length > 0 ? "pi pi-angle-down" : "pi pi-angle-right"
                  }
                  className="p-button-text p-button-sm no-print"
                  tooltip={
                    Object.keys(expandedTrend).length > 0 ? t('OTKEvaluation.CollapseAll') : t('OTKEvaluation.RozbalitVsetky')
                  }
                  tooltipOptions={{ position: "left" }}
                  onClick={() => {
                    const hasExpanded = Object.keys(expandedTrend).length > 0;
                    if (hasExpanded) {
                      setExpandedTrend({});
                    } else {
                      setExpandTrendDialogVisible(true);
                    }
                  }}
                />
              </div>
            }
          >
            <div className="flex flex-wrap items-center gap-4 mt-2">
              <div className="flex items-center gap-2 no-print">
                <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Zobrazit')}</span>
                <Dropdown
                  value={trendPeriodMode}
                  options={TREND_PERIOD_MODE_OPTIONS}
                  onChange={(e) => {
                    setTrendPeriodMode(e.value);
                    setExpandedTrend({});
                  }}
                  className="w-[160px] h-[38px] items-center border border-gray-400 rounded-md"
                />
              </div>

              <div className="flex items-center gap-2 no-print">
                <span className="text-sm text-gray-700 font-medium">Obdobie:</span>
                <Calendar
                  value={trendRangeUI}
                  onChange={(e) => {
                    handleTrendRangeChange(e);
                    setExpandedTrend({});
                  }}
                  selectionMode="range"
                  view={trendPeriodMode === "year" ? "year" : "month"}
                  dateFormat={trendPeriodMode === "year" ? "yy" : "mm/yy"}
                  showIcon
                  readOnlyInput
                  hideOnRangeSelection={false}
                  className="w-[220px] border border-gray-400 rounded-md"
                />
              </div>

              <div className="flex items-center gap-2 no-print">
                <span className="text-sm text-gray-700 font-medium">Zobrazenie:</span>
                <Dropdown
                  value={trendTableMode}
                  options={[
                    { label: t('OTKEvaluation.CountsPlusCurrency', { currency: currencySymbol }), value: "abs" },
                    { label: t('OTKEvaluation.Percenta'), value: "pct" },
                  ]}
                  onChange={(e) => setTrendTableMode(e.value)}
                  className="w-[180px] h-[38px] items-center border border-gray-400 rounded-md"
                />
              </div>
            </div>

            <div className="mt-4">
              <TreeTable
                value={trendFilteredTree}
                dataKey="key"
                expandedKeys={expandedTrend}
                onToggle={(e) => setExpandedTrend(e.value)}
                onRowClick={(e) => setTrendActiveNode(e.node)}
                filters={filtersTrend}
                onFilter={(e) => setFiltersTrend(e.filters)}
                filterMode="lenient"
                resizableColumns
                columnResizeMode="expand"
                rowClassName={trendRowClassName}
                tableStyle={{ minWidth: TT_MINW, tableLayout: "auto", width: "100%" }}
                scrollable={!isPrintMode}
                scrollHeight={isPrintMode ? undefined : "500px"}
                scrollDirection={isPrintMode ? undefined : "both"}
                sortMode="multiple"
                multiSortMeta={trendSortMeta}
                onSort={(e) => setTrendSortMeta(e.multiSortMeta)}
                removableSort
              >
                <Column
                  expander
                  field="departmentName"
                  sortField="periodKey"
                  header={trendPeriodMode === "year" ? t('OTKEvaluation.YearCustomer') : t('OTKEvaluation.ObdobieZakaznik')}
                  style={{ minWidth: WTrend.nameMin, width: WTrend.nameMin }}
                  body={trendNameBodyTemplate}
                  headerStyle={TREND_HD_FIRST}
                  filterHeaderStyle={TREND_HD_FIRST}
                  footerStyle={TREND_FT_FIRST}
                  filter
                  sortable
                  pt={TREND_PT_HEADER}
                  showFilterMenu={false}
                  footer="Celkovo"
                  filterMatchMode="equals"
                  filterElement={
                    <Dropdown
                      value={filtersTrend.departmentName?.value ?? null}
                      options={trendCustomerNameOptions}
                      onChange={(e) =>
                        setFiltersTrend((prev) => ({
                          ...prev,
                          departmentName: { value: e.value, matchMode: "equals" },
                        }))
                      }
                      placeholder={t('OTKEvaluation.VyberZakaznika')}
                      className="w-full h-[42px]"
                      showClear
                    />
                  }
                />

                <Column
                  field="ordersTotal"
                  header={t('OTKEvaluation.DodZakazkyPc')}
                  style={{ width: WTrend.num, minWidth: WTrend.num }}
                  headerStyle={TREND_HD}
                  filterHeaderStyle={TREND_HD}
                  footerStyle={TREND_FT}
                  body={(node) => FORMAT_INT(node?.data?.ordersTotal ?? 0)}
                  filter
                  sortable
                  pt={TREND_PT_HEADER}
                  showFilterMenu={false}
                  footer={FORMAT_INT(totalsTrend.ordersTotal)}
                  filterElement={
                    <SearchFilterInput
                      value={filtersTrend.ordersTotal?.value}
                      onChange={(val) =>
                        setFiltersTrend((p) => ({ ...p, ordersTotal: { value: val, matchMode: "contains" } }))
                      }
                    />
                  }
                />

                {trendTableMode === "abs" ? (
                  <Column
                    field="ordersCount"
                    header={t('OTKEvaluation.ReklZakazkyPc')}
                    style={{ width: WTrend.num, minWidth: WTrend.num }}
                    headerStyle={TREND_HD}
                    filterHeaderStyle={TREND_HD}
                    footerStyle={TREND_FT}
                    body={renderTrendClickableInt("ordersCount")}
                    filter
                    sortable
                    pt={TREND_PT_HEADER}
                    showFilterMenu={false}
                    footer={FORMAT_INT(totalsTrend.ordersCount)}
                    filterElement={
                      <SearchFilterInput
                        value={filtersTrend.ordersCount?.value}
                        onChange={(val) =>
                          setFiltersTrend((p) => ({ ...p, ordersCount: { value: val, matchMode: "contains" } }))
                        }
                      />
                    }
                  />
                ) : (
                  <Column
                    field="ordersPct"
                    header={t('OTKEvaluation.ReklZakazky')}
                    style={{ width: WTrend.num, minWidth: WTrend.num }}
                    headerStyle={TREND_HD}
                    filterHeaderStyle={TREND_HD}
                    footerStyle={TREND_FT}
                    body={renderTrendClickablePct("ordersPct")}
                    filter
                    sortable
                    pt={TREND_PT_HEADER}
                    showFilterMenu={false}
                    footer={FORMAT_PCT(totalsTrend.ordersPct)}
                    filterElement={
                      <SearchFilterInput
                        value={filtersTrend.ordersPct?.value}
                        onChange={(val) =>
                          setFiltersTrend((p) => ({ ...p, ordersPct: { value: val, matchMode: "contains" } }))
                        }
                      />
                    }
                  />
                )}

                <Column
                  field="partsTotal"
                  header={t('OTKEvaluation.DodKusyPc')}
                  style={{ width: WTrend.num, minWidth: WTrend.num }}
                  headerStyle={TREND_HD}
                  filterHeaderStyle={TREND_HD}
                  footerStyle={TREND_FT}
                  body={(node) => FORMAT_INT(node?.data?.partsTotal ?? 0)}
                  filter
                  sortable
                  pt={TREND_PT_HEADER}
                  showFilterMenu={false}
                  footer={FORMAT_INT(totalsTrend.partsTotal)}
                  filterElement={
                    <SearchFilterInput
                      value={filtersTrend.partsTotal?.value}
                      onChange={(val) =>
                        setFiltersTrend((p) => ({ ...p, partsTotal: { value: val, matchMode: "contains" } }))
                      }
                    />
                  }
                />

                {trendTableMode === "abs" ? (
                  <Column
                    field="partsCount"
                    header={t('OTKEvaluation.ReklKusyPc')}
                    style={{ width: WTrend.num, minWidth: WTrend.num }}
                    headerStyle={TREND_HD}
                    filterHeaderStyle={TREND_HD}
                    footerStyle={TREND_FT}
                    body={renderTrendClickableInt("partsCount")}
                    filter
                    sortable
                    pt={TREND_PT_HEADER}
                    showFilterMenu={false}
                    footer={FORMAT_INT(totalsTrend.partsCount)}
                    filterElement={
                      <SearchFilterInput
                        value={filtersTrend.partsCount?.value}
                        onChange={(val) =>
                          setFiltersTrend((p) => ({ ...p, partsCount: { value: val, matchMode: "contains" } }))
                        }
                      />
                    }
                  />
                ) : (
                  <Column
                    field="partsPct"
                    header="Rekl. kusy (%)"
                    style={{ width: WTrend.num, minWidth: WTrend.num }}
                    headerStyle={TREND_HD}
                    filterHeaderStyle={TREND_HD}
                    footerStyle={TREND_FT}
                    body={renderTrendClickablePct("partsPct")}
                    filter
                    sortable
                    pt={TREND_PT_HEADER}
                    showFilterMenu={false}
                    footer={FORMAT_PCT(totalsTrend.partsPct)}
                    filterElement={
                      <SearchFilterInput
                        value={filtersTrend.partsPct?.value}
                        onChange={(val) =>
                          setFiltersTrend((p) => ({ ...p, partsPct: { value: val, matchMode: "contains" } }))
                        }
                      />
                    }
                  />
                )}

                <Column
                  field="revenue"
                  header={t('OTKEvaluation.DelAmountCurrency', { currency: currencySymbol })}
                  style={{ width: WTrend.num, minWidth: WTrend.num }}
                  headerStyle={TREND_HD}
                  filterHeaderStyle={TREND_HD}
                  footerStyle={TREND_FT}
                  body={(node) => FORMAT_CURRENCY(node?.data?.revenue ?? 0)}
                  filter
                  sortable
                  pt={TREND_PT_HEADER}
                  showFilterMenu={false}
                  footer={FORMAT_CURRENCY(totalsTrend.revenue)}
                  filterElement={
                    <CurrencyFilterInput
                      value={filtersTrend.revenue?.value}
                      onChange={(val) =>
                        setFiltersTrend((p) => ({ ...p, revenue: { value: val, matchMode: "contains" } }))
                      }
                    />
                  }
                />

                {trendTableMode === "abs" ? (
                  <Column
                    field="claimsCost"
                    header={t('OTKEvaluation.RepairCostsShortCurrency', { currency: currencySymbol })}
                    style={{ width: WTrend.num, minWidth: WTrend.num }}
                    headerStyle={TREND_HD}
                    filterHeaderStyle={TREND_HD}
                    footerStyle={TREND_FT}
                    body={renderTrendClickableCurrency("claimsCost")}
                    filter
                    sortable
                    pt={TREND_PT_HEADER}
                    showFilterMenu={false}
                    footer={FORMAT_CURRENCY(totalsTrend.claimsCost)}
                    filterElement={
                      <CurrencyFilterInput
                        value={filtersTrend.claimsCost?.value}
                        onChange={(val) =>
                          setFiltersTrend((p) => ({ ...p, claimsCost: { value: val, matchMode: "contains" } }))
                        }
                      />
                    }
                  />
                ) : (
                  <Column
                    field="costPct"
                    header={t('OTKEvaluation.NaklNaOpravy')}
                    style={{ width: WTrend.num, minWidth: WTrend.num }}
                    headerStyle={TREND_HD}
                    filterHeaderStyle={TREND_HD}
                    footerStyle={TREND_FT}
                    body={renderTrendClickablePct("costPct")}
                    filter
                    sortable
                    pt={TREND_PT_HEADER}
                    showFilterMenu={false}
                    footer={FORMAT_PCT(totalsTrend.costPct)}
                    filterElement={
                      <SearchFilterInput
                        value={filtersTrend.costPct?.value}
                        onChange={(val) =>
                          setFiltersTrend((p) => ({ ...p, costPct: { value: val, matchMode: "contains" } }))
                        }
                      />
                    }
                  />
                )}
              </TreeTable>
            </div>

            <div className="mt-4">
            <div className="flex flex-col gap-4">
              {trendCharts.map((ch, idx) => (
                <div
                  key={ch.id}
                  className="border border-[var(--surface-200)] rounded-lg bg-white p-3 print-chart-item"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3 no-print">
                    <div className="font-semibold text-slate-800">
                      Graf {idx + 1}
                    </div>
                    {idx > 0 && (
                      <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger p-button-sm"
                        onClick={() => removeTrendChart(ch.id)}
                        tooltip={t('OTKEvaluation.OdstranitGraf')}
                        tooltipOptions={{ position: "top" }}
                      />
                    )}
                  </div>

                  <div className="print-trend-chart-info mb-2 text-xs text-gray-700" style={{ display: "none" }}>
                    <span><strong>Zobrazenie:</strong> {TREND_DISPLAY_OPTIONS.find((o) => o.value === ch.displayType)?.label ?? ch.displayType}</span>
                    {" · "}
                    <span><strong>Typ:</strong> {TREND_CHART_TYPE_OPTIONS.find((o) => o.value === (ch.chartType ?? "bar"))?.label ?? t('OTKEvaluation.Stlpcovy')}</span>
                    {" · "}
                    <span><strong>Metriky:</strong> {(ch.series || []).map((v) => TREND_METRIC_LABELS[v] ?? v).join(", ") || "—"}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mb-3 no-print">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-medium">Zobrazenie:</span>
                      <Dropdown
                        value={ch.displayType}
                        options={TREND_DISPLAY_OPTIONS}
                        onChange={(e) => setTrendChartDisplayType(ch.id, e.value)}
                        className="w-[180px] h-[38px] items-center border border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-medium">Typ grafu:</span>
                      <Dropdown
                        value={ch.chartType ?? "bar"}
                        options={TREND_CHART_TYPE_OPTIONS}
                        onChange={(e) => setTrendChartChartType(ch.id, e.value)}
                        className="w-[140px] h-[38px] items-center border border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-medium">Metriky:</span>
                      <MultiSelect
                        value={ch.series}
                        options={getMetricsForDisplayType(ch.displayType)}
                        onChange={(e) => setTrendChartSeries(ch.id, e.value)}
                        className="w-[420px] h-[38px] items-center border border-gray-400 rounded-md"
                        maxSelectedLabels={3}
                        selectedItemsLabel={t('OTKEvaluation.0Metrik')}
                        placeholder={t('OTKEvaluation.VyberMetriky')}
                      />
                    </div>
                  </div>

                  <div className="h-[420px]">
                    <Chart
                      type={ch.chartType ?? "bar"}
                      style={{ width: "100%", height: "100%" }}
                      data={trendChartsDataAndOptions[idx]?.data}
                      options={trendChartsDataAndOptions[idx]?.options}
                    />
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between gap-3 pt-2 no-print">
                <span className="text-sm text-gray-600">
                  Grafy ({trendCharts.length}/{TREND_MAX_CHARTS})
                </span>
                <Button
                  label={t('OTKEvaluation.PridatGraf')}
                  icon="pi pi-plus"
                  className="p-button-sm p-3"
                  disabled={trendCharts.length >= TREND_MAX_CHARTS}
                  onClick={addTrendChart}
                />
              </div>
            </div>
          </div>
          </Card>
            </div>
            </div>
          </TabPanel>
        </TabView>
      </div>

      {/* Tlač – dialóg (Tab1 alebo Tab2 podľa toho, kde bolo kliknuté) */}
      <Dialog
        header={printDialogTab === 0 ? t('OTKEvaluation.PrintDeptEval') : t('OTKEvaluation.TlacVyhodnotenieReklamacii')}
        visible={printDialogVisible}
        style={{ width: "32rem" }}
        modal
        onHide={() => setPrintDialogVisible(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label={t('Common.Cancel')} icon="pi pi-times" className="p-button-text" onClick={() => setPrintDialogVisible(false)} />
            <Button
              label={t('Common.PrintAction')}
              icon="pi pi-print"
              disabled={printSelection.length === 0}
              onClick={() => {
                setPrintDialogVisible(false);
                setTimeout(() => handlePrint(), 350);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3 mt-3">
          {printDialogTab === 0 ? (
            <>
              <div className="flex items-center gap-2 cursor-pointer">
                <Checkbox inputId="printAll1" checked={printSelection.includes("all")} onChange={(e) => setPrintSelection(e.checked ? ["all"] : [])} />
                <label htmlFor="printAll1" className="cursor-pointer">{t('Ui.R67.Tlacit_vsetky_tabulky_oddelenia')}</label>
              </div>
              <Divider />
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printDeptCounts" checked={printSelection.includes("deptCounts")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["deptCounts"] : prev.includes("deptCounts") ? prev : [...prev, "deptCounts"]) : prev.filter(k => k !== "deptCounts"))} />
                  <label htmlFor="printDeptCounts" className="cursor-pointer">{t('Ui.R67.Pocet_zakazok_za_oddelenie')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printDeptPieces" checked={printSelection.includes("deptPieces")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["deptPieces"] : prev.includes("deptPieces") ? prev : [...prev, "deptPieces"]) : prev.filter(k => k !== "deptPieces"))} />
                  <label htmlFor="printDeptPieces" className="cursor-pointer">{t('Ui.R67.Pocet_kusov_za_oddelenie')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printDeptCosts" checked={printSelection.includes("deptCosts")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["deptCosts"] : prev.includes("deptCosts") ? prev : [...prev, "deptCosts"]) : prev.filter(k => k !== "deptCosts"))} />
                  <label htmlFor="printDeptCosts" className="cursor-pointer">{t('OTKEvaluation.NakladyZaOddelenie')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printRootCauseCounts" checked={printSelection.includes("rootCauseCounts")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["rootCauseCounts"] : prev.includes("rootCauseCounts") ? prev : [...prev, "rootCauseCounts"]) : prev.filter(k => k !== "rootCauseCounts"))} />
                  <label htmlFor="printRootCauseCounts" className="cursor-pointer">{t('Ui.R67.Korenova_pricina_voci_oddeleniu_a_zamestnancovi')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printTypeGroup" checked={printSelection.includes("typeGroup")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["typeGroup"] : prev.includes("typeGroup") ? prev : [...prev, "typeGroup"]) : prev.filter(k => k !== "typeGroup"))} />
                  <label htmlFor="printTypeGroup" className="cursor-pointer">{t('Ui.R67.Druh_chyby_voci_skupine_chyb')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printGroupType" checked={printSelection.includes("groupType")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["groupType"] : prev.includes("groupType") ? prev : [...prev, "groupType"]) : prev.filter(k => k !== "groupType"))} />
                  <label htmlFor="printGroupType" className="cursor-pointer">{t('Ui.R67.Skupina_chyb_voci_druhu_chyb')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printDeptType" checked={printSelection.includes("deptType")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["deptType"] : prev.includes("deptType") ? prev : [...prev, "deptType"]) : prev.filter(k => k !== "deptType"))} />
                  <label htmlFor="printDeptType" className="cursor-pointer">{t('Ui.R67.Oddelenie_voci_druhu_chyb')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printDeptGroup" checked={printSelection.includes("deptGroup")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["deptGroup"] : prev.includes("deptGroup") ? prev : [...prev, "deptGroup"]) : prev.filter(k => k !== "deptGroup"))} />
                  <label htmlFor="printDeptGroup" className="cursor-pointer">{t('Ui.R67.Oddelenie_voci_skupine_chyb')}</label>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 cursor-pointer">
                <Checkbox inputId="printAll2" checked={printSelection.includes("all")} onChange={(e) => setPrintSelection(e.checked ? ["all"] : [])} />
                <label htmlFor="printAll2" className="cursor-pointer">{t('Ui.R67.Tlacit_vsetky_tabulky_reklamacie')}</label>
              </div>
              <Divider />
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printClaimedOrders" checked={printSelection.includes("claimedOrders")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["claimedOrders"] : prev.includes("claimedOrders") ? prev : [...prev, "claimedOrders"]) : prev.filter(k => k !== "claimedOrders"))} />
                  <label htmlFor="printClaimedOrders" className="cursor-pointer">{t('Ui.R67.Reklamovane_zakazky_za_zvolene_obdobie')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printClaimedPieces" checked={printSelection.includes("claimedPieces")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["claimedPieces"] : prev.includes("claimedPieces") ? prev : [...prev, "claimedPieces"]) : prev.filter(k => k !== "claimedPieces"))} />
                  <label htmlFor="printClaimedPieces" className="cursor-pointer">{t('CrmEval.Section.ClaimedPieces')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printRepairCost" checked={printSelection.includes("repairCost")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["repairCost"] : prev.includes("repairCost") ? prev : [...prev, "repairCost"]) : prev.filter(k => k !== "repairCost"))} />
                  <label htmlFor="printRepairCost" className="cursor-pointer">{t('CrmEval.Section.RepairCost')}</label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <Checkbox inputId="printTrendPeriod" checked={printSelection.includes("trendPeriod")} onChange={(e) => setPrintSelection(prev => e.checked ? (prev.includes("all") ? ["trendPeriod"] : prev.includes("trendPeriod") ? prev : [...prev, "trendPeriod"]) : prev.filter(k => k !== "trendPeriod"))} />
                  <label htmlFor="printTrendPeriod" className="cursor-pointer">{t('CrmEval.Section.Trend')}</label>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Skrytý kontajner pre tlač viacerých tabuliek */}
      <div ref={printContainerRef} className="print-body absolute -left-[9999px] w-px h-px overflow-hidden flex flex-col gap-4 print:static print:w-auto print:h-auto print:overflow-visible" aria-hidden="true" />

      {/* Vytvoriť vlastnú tabuľku – dialóg (dočasne skryté) */}
      {SHOW_CUSTOM_TABLES && (
      <Dialog
        header={t('OTKEvaluation.VytvoritVlastnuTabulku')}
        visible={addTableDialogVisible}
        style={{ width: "40rem" }}
        modal
        onHide={() => setAddTableDialogVisible(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label={t('Common.Cancel')} icon="pi pi-times" className="p-button-text" onClick={() => setAddTableDialogVisible(false)} />
            <Button
              label={t('Common.Save')}
              icon="pi pi-check"
              onClick={handleSaveNewTable}
              disabled={!newTableConfig.title || !newTableConfig.columns.length}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="block font-medium mb-1">{t('Ui.R67.Nazov_tabulky')}</label>
            <input
              type="text"
              value={newTableConfig.title}
              onChange={(e) => setNewTableConfig((p) => ({ ...p, title: e.target.value }))}
              className="p-inputtext p-component w-full"
              placeholder={t('OTKEvaluation.ZadajteNazov')}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Typ zobrazenia</label>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioButton
                  inputId="typeTable"
                  name="viewType"
                  value="table"
                  onChange={(e) => setNewTableConfig((p) => ({ ...p, type: e.value }))}
                  checked={newTableConfig.type === "table"}
                />
                <label htmlFor="typeTable">{t('OTKEvaluation.Tabulka')}</label>
              </div>
              <div className="flex items-center gap-2">
                <RadioButton
                  inputId="typeChart"
                  name="viewType"
                  value="chart"
                  onChange={(e) => setNewTableConfig((p) => ({ ...p, type: e.value }))}
                  checked={newTableConfig.type === "chart"}
                />
                <label htmlFor="typeChart">Graf</label>
              </div>
            </div>
          </div>

          <div>
            <label className="block font-medium mb-1">Zdroj údajov</label>
            <Dropdown
              value={newTableConfig.source}
              options={[
                { label: t('OTKEvaluation.PoctyZaOddelenie'), value: "byDept" },
                { label: t('OTKEvaluation.DruhChybyVociSkupine'), value: "byTypeGroup" },
                { label: "Druh chyby za oddelenie", value: "byTypeDept" },
                { label: t('OTKEvaluation.ZaznamyChybErrorRecords'), value: "error_records" },
                { label: t('OTKEvaluation.StatistikaZakaznikov'), value: "companyStats" },
              ]}
              onChange={(e) => {
                const sample = getDataBySource(e.value)[0] || {};
                const availableCols = Object.keys(sample);
                setNewTableConfig((p) => ({
                  ...p,
                  source: e.value,
                  columns: availableCols.slice(0, 3),
                }));
              }}
              placeholder={t('OTKEvaluation.VyberZdrojDat')}
              className="w-full"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t('Ui.R67.Zobrazene_stlpce')}</label>
            <MultiSelect
              value={newTableConfig.columns}
              options={Object.keys(getDataBySource(newTableConfig.source)[0] || {}).map((col) => ({ label: col, value: col }))}
              onChange={(e) => setNewTableConfig((p) => ({ ...p, columns: e.value }))}
              placeholder={t('OTKEvaluation.VyberStlpce')}
              display="chip"
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
      )}
      <Dialog
        visible={expandDialogVisible}
        onHide={() => setExpandDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetky')}
        style={{ width: '320px' }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          {/* 2 úrovne */}
          <Button
            label={t('OTKEvaluation.OddelenieZamestnanci2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              if (expandDialogTarget === 'counts') {
                const left = buildExpandedToDepth(countsLeftTree, 2);
                if (!compareCountsMode) {
                  setExpandedCountsLeft(left);
                } else {
                  const right = buildExpandedToDepth(countsRightTree, 2);
                  setExpandedCountsLeft(left);
                  setExpandedCountsRight(right);
                }
              } else if (expandDialogTarget === 'pieces') {
                const left = buildExpandedToDepth(piecesLeftTree, 2);
                if (!comparePiecesMode) {
                  setExpandedPiecesLeft(left);
                } else {
                  const right = buildExpandedToDepth(piecesRightTree, 2);
                  setExpandedPiecesLeft(left);
                  setExpandedPiecesRight(right);
                }
              } else if (expandDialogTarget === 'costs') {
                const left = buildExpandedToDepth(costsLeftTree, 2);
                if (!compareCostsMode) {
                  setExpandedCostsLeft(left);
                } else {
                  const right = buildExpandedToDepth(costsRightTree, 2);
                  setExpandedCostsLeft(left);
                  setExpandedCostsRight(right);
                }
              }

              setExpandDialogVisible(false);
              setExpandDialogTarget(null);
            }}
          />

          {/* 3 úrovne */}
          <Button
            label={t('OTKEvaluation.OddelenieZamestnanciArtikle3Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              if (expandDialogTarget === 'counts') {
                const left = buildExpandedToDepth(countsLeftTree, 3);
                if (!compareCountsMode) {
                  setExpandedCountsLeft(left);
                } else {
                  const right = buildExpandedToDepth(countsRightTree, 3);
                  setExpandedCountsLeft(left);
                  setExpandedCountsRight(right);
                }
              } else if (expandDialogTarget === 'pieces') {
                const left = buildExpandedToDepth(piecesLeftTree, 3);
                if (!comparePiecesMode) {
                  setExpandedPiecesLeft(left);
                } else {
                  const right = buildExpandedToDepth(piecesRightTree, 3);
                  setExpandedPiecesLeft(left);
                  setExpandedPiecesRight(right);
                }
              } else if (expandDialogTarget === 'costs') {
                const left = buildExpandedToDepth(costsLeftTree, 3);
                if (!compareCostsMode) {
                  setExpandedCostsLeft(left);
                } else {
                  const right = buildExpandedToDepth(costsRightTree, 3);
                  setExpandedCostsLeft(left);
                  setExpandedCostsRight(right);
                }
              }

              setExpandDialogVisible(false);
              setExpandDialogTarget(null);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        visible={expandRootCauseDialogVisible}
        onHide={() => setExpandRootCauseDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetky')}
        style={{ width: "360px" }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          {/* 2 úrovne = Koreňová príčina + oddelenia */}
          <Button
            label={t('OTKEvaluation.Oddelenia2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
                const leftExpanded = buildExpandedToDepth(rcLeftTree, 2);

                if (!compareRcMode) {
                  setExpandedRcLeft(leftExpanded);
                } else {
                  const rightExpanded = buildExpandedToDepth(rcRightTree, 2);
                  setExpandedRcLeft(leftExpanded);
                  setExpandedRcRight(rightExpanded);
                }

              setExpandRootCauseDialogVisible(false);
            }}
          />

          {/* 3 úrovne = Koreňová príčina + oddelenia + zamestnanci */}
          <Button
            label={t('OTKEvaluation.OddeleniaZamestnanci3Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
                const left = buildExpandedToDepth(rcLeftTree, 3);

                if (!compareRcMode) {
                  setExpandedRcLeft(left);
                } else {
                  const right = buildExpandedToDepth(rcRightTree, 3);
                  setExpandedRcLeft(left);
                  setExpandedRcRight(right);
                }

              setExpandRootCauseDialogVisible(false);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        visible={expandTypeGroupDialogVisible}
        onHide={() => setExpandTypeGroupDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetkyDruhChybyVsSkupina')}
        style={{ width: "360px" }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          <Button
            label={t('OTKEvaluation.DruhChybySkupiny2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(tgLeftTree, 2);

              if (!compareTypeGroupMode) {
                setExpandedTypeGroupLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(tgRightTree, 2);
                setExpandedTypeGroupLeft(leftExpanded);
                setExpandedTypeGroupRight(rightExpanded);
              }

              setExpandTypeGroupDialogVisible(false);
            }}
          />
          <Button
            label={t('OTKEvaluation.DruhChybySkupinyOddelenia3Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(tgLeftTree, 3);

              if (!compareTypeGroupMode) {
                setExpandedTypeGroupLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(tgRightTree, 3);
                setExpandedTypeGroupLeft(leftExpanded);
                setExpandedTypeGroupRight(rightExpanded);
              }

              setExpandTypeGroupDialogVisible(false);
            }}
          />
          <Button
            label={t('OTKEvaluation.DruhChybySkupinyOddeleniaZamestnanci4Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(tgLeftTree, 4);

              if (!compareTypeGroupMode) {
                setExpandedTypeGroupLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(tgRightTree, 4);
                setExpandedTypeGroupLeft(leftExpanded);
                setExpandedTypeGroupRight(rightExpanded);
              }

              setExpandTypeGroupDialogVisible(false);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        visible={expandGroupTypeDialogVisible}
        onHide={() => setExpandGroupTypeDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetkySkupinaVsDruhChyby')}
        style={{ width: "360px" }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          {/* 2 úrovne – Skupina + druhy chýb */}
          <Button
            label={t('OTKEvaluation.SkupinyDruhyChyb2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(gtLeftTree, 2);

              if (!compareGroupTypeMode) {
                setExpandedGroupTypeLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(gtRightTree, 2);
                setExpandedGroupTypeLeft(leftExpanded);
                setExpandedGroupTypeRight(rightExpanded);
              }

              setExpandGroupTypeDialogVisible(false);
            }}
          />

          {/* 3 úrovne – Skupiny + druhy chýb + oddelenia */}
          <Button
            label={t('OTKEvaluation.SkupinyDruhyChybOddelenia3Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(gtLeftTree, 3);

              if (!compareGroupTypeMode) {
                setExpandedGroupTypeLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(gtRightTree, 3);
                setExpandedGroupTypeLeft(leftExpanded);
                setExpandedGroupTypeRight(rightExpanded);
              }

              setExpandGroupTypeDialogVisible(false);
            }}
          />

          {/* 4 úrovne – Skupiny + druhy chýb + oddelenia + zamestnanci */}
          <Button
            label={t('OTKEvaluation.SkupinyDruhyChybOddeleniaZamestnanci4Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(gtLeftTree, 4);

              if (!compareGroupTypeMode) {
                setExpandedGroupTypeLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(gtRightTree, 4);
                setExpandedGroupTypeLeft(leftExpanded);
                setExpandedGroupTypeRight(rightExpanded);
              }

              setExpandGroupTypeDialogVisible(false);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        visible={expandTypeDeptDialogVisible}
        onHide={() => setExpandTypeDeptDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetkyOddelenieVsDruhChyb')}
        style={{ width: "360px" }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          {/* 2 úrovne – Oddelenia + druhy chýb */}
          <Button
            label={t('OTKEvaluation.OddeleniaDruhyChyb2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(tdLeftTree, 2);

              if (!compareTypeDeptMode) {
                // single režim – len ľavá tabuľka
                setExpandedTypeDeptLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(tdRightTree, 2);
                setExpandedTypeDeptLeft(leftExpanded);
                setExpandedTypeDeptRight(rightExpanded);
              }

              setExpandTypeDeptDialogVisible(false);
            }}
          />

          {/* 3 úrovne – Oddelenia + druhy chýb + zamestnanci */}
          <Button
            label={t('OTKEvaluation.OddeleniaDruhyChybZamestnanci3Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(tdLeftTree, 3);

              if (!compareTypeDeptMode) {
                setExpandedTypeDeptLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(tdRightTree, 3);
                setExpandedTypeDeptLeft(leftExpanded);
                setExpandedTypeDeptRight(rightExpanded);
              }

              setExpandTypeDeptDialogVisible(false);
            }}
          />

          {/* 4 úrovne – Oddelenia + druhy chýb + zamestnanci + zákazky / artikle */}
          <Button
            label={t('OTKEvaluation.OddeleniaDruhyChybZamestnanciZakazkyArtikle4Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(tdLeftTree, 4);

              if (!compareTypeDeptMode) {
                setExpandedTypeDeptLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(tdRightTree, 4);
                setExpandedTypeDeptLeft(leftExpanded);
                setExpandedTypeDeptRight(rightExpanded);
              }

              setExpandTypeDeptDialogVisible(false);
            }}
          />
        </div>
      </Dialog>
      {/* Dialog – rozbalenie stromu pre „Oddelenie voči skupine chýb“ */}
      <Dialog
        visible={expandDeptGroupDialogVisible}
        onHide={() => setExpandDeptGroupDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetkyOddelenieVsSkupinaChyb')}
        style={{ width: "360px" }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          {/* 2 úrovne – Oddelenia + skupiny */}
          <Button
            label={t('OTKEvaluation.OddeleniaSkupiny2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(dgLeftTree, 2);

              if (!compareDeptGroupMode) {
                setExpandedDeptGroupLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(dgRightTree, 2);
                setExpandedDeptGroupLeft(leftExpanded);
                setExpandedDeptGroupRight(rightExpanded);
              }

              setExpandDeptGroupDialogVisible(false);
            }}
          />

          {/* 3 úrovne – Oddelenia + skupiny + zamestnanci */}
          <Button
            label={t('OTKEvaluation.OddeleniaSkupinyZamestnanci3Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(dgLeftTree, 3);
              if (!compareDeptGroupMode) {
                setExpandedDeptGroupLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(dgRightTree, 3);
                setExpandedDeptGroupLeft(leftExpanded);
                setExpandedDeptGroupRight(rightExpanded);
              }
              setExpandDeptGroupDialogVisible(false);
            }}
          />

          {/* 4 úrovne – Oddelenia + skupiny + zamestnanci + zákazky/artikle */}
          <Button
            label={t('OTKEvaluation.OddeleniaSkupinyZamestnanciZakazkyArtikle4Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              const leftExpanded = buildExpandedToDepth(dgLeftTree, 4);

              if (!compareDeptGroupMode) {
                setExpandedDeptGroupLeft(leftExpanded);
              } else {
                const rightExpanded = buildExpandedToDepth(dgRightTree, 4);
                setExpandedDeptGroupLeft(leftExpanded);
                setExpandedDeptGroupRight(rightExpanded);
              }

              setExpandDeptGroupDialogVisible(false);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        visible={expandTrendDialogVisible}
        onHide={() => setExpandTrendDialogVisible(false)}
        header={t('OTKEvaluation.RozbalitVsetkyTrendChybovosti')}
        style={{ width: "360px" }}
        modal
      >
        <p className="mb-3 text-sm text-gray-600">{t('Ui.R67.Zvol_po_aku_uroven_chces_strom_rozbalit')}</p>

        <div className="flex flex-col gap-2">
          <Button
            label={t('OTKEvaluation.ObdobieZakaznici2Urovne')}
            className="p-button-text justify-start"
            onClick={() => {
              setExpandedTrend(buildExpandedToDepth(trendTree, 2));
              setExpandTrendDialogVisible(false);
            }}
          />
        </div>
      </Dialog>
      </>
      )}
    </div>
  );
}
